// P4M v2 — build 20260419-1402
import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "./supabase";
import * as db from "./p4m_db";

// ═══════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════
const uid = () => Math.random().toString(36).slice(2, 9);
const fmtDate = d => { try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); } catch { return d; } };
const fmtShort = d => { try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); } catch { return d; } };
const timeSince = ts => { const s = Math.floor((Date.now() - ts) / 1000); if (s < 60) return "just now"; if (s < 3600) return `${Math.floor(s / 60)}m ago`; if (s < 86400) return `${Math.floor(s / 3600)}h ago`; return `${Math.floor(s / 86400)}d ago`; };
const avCol = n => { const c = ["#E63946","#2563eb","#2ecc71","#f0b429","#7c5cbf","#0891b2","#e67e22","#be185d"]; let h = 0; for (let ch of (n||"?")) h = ((h<<5)-h)+ch.charCodeAt(0); return c[Math.abs(h)%c.length]; };
const inits = n => (n||"?").split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2);
const to99 = v => { const n=v||1; return n>10 ? Math.round(30+(n-1)*(69/99)) : Math.round(30+(n-1)*(69/9)); };

// ═══════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════
const SPORTS = [
  {e:"🏑",n:"Hockey"},{e:"⚽",n:"Football"},{e:"🏊",n:"Swimming"},
];
const JUNIOR_AGES = ["Under 8","Under 10","Under 12","Under 14","Under 16","Under 18"];
const COLOURS = [
  {n:"Red",h:"#E63946"},{n:"Crimson",h:"#dc2626"},{n:"Maroon",h:"#7f1d1d"},
  {n:"Orange",h:"#f97316"},{n:"Amber",h:"#f59e0b"},{n:"Gold",h:"#f0b429"},
  {n:"Yellow",h:"#eab308"},{n:"Lime",h:"#84cc16"},{n:"Green",h:"#22c55e"},
  {n:"Emerald",h:"#10b981"},{n:"Teal",h:"#14b8a6"},{n:"Cyan",h:"#06b6d4"},
  {n:"Sky",h:"#38bdf8"},{n:"Blue",h:"#3b82f6"},{n:"Royal",h:"#1d4ed8"},
  {n:"Navy",h:"#1e3a8a"},{n:"Indigo",h:"#6366f1"},{n:"Violet",h:"#8b5cf6"},
  {n:"Purple",h:"#a855f7"},{n:"Magenta",h:"#d946ef"},{n:"Pink",h:"#ec4899"},
  {n:"Claret",h:"#9d174d"},{n:"White",h:"#f1f5f9"},{n:"Silver",h:"#94a3b8"},
  {n:"Charcoal",h:"#374151"},{n:"Black",h:"#111827"},
];
const MEMBER_TERM = {
  Hockey:"Player", Football:"Player", Rugby:"Player",
  Tennis:"Player", Swimming:"Athlete", Netball:"Player",
  Cricket:"Player", Basketball:"Player", Other:"Member",
};

const ROLES = [
  {r:"admin",icon:"⚙️",name:"Club Admin",desc:"Full access — players, finances, settings, all teams"},
  {r:"lead_coach",icon:"🏆",name:"Lead Coach",desc:"Manage your team — players, profiles, training framework"},
  {r:"coach",icon:"📋",name:"Coach",desc:"Manage your team and player profiles"},
  {r:"captain",icon:"🎖️",name:"Captain",desc:"Team selection, team sheet and availability"},
  {r:"player",icon:"👤",name:"Player",desc:"Your profile, availability, ClubHub and payments"},
  {r:"parent",icon:"👨‍👧",name:"Parent / Player-Parent",desc:"View your child's profile and club information"},
];
const ATTRS_BY_SPORT = {
  Hockey: {
    physical:["Speed","Agility","Stamina","Strength","Power","Aerial"],
    technical:["First Touch","Passing","Shooting","Dribbling","Finishing","Marking","Tackling"],
    tactical:["Positioning","Decision Making","Pressing","Reading the Game","Set Plays","Composure"],
    mental:["Work Rate","Resilience","Communication","Leadership","Determination"]
  },
  "Hockey-GK": {
    physical:["Speed","Agility","Stamina","Strength","Power","Reflexes"],
    technical:["Shot Stopping","Distribution","Handling","Kicking","Sweeping","One-on-One"],
    tactical:["Positioning","Decision Making","Commanding Area","Reading the Game","Set Plays","Composure"],
    mental:["Work Rate","Resilience","Communication","Leadership","Concentration"]
  },
  Football: {
    physical:["Speed","Agility","Stamina","Strength","Power","Jumping"],
    technical:["First Touch","Passing","Shooting","Dribbling","Long Passing","Crossing","Long Shots","Finishing","Marking","Tackling"],
    tactical:["Positioning","Decision Making","Pressing","Reading the Game","Set Plays","Composure"],
    mental:["Work Rate","Resilience","Communication","Leadership","Determination"]
  },
  "Football-GK": {
    physical:["Speed","Agility","Stamina","Strength","Power","Reflexes"],
    technical:["Shot Stopping","Distribution","Handling","Kicking","Sweeping","Penalty Saving"],
    tactical:["Positioning","Decision Making","Commanding Area","Reading the Game","Set Plays","Composure"],
    mental:["Work Rate","Resilience","Communication","Leadership","Concentration"]
  },
  Swimming: {
    physical:["Endurance","Sprint Power","Flexibility","Strength","Recovery"],
    technical:["Stroke Technique","Turns","Starts","Underwater Phase","Breathing"],
    tactical:["Pacing","Race Strategy","Splits","Taper Management","Race Awareness"],
    mental:["Composure","Resilience","Determination","Mental Toughness","Consistency"]
  },
  Rugby: {
    physical:["Speed","Strength","Stamina","Agility","Power"],
    technical:["Handling","Passing","Kicking","Tackling","Lineout"],
    tactical:["Positioning","Decision Making","Pressing","Reading the Game","Set Plays"],
    mental:["Composure","Resilience","Work Rate","Communication","Leadership"]
  },
  Tennis: {
    physical:["Speed","Strength","Stamina","Agility","Power"],
    technical:["Serve","Forehand","Backhand","Volley","Court Coverage"],
    tactical:["Positioning","Decision Making","Pressing","Reading the Game","Net Play"],
    mental:["Composure","Resilience","Work Rate","Communication","Leadership"]
  },
  default: {
    physical:["Speed","Strength","Stamina","Agility","Power"],
    technical:["First Touch","Passing","Shooting","Dribbling","Technique"],
    tactical:["Positioning","Decision Making","Pressing","Reading the Game","Set Plays"],
    mental:["Composure","Resilience","Work Rate","Communication","Leadership"]
  }
};
const ATTRS = ATTRS_BY_SPORT["Hockey"];
const ALL_ATTRS = Object.values(ATTRS).flat();
const ATTR_COLS = {physical:"#E63946",technical:"#38bdf8",tactical:"#22c55e",mental:"#f0b429"};
const CARD_MAP = {spd:"Speed",str:"Strength",sta:"Stamina",def:"Defending",att:"Attacking",sho:"Shooting",pos:"Positioning",wkr:"Work Rate"};

// Composite card stats — each is an array of attributes averaged together (FIFA-style)
const CARD_COMPOSITE = {
  Hockey: {
    spd: ["Speed","Agility"],
    sta: ["Stamina","Work Rate","Determination"],
    att: ["First Touch","Dribbling","Passing","Finishing"],
    pos: ["Positioning","Decision Making","Reading the Game","Set Plays"],
    str: ["Strength","Power","Aerial"],
    def: ["Marking","Tackling","Pressing","Positioning","Work Rate","Resilience"],
    sho: ["Shooting","Finishing","Composure"],
    wkr: ["Work Rate","Resilience","Communication","Leadership","Determination"],
  },
  "Hockey-GK": {
    spd: ["Speed","Agility","Reflexes"],                 // REF — Reflexes
    sta: ["Stamina","Concentration"],                    // STA
    att: ["Distribution","Passing"],                     // DIS — Distribution
    pos: ["Positioning","Commanding Area","Reading the Game"], // POS
    str: ["Strength","Power","Handling"],                // PHY
    def: ["Shot Stopping","One-on-One","Sweeping"],      // SPG — Shot Stopping
    sho: ["Kicking","Distribution","Composure"],         // KIC — Kicking
    wkr: ["Work Rate","Resilience","Communication","Leadership","Concentration"], // WRK
  },
  Football: {
    spd: ["Speed","Agility"],
    sta: ["Stamina","Work Rate","Determination"],
    att: ["First Touch","Dribbling","Crossing","Long Passing"],
    pos: ["Positioning","Decision Making","Reading the Game","Set Plays"],
    str: ["Strength","Power","Jumping"],
    def: ["Marking","Tackling","Pressing","Positioning","Work Rate","Resilience"],
    sho: ["Shooting","Long Shots","Finishing","Composure"],
    wkr: ["Work Rate","Resilience","Communication","Leadership","Determination"],
  },
  "Football-GK": {
    spd: ["Speed","Agility","Reflexes"],
    sta: ["Stamina","Concentration"],
    att: ["Distribution","Passing"],
    pos: ["Positioning","Commanding Area","Reading the Game"],
    str: ["Strength","Power","Handling"],
    def: ["Shot Stopping","Penalty Saving","Sweeping"],
    sho: ["Kicking","Distribution","Composure"],
    wkr: ["Work Rate","Resilience","Communication","Leadership","Concentration"],
  },
  Swimming: {
    spd: ["Sprint Power"],
    sta: ["Endurance"],
    att: ["Stroke Technique"],
    pos: ["Pacing"],
    str: ["Strength"],
    def: ["Turns"],
    sho: ["Starts"],
    wkr: ["Consistency"],
  },
};

// Helper: calculate composite card stat from scores
const calcCardStat = (scores, attrs) => {
  if (!scores || !attrs || attrs.length === 0) return 50;
  const vals = attrs.map(a => scores[a] || 50);
  const mean = vals.reduce((a,b)=>a+b,0) / vals.length;
  return Math.round(mean);
};

const CARD_MAP_BY_SPORT = {
  Hockey:   {spd:"Speed",str:"Strength",sta:"Stamina",def:"Defending",att:"Attacking",sho:"Shooting",pos:"Positioning",wkr:"Work Rate"},
  Football: {spd:"Speed",str:"Strength",sta:"Stamina",def:"Defending",att:"Attacking",sho:"Shooting",pos:"Positioning",wkr:"Work Rate"},
  Swimming: {spd:"Sprint Power",str:"Strength",sta:"Endurance",def:"Turns",att:"Stroke Technique",sho:"Starts",pos:"Pacing",wkr:"Consistency"},
};
const FIT_CFG_BY_SPORT = {
  Hockey: [
    {key:"bleep",    label:"Bleep Test",      unit:"level", higher:true,  color:"#E63946"},
    {key:"sprint30", label:"30m Sprint",       unit:"s",     higher:false, color:"#38bdf8"},
    {key:"agility",  label:"Illinois Agility", unit:"s",     higher:false, color:"#22c55e"},
    {key:"yo_yo",    label:"Yo-Yo Test",       unit:"level", higher:true,  color:"#f0b429"},
  ],
  Football: [
    {key:"bleep",    label:"Bleep Test",      unit:"level", higher:true,  color:"#E63946"},
    {key:"sprint40", label:"40m Sprint",       unit:"s",     higher:false, color:"#38bdf8"},
    {key:"agility",  label:"Agility Run",      unit:"s",     higher:false, color:"#22c55e"},
    {key:"vertical", label:"Vertical Jump",    unit:"cm",    higher:true,  color:"#f0b429"},
  ],
  Rugby: [
    {key:"bleep",    label:"Bleep Test",      unit:"level", higher:true,  color:"#E63946"},
    {key:"sprint40", label:"40m Sprint",       unit:"s",     higher:false, color:"#38bdf8"},
    {key:"bench",    label:"Bench Press",      unit:"kg",    higher:true,  color:"#22c55e"},
    {key:"broad",    label:"Broad Jump",       unit:"m",     higher:true,  color:"#f0b429"},
  ],
  Tennis: [
    {key:"sprint20", label:"20m Sprint",       unit:"s",     higher:false, color:"#E63946"},
    {key:"agility",  label:"Agility Run",      unit:"s",     higher:false, color:"#38bdf8"},
    {key:"serve",    label:"Serve Speed",       unit:"mph",   higher:true,  color:"#22c55e"},
    {key:"endurance",label:"Endurance Run",    unit:"min",   higher:true,  color:"#f0b429"},
  ],
  Swimming: [
    {key:"free50",   label:"50m Freestyle",   unit:"s",     higher:false, color:"#E63946"},
    {key:"free100",  label:"100m Freestyle",  unit:"s",     higher:false, color:"#38bdf8"},
    {key:"back50",   label:"50m Backstroke",  unit:"s",     higher:false, color:"#22c55e"},
    {key:"fly50",    label:"50m Butterfly",   unit:"s",     higher:false, color:"#f0b429"},
  ],
  default: [
    {key:"bleep",    label:"Bleep Test",      unit:"level", higher:true,  color:"#E63946"},
    {key:"sprint",   label:"Sprint",           unit:"s",     higher:false, color:"#38bdf8"},
    {key:"agility",  label:"Agility",          unit:"s",     higher:false, color:"#22c55e"},
    {key:"strength", label:"Strength Test",    unit:"kg",    higher:true,  color:"#f0b429"},
  ],
};
// Legacy compat
const FIT_CFG = {
  bleep:{label:"Bleep Test",unit:"level",higher:true,color:"#E63946"},
  sprint:{label:"Sprint",unit:"s",higher:false,color:"#38bdf8"},
  agility:{label:"Agility",unit:"s",higher:false,color:"#22c55e"}
};

// Wizard constants
const SEASON_GOALS = [
  "Gain promotion","Avoid relegation","Cement our place in the league",
  "Achieve a top half finish","Win a cup competition","Develop young players",
  "Build team cohesion","Establish a clear playing style",
  "Improve fitness and athleticism","Have fun and enjoy the season"
];
const SQUAD_DESCRIPTIONS = [
  "Experienced and established","Mix of experience and youth",
  "Young and developing","New players — building from scratch",
  "Strong individually but need work as a team"
];
const STRENGTHS_LIST = [
  "Attacking play","Pressing high up the pitch","Defending as a unit",
  "Set plays and penalty corners","Physicality and fitness",
  "Technical skill on the ball","Game management","Transitions — attack to defence",
  "Transitions — defence to attack","Mental resilience and fighting spirit"
];

// ═══════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Barlow+Condensed:wght@500;600;700;800;900&display=swap');
  * { margin:0; padding:0; box-sizing:border-box; -webkit-tap-highlight-color:transparent; }
  :root {
    --red: #E63946;
    --dark: #09090f;
    --darker: #0f0f18;
    --card: #14141f;
    --card2: #1a1a28;
    --border: rgba(255,255,255,0.07);
    --border2: rgba(255,255,255,0.12);
    --muted: rgba(255,255,255,0.38);
    --text: rgba(255,255,255,0.85);
    --white: #ffffff;
    --green: #22c55e;
    --gold: #f0b429;
    --blue: #3b82f6;
    --primary: #E63946;
    --primary2: #E63946;
  }
  html,body,#root { height:100%; overflow:hidden; background:var(--dark); color:var(--text); font-family:'Inter',sans-serif; }
  ::-webkit-scrollbar { width:0; }
  input,textarea,select { font-family:'Inter',sans-serif; }
  button { font-family:'Inter',sans-serif; }
`;

const G = {
  // layout
  app: { height:"100vh", display:"flex", flexDirection:"column", overflow:"hidden", background:"#09090f", color:"rgba(255,255,255,0.85)", fontFamily:"'Inter',sans-serif", position:"relative" },
  content: { flex:1, overflowY:"auto", WebkitOverflowScrolling:"touch" },
  panel: { padding:"20px 16px", paddingBottom:80 },
  row: { display:"flex", gap:10 },
  col: { display:"flex", flexDirection:"column", gap:10 },
  center: { display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" },
  // typography
  h1: { fontFamily:"'Barlow Condensed',sans-serif", fontSize:28, fontWeight:800, letterSpacing:0.5, color:"#fff", lineHeight:1.1 },
  h2: { fontFamily:"'Barlow Condensed',sans-serif", fontSize:22, fontWeight:700, letterSpacing:0.5, color:"#fff" },
  h3: { fontFamily:"'Barlow Condensed',sans-serif", fontSize:17, fontWeight:700, letterSpacing:0.5, color:"#fff" },
  label: { fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:2, color:"rgba(255,255,255,0.38)", marginBottom:6, display:"block" },
  muted: { fontSize:12, color:"rgba(255,255,255,0.38)" },
  // inputs
  input: { width:"100%", padding:"12px 14px", background:"rgba(255,255,255,0.04)", border:"1.5px solid rgba(255,255,255,0.1)", borderRadius:10, color:"#fff", fontSize:14, outline:"none", boxSizing:"border-box", transition:"border-color 0.2s" },
  textarea: { width:"100%", padding:"12px 14px", background:"rgba(255,255,255,0.04)", border:"1.5px solid rgba(255,255,255,0.1)", borderRadius:10, color:"#fff", fontSize:14, outline:"none", boxSizing:"border-box", resize:"none", lineHeight:1.6, fontFamily:"'Inter',sans-serif" },
  select: { width:"100%", padding:"12px 14px", background:"#1a1a2e", border:"1.5px solid rgba(255,255,255,0.1)", borderRadius:10, color:"#fff", fontSize:14, outline:"none", cursor:"pointer", appearance:"none", boxSizing:"border-box" },
  // buttons
  btnPrimary: { width:"100%", padding:"14px 20px", background:"#E63946", border:"none", borderRadius:12, color:"#fff", fontFamily:"'Barlow Condensed',sans-serif", fontSize:17, fontWeight:700, letterSpacing:1.5, cursor:"pointer", textTransform:"uppercase", transition:"all 0.2s" },
  btnGhost: { padding:"9px 16px", background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:9, color:"rgba(255,255,255,0.7)", fontSize:13, cursor:"pointer", transition:"all 0.2s", fontWeight:500 },
  btnSm: { padding:"7px 12px", background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:7, color:"rgba(255,255,255,0.6)", fontSize:12, cursor:"pointer" },
  // cards
  card: { background:"#14141f", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:16, marginBottom:10 },
  cardAccent: (col="#E63946") => ({ background:"#14141f", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:16, marginBottom:10, position:"relative", overflow:"hidden", borderTop:`3px solid ${col}` }),
  // header
  header: { display:"flex", alignItems:"center", padding:"12px 16px", background:"#0f0f18", borderBottom:"1px solid rgba(255,255,255,0.07)", flexShrink:0, gap:10, zIndex:10 },
  // nav
  nav: { display:"flex", background:"#0f0f18", borderTop:"1px solid rgba(255,255,255,0.07)", flexShrink:0 },
  navBtn: (active, col="#E63946") => ({ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"9px 4px", background:"none", border:"none", cursor:"pointer", color: active ? col : "rgba(255,255,255,0.35)", fontSize:10, fontWeight:600, gap:3, position:"relative", letterSpacing:0.5 }),
  // misc
  badge: (col) => ({ display:"inline-flex", alignItems:"center", justifyContent:"center", width:36, height:36, borderRadius:"50%", background:col, fontFamily:"'Barlow Condensed',sans-serif", fontSize:12, fontWeight:800, color:"#fff", flexShrink:0, overflow:"hidden" }),
  pill: (col="#E63946", bg) => ({ display:"inline-block", padding:"3px 10px", borderRadius:20, background:bg||`${col}22`, color:col, fontSize:11, fontWeight:600, letterSpacing:0.5 }),
  // modal
  overlay: { position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:900, display:"flex", alignItems:"flex-end", justifyContent:"center" },
  sheet: { width:"100%", maxWidth:520, maxHeight:"90vh", background:"#14141f", borderRadius:"20px 20px 0 0", padding:"20px 20px 40px", overflowY:"auto" },
  handle: { width:40, height:4, background:"rgba(255,255,255,0.12)", borderRadius:2, margin:"0 auto 18px" },
  // section header
  secHdr: { display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 },
  secAct: { fontSize:13, color:"#E63946", cursor:"pointer", background:"none", border:"none", fontWeight:600 },
  // splash
  splashLogo: { fontFamily:"'Barlow Condensed',sans-serif", fontSize:108, fontWeight:900, letterSpacing:-3, lineHeight:0.85, background:"linear-gradient(135deg, #ffffff 0%, #E63946 55%, #ff8c94 100%)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text" },
  splashTag: { fontFamily:"'Barlow Condensed',sans-serif", fontSize:15, letterSpacing:9, color:"rgba(255,255,255,0.35)", marginTop:10, textTransform:"uppercase", fontWeight:500 },
};

// ═══════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════
function StyleTag() {
  return <style>{css}</style>;
}

// P4M Logo SVG component
function P4MLogo({ width=70 }) {
  const h = Math.round(width * (101/294));
  return (
    <svg width={width} height={h} viewBox="1336.98 1052.56 294 101"
      xmlns="http://www.w3.org/2000/svg"
      style={{fillRule:"evenodd",clipRule:"evenodd",strokeLinejoin:"round",strokeMiterlimit:2}}>
      <g transform="matrix(1,0,0,1,-1336.98,-1052.56)">
        <path d="M1431.12,1052.7L1468.29,1052.56L1435.05,1131.27L1435.54,1141.58L1490.36,1141.43L1490.21,1151.11L1434.56,1151.9L1386.23,1152.36L1386.44,1143.45L1429.42,1143.52L1428.77,1061.03L1345.63,1061.33L1345.17,1145.39L1361.89,1145.25L1362.05,1117.25C1395.16,1120.72 1407.39,1107.35 1403.51,1081C1420.42,1114.52 1403.54,1125.07 1370.34,1125.18L1369.7,1152.52L1338.13,1152.83L1336.98,1053.27L1431.12,1052.7" style={{fill:"white"}}/>
        <path d="M1535.77,1054.23L1536.43,1152.11L1555.05,1152.11L1555.21,1112.56L1583.23,1149.63L1610.74,1111.41L1611.4,1151.94L1630.19,1151.94L1629.69,1054.56L1583.23,1117.34L1535.77,1054.23" style={{fill:"rgb(255,0,0)"}}/>
        <path d="M1478.1,1054.89L1448.93,1131.02L1499.52,1130.85L1499.35,1152.27L1518.96,1152.11L1518.8,1131.18L1528.52,1131.02L1528.36,1111.9L1518.96,1111.74L1518.3,1074.5L1499.35,1074L1499.35,1111.41L1479.09,1111.24L1498.37,1055.55L1478.1,1054.89" style={{fill:"rgb(255,0,0)"}}/>
        <path d="M1360.1,1101.52L1370.4,1101.52L1370.4,1087.36L1388.55,1087.36C1388.26,1081.07 1383.98,1077.98 1376.83,1077.19L1360.61,1077.19L1360.1,1101.52" style={{fill:"white"}}/>
      </g>
    </svg>
  );
}

function P4MLogoFull({ width=280 }) {
  const vw = 294;
  const logoH = 101;
  const totalVH = Math.round(logoH * 1.28);
  const h = Math.round(width * (totalVH / vw));
  return (
    <svg width={width} height={h} viewBox={`0 0 ${vw} ${totalVH}`}
      xmlns="http://www.w3.org/2000/svg"
      style={{fillRule:"evenodd",clipRule:"evenodd",strokeLinejoin:"round",strokeMiterlimit:2}}>
      <g transform="matrix(1,0,0,1,-1336.98,-1052.56)">
        <path d="M1431.12,1052.7L1468.29,1052.56L1435.05,1131.27L1435.54,1141.58L1490.36,1141.43L1490.21,1151.11L1434.56,1151.9L1386.23,1152.36L1386.44,1143.45L1429.42,1143.52L1428.77,1061.03L1345.63,1061.33L1345.17,1145.39L1361.89,1145.25L1362.05,1117.25C1395.16,1120.72 1407.39,1107.35 1403.51,1081C1420.42,1114.52 1403.54,1125.07 1370.34,1125.18L1369.7,1152.52L1338.13,1152.83L1336.98,1053.27L1431.12,1052.7" style={{fill:"white"}}/>
        <path d="M1535.77,1054.23L1536.43,1152.11L1555.05,1152.11L1555.21,1112.56L1583.23,1149.63L1610.74,1111.41L1611.4,1151.94L1630.19,1151.94L1629.69,1054.56L1583.23,1117.34L1535.77,1054.23" style={{fill:"rgb(255,0,0)"}}/>
        <path d="M1478.1,1054.89L1448.93,1131.02L1499.52,1130.85L1499.35,1152.27L1518.96,1152.11L1518.8,1131.18L1528.52,1131.02L1528.36,1111.9L1518.96,1111.74L1518.3,1074.5L1499.35,1074L1499.35,1111.41L1479.09,1111.24L1498.37,1055.55L1478.1,1054.89" style={{fill:"rgb(255,0,0)"}}/>
        <path d="M1360.1,1101.52L1370.4,1101.52L1370.4,1087.36L1388.55,1087.36C1388.26,1081.07 1383.98,1077.98 1376.83,1077.19L1360.61,1077.19L1360.1,1101.52" style={{fill:"white"}}/>
      </g>
      <text x={vw/2} y={totalVH-4}
        fontFamily="Arial,sans-serif" fontSize="11" fontWeight="400"
        fill="rgba(255,255,255,0.6)" textAnchor="middle" letterSpacing="3.5">
        PERFORM . DEVELOP . COMPETE
      </text>
    </svg>
  );
}


function SwimRaceAdder({ onAdd, primaryCol }) {
  const [event, setEvent] = useState("");
  const [time, setTime] = useState("");
  const [pb, setPb] = useState(false);
  const EVENTS = ["50m Freestyle","100m Freestyle","200m Freestyle","400m Freestyle","800m Freestyle","1500m Freestyle",
    "50m Backstroke","100m Backstroke","200m Backstroke","50m Breaststroke","100m Breaststroke","200m Breaststroke",
    "50m Butterfly","100m Butterfly","200m Butterfly","200m IM","400m IM",
    "4x100m Freestyle Relay","4x200m Freestyle Relay","4x100m Medley Relay"];
  return (
    <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap",marginTop:8}}>
      <select value={event} onChange={e=>setEvent(e.target.value)}
        style={{flex:2,padding:"8px 10px",background:"#1a1a2e",border:"1px solid rgba(255,255,255,0.1)",borderRadius:7,color:"#fff",fontSize:12,minWidth:140}}>
        <option value="">Select event...</option>
        {EVENTS.map(e=><option key={e} value={e}>{e}</option>)}
      </select>
      <input value={time} onChange={e=>setTime(e.target.value)} placeholder="e.g. 1:02.34"
        style={{flex:1,padding:"8px 10px",background:"rgba(0,0,0,0.3)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:7,color:"#fff",fontSize:12,minWidth:80}}/>
      <label style={{display:"flex",alignItems:"center",gap:4,fontSize:11,color:"#f0b429",cursor:"pointer"}}>
        <input type="checkbox" checked={pb} onChange={e=>setPb(e.target.checked)}/>PB
      </label>
      <button onClick={()=>{if(!event||!time)return;onAdd({event,time,pb});setEvent("");setTime("");setPb(false);}}
        style={{padding:"7px 12px",background:primaryCol,border:"none",borderRadius:7,color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer"}}>+ Add</button>
    </div>
  );
}


function Toast({ msg }) {
  if (!msg) return null;
  return (
    <div style={{ position:"fixed", bottom:80, left:"50%", transform:"translateX(-50%)", background:"#1a1a28", border:"1px solid rgba(255,255,255,0.12)", borderRadius:30, padding:"10px 22px", fontSize:13, color:"#fff", boxShadow:"0 8px 40px rgba(0,0,0,0.6)", zIndex:9999, whiteSpace:"nowrap", fontWeight:500 }}>
      {msg}
    </div>
  );
}

function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div style={G.overlay} onClick={onClose}>
      <div style={G.sheet} onClick={e => e.stopPropagation()}>
        <div style={G.handle} />
        {title && <div style={{ ...G.h2, marginBottom:18 }}>{title}</div>}
        {children}
      </div>
    </div>
  );
}

function Avatar({ name, photo, size=36, colour }) {
  const bg = colour || avCol(name);
  return (
    <div style={{ width:size, height:size, borderRadius:"50%", background:photo?"transparent":bg, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Barlow Condensed',sans-serif", fontSize:size*0.36, fontWeight:800, color:"#fff", flexShrink:0, overflow:"hidden" }}>
      {photo ? <img src={photo} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : inits(name)}
    </div>
  );
}

function FieldLabel({ children }) {
  return <div style={G.label}>{children}</div>;
}

function Input({ label, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom:12 }}>
      {label && <FieldLabel>{label}</FieldLabel>}
      <input style={{ ...G.input, borderColor: focused ? "var(--primary)" : "rgba(255,255,255,0.1)", boxShadow: focused ? "0 0 0 3px rgba(230,57,70,0.12)" : "none" }}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} {...props} />
    </div>
  );
}

function TextArea({ label, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom:12 }}>
      {label && <FieldLabel>{label}</FieldLabel>}
      <textarea style={{ ...G.textarea, borderColor: focused ? "var(--primary)" : "rgba(255,255,255,0.1)" }}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} {...props} />
    </div>
  );
}

function Select({ label, children, ...props }) {
  return (
    <div style={{ marginBottom:12 }}>
      {label && <FieldLabel>{label}</FieldLabel>}
      <select style={G.select} {...props}>{children}</select>
    </div>
  );
}

function Btn({ children, variant="primary", col, style={}, ...props }) {
  const base = variant === "primary" ? { ...G.btnPrimary, background: col || "#E63946" } : variant === "ghost" ? G.btnGhost : G.btnSm;
  return <button style={{ ...base, ...style }} {...props}>{children}</button>;
}

function SectionHdr({ title, action, onAction }) {
  return (
    <div style={G.secHdr}>
      <div style={G.h3}>{title}</div>
      {action && <button style={G.secAct} onClick={onAction}>{action}</button>}
    </div>
  );
}

// Player Card Component
function PlayerCard({ player, size="full", clubLogo=null, primaryCol="#E63946", secondaryCol="#1e3a8a", sport="Hockey" }) {
  const scores    = player?.scores || {};
  const hasScores = Object.keys(scores).length > 0;
  const isGK = player?.position === "GK";
  const sportKey = isGK ? `${sport}-GK` : sport;
  const sportCardMap = CARD_MAP_BY_SPORT?.[sport] || CARD_MAP;
  const sportComposite = CARD_COMPOSITE?.[sportKey] || CARD_COMPOSITE?.[sport] || null;
  const getCardStat = (k) => {
    if (sportComposite?.[k]) return calcCardStat(scores, sportComposite[k]);
    return scores[sportCardMap[k]] || 50;
  };
  const cardStatKeys = ["spd","sta","att","pos","str","def","sho","wkr"];
  const ovRaw = hasScores
    ? cardStatKeys.map(k => to99(getCardStat(k))).reduce((a,b)=>a+b,0) / 8
    : null;
  const ov = ovRaw ? Math.round(ovRaw) : null;

  const mini = size === "mini";
  const W = mini ? 170 : 300;
  const H = mini ? 238 : 420;
  const pd = mini ? 9 : 14;
  const fs = (a,b) => mini ? a : b;

  // Tier: Bronze ≤64, Silver 65-74, Gold 75-83, Elite 84+ (black card)
  const tier = !ov||ov<=64
    ? {border:primaryCol, shadow:`${primaryCol}44`, label:"BRONZE", cardBg:`linear-gradient(160deg,${secondaryCol}ee 0%,#08080f 50%,${primaryCol}22 100%)`}
    : ov<=74
    ? {border:"#9aa3b0", shadow:"rgba(154,163,176,0.45)", label:"SILVER", cardBg:`linear-gradient(160deg,${secondaryCol}ee 0%,#08080f 50%,${primaryCol}22 100%)`}
    : ov<=83
    ? {border:"#d4af37", shadow:"rgba(212,175,55,0.6)", label:"GOLD", cardBg:`linear-gradient(160deg,${secondaryCol}ee 0%,#08080f 50%,${primaryCol}22 100%)`}
    : {border:"#e8e8e8", shadow:"rgba(232,232,232,0.5)", label:"ELITE", cardBg:"linear-gradient(160deg,#0a0a0a 0%,#1a1a1a 50%,#050505 100%)"};

  const B = tier.border;

  const activeCardMap = CARD_MAP_BY_SPORT?.[sport] || CARD_MAP;
  const SWIM_LABELS   = {spd:"SPR",sta:"END",att:"STK",pos:"PAC",str:"STR",def:"TRN",sho:"STA",wkr:"CON"};
  const HOCKEY_LABELS = {spd:"PAC",sta:"STA",att:"DRI",pos:"PAS",str:"PHY",def:"DEF",sho:"SHO",wkr:"WRK"};
  const GK_LABELS     = {spd:"REF",sta:"STA",att:"DIS",pos:"POS",str:"PHY",def:"SPG",sho:"KIC",wkr:"WRK"};
  const labelFor = k => sport==="Swimming" ? (SWIM_LABELS[k]||k.toUpperCase()) : isGK ? (GK_LABELS[k]||k.toUpperCase()) : (HOCKEY_LABELS[k]||k.toUpperCase());

  // Full names for legend
  const HOCKEY_FULL = {spd:"Pace",sta:"Stamina",att:"Dribbling",pos:"Passing",str:"Physical",def:"Defending",sho:"Shooting",wkr:"Work Rate"};
  const GK_FULL     = {spd:"Reflexes",sta:"Stamina",att:"Distribution",pos:"Positioning",str:"Physical",def:"Shot Stopping",sho:"Kicking",wkr:"Work Rate"};
  const SWIM_FULL   = {spd:"Sprint",sta:"Endurance",att:"Stroke",pos:"Pacing",str:"Strength",def:"Turns",sho:"Starts",wkr:"Consistency"};
  const fullFor = k => sport==="Swimming" ? (SWIM_FULL[k]||k) : isGK ? (GK_FULL[k]||k) : (HOCKEY_FULL[k]||k);
  const SL = [{k:"spd",l:labelFor("spd")},{k:"sta",l:labelFor("sta")},{k:"att",l:labelFor("att")},{k:"pos",l:labelFor("pos")}];
  const SR = [{k:"str",l:labelFor("str")},{k:"def",l:labelFor("def")},{k:"sho",l:labelFor("sho")},{k:"wkr",l:labelFor("wkr")}];

  const statsH = mini ? 110 : 190;

  return (
    <div style={{
      width:W, height:H, borderRadius:fs(10,16),
      position:"relative", overflow:"hidden", flexShrink:0,
      background: tier.cardBg,
      border:`${fs(2,3)}px solid ${B}`,
      boxShadow:`0 0 ${fs(16,32)}px ${tier.shadow}, 0 12px 48px rgba(0,0,0,0.85)`,
    }}>

      {/* Diagonal ray texture */}
      <svg style={{position:"absolute",inset:0,width:"100%",height:"100%",opacity:0.06,pointerEvents:"none",zIndex:0}} viewBox="0 0 300 420" preserveAspectRatio="none">
        {[-60,-20,20,60,100,140,180,220,260].map((x,i)=>(
          <line key={i} x1={x} y1={0} x2={x+260} y2={420} stroke="#fff" strokeWidth={fs(18,30)}/>
        ))}
      </svg>

      {/* ── LAYER 1: Photo — top of card, above stats zone ── */}
      {(player?.photo || player?.photo_url) && (
        <div style={{
          position:"absolute",
          top: 0,
          left: 0,
          right: 0,
          // Photo fills everything ABOVE the stats section
          bottom: statsH,
          zIndex: 2,
          overflow:"hidden",
        }}>
          <img src={player.photo||player?.photo_url} alt="" crossOrigin="anonymous" style={{
            position:"absolute",
            width:"100%",
            height:"auto",
            minHeight:"100%",
            top:"50%",
            left:"50%",
            transform:`translate(-50%,-50%) scale(${((player?.photoZoom??player?.scores?._photoZoom??120)>10?(player?.photoZoom??player?.scores?._photoZoom??120)/100:(player?.photoZoom??player?.scores?._photoZoom??1.2))}) translate(${player?.photoX??player?.scores?._photoX??0}px, ${player?.photoY??player?.scores?._photoY??0}px)`,
            display:"block",
          }}/>
          {/* Fade bottom of photo into card */}
          <div style={{
            position:"absolute", bottom:0, left:0, right:0,
            height:"35%",
            background:`linear-gradient(to top, #08080f 0%, transparent 100%)`,
          }}/>
        </div>
      )}

      {/* Silhouette if no photo */}
      {!player?.photo && (
        <div style={{
          position:"absolute",
          top:0, left:0, right:0,
          bottom: statsH,
          zIndex:2,
          display:"flex",
          alignItems:"center",
          justifyContent:"center",
          opacity:0.15,
        }}>
          <svg width={fs(90,155)} height={fs(110,185)} viewBox="0 0 155 185">
            <defs>
              <radialGradient id="silG" cx="50%" cy="18%" r="65%">
                <stop offset="0%" stopColor="#fff" stopOpacity="1"/>
                <stop offset="100%" stopColor="#fff" stopOpacity="0"/>
              </radialGradient>
            </defs>
            <ellipse cx="77" cy="22" rx="20" ry="21" fill="url(#silG)"/>
            <rect x="67" y="42" width="20" height="13" rx="5" fill="url(#silG)"/>
            <path d="M18 65 Q36 50 77 53 Q118 50 136 65 L142 120 Q77 113 12 120 Z" fill="url(#silG)"/>
            <path d="M28 117 L35 182 L62 178 L77 152 L92 178 L119 182 L126 117 Z" fill="url(#silG)"/>
            <path d="M18 65 L3 110 L22 118 L33 77 Z" fill="url(#silG)"/>
            <path d="M136 65 L151 110 L132 118 L121 77 Z" fill="url(#silG)"/>
          </svg>
        </div>
      )}

      {/* ── LAYER 3: OVR + badge — always on top of photo ── */}
      <div style={{
        position:"absolute", top:0, left:0, right:0,
        zIndex:5,
        display:"flex", alignItems:"flex-start", justifyContent:"space-between",
        padding:`${pd}px ${pd}px 0`,
        // Dark gradient so OVR is always readable over photo
        background:"linear-gradient(to bottom, rgba(0,0,0,0.65) 0%, transparent 100%)",
        paddingBottom: fs(16,28),
      }}>
        <div>
          <div style={{fontSize:fs(36,62),fontWeight:900,color:"#fff",lineHeight:1,letterSpacing:-1,textShadow:"0 2px 10px rgba(0,0,0,0.9)"}}>{ov||"--"}</div>
          <div style={{fontSize:fs(7,10),fontWeight:700,color:"rgba(255,255,255,0.65)",letterSpacing:3,marginTop:1}}>OVR</div>
          <div style={{fontSize:fs(11,16),fontWeight:800,color:"#fff",marginTop:fs(2,3),letterSpacing:1,textShadow:"0 1px 6px rgba(0,0,0,0.8)"}}>{player?.position||"MID"}</div>
        </div>
        <div style={{width:fs(24,42),height:fs(24,42),borderRadius:fs(5,9),overflow:"hidden",border:"2px solid rgba(255,255,255,0.3)",background:"rgba(0,0,0,0.4)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
          {clubLogo
            ? <img src={clubLogo} alt="" style={{width:"100%",height:"100%",objectFit:"cover",objectPosition:window._p4mLogoPos||"center center"}}/>
            : <div style={{fontSize:fs(7,12),fontWeight:900,color:"#fff",opacity:0.8,textAlign:"center",lineHeight:1.1}}>P4M</div>}
        </div>
      </div>

      {/* ── LAYER 4: Stats section — fixed height at bottom ── */}
      <div style={{
        position:"absolute",
        bottom:0, left:0, right:0,
        height: statsH,
        zIndex:4,
        overflow:"visible",
        // Solid dark background so stats are always fully readable
        background:`linear-gradient(to bottom, rgba(5,5,15,0.95) 0%, rgba(5,5,15,1) 100%)`,
        display:"flex",
        flexDirection:"column",
        justifyContent:"flex-end",
        paddingBottom: fs(5,10),
      }}>

        {/* Name */}
        <div style={{
          textAlign:"center",
          fontSize:fs(11,18),fontWeight:900,color:"#fff",
          letterSpacing:fs(2,4),
          padding:`0 ${pd}px ${fs(2,4)}px`,
          whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",
          textShadow:"0 1px 4px rgba(0,0,0,0.8)",
        }}>
          {(player?.displayName||player?.name||"PLAYER NAME").toUpperCase()}
        </div>

        {/* Divider */}
        <div style={{height:1,margin:`0 ${pd}px ${fs(3,5)}px`,background:`linear-gradient(90deg,transparent,${primaryCol},transparent)`}}/>

        {/* Stats grid */}
        <div style={{display:"grid",gridTemplateColumns:"1fr auto 1fr",gap:fs(3,8),padding:`0 ${pd}px`}}>
          <div style={{display:"flex",flexDirection:"column",gap:fs(2,4)}}>
            {SL.map(s=>(
              <div key={s.k} style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <span style={{fontSize:fs(8,12),fontWeight:600,color:"rgba(255,255,255,0.5)",letterSpacing:1}}>{s.l}</span>
                <span style={{fontSize:fs(11,16),fontWeight:900,color:"#fff"}}>{to99(getCardStat(s.k))}</span>
              </div>
            ))}
          </div>
          <div style={{width:1,background:`rgba(255,255,255,0.15)`,margin:"2px 0"}}/>
          <div style={{display:"flex",flexDirection:"column",gap:fs(2,4)}}>
            {SR.map(s=>(
              <div key={s.k} style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <span style={{fontSize:fs(8,12),fontWeight:600,color:"rgba(255,255,255,0.5)",letterSpacing:1}}>{s.l}</span>
                <span style={{fontSize:fs(11,16),fontWeight:900,color:"#fff"}}>{to99(getCardStat(s.k))}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div style={{height:1,margin:`${fs(3,5)}px ${pd}px ${fs(3,5)}px`,background:`linear-gradient(90deg,transparent,${primaryCol},transparent)`}}/>

        {/* P4M branding — always rendered, white on dark */}
        <div style={{display:"flex",justifyContent:"center",alignItems:"center",gap:fs(4,6),paddingTop:fs(2,4),paddingBottom:fs(4,8)}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <P4MLogo width={mini?26:42}/>
          </div>
          <div style={{fontSize:fs(5,7),fontWeight:700,color:"rgba(255,255,255,0.6)",letterSpacing:fs(1,2),fontFamily:"'Barlow Condensed',Arial,sans-serif",textTransform:"uppercase"}}>Perform · Develop · Compete</div>
        </div>

      </div>

      {/* Inner border glow */}
      <div style={{position:"absolute",inset:fs(3,5),borderRadius:fs(8,13),border:`1px solid ${primaryCol}22`,zIndex:6,pointerEvents:"none"}}/>
    </div>
  );
}


function CoachCard({ user, size="full", clubLogo=null, primaryCol="#E63946", secondaryCol="#1e3a8a", teamColour=null }) {
  const mini = size === "mini";
  const W = mini ? 170 : 300;
  const H = mini ? 238 : 420;
  const pd = mini ? 9 : 14;
  const fs = (a,b) => mini ? a : b;
  const statsH = mini ? 80 : 130;
  // Coach card uses team colour as border — defaults to primary
  const B = teamColour || primaryCol;
  const cardBg = `linear-gradient(160deg, ${secondaryCol}dd 0%, #08080f 55%, ${B}18 100%)`;

  const roleLabel = (role) => {
    if (role==="lead_coach") return "Lead Coach";
    if (role==="coach") return "Coach";
    if (role==="captain") return "Captain";
    if (role==="admin") return "Club Admin";
    return "Manager";
  };

  return (
    <div style={{
      width:W, height:H, borderRadius:fs(10,16),
      position:"relative", overflow:"hidden", flexShrink:0,
      background: cardBg,
      border:`${fs(2,3)}px solid ${B}`,
      boxShadow:`0 0 ${fs(16,32)}px ${B}55, 0 12px 48px rgba(0,0,0,0.85)`,
    }}>
      {/* Ray texture */}
      <svg style={{position:"absolute",inset:0,width:"100%",height:"100%",opacity:0.06,pointerEvents:"none",zIndex:0}} viewBox="0 0 300 420" preserveAspectRatio="none">
        {[-60,-20,20,60,100,140,180,220,260].map((x,i)=>(
          <line key={i} x1={x} y1={0} x2={x+260} y2={420} stroke="#fff" strokeWidth={fs(18,30)}/>
        ))}
      </svg>

      {/* Photo zone — top portion */}
      {user?.photo && (
        <div style={{position:"absolute",top:0,left:0,right:0,bottom:statsH,zIndex:1,overflow:"hidden"}}>
          <img src={user.photo} alt="" style={{
            position:"absolute",
            width:"100%",
            height:"auto",
            minHeight:"100%",
            top:"50%",
            left:"50%",
            transform:`translate(-50%,-50%) scale(${(user?.photoZoom??120)>10?(user?.photoZoom??120)/100:1.2}) translate(${user?.photoX??0}px, ${user?.photoY??0}px)`,
            display:"block",
          }}/>
          <div style={{position:"absolute",bottom:0,left:0,right:0,height:"35%",background:`linear-gradient(to top,#08080f,transparent)`}}/>
        </div>
      )}

      {/* Silhouette if no photo */}
      {!user?.photo && (
        <div style={{position:"absolute",top:0,left:0,right:0,bottom:statsH,zIndex:1,display:"flex",alignItems:"center",justifyContent:"center",opacity:0.12}}>
          <svg width={fs(90,155)} height={fs(110,185)} viewBox="0 0 155 185">
            <defs><radialGradient id="cSil" cx="50%" cy="18%" r="65%"><stop offset="0%" stopColor="#fff" stopOpacity="1"/><stop offset="100%" stopColor="#fff" stopOpacity="0"/></radialGradient></defs>
            <ellipse cx="77" cy="22" rx="20" ry="21" fill="url(#cSil)"/>
            <rect x="67" y="42" width="20" height="13" rx="5" fill="url(#cSil)"/>
            <path d="M18 65 Q36 50 77 53 Q118 50 136 65 L142 120 Q77 113 12 120 Z" fill="url(#cSil)"/>
            <path d="M28 117 L35 182 L62 178 L77 152 L92 178 L119 182 L126 117 Z" fill="url(#cSil)"/>
          </svg>
        </div>
      )}

      {/* Top gradient for badge readability */}
      <div style={{position:"absolute",top:0,left:0,right:0,height:"35%",background:"linear-gradient(to bottom,rgba(0,0,0,0.6),transparent)",zIndex:2,pointerEvents:"none"}}/>

      {/* Club badge top-right */}
      <div style={{position:"absolute",top:pd,right:pd,zIndex:5,width:fs(24,42),height:fs(24,42),borderRadius:fs(5,9),overflow:"hidden",border:"2px solid rgba(255,255,255,0.3)",background:"rgba(0,0,0,0.4)",display:"flex",alignItems:"center",justifyContent:"center"}}>
        {clubLogo ? <img src={clubLogo} alt="" style={{width:"100%",height:"100%",objectFit:"cover",objectPosition:window._p4mLogoPos||"center center"}}/> : <P4MLogo width={fs(18,30)}/>}
      </div>

      {/* Bottom info section */}
      <div style={{position:"absolute",bottom:0,left:0,right:0,height:statsH,zIndex:4,background:`linear-gradient(to bottom,rgba(5,5,15,0.95),rgba(5,5,15,1))`,display:"flex",flexDirection:"column",justifyContent:"center",padding:`${pd}px`}}>
        {/* Role label */}
        <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:fs(9,13),fontWeight:700,color:B,letterSpacing:fs(1,2),textTransform:"uppercase",marginBottom:fs(2,4)}}>
          {roleLabel(user?.role)} {user?.team&&user.team!=="all"?`· ${user.team}`:""}
        </div>
        {/* Name */}
        <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:fs(15,26),fontWeight:900,color:"#fff",letterSpacing:fs(1,3),lineHeight:1,marginBottom:fs(6,12),whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
          {(user?.name||"COACH").toUpperCase()}
        </div>
        {/* Divider */}
        <div style={{height:1,background:`linear-gradient(90deg,transparent,${B},transparent)`,marginBottom:fs(6,10)}}/>
        {/* P4M branding */}
        <div style={{display:"flex",justifyContent:"center"}}>
          <P4MLogo width={fs(28,48)}/>
        </div>
      </div>

      {/* Inner border */}
      <div style={{position:"absolute",inset:fs(3,5),borderRadius:fs(8,13),border:`1px solid ${B}22`,zIndex:6,pointerEvents:"none"}}/>
    </div>
  );
}


function RadarChart({ scores={}, size=280, sport="Hockey", position="" }) {
  const cx=size/2, cy=size/2, R=size*0.38;
  const isGKPos = position==="GK" && ATTRS_BY_SPORT[sport+"-GK"];
  const sportAttrs = isGKPos ? ATTRS_BY_SPORT[sport+"-GK"] : (ATTRS_BY_SPORT[sport]||ATTRS_BY_SPORT.default);
  const RADAR_ATTRS = Object.values(sportAttrs).flat();
  const N=RADAR_ATTRS.length;
  const catOf={};
  Object.entries(sportAttrs).forEach(([c,as])=>as.forEach(a=>catOf[a]=c));
  const pt=(i,r)=>{const a=(i/N)*2*Math.PI-Math.PI/2;return[cx+r*Math.cos(a),cy+r*Math.sin(a)];};
  return (
    <svg viewBox={`0 0 ${size} ${size}`} style={{ width:"100%", maxWidth:size, display:"block", margin:"0 auto" }}>
      {[20,40,60,80,100].map(ring=>(
        <polygon key={ring} points={RADAR_ATTRS.map((_,i)=>pt(i,ring/100*R).join(",")).join(" ")} fill={ring===100?"rgba(255,255,255,0.02)":"none"} stroke="rgba(255,255,255,0.06)" strokeWidth={1}/>
      ))}
      {RADAR_ATTRS.map((_,i)=>{const[x,y]=pt(i,R);return<line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="rgba(255,255,255,0.06)" strokeWidth={1}/>;;})}
      <polygon points={RADAR_ATTRS.map((_,i)=>pt(i,0.9*R).join(",")).join(" ")} fill="rgba(240,180,41,0.08)" stroke="#f0b429" strokeWidth={1.5} strokeDasharray="4 3"/>
      <polygon points={RADAR_ATTRS.map((a,i)=>pt(i,(Math.min(scores[a]||50,100)/100)*R).join(",")).join(" ")} fill="rgba(230,57,70,0.18)" stroke="#E63946" strokeWidth={2}/>
      {RADAR_ATTRS.map((attr,i)=>{
        const[x,y]=pt(i,((scores[attr]||5)/10)*R);
        const[lx,ly]=pt(i,R+15);
        const col=ATTR_COLS[catOf[attr]];
        const a=(i/N)*2*Math.PI-Math.PI/2;
        const anc=Math.abs(Math.cos(a))<0.15?"middle":Math.cos(a)>0?"start":"end";
        const ws=attr.split(" ");
        return(
          <g key={attr}>
            <circle cx={x} cy={y} r={3} fill={col} stroke="#fff" strokeWidth={1.5}/>
            {ws.length===1?<text x={lx} y={ly+3} textAnchor={anc} fill={col} fontSize={7.5} fontFamily="Inter" fontWeight="700">{attr}</text>:
              <><text x={lx} y={ly-2} textAnchor={anc} fill={col} fontSize={7.5} fontFamily="Inter" fontWeight="700">{ws[0]}</text>
              <text x={lx} y={ly+7} textAnchor={anc} fill={col} fontSize={7.5} fontFamily="Inter" fontWeight="700">{ws.slice(1).join(" ")}</text></>}
          </g>
        );
      })}
    </svg>
  );
}

// ═══════════════════════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════════════════════
const contrastText = (hex) => {
  try {
    const r=parseInt(hex.slice(1,3),16),g=parseInt(hex.slice(3,5),16),b=parseInt(hex.slice(5,7),16);
    return (r*299+g*587+b*114)/1000 > 128 ? "#000" : "#fff";
  } catch { return "#fff"; }
};

const FORMATIONS_BY_SPORT = {
  Hockey: [
    {name:"4-3-3", rows:[{pos:"GK",count:1},{pos:"DEF",count:4},{pos:"MID",count:3},{pos:"FWD",count:3}]},
    {name:"3-4-3", rows:[{pos:"GK",count:1},{pos:"DEF",count:3},{pos:"MID",count:4},{pos:"FWD",count:3}]},
    {name:"4-4-2", rows:[{pos:"GK",count:1},{pos:"DEF",count:4},{pos:"MID",count:4},{pos:"FWD",count:2}]},
    {name:"3-3-3-1",rows:[{pos:"GK",count:1},{pos:"DEF",count:3},{pos:"DM",count:3},{pos:"AM",count:3},{pos:"ST",count:1}]},
    {name:"3-2-3-2",rows:[{pos:"GK",count:1},{pos:"DEF",count:3},{pos:"DM",count:2},{pos:"AM",count:3},{pos:"ST",count:2}]},
  ],
  Football: [
    {name:"4-3-3", rows:[{pos:"GK",count:1},{pos:"DEF",count:4},{pos:"MID",count:3},{pos:"FWD",count:3}]},
    {name:"4-4-2", rows:[{pos:"GK",count:1},{pos:"DEF",count:4},{pos:"MID",count:4},{pos:"FWD",count:2}]},
    {name:"3-5-2", rows:[{pos:"GK",count:1},{pos:"DEF",count:3},{pos:"MID",count:5},{pos:"FWD",count:2}]},
    {name:"4-2-3-1",rows:[{pos:"GK",count:1},{pos:"DEF",count:4},{pos:"DM",count:2},{pos:"AM",count:3},{pos:"ST",count:1}]},
    {name:"5-3-2", rows:[{pos:"GK",count:1},{pos:"DEF",count:5},{pos:"MID",count:3},{pos:"FWD",count:2}]},
  ],
  default: [
    {name:"4-3-3", rows:[{pos:"GK",count:1},{pos:"DEF",count:4},{pos:"MID",count:3},{pos:"FWD",count:3}]},
    {name:"4-4-2", rows:[{pos:"GK",count:1},{pos:"DEF",count:4},{pos:"MID",count:4},{pos:"FWD",count:2}]},
  ],
};


function PhotoFrameEditor({ p, primaryCol, onNudge, onLock }) {
  const px = p.photoX??0, py = p.photoY??0, pz = p.photoZoom??100;
  const step = 8, zstep = 10;
  const btn = (label, dx, dy, dz) => (
    <button
      key={label}
      onPointerDown={e=>{ e.stopPropagation(); e.preventDefault(); onNudge(dx,dy,dz); }}
      style={{width:38,height:38,background:"rgba(255,255,255,0.07)",border:"1px solid rgba(255,255,255,0.15)",borderRadius:8,color:"#fff",fontSize:16,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,flexShrink:0,touchAction:"none"}}>
      {label}
    </button>
  );
  return (
    <div onPointerDown={e=>e.stopPropagation()} style={{width:"100%",maxWidth:300,padding:"12px 14px",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:12}}>
      <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.5)",letterSpacing:1,marginBottom:10,textAlign:"center"}}>📐 FRAME PHOTO</div>
      <div style={{display:"grid",gridTemplateColumns:"38px 38px 38px",gridTemplateRows:"38px 38px 38px",gap:4,justifyContent:"center",marginBottom:10}}>
        <div/>{btn("▲",0,-step,0)}<div/>
        {btn("◀",-step,0,0)}
        <div style={{width:38,height:38,background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,color:"rgba(255,255,255,0.3)"}}>MOVE</div>
        {btn("▶",step,0,0)}
        <div/>{btn("▼",0,step,0)}<div/>
      </div>
      <div style={{display:"flex",alignItems:"center",gap:8,justifyContent:"center",marginBottom:8}}>
        {btn("−",0,0,-zstep)}
        <div style={{flex:1,textAlign:"center"}}>
          <div style={{fontSize:10,color:"rgba(255,255,255,0.4)"}}>Zoom</div>
          <div style={{fontSize:13,fontWeight:700,color:"#fff"}}>{pz}%</div>
        </div>
        {btn("+",0,0,zstep)}
      </div>
      <button onPointerDown={e=>{e.stopPropagation();e.preventDefault();onNudge(-(p.photoX??0),-(p.photoY??0),100-(p.photoZoom??100));}} style={{width:"100%",padding:"6px",background:"none",border:"1px solid rgba(255,255,255,0.08)",borderRadius:7,color:"rgba(255,255,255,0.3)",fontSize:10,cursor:"pointer",marginBottom:6}}>Reset</button>
      <button onPointerDown={e=>{e.stopPropagation();e.preventDefault();onLock();}} style={{width:"100%",padding:"8px",background:`${primaryCol}22`,border:`1px solid ${primaryCol}66`,borderRadius:8,color:primaryCol,fontSize:12,fontWeight:700,cursor:"pointer"}}>🔒 Lock Image Position</button>
    </div>
  );
}



// ── DRILL DIAGRAMS (SVG) ─────────────────────────────────────────────────────
const DRILL_DIAGRAMS = {
  hd1: ({ col="#E63946" }) => (
    <svg viewBox="0 0 280 200" style={{width:"100%",maxWidth:280,display:"block",margin:"0 auto"}}>
      {/* Pitch background */}
      <rect x="10" y="10" width="260" height="180" rx="6" fill="#1a6b1a" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5"/>
      {/* Scattered cones */}
      {[[60,40],[120,60],[80,100],[160,80],[200,50],[50,140],[140,130],[210,120],[170,160],[90,170]].map(([x,y],i)=>(
        <g key={i}>
          <polygon points={`${x},${y-8} ${x-5},${y+4} ${x+5},${y+4}`} fill="#f0b429" opacity="0.9"/>
        </g>
      ))}
      {/* Player arrows showing dribble path */}
      <circle cx="30" cy="170" r="8" fill={col} stroke="#fff" strokeWidth="1.5"/>
      <text x="30" y="174" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="bold">P</text>
      <path d="M38,162 Q80,150 75,130 Q70,110 120,95 Q165,80 200,55" stroke="#fff" strokeWidth="1.5" fill="none" strokeDasharray="4,3" markerEnd="url(#arr)"/>
      <defs>
        <marker id="arr" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill="#fff"/>
        </marker>
      </defs>
      {/* Labels */}
      <text x="140" y="195" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="9">Navigate the minefield — head up!</text>
    </svg>
  ),
  hd2: ({ col="#E63946" }) => (
    <svg viewBox="0 0 280 200" style={{width:"100%",maxWidth:280,display:"block",margin:"0 auto"}}>
      <rect x="10" y="10" width="260" height="180" rx="6" fill="#1a6b1a" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5"/>
      {/* Triangle cones */}
      <polygon points="140,25 135,35 145,35" fill="#f0b429"/>
      <polygon points="30,165 25,175 35,175" fill="#f0b429"/>
      <polygon points="250,165 245,175 255,175" fill="#f0b429"/>
      {/* Players */}
      {[[140,45],[30,150],[250,150]].map(([x,y],i)=>(
        <g key={i}>
          <circle cx={x} cy={y} r="10" fill={col} stroke="#fff" strokeWidth="1.5"/>
          <text x={x} y={y+4} textAnchor="middle" fill="#fff" fontSize="9" fontWeight="bold">{String.fromCharCode(65+i)}</text>
        </g>
      ))}
      {/* Pass arrows */}
      <path d="M140,55 L40,145" stroke="#fff" strokeWidth="1.5" markerEnd="url(#arr2)" fill="none"/>
      <path d="M45,145 L240,145" stroke="#fff" strokeWidth="1.5" markerEnd="url(#arr2)" fill="none" strokeDasharray="4,3"/>
      <path d="M245,142 L148,52" stroke={col} strokeWidth="1.5" markerEnd="url(#arr2)" fill="none" strokeDasharray="4,3"/>
      <defs>
        <marker id="arr2" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill="#fff"/>
        </marker>
      </defs>
      {/* Movement */}
      <path d="M135,55 Q110,90 38,148" stroke="rgba(255,255,0,0.5)" strokeWidth="1" fill="none" strokeDasharray="2,4"/>
      <text x="140" y="195" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="9">Pass and follow — keep moving!</text>
    </svg>
  ),
  hd3: ({ col="#E63946" }) => (
    <svg viewBox="0 0 280 200" style={{width:"100%",maxWidth:280,display:"block",margin:"0 auto"}}>
      <rect x="10" y="10" width="260" height="180" rx="6" fill="#1a6b1a" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5"/>
      {/* Goal */}
      <rect x="110" y="12" width="60" height="10" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="2"/>
      {/* Attackers */}
      <circle cx="60" cy="120" r="10" fill={col} stroke="#fff" strokeWidth="1.5"/>
      <text x="60" y="124" textAnchor="middle" fill="#fff" fontSize="9" fontWeight="bold">A</text>
      <circle cx="140" cy="100" r="10" fill={col} stroke="#fff" strokeWidth="1.5"/>
      <text x="140" y="104" textAnchor="middle" fill="#fff" fontSize="9" fontWeight="bold">A</text>
      <circle cx="220" cy="120" r="10" fill={col} stroke="#fff" strokeWidth="1.5"/>
      <text x="220" y="124" textAnchor="middle" fill="#fff" fontSize="9" fontWeight="bold">A</text>
      {/* Defenders */}
      <circle cx="100" cy="60" r="10" fill="#1e3a8a" stroke="#fff" strokeWidth="1.5"/>
      <text x="100" y="64" textAnchor="middle" fill="#fff" fontSize="9" fontWeight="bold">D</text>
      <circle cx="180" cy="60" r="10" fill="#1e3a8a" stroke="#fff" strokeWidth="1.5"/>
      <text x="180" y="64" textAnchor="middle" fill="#fff" fontSize="9" fontWeight="bold">D</text>
      {/* Area */}
      <rect x="30" y="90" width="220" height="80" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1" strokeDasharray="4,3" rx="3"/>
      {/* Attack arrows */}
      <path d="M65,113 Q80,80 100,68" stroke={col} strokeWidth="1.5" fill="none" markerEnd="url(#arr3)"/>
      <path d="M215,113 Q200,80 185,68" stroke={col} strokeWidth="1.5" fill="none" markerEnd="url(#arr3)"/>
      <defs>
        <marker id="arr3" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill={col}/>
        </marker>
      </defs>
      <text x="140" y="185" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="9">Use width — spread the defenders</text>
    </svg>
  ),
  hd4: ({ col="#E63946" }) => (
    <svg viewBox="0 0 280 200" style={{width:"100%",maxWidth:280,display:"block",margin:"0 auto"}}>
      <rect x="10" y="10" width="260" height="180" rx="6" fill="#1a6b1a" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5"/>
      {/* Channel lines */}
      <line x1="80" y1="10" x2="80" y2="190" stroke="rgba(255,255,255,0.15)" strokeWidth="1" strokeDasharray="4,3"/>
      <line x1="200" y1="10" x2="200" y2="190" stroke="rgba(255,255,255,0.15)" strokeWidth="1" strokeDasharray="4,3"/>
      {/* Attacker */}
      <circle cx="140" cy="160" r="10" fill={col} stroke="#fff" strokeWidth="1.5"/>
      <text x="140" y="164" textAnchor="middle" fill="#fff" fontSize="9" fontWeight="bold">A</text>
      {/* Defender */}
      <circle cx="140" cy="100" r="10" fill="#1e3a8a" stroke="#fff" strokeWidth="1.5"/>
      <text x="140" y="104" textAnchor="middle" fill="#fff" fontSize="9" fontWeight="bold">D</text>
      {/* End line */}
      <line x1="80" y1="30" x2="200" y2="30" stroke="#fff" strokeWidth="2"/>
      {/* Attacker path */}
      <path d="M140,150 Q130,120 115,90 Q100,65 120,40" stroke={col} strokeWidth="1.5" fill="none" strokeDasharray="4,3" markerEnd="url(#arr4)"/>
      {/* Defender channel arrow */}
      <path d="M135,93 Q105,85 90,70" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5" fill="none" markerEnd="url(#arr4w)"/>
      <defs>
        <marker id="arr4" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill={col}/>
        </marker>
        <marker id="arr4w" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill="rgba(255,255,255,0.6)"/>
        </marker>
      </defs>
      <text x="86" y="62" fill="rgba(255,255,255,0.6)" fontSize="8">Channel wide</text>
      <text x="140" y="195" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="9">Defender channels attacker to sideline</text>
    </svg>
  ),
  hd5: ({ col="#E63946" }) => (
    <svg viewBox="0 0 280 200" style={{width:"100%",maxWidth:280,display:"block",margin:"0 auto"}}>
      <rect x="10" y="10" width="260" height="180" rx="6" fill="#1a6b1a" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5"/>
      {/* Goal */}
      <rect x="110" y="12" width="60" height="10" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2"/>
      {/* 4 shooting positions */}
      {[[60,80],[80,120],[200,80],[220,120]].map(([x,y],i)=>(
        <g key={i}>
          <polygon points={`${x},${y-6} ${x-4},${y+4} ${x+4},${y+4}`} fill="#f0b429"/>
          <path d={`M${x},${y-6} Q${x<140?x+30:x-30},${y-40} 140,22`} stroke={col} strokeWidth="1" fill="none" strokeDasharray="3,2" opacity="0.7"/>
        </g>
      ))}
      {/* Runner path */}
      <path d="M140,170 Q90,140 65,115 Q50,95 62,83" stroke="#fff" strokeWidth="1.5" fill="none" strokeDasharray="4,3" markerEnd="url(#arr5)"/>
      <circle cx="140" cy="170" r="10" fill={col} stroke="#fff" strokeWidth="1.5"/>
      <text x="140" y="174" textAnchor="middle" fill="#fff" fontSize="9" fontWeight="bold">P</text>
      <defs>
        <marker id="arr5" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill="#fff"/>
        </marker>
      </defs>
      <text x="140" y="195" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="9">Sprint between positions — shoot each cone</text>
    </svg>
  ),
  hd6: ({ col="#E63946" }) => (
    <svg viewBox="0 0 280 200" style={{width:"100%",maxWidth:280,display:"block",margin:"0 auto"}}>
      <rect x="10" y="10" width="260" height="180" rx="6" fill="#1a6b1a" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5"/>
      {/* Square */}
      <rect x="65" y="45" width="150" height="120" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1" strokeDasharray="4,3"/>
      {/* Cones at corners */}
      {[[65,45],[215,45],[65,165],[215,165]].map(([x,y],i)=>(
        <polygon key={i} points={`${x},${y-7} ${x-5},${y+5} ${x+5},${y+5}`} fill="#f0b429"/>
      ))}
      {/* Outside players */}
      {[[65,105,col],[140,45,col],[215,105,col],[140,165,col]].map(([x,y,c],i)=>(
        <g key={i}>
          <circle cx={x} cy={y} r="10" fill={c} stroke="#fff" strokeWidth="1.5"/>
          <text x={x} y={y+4} textAnchor="middle" fill="#fff" fontSize="9" fontWeight="bold">{String.fromCharCode(65+i)}</text>
        </g>
      ))}
      {/* Middle defender */}
      <circle cx="140" cy="105" r="10" fill="#1e3a8a" stroke="#fff" strokeWidth="1.5"/>
      <text x="140" y="109" textAnchor="middle" fill="#fff" fontSize="9" fontWeight="bold">D</text>
      {/* Pass arrow */}
      <path d="M78,105 L128,105" stroke="#fff" strokeWidth="1.5" fill="none" markerEnd="url(#arr6)"/>
      <defs>
        <marker id="arr6" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill="#fff"/>
        </marker>
      </defs>
      <text x="140" y="195" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="9">Keep ball from middle — 4v1 rondo</text>
    </svg>
  ),
  hd7: ({ col="#E63946" }) => (
    <svg viewBox="0 0 280 200" style={{width:"100%",maxWidth:280,display:"block",margin:"0 auto"}}>
      <rect x="10" y="10" width="260" height="180" rx="6" fill="#1a6b1a" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5"/>
      {/* Goal and circle */}
      <rect x="110" y="12" width="60" height="10" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2"/>
      <path d="M60,90 Q140,40 220,90" stroke="rgba(255,255,255,0.3)" strokeWidth="1" fill="none" strokeDasharray="3,3"/>
      {/* Backline */}
      <line x1="10" y1="185" x2="270" y2="185" stroke="rgba(255,255,255,0.3)" strokeWidth="1"/>
      {/* Players */}
      <circle cx="140" cy="175" r="10" fill={col} stroke="#fff" strokeWidth="1.5"/>
      <text x="140" y="179" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="bold">INJ</text>
      <circle cx="140" cy="115" r="10" fill={col} stroke="#fff" strokeWidth="1.5"/>
      <text x="140" y="119" textAnchor="middle" fill="#fff" fontSize="7" fontWeight="bold">STP</text>
      <circle cx="200" cy="85" r="10" fill={col} stroke="#fff" strokeWidth="1.5"/>
      <text x="200" y="89" textAnchor="middle" fill="#fff" fontSize="7" fontWeight="bold">SHT</text>
      {/* Ball path */}
      <path d="M140,172 L140,128" stroke="#fff" strokeWidth="2" fill="none" markerEnd="url(#arr7)"/>
      <path d="M140,108 Q165,95 193,90" stroke="#fff" strokeWidth="1.5" fill="none" strokeDasharray="4,2" markerEnd="url(#arr7)"/>
      <path d="M200,77 Q190,50 155,30" stroke={col} strokeWidth="2" fill="none" markerEnd="url(#arr7c)"/>
      <defs>
        <marker id="arr7" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill="#fff"/>
        </marker>
        <marker id="arr7c" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill={col}/>
        </marker>
      </defs>
      <text x="140" y="195" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="9">Inject → Stop → Drag flick at goal</text>
    </svg>
  ),
};

// Football drill SVG diagrams
const FOOTBALL_DIAGRAMS = {
  fd1: ({col}) => (
    <svg viewBox="0 0 300 200" style={{width:"100%",height:"auto"}} xmlns="http://www.w3.org/2000/svg">
      <rect width="300" height="200" fill="#1a4a1a" rx="8"/>
      {/* Circle */}
      <circle cx="150" cy="100" r="70" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeDasharray="6,4"/>
      {/* Outer players */}
      {[[150,30],[220,65],[220,135],[150,170],[80,135],[80,65]].map(([x,y],i)=>(
        <g key={i}><circle cx={x} cy={y} r="12" fill={col} opacity="0.9"/><text x={x} y={y+4} textAnchor="middle" fontSize="10" fill="#fff" fontWeight="bold">P</text></g>
      ))}
      {/* Defenders */}
      <circle cx="150" cy="90" r="12" fill="#E63946" opacity="0.9"/><text x="150" y="94" textAnchor="middle" fontSize="10" fill="#fff" fontWeight="bold">D</text>
      <circle cx="160" cy="110" r="12" fill="#E63946" opacity="0.9"/><text x="160" y="114" textAnchor="middle" fontSize="10" fill="#fff" fontWeight="bold">D</text>
      {/* Pass arrows */}
      <path d="M 150 42 Q 200 55 208 65" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5" fill="none" markerEnd="url(#arr)"/>
      <defs><marker id="arr" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="rgba(255,255,255,0.6)"/></marker></defs>
      <text x="150" y="192" textAnchor="middle" fontSize="11" fill="rgba(255,255,255,0.6)">Keep ball away from defenders</text>
    </svg>
  ),
  fd2: ({col}) => (
    <svg viewBox="0 0 300 220" style={{width:"100%",height:"auto"}} xmlns="http://www.w3.org/2000/svg">
      <rect width="300" height="220" fill="#1a4a1a" rx="8"/>
      {/* Goal */}
      <rect x="125" y="4" width="50" height="16" fill="none" stroke="white" strokeWidth="2"/>
      {/* Box outline */}
      <rect x="40" y="4" width="220" height="200" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" strokeDasharray="5,4"/>
      {/* GK */}
      <circle cx="150" cy="26" r="11" fill="#f0b429"/><text x="150" y="30" textAnchor="middle" fontSize="9" fill="#fff" fontWeight="bold">GK</text>
      {/* Defender */}
      <circle cx="150" cy="90" r="12" fill="#E63946" opacity="0.9"/><text x="150" y="94" textAnchor="middle" fontSize="10" fill="#fff" fontWeight="bold">D</text>
      {/* 3 attackers */}
      <circle cx="80" cy="155" r="12" fill={col} opacity="0.9"/><text x="80" y="159" textAnchor="middle" fontSize="10" fill="#fff" fontWeight="bold">A</text>
      <circle cx="150" cy="165" r="12" fill={col} opacity="0.9"/><text x="150" y="169" textAnchor="middle" fontSize="10" fill="#fff" fontWeight="bold">A</text>
      <circle cx="220" cy="155" r="12" fill={col} opacity="0.9"/><text x="220" y="159" textAnchor="middle" fontSize="10" fill="#fff" fontWeight="bold">A</text>
      {/* Arrows */}
      <path d="M80 143 L100 100" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" fill="none" markerEnd="url(#a2)"/>
      <path d="M150 153 L150 105" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" fill="none" markerEnd="url(#a2)"/>
      <path d="M220 143 L200 100" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" fill="none" markerEnd="url(#a2)"/>
      <defs><marker id="a2" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="rgba(255,255,255,0.5)"/></marker></defs>
      <text x="150" y="212" textAnchor="middle" fontSize="11" fill="rgba(255,255,255,0.6)">3 attackers vs 1 defender + GK</text>
    </svg>
  ),
  fd3: ({col}) => (
    <svg viewBox="0 0 300 200" style={{width:"100%",height:"auto"}} xmlns="http://www.w3.org/2000/svg">
      <rect width="300" height="200" fill="#1a4a1a" rx="8"/>
      {/* 4 small goals at corners */}
      <rect x="4" y="8" width="28" height="10" fill="none" stroke="white" strokeWidth="2"/>
      <rect x="268" y="8" width="28" height="10" fill="none" stroke="white" strokeWidth="2"/>
      <rect x="4" y="182" width="28" height="10" fill="none" stroke="white" strokeWidth="2"/>
      <rect x="268" y="182" width="28" height="10" fill="none" stroke="white" strokeWidth="2"/>
      {/* Pitch outline */}
      <rect x="4" y="4" width="292" height="192" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1"/>
      {/* Team A players — red */}
      {[[80,80],[100,100],[80,120]].map(([x,y],i)=>(
        <g key={i}><circle cx={x} cy={y} r="12" fill="#E63946" opacity="0.9"/><text x={x} y={y+4} textAnchor="middle" fontSize="9" fill="#fff" fontWeight="bold">A</text></g>
      ))}
      {/* Team B players — primary */}
      {[[200,80],[180,100],[200,120]].map(([x,y],i)=>(
        <g key={i}><circle cx={x} cy={y} r="12" fill={col} opacity="0.9"/><text x={x} y={y+4} textAnchor="middle" fontSize="9" fill="#fff" fontWeight="bold">B</text></g>
      ))}
      {/* Arrow showing switch */}
      <path d="M100 100 Q 150 70 180 100" stroke="rgba(255,255,255,0.6)" strokeWidth="2" fill="none" strokeDasharray="4,3" markerEnd="url(#a3)"/>
      <defs><marker id="a3" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="rgba(255,255,255,0.6)"/></marker></defs>
      <text x="150" y="192" textAnchor="middle" fontSize="11" fill="rgba(255,255,255,0.6)">Score in either of your 2 goals</text>
    </svg>
  ),
  fd5: ({col}) => (
    <svg viewBox="0 0 300 200" style={{width:"100%",height:"auto"}} xmlns="http://www.w3.org/2000/svg">
      <rect width="300" height="200" fill="#1a4a1a" rx="8"/>
      {/* Goal */}
      <rect x="125" y="4" width="50" height="14" fill="none" stroke="white" strokeWidth="2"/>
      {/* Pitch lines */}
      <rect x="4" y="4" width="292" height="192" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1"/>
      {/* Player A */}
      <circle cx="50" cy="170" r="12" fill={col}/><text x="50" y="174" textAnchor="middle" fontSize="10" fill="#fff" fontWeight="bold">A</text>
      {/* Player B */}
      <circle cx="150" cy="120" r="12" fill={col}/><text x="150" y="124" textAnchor="middle" fontSize="10" fill="#fff" fontWeight="bold">B</text>
      {/* Player C */}
      <circle cx="250" cy="170" r="12" fill="#22c55e"/><text x="250" y="174" textAnchor="middle" fontSize="10" fill="#fff" fontWeight="bold">C</text>
      {/* Pass A to B */}
      <path d="M62 163 L138 127" stroke="rgba(255,255,255,0.7)" strokeWidth="2" fill="none" markerEnd="url(#a4)"/>
      {/* Lay-off B to A */}
      <path d="M138 115 L67 163" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" fill="none" strokeDasharray="4,3" markerEnd="url(#a4)"/>
      {/* Through ball A to C */}
      <path d="M58 158 L100 80" stroke={col} strokeWidth="2" fill="none" markerEnd="url(#a4)"/>
      {/* C run */}
      <path d="M250 158 L200 60" stroke="#22c55e" strokeWidth="2" fill="none" strokeDasharray="5,3" markerEnd="url(#a4)"/>
      <defs><marker id="a4" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="rgba(255,255,255,0.6)"/></marker></defs>
      <text x="60" y="14" fontSize="9" fill="rgba(255,255,255,0.5)">1. A→B</text>
      <text x="120" y="95" fontSize="9" fill="rgba(255,255,255,0.5)">3. A→C</text>
      <text x="220" y="95" fontSize="9" fill="#22c55e">C run</text>
      <text x="150" y="192" textAnchor="middle" fontSize="11" fill="rgba(255,255,255,0.6)">Third man combination</text>
    </svg>
  ),
  fd6: ({col}) => (
    <svg viewBox="0 0 300 220" style={{width:"100%",height:"auto"}} xmlns="http://www.w3.org/2000/svg">
      <rect width="300" height="220" fill="#1a4a1a" rx="8"/>
      {/* Goal */}
      <rect x="110" y="4" width="80" height="16" fill="none" stroke="white" strokeWidth="2"/>
      {/* Penalty box */}
      <rect x="60" y="4" width="180" height="80" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5"/>
      {/* GK */}
      <circle cx="150" cy="28" r="11" fill="#f0b429"/><text x="150" y="32" textAnchor="middle" fontSize="9" fill="#fff" fontWeight="bold">GK</text>
      {/* Defenders */}
      <circle cx="120" cy="60" r="11" fill="#E63946"/><text x="120" y="64" textAnchor="middle" fontSize="9" fill="#fff" fontWeight="bold">D</text>
      <circle cx="180" cy="60" r="11" fill="#E63946"/><text x="180" y="64" textAnchor="middle" fontSize="9" fill="#fff" fontWeight="bold">D</text>
      {/* Crosser */}
      <circle cx="20" cy="130" r="12" fill={col}/><text x="20" y="134" textAnchor="middle" fontSize="9" fill="#fff" fontWeight="bold">W</text>
      {/* Attackers with runs */}
      <circle cx="110" cy="110" r="12" fill={col}/><text x="110" y="114" textAnchor="middle" fontSize="9" fill="#fff" fontWeight="bold">NP</text>
      <circle cx="155" cy="95" r="12" fill={col}/><text x="155" y="99" textAnchor="middle" fontSize="9" fill="#fff" fontWeight="bold">P</text>
      <circle cx="210" cy="130" r="12" fill={col}/><text x="210" y="134" textAnchor="middle" fontSize="9" fill="#fff" fontWeight="bold">FP</text>
      {/* Cross arc */}
      <path d="M32 130 Q 60 50 110 45" stroke="rgba(255,255,255,0.7)" strokeWidth="2" fill="none" markerEnd="url(#a5)"/>
      {/* Attacker runs */}
      <path d="M110 98 L120 55" stroke={col} strokeWidth="1.5" fill="none" strokeDasharray="4,3" markerEnd="url(#a5)"/>
      <path d="M210 118 L190 55" stroke={col} strokeWidth="1.5" fill="none" strokeDasharray="4,3" markerEnd="url(#a5)"/>
      <defs><marker id="a5" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="rgba(255,255,255,0.6)"/></marker></defs>
      <text x="130" y="148" fontSize="9" fill="rgba(255,255,255,0.4)">NP=Near Post FP=Far Post P=Penalty spot</text>
      <text x="150" y="212" textAnchor="middle" fontSize="11" fill="rgba(255,255,255,0.6)">Crossing & finishing — coordinated runs</text>
    </svg>
  ),
}


// ── P4M Session Diagram Templates ────────────────────────────────────────────
// Detects drill type from keywords and renders appropriate pitch diagram

function detectDrillShape(text) {
  const t = (text||"").toLowerCase();
  if(t.includes("penalty corner")||t.includes("short corner")||t.includes("pc")) return "penalty_corner";
  if(t.includes("rondo")||t.includes("keep ball")||t.includes("possession circle")) return "rondo";
  if(t.includes("1v1")||t.includes("one v one")||t.includes("1 v 1")) return "one_v_one";
  if(t.includes("pressing")||t.includes("press")||t.includes("high press")) return "press";
  if(t.includes("transition")||t.includes("counter")) return "transition";
  if(t.includes("penalty stroke")||t.includes("penalty flick")) return "penalty_stroke";
  if(t.includes("channel")||t.includes("wide")||t.includes("overlap")||t.includes("crossing")) return "channels";
  if(t.includes("small sided")||t.includes("sssg")||t.includes("game")||t.includes("3v3")||t.includes("4v4")||t.includes("5v5")||t.includes("6v6")) return "small_sided";
  if(t.includes("passing")||t.includes("combination")||t.includes("pattern")||t.includes("third man")) return "passing_pattern";
  if(t.includes("shooting")||t.includes("finish")||t.includes("goal")||t.includes("circle entry")) return "shooting";
  if(t.includes("defend")||t.includes("defensive")||t.includes("shape")||t.includes("structure")) return "defensive_shape";
  return "full_pitch"; // default
}

function SessionDiagram({drillText, sessionText, drillShape=null, diagramDetail=null, col="#E63946", sport="Hockey"}) {
  const shape = drillShape || diagramDetail?.shape || detectDrillShape(drillText+" "+(sessionText||""));
  const c = col||"#E63946";
  const w = 280, h = 180;

  // Shared pitch background
  const PitchBg = ({half=false}) => {
    const ph = half ? h : h;
    const pw = w;
    return (
      <g>
        {/* Grass */}
        <rect x={0} y={0} width={pw} height={ph} fill="#1a5c1a" rx={4}/>
        {/* Stripe pattern */}
        {[0,1,2,3,4].map(i=>(
          <rect key={i} x={i*(pw/5)} y={0} width={pw/10} height={ph} fill="rgba(0,0,0,0.08)"/>
        ))}
        {/* Pitch outline */}
        <rect x={6} y={6} width={pw-12} height={ph-12} fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth={1.5}/>
        {/* Centre line */}
        {!half&&<line x1={pw/2} y1={6} x2={pw/2} y2={ph-6} stroke="rgba(255,255,255,0.5)" strokeWidth={1}/>}
        {!half&&<circle cx={pw/2} cy={ph/2} r={20} fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth={1}/>}
      </g>
    );
  };

  const HalfPitchBg = () => {
    return (
      <g>
        <rect x={0} y={0} width={w} height={h} fill="#1a5c1a" rx={4}/>
        {[0,1,2,3,4].map(i=>(<rect key={i} x={i*(w/5)} y={0} width={w/10} height={h} fill="rgba(0,0,0,0.08)"/>))}
        <rect x={6} y={6} width={w-12} height={h-12} fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth={1.5}/>
        {/* Goal line */}
        <line x1={6} y1={h-6} x2={w-6} y2={h-6} stroke="rgba(255,255,255,0.7)" strokeWidth={1.5}/>
        {/* D circle */}
        <ellipse cx={w/2} cy={h-6} rx={65} ry={45} fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth={1} strokeDasharray="3,2"/>
        {/* Goal */}
        <rect x={w/2-20} y={h-10} width={40} height={8} fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth={2}/>
        {/* 23m line */}
        <line x1={6} y1={h*0.45} x2={w-6} y2={h*0.45} stroke="rgba(255,255,255,0.4)" strokeWidth={1} strokeDasharray="4,3"/>
      </g>
    );
  };

  const GridBg = () => (
    <g>
      <rect x={0} y={0} width={w} height={h} fill="#1a5c1a" rx={4}/>
      {[0,1,2,3].map(i=>(<rect key={i} x={i*(w/4)} y={0} width={w/8} height={h} fill="rgba(0,0,0,0.08)"/>))}
      <rect x={20} y={16} width={w-40} height={h-32} fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth={1.5} strokeDasharray="5,3"/>
    </g>
  );

  // Player marker
  const P = ({x,y,team="a",num=""}) => (
    <g>
      <circle cx={x} cy={y} r={8} fill={team==="a"?c:"rgba(255,255,255,0.9)"} stroke="rgba(0,0,0,0.4)" strokeWidth={1}/>
      {num&&<text x={x} y={y+4} textAnchor="middle" fill={team==="a"?"#fff":"#000"} fontSize={7} fontWeight="bold">{num}</text>}
    </g>
  );

  // Arrow
  const Arrow = ({x1,y1,x2,y2,col:ac="rgba(255,255,0,0.8)",dashed=false}) => {
    const dx=x2-x1, dy=y2-y1, len=Math.sqrt(dx*dx+dy*dy);
    if(len<2) return null;
    const ux=dx/len, uy=dy/len;
    const hx=x2-ux*8, hy=y2-uy*8;
    return (
      <g>
        <line x1={x1} y1={y1} x2={hx} y2={hy} stroke={ac} strokeWidth={1.5} strokeDasharray={dashed?"4,3":undefined}/>
        <polygon points={`${x2},${y2} ${hx-uy*4},${hy+ux*4} ${hx+uy*4},${hy-ux*4}`} fill={ac}/>
      </g>
    );
  };

  // Ball
  const Ball = ({x,y}) => (
    <g>
      <circle cx={x} cy={y} r={5} fill="#fff" stroke="#333" strokeWidth={1}/>
      <circle cx={x} cy={y} r={2} fill="#333"/>
    </g>
  );

  // ── Diagram variants ──────────────────────────────────────────────────────
  if(shape==="penalty_corner") return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" style={{maxWidth:w,display:"block",borderRadius:8}}>
      <HalfPitchBg/>
      {/* Injector */}
      <P x={6} y={h*0.55} team="a" num="I"/>
      {/* Stopper */}
      <P x={w/2-30} y={h-22} team="a" num="S"/>
      {/* First runner */}
      <P x={w/2+10} y={h-28} team="a" num="1"/>
      {/* Second runner */}
      <P x={w/2+40} y={h-35} team="a" num="2"/>
      {/* Shooter */}
      <P x={w/2-10} y={h-45} team="a" num="SH"/>
      {/* Defenders */}
      <P x={w/2-15} y={h-18} team="b"/>
      <P x={w/2+5} y={h-18} team="b"/>
      <P x={w/2+25} y={h-18} team="b"/>
      <P x={w/2+45} y={h-18} team="b"/>
      {/* Ball at corner spot */}
      <Ball x={6} y={h*0.55}/>
      {/* Injection arrow */}
      <Arrow x1={14} y1={h*0.55} x2={w/2-38} y2={h-22}/>
      {/* Pass arrow */}
      <Arrow x1={w/2-22} y1={h-22} x2={w/2-18} y2={h-45} dashed/>
      <text x={w/2} y={14} textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize={9}>PENALTY CORNER</text>
    </svg>
  );

  if(shape==="rondo") return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" style={{maxWidth:w,display:"block",borderRadius:8}}>
      <GridBg/>
      {/* Circle of players */}
      {[0,1,2,3,4,5].map(i=>{
        const angle = (i/6)*Math.PI*2 - Math.PI/2;
        const rx=60, ry=50;
        return <P key={i} x={w/2+rx*Math.cos(angle)} y={h/2+ry*Math.sin(angle)} team="a"/>;
      })}
      {/* Defenders in middle */}
      <P x={w/2-12} y={h/2} team="b"/>
      <P x={w/2+12} y={h/2} team="b"/>
      {/* Ball */}
      <Ball x={w/2+60} y={h/2}/>
      {/* Pass arrows */}
      <Arrow x1={w/2+52} y1={h/2} x2={w/2+30} y2={h/2-48} col="rgba(255,255,0,0.7)"/>
      <text x={w/2} y={14} textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize={9}>RONDO / POSSESSION</text>
    </svg>
  );

  if(shape==="one_v_one") return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" style={{maxWidth:w,display:"block",borderRadius:8}}>
      <HalfPitchBg/>
      {/* Attacker */}
      <P x={w/2} y={h*0.35} team="a"/>
      {/* Defender */}
      <P x={w/2} y={h*0.65} team="b"/>
      {/* Ball */}
      <Ball x={w/2} y={h*0.35}/>
      {/* Movement arrows */}
      <Arrow x1={w/2} y1={h*0.43} x2={w/2+25} y2={h*0.58} col="rgba(255,255,0,0.8)"/>
      <Arrow x1={w/2} y1={h*0.43} x2={w/2-25} y2={h*0.58} col="rgba(255,255,0,0.8)" dashed/>
      <text x={w/2} y={14} textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize={9}>1 v 1</text>
    </svg>
  );

  if(shape==="press") return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" style={{maxWidth:w,display:"block",borderRadius:8}}>
      <PitchBg/>
      {/* Ball carrier (opposition) */}
      <P x={w*0.25} y={h*0.3} team="b"/>
      <Ball x={w*0.25} y={h*0.3}/>
      {/* Press players */}
      <P x={w*0.35} y={h*0.25} team="a"/>
      <P x={w*0.35} y={h*0.42} team="a"/>
      <P x={w*0.5} y={h*0.3} team="a"/>
      {/* Press arrows */}
      <Arrow x1={w*0.33} y1={h*0.27} x2={w*0.27} y2={h*0.3} col="rgba(255,100,100,0.9)"/>
      <Arrow x1={w*0.33} y1={h*0.4} x2={w*0.27} y2={h*0.33} col="rgba(255,100,100,0.9)"/>
      <Arrow x1={w*0.48} y1={h*0.3} x2={w*0.37} y2={h*0.3} col="rgba(255,100,100,0.9)" dashed/>
      {/* Press trigger zone */}
      <rect x={w*0.1} y={h*0.1} width={w*0.35} height={h*0.4} fill="rgba(255,100,100,0.08)" stroke="rgba(255,100,100,0.3)" strokeWidth={1} strokeDasharray="4,3"/>
      <text x={w/2} y={14} textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize={9}>PRESSING TRIGGER</text>
    </svg>
  );

  if(shape==="channels") return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" style={{maxWidth:w,display:"block",borderRadius:8}}>
      <PitchBg/>
      {/* Channel markers */}
      <line x1={w*0.33} y1={6} x2={w*0.33} y2={h-6} stroke="rgba(255,255,255,0.25)" strokeWidth={1} strokeDasharray="4,3"/>
      <line x1={w*0.67} y1={6} x2={w*0.67} y2={h-6} stroke="rgba(255,255,255,0.25)" strokeWidth={1} strokeDasharray="4,3"/>
      {/* Wide player */}
      <P x={w*0.12} y={h*0.45} team="a"/>
      <Ball x={w*0.12} y={h*0.45}/>
      {/* Central player */}
      <P x={w*0.5} y={h*0.55} team="a"/>
      {/* Run arrows */}
      <Arrow x1={w*0.12} y1={h*0.4} x2={w*0.12} y2={h*0.2} col="rgba(255,255,0,0.8)"/>
      <Arrow x1={w*0.2} y1={h*0.45} x2={w*0.42} y2={h*0.55} col="rgba(255,255,0,0.7)" dashed/>
      <text x={w/2} y={14} textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize={9}>WIDE CHANNELS</text>
    </svg>
  );

  if(shape==="small_sided") return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" style={{maxWidth:w,display:"block",borderRadius:8}}>
      <GridBg/>
      {/* Mini goals */}
      <rect x={20} y={h/2-12} width={6} height={24} fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth={2}/>
      <rect x={w-26} y={h/2-12} width={6} height={24} fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth={2}/>
      {/* Team A */}
      <P x={80} y={h*0.35} team="a"/>
      <P x={110} y={h*0.6} team="a"/>
      <P x={145} y={h*0.42} team="a"/>
      {/* Team B */}
      <P x={200} y={h*0.38} team="b"/>
      <P x={170} y={h*0.62} team="b"/>
      <P x={135} y={h*0.55} team="b"/>
      {/* Ball */}
      <Ball x={145} y={h*0.42}/>
      <text x={w/2} y={14} textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize={9}>SMALL SIDED GAME</text>
    </svg>
  );

  if(shape==="passing_pattern") return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" style={{maxWidth:w,display:"block",borderRadius:8}}>
      <GridBg/>
      {/* Triangle / pattern players */}
      <P x={w*0.2} y={h*0.7} team="a" num="A"/>
      <P x={w*0.5} y={h*0.3} team="a" num="B"/>
      <P x={w*0.8} y={h*0.7} team="a" num="C"/>
      <P x={w*0.5} y={h*0.65} team="a" num="D"/>
      <Ball x={w*0.2} y={h*0.7}/>
      {/* Pass arrows */}
      <Arrow x1={w*0.28} y1={h*0.67} x2={w*0.42} y2={h*0.35} col="rgba(255,255,0,0.8)"/>
      <Arrow x1={w*0.5} y1={h*0.38} x2={w*0.5} y2={h*0.56} col="rgba(255,255,0,0.7)" dashed/>
      <Arrow x1={w*0.58} y1={h*0.65} x2={w*0.72} y2={h*0.68} col="rgba(255,255,0,0.6)" dashed/>
      <text x={w/2} y={14} textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize={9}>PASSING COMBINATION</text>
    </svg>
  );

  if(shape==="shooting") return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" style={{maxWidth:w,display:"block",borderRadius:8}}>
      <HalfPitchBg/>
      {/* Shooter */}
      <P x={w/2-20} y={h*0.45} team="a"/>
      <Ball x={w/2-20} y={h*0.45}/>
      {/* Support */}
      <P x={w*0.25} y={h*0.5} team="a"/>
      <P x={w*0.75} y={h*0.5} team="a"/>
      {/* Shot arrow */}
      <Arrow x1={w/2-14} y1={h*0.45} x2={w/2-5} y2={h-16} col="rgba(255,200,0,0.9)"/>
      {/* Support runs */}
      <Arrow x1={w*0.25} y1={h*0.45} x2={w*0.35} y2={h*0.35} col="rgba(255,255,255,0.5)" dashed/>
      <Arrow x1={w*0.75} y1={h*0.45} x2={w*0.65} y2={h*0.35} col="rgba(255,255,255,0.5)" dashed/>
      <text x={w/2} y={14} textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize={9}>SHOOTING / CIRCLE ENTRY</text>
    </svg>
  );

  if(shape==="defensive_shape") return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" style={{maxWidth:w,display:"block",borderRadius:8}}>
      <PitchBg/>
      {/* Defensive block */}
      <P x={w*0.45} y={h*0.65} team="a"/>
      <P x={w*0.55} y={h*0.65} team="a"/>
      <P x={w*0.35} y={h*0.72} team="a"/>
      <P x={w*0.65} y={h*0.72} team="a"/>
      <P x={w*0.5} y={h*0.78} team="a"/>
      {/* Attacking team */}
      <P x={w*0.35} y={h*0.45} team="b"/>
      <P x={w*0.5} y={h*0.4} team="b"/>
      <P x={w*0.65} y={h*0.45} team="b"/>
      <Ball x={w*0.5} y={h*0.4}/>
      {/* Shape lines */}
      <line x1={w*0.35} y1={h*0.72} x2={w*0.65} y2={h*0.72} stroke={c} strokeWidth={1} strokeDasharray="3,2" opacity={0.5}/>
      <line x1={w*0.45} y1={h*0.65} x2={w*0.55} y2={h*0.65} stroke={c} strokeWidth={1} strokeDasharray="3,2" opacity={0.5}/>
      <text x={w/2} y={14} textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize={9}>DEFENSIVE SHAPE</text>
    </svg>
  );

  if(shape==="transition") return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" style={{maxWidth:w,display:"block",borderRadius:8}}>
      <PitchBg/>
      {/* Ball won in midfield */}
      <P x={w*0.5} y={h*0.5} team="a"/>
      <Ball x={w*0.5} y={h*0.5}/>
      {/* Counter attack runners */}
      <P x={w*0.35} y={h*0.65} team="a"/>
      <P x={w*0.65} y={h*0.65} team="a"/>
      {/* Opposition recovering */}
      <P x={w*0.4} y={h*0.35} team="b"/>
      <P x={w*0.6} y={h*0.35} team="b"/>
      {/* Transition arrows */}
      <Arrow x1={w*0.5} y1={h*0.55} x2={w*0.38} y2={h*0.68} col="rgba(255,255,0,0.9)"/>
      <Arrow x1={w*0.5} y1={h*0.55} x2={w*0.62} y2={h*0.68} col="rgba(255,255,0,0.9)"/>
      <Arrow x1={w*0.5} y1={h*0.55} x2={w*0.5} y2={h*0.7} col="rgba(255,255,0,0.7)" dashed/>
      <text x={w/2} y={14} textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize={9}>TRANSITION / COUNTER</text>
    </svg>
  );

  if(shape==="penalty_stroke") return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" style={{maxWidth:w,display:"block",borderRadius:8}}>
      <HalfPitchBg/>
      <P x={w/2} y={h*0.45} team="a"/>
      <Ball x={w/2} y={h*0.45}/>
      <Arrow x1={w/2} y1={h*0.5} x2={w/2+15} y2={h-20} col="rgba(255,200,0,0.9)"/>
      <text x={w/2} y={14} textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize={9}>PENALTY STROKE</text>
    </svg>
  );

  // Default — full pitch overview
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" style={{maxWidth:w,display:"block",borderRadius:8}}>
      <PitchBg/>
      {/* Generic players */}
      <P x={w*0.25} y={h*0.3} team="a"/>
      <P x={w*0.5} y={h*0.25} team="a"/>
      <P x={w*0.75} y={h*0.3} team="a"/>
      <P x={w*0.4} y={h*0.5} team="a"/>
      <P x={w*0.6} y={h*0.5} team="a"/>
      <Ball x={w*0.5} y={h*0.25}/>
      <Arrow x1={w*0.5} y1={h*0.3} x2={w*0.42} y2={h*0.45} col="rgba(255,255,0,0.7)"/>
      <text x={w/2} y={14} textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize={9}>TRAINING SESSION</text>
    </svg>
  );
}

;


// ── P4M CONTACT CONSTANTS ────────────────────────────────────────────────────
const P4M_PRIVACY_EMAIL = "lrowan3669@gmail.com";
const P4M_TERMS_URL     = "https://p4m.club/terms";
const P4M_PRIVACY_URL   = "https://p4m.club/privacy";

export default function P4M() {
  // ── screen state ──
  const [screen, setScreen] = useState("splash");
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  const [clubId, setClubId] = useState(null);
  const [authError, setAuthError] = useState("");

  // ── Restore session on app load ──
  // ── PWA INSTALL ────────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", () => {
      setInstallPrompt(null);
      setInstallDismissed(true);
      localStorage.setItem("p4m_install_dismissed","1");
    });
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  useEffect(() => {
    const restoreSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const member = await db.getCurrentMember();
          if (member && member.approved) {
            setCurrentUser({ ...member, id: member.id });
            setClubId(member.club_id);
            // Load club data
            const clubData = await db.getClub(member.club_id);
            if (clubData) {
              setClub({
                name: clubData.name, sport: clubData.sport,
                primary: clubData.primary_col, secondary: clubData.secondary_col,
                logo: clubData.logo_url, logoPos: clubData.logo_pos,
                ethos: clubData.ethos, mens: clubData.mens, ladies: clubData.ladies,
                juniorAges: clubData.junior_ages || [], swimGroups: clubData.swim_groups || [], customAgeGroups: clubData.custom_age_groups || [], customTeams: clubData.custom_teams || [], ftClubCode: clubData.ft_club_code || "",
                gmsClubId: clubData.gms_club_id||"",
                  gmsTeams: clubData.gms_teams||[],
                  xeroConnected: clubData.xero_connected||false,
                  xeroTenantName: clubData.xero_tenant_name||"",
                  subscriptionStatus: localStorage.getItem("p4m_sub_override")||clubData.subscription_status||"free",
                  stripeCustomerId: clubData.stripe_customer_id||"",
              });
            }
            await loadAllData(member.club_id, member.id);
            // Check for Stripe payment success redirect
            const urlParams = new URLSearchParams(window.location.search);
            if(urlParams.get("payment")==="success"){
              window.history.replaceState({},"",window.location.pathname);
              // Persist Pro locally so it survives navigation/refresh while webhook catches up
              localStorage.setItem("p4m_sub_override","active");
              setClub(c=>({...c,subscriptionStatus:"active"}));
              setScreen("app");
              showToast("🎉 Subscription activated — welcome to P4M Pro!");
              // Also write directly to DB — don't rely solely on webhook
              supabase.from("clubs").update({
                subscription_status:"active",
                subscription_plan: urlParams.get("plan")||"pro",
              }).eq("id",member.club_id).then(({error})=>{
                if(!error) localStorage.removeItem("p4m_sub_override");
              });
            } else {
              setScreen("app");
            }
            // Only show if not already decided
      setTimeout(() => {
        const dismissed = localStorage.getItem("p4m_notif_dismissed");
        if(Notification.permission === "default" && !dismissed) setShowNotifPrompt(true);
        else if(Notification.permission === "granted") registerPushNotifications();
      }, 3000);
          }
        }
      } catch(e) { console.error("Session restore failed:", e); }
      setLoading(false);
    };
    restoreSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === "SIGNED_OUT") {
        setCurrentUser(null); setClubId(null); setScreen("auth"); setAuthMode("login");
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // ── Load all club data from Supabase ──
  const loadAllData = useCallback(async (cid, passedMemberId=null) => {
    if (!cid) return;
    try {
      const [
        playersData, fixturesData, lineupMap,
        postsData, paymentsData, eventsData, tournamentsData,
      ] = await Promise.all([
        db.getPlayers(cid),
        db.getFixtures(cid),
        db.getLineups(cid),
        db.getPosts(cid),
        db.getPayments(cid),
        db.getEvents(cid),
        db.getTournaments(cid),
      ]);
      // Load all members including pending approvals
      const { data: membersData } = await supabase
        .from("members")
        .select("*")
        .eq("club_id", cid);
      if(membersData) {
        setUsers(membersData.map(m=>({
          id:m.id, name:m.name, email:m.email,
          role:m.role, team:m.team, approved:m.approved,
          auth_id:m.auth_id, photo:m.avatar_url,
        })));
      }
      // Map Supabase column names back to app state shape
      setPlayers(playersData.map(p => ({
        id: p.id, name: p.name, team: p.team, position: p.position,
        events: p.events || [], photo: p.photo_url, scores: p.scores || {},
        fitness: p.fitness || {}, pin: p.pin, lastAssessed: p.last_assessed,
        feedback: [], userId: p.member_id,
        photoPosition: p.scores?._photoPosition ?? 20,
        photoX: p.scores?._photoX ?? 0,
        photoY: p.scores?._photoY ?? 0,
        photoZoom: p.scores?._photoZoom ?? 100,
      })));
      setFixtures(fixturesData.map(f => ({
        id: f.id, opponent: f.opponent, date: f.date, time: f.time,
        location: f.location, team: f.team, teams: f.teams || [],
        isHome: f.is_home, result: f.result, goalsFor: f.goals_for,
        goalsAgainst: f.goals_against, availability: f.availability || {},
        isSession: f.is_session,
      })));
      setLineups(lineupMap);
      setPosts(postsData.map(p => ({
        id: p.id, authorId: p.author_id, author: p.author_name, authorPhoto: p.author_photo||null, body: p.body,
        tag: p.tag, team: p.team, photo: p.photo_url, pinned: p.pinned,
        time: new Date(p.created_at).getTime(), likes: 0,
        reactions: p.reactions || {}, liked: false,
        comments: (p.comments || []).map(c => ({
          id: c.id, author: c.author_name, text: c.body,
          time: new Date(c.created_at).getTime(),
        })),
      })));
      setPayments(paymentsData.map(p => ({
        id: p.id, title: p.title, amount: p.amount, type: p.type,
        dueDate: p.due_date, status: p.status, team: p.team,
        playerId: p.player_id, playerName: p.player_name, description: p.description,
      })));
      setEvents(eventsData.map(e => ({
        id: e.id, title: e.title, date: e.date, time: e.time,
        location: e.location, description: e.description, type: e.type,
        photo: e.photo_url, rsvp: e.rsvp || {},
      })));
      setFestivals(tournamentsData.map(t => ({
        id: t.id, title: t.title, date: t.date, location: t.location,
        description: t.description, ageGroup: t.age_group, type: t.type,
        photo: t.photo_url, availability: t.availability || {},
      })));
      // Load conversations and calculate unread count
      const { data: convData } = await supabase
        .from("conversations")
        .select("*")
        .eq("club_id", cid)
        .order("last_message_at", {ascending:false});
      if(convData) {
        // Use passedMemberId if available (avoids an extra RLS-restricted query)
        let myMemberId = passedMemberId;
        if(!myMemberId) {
          const { data: { user: authUser } } = await supabase.auth.getUser();
          const { data: myMemberRec } = await supabase
            .from("members").select("id").eq("auth_id", authUser?.id||"").single();
          myMemberId = myMemberRec?.id || authUser?.id || "";
        }
        // Filter to conversations the current user is in
        const myConvs = convData.filter(c=>
          (c.member_ids||[]).some(id=>id===myMemberId)
        );
        // Deduplicate direct conversations — group all conv IDs per pair,
        // keep the one with a last_message (actual messages), else the oldest.
        const pairGroups = {}; // pairKey -> [conv, ...]
        myConvs.forEach(c=>{
          if(c.type!=="direct"){ return; }
          const pairKey = [...(c.member_ids||[])].sort().join("|");
          if(!pairGroups[pairKey]) pairGroups[pairKey] = [];
          pairGroups[pairKey].push(c);
        });
        const groupMap = {}; // primaryConvId -> [allConvIds]
        const deduped = myConvs.filter(c=>{
          if(c.type!=="direct") return true;
          const pairKey = [...(c.member_ids||[])].sort().join("|");
          const group = pairGroups[pairKey];
          // Pick the canonical conv: prefer the one with a last_message, else oldest
          const canonical = group.find(x=>x.last_message) || group[group.length-1];
          if(c.id !== canonical.id) return false;
          groupMap[canonical.id] = group.map(x=>x.id);
          return true;
        });
        setConvGroupMap(groupMap);
        setConversations(deduped);
        // Count unread: last message not from me and I haven't read it
        const unread = deduped.filter(c=>
          c.last_message &&
          c.last_sender &&
          c.last_sender !== myMemberId &&
          !c._readByMe
        ).length;
        setUnreadCount(unread);
      }

      // Load training frameworks
      const { data: fwData } = await supabase
        .from("training_frameworks")
        .select("*")
        .eq("club_id", cid)
        .order("created_at", { ascending: false });
      if(fwData?.length) setSavedFrameworks(fwData);

      // Load individual training programmes
      const { data: progData } = await supabase
        .from("training_programs")
        .select("*")
        .eq("club_id", cid);
      if(progData?.length) {
        const progMap = {};
        progData.forEach(p => { progMap[p.player_id] = p.program_data; });
        setAthletePrograms(progMap);
      }

    } catch(e) { console.error("Failed to load data:", e); showToast("Failed to load some data"); }
  }, []);
  // splash → onboard → auth → app

  // ── onboarding ──
  const [onboardTip, setOnboardTip]   = useState(null); // {key, title, msg, nextPanel}
  const [seenTips, setSeenTips]       = useState(()=>JSON.parse(localStorage.getItem("p4m_seen_tips")||"[]"));

  const ONBOARD_TIPS = {
    first_login:    { title:"Welcome to P4M! 👋", msg:"Let's get you set up. Start by going to Club Settings to add your teams, upload your club badge and set your colours.", nextPanel:"club-settings", btn:"Go to Club Settings" },
    add_players:    { title:"Add your squad 👥", msg:"Head to the Players section to invite or add your players. They can sign up themselves and you approve them.", nextPanel:"players", btn:"Go to Players" },
    first_fixture:  { title:"Add your first fixture 📅", msg:"Go to Upcoming to add your fixtures. You can also import a full season CSV from your league secretary.", nextPanel:"fixtures", btn:"Go to Upcoming" },
    build_lineup:   { title:"Build your lineup 🏆", msg:"Once you have a fixture and players, tap on a fixture to build the lineup. Set formations, tactics and individual player instructions.", btn:"Got it" },
    clubhub_tip:    { title:"ClubHub 💬", msg:"ClubHub is your team's social feed. Post announcements, pin important updates to players' home screens, and share photos.", btn:"Got it" },
  };

  const showTip = (key) => {
    if(seenTips.includes(key)) return;
    setOnboardTip({key, ...ONBOARD_TIPS[key]});
  };

  const dismissTip = (tip, navigate_to) => {
    const seen=[...seenTips,tip.key];
    setSeenTips(seen);
    localStorage.setItem("p4m_seen_tips",JSON.stringify(seen));
    setOnboardTip(null);
    if(navigate_to) navigate(navigate_to);
  };
  const [obStep, setObStep] = useState(1);
  const OB_TOTAL = 7;
  const [club, setClub] = useState({
    name:"", sport:"Hockey", primary:"#E63946", secondary:"#1e3a8a",
    juniorAges:[], logo:null, ethos:"", ethosPoints:[], ftClubCode:""
  });

  // ── auth ──
  const [authMode, setAuthMode] = useState("login");
  const [authForm, setAuthForm] = useState({ name:"", email:"", password:"", confirm:"", role:"player", team:"", sport:"Hockey", clubId:"", clubName:"", isPlayerCoach:false, alsoPlayer:false, alsoPlayerTeam:"", alsoPlayerPosition:"" });
  const [clubSearch, setClubSearch] = useState("");
  const [clubResults, setClubResults] = useState([]);
  const [clubSearching, setClubSearching] = useState(false);
  const [selectedSignupClub, setSelectedSignupClub] = useState(null); // {id, name, sport, teams}
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);

  // ── app state ──
  const [panel, setPanel] = useState("home");
  const [photoFrameLocked, setPhotoFrameLocked] = useState(true);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [installDismissed, setInstallDismissed] = useState(()=>!!localStorage.getItem("p4m_install_dismissed"));
  const isPWA = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
  const isMobile = /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);
  const [showNotifBanner, setShowNotifBanner] = useState(false);
  const [showNotifPrompt, setShowNotifPrompt] = useState(false);
  const [modal, setModal] = useState(null);
  const [toast, setToastMsg] = useState("");
  const [fixtures, setFixtures] = useState([]);
  const [players, setPlayers] = useState([]);
  const [posts, setPosts] = useState([
  ]);
  const [payments, setPayments] = useState([
    { id:uid(), title:"April Match Fee", amount:5, type:"match", dueDate:"2026-04-01", status:"pending", team:"Men's 1s" }
  ]);
  const [leagueTable, setLeagueTable] = useState([]);
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [feedFilter, setFeedFilter] = useState("all");
  const [detailTab, setDetailTab] = useState("fitness");
  // subscribe screen
  const [subPlan, setSubPlan]       = useState("pro");
  const [promoCode, setPromoCode]   = useState("");
  const [subLoading, setSubLoading] = useState(false);
  const [currentStaff, setCurrentStaff] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  // coach wizard
  const [wizardStep, setWizardStep] = useState(0);
  const [swimWizardStep, setSwimWizardStep] = useState(0);
  const [swimPlan, setSwimPlan] = useState(null); // group plans keyed by squad name
  const [swimPlans, setSwimPlans] = useState({}); // {squadName: plan}
  const [athletePrograms, setAthletePrograms] = useState({}); // {playerId: program}
  // ── Messaging ──
  const [conversations, setConversations]   = useState([]);
  const [convGroupMap, setConvGroupMap]     = useState({}); // primaryConvId -> [allConvIds]
  const [activeConvId, setActiveConvId]     = useState(null);
  const [convMessages, setConvMessages]     = useState({}); // {convId: [msgs]}
  // msgDraft is now local state inside MessagesPanel
  const [msgTab, setMsgTab]                 = useState("convs"); // convs | new

  const [newGroupName, setNewGroupName]     = useState("");
  const [newGroupMembers, setNewGroupMembers] = useState([]);
  const [unreadCount, setUnreadCount]       = useState(0);
  const [indivWizardType, setIndivWizardType] = useState(null); // null | "squad" | "individual"
  const [indivAthleteId, setIndivAthleteId] = useState("");
  const [indivGoals, setIndivGoals] = useState([]);
  const [indivGenerating, setIndivGenerating] = useState(false);
  const [swimWizardData, setSwimWizardData] = useState({
    ageGroup:"", targetComp:"", targetDate:"", targetEvents:[],
    currentPBs:{}, qualTimes:{}, sessionsPerWeek:5, sessionLength:90,
    strengths:[], focusAreas:[], annualGoals:[], maxHR:190
  });
  const [wizardData, setWizardData] = useState({ goals:[], squadDesc:"", sessions:2, sessionLen:75, strengths:[], weaknesses:[], blockLength:6, seasonWeeks:28, startDate:"", trainingDays:[], trainingTimes:{}, trainingLocations:{} });
  const [framework, setFramework] = useState(null);
  const [savedFrameworks, setSavedFrameworks] = useState([]); // [{id, team, sport, createdAt, data}]
  const [frameworkTeam, setFrameworkTeam] = useState("");
  const [wizardLoading, setWizardLoading] = useState(false);
  const [teamFilter, setTeamFilter] = useState("");
  const [squadCardsTeam, setSquadCardsTeam] = useState("");
  const [mesocycle, setMesocycle] = useState(null);
  const [mesoLoading, setMesoLoading] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState(0);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [coachTab, setCoachTab] = useState("macro"); // macro | meso | micro
  const [events, setEvents] = useState([
    {id:"e1",title:"End of Season Awards",date:"2026-05-15",time:"19:00",location:"Clitheroe Golf Club",description:"Annual awards evening celebrating the season.",type:"social",rsvp:{}},
    {id:"e2",title:"Christmas Party",date:"2026-12-13",time:"20:00",location:"TBC",description:"Club Christmas party — all members welcome.",type:"social",rsvp:{}},
  ]);
  const [festivals, setFestivals] = useState([
    {id:"f1",title:"North West Junior Festival U12",date:"2026-05-03",location:"Altrincham HC",description:"U12 festival — 6-a-side format, 8 teams.",type:"festival",ageGroup:"U12",availability:{}},
    {id:"f2",title:"Lancashire Junior Festival U10",date:"2026-06-14",location:"Preston HC",description:"U10 festival — 5-a-side format.",type:"festival",ageGroup:"U10",availability:{}},
  ]);
  const [eventForm, setEventForm] = useState({title:"",date:"",time:"",location:"",description:"",type:"social",photo:null});
  const [festivalForm, setFestivalForm] = useState({title:"",date:"",location:"",description:"",ageGroup:"U12",photo:null});
  const [extraSessions, setExtraSessions] = useState([]);
  const [lineups, setLineups] = useState({}); // keyed by fixtureId
  const [menuOpen, setMenuOpen] = useState(false);
  const isPlayer = currentUser?.role === "player" || currentUser?.role === "parent" || currentUser?.is_player_coach || currentUser?.also_player;

  // form state
  const [fixForm, setFixForm] = useState({ opponent:"", date:"", time:"14:00", location:"", team:"", isHome:true });
  const [postForm, setPostForm] = useState({ body:"", tag:"team", team:"", teams:[], photo:null, pinned:false });
  const [payForm, setPayForm] = useState({ title:"", amount:"", type:"match", dueDate:"", team:"" });
  const [fbForm, setFbForm] = useState({ fixIdx:"", custom:"", result:"", goalsFor:"", goalsAgainst:"", note:"" });
  const [playerForm, setPlayerForm] = useState({ name:"", position:"", team:"", pin:"" });
  const [ltForm, setLtForm] = useState({ team:"", p:0, w:0, d:0, l:0, gf:0, ga:0 });

  const showToast = msg => { setToastMsg(msg); setTimeout(()=>setToastMsg(""),2400); };

  // ── Global real-time message subscription (after all state/functions declared) ──
  React.useEffect(()=>{
    if(!clubId||!currentUser?.id) return;
    const myId_ = currentUser.id;
    const sub = supabase.channel("global-messages-"+clubId)
      .on("postgres_changes",{event:"INSERT",schema:"public",table:"messages",filter:`club_id=eq.${clubId}`},(payload)=>{
        const msg = payload.new;
        if(msg.sender_id===myId_) return;
        setConversations(cs=>cs.map(c=>c.id===msg.conversation_id
          ?{...c,last_message:msg.body||"📎 Attachment",last_message_at:msg.created_at,last_sender:msg.sender_id,_unread:true}
          :c
        ).sort((a,b)=>new Date(b.last_message_at)-new Date(a.last_message_at)));
        setConvMessages(cm=>{
          if(!cm[msg.conversation_id]) return cm;
          const existing = cm[msg.conversation_id];
          if(existing.find(m=>m.id===msg.id)) return cm;
          return {...cm,[msg.conversation_id]:[...existing,msg]};
        });
        setUnreadCount(u=>u+1);
        const senderName = msg.sender_name||"Someone";
        const preview = msg.body?msg.body.slice(0,40)+(msg.body.length>40?"...":""):"📎 Attachment";
        showToast(`💬 ${senderName}: ${preview}`);
      })
      .on("postgres_changes",{event:"INSERT",schema:"public",table:"conversations",filter:`club_id=eq.${clubId}`},(payload)=>{
        const conv = payload.new;
        if(!conv.member_ids?.includes(myId_)) return;
        if(conv.created_by===myId_) return;
        setConversations(cs=>{
          if(cs.find(c=>c.id===conv.id)) return cs;
          return [conv,...cs];
        });
        if(conv.type==="group"){
          showToast(`👥 You were added to group: ${conv.name||"New group"}`);
          setUnreadCount(u=>u+1);
        }
      })
      .subscribe();
    return ()=>{ supabase.removeChannel(sub); };
  },[clubId, currentUser?.id]);

  // ── SWEAR WORD FILTER ──────────────────────────────────────────────────────
  const BANNED = ["fuck","shit","cunt","bastard","wank","arse","bollocks","twat","piss","dick","cock","bitch","slag","prick"];
  const filterSwears = (text) => {
    let out = text;
    BANNED.forEach(word => {
      const re = new RegExp(`\\b${word}\\b`, "gi");
      out = out.replace(re, m => m[0] + "*".repeat(m.length-2) + m[m.length-1]);
    });
    return out;
  };


  const primaryCol = club.primary || "#E63946";
  const secondaryCol = club.secondary || "#1e3a8a";
  const isSwimming    = club.sport === "Swimming";
  const FIXTURE_TERM  = isSwimming ? "Gala" : "Fixture";
  const FIXTURES_TERM = isSwimming ? "Galas & Meets" : "Upcoming";
  const TEAM_TERM     = isSwimming ? "Squad" : "Team";
  if (typeof window !== "undefined") { window._p4mSport = club.sport; window._p4mLogoPos = club.logoPos||"center center"; }

  // ── derived data ──
  const isAdmin = currentUser?.role === "admin";
  const isCoach = ["admin","lead_coach","coach"].includes(currentUser?.role||"");
  const isCaptain = currentUser?.role === "captain";
  const isPro = club.subscriptionStatus==="active" || club.subscriptionStatus==="founding";

  // Upgrade prompt shown inside any gated section
  const ProGate = ({feature="This feature", children}) => {
    if(isPro) return children;
    return (
      <div style={{...G.card,textAlign:"center",padding:"36px 24px",margin:"8px 0"}}>
        <div style={{fontSize:36,marginBottom:12}}>⚡</div>
        <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,fontWeight:900,color:"#fff",marginBottom:8,letterSpacing:1}}>P4M Pro Feature</div>
        <div style={{fontSize:13,color:"rgba(255,255,255,0.5)",marginBottom:20,lineHeight:1.7}}>{feature} is available on P4M Pro.<br/>Upgrade to unlock for your club.</div>
        <button onClick={()=>setScreen("subscribe")} style={{padding:"12px 28px",background:primaryCol,border:"none",borderRadius:10,color:"#fff",fontSize:15,fontWeight:800,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1}}>
          Upgrade to Pro →
        </button>
        <div style={{fontSize:11,color:"rgba(255,255,255,0.2)",marginTop:12}}>From £9.99/month · Cancel anytime</div>
      </div>
    );
  };

  // ── PRODUCT TOUR ─────────────────────────────────────────────────────────────
  const TourOverlay = () => {
    // Steps — target points at a nav tab or nav drawer item, not panel content
    // beforeNav: called before navigation (e.g. open More drawer)
    // noScroll:  true for nav-bar items which are always visible
    const STEPS = React.useMemo(()=>[
      { key:"welcome",
        title:"Welcome to P4M! 👋",
        desc:"Let's take a 60-second tour so you know exactly where everything lives. You can skip or re-run this any time from Club Settings.",
        panel:null, target:null },
      { key:"club-settings",
        title:"Club Settings ⚙️",
        desc:`Tap the ☰ More menu at the bottom, then "Club" — that's where you set your club name, badge, colours and teams. It's your control centre.`,
        panel:"club-settings", target:"nav-more-club", beforeNav:()=>setMenuOpen(true), noScroll:true },
      { key:"teams",
        title:`Your ${club.sport==="Swimming"?"Squads":"Teams"} 👥`,
        desc:`From the home screen, tap any team card to open its profile — fixtures, availability, results and the lineup builder all live there.`,
        panel:"home", target:"teams-section", noScroll:false },
      { key:"players",
        title:`${MEMBER_TERM[club.sport]||"Player"} Profiles 👤`,
        desc:`Tap the ${MEMBER_TERM[club.sport]||"Players"} tab in the bottom nav to manage your squad. Each player gets ratings, fitness data and a personal programme.`,
        panel:"players", target:"nav-players", noScroll:true },
      { key:"payments",
        title:"Payments & Fees 💸",
        desc:`Open ☰ More and tap "Pay" — send match fees and subscriptions directly to players and track who's paid at a glance.`,
        panel:"payments", target:"nav-more-payments", beforeNav:()=>setMenuOpen(true), noScroll:true },
      { key:"league-data",
        title: club.sport==="Football"?"FA Full-Time 📊": club.sport==="Hockey"?"GMS Results 📊":"Results & Tables 📊",
        desc: club.sport==="Football"
          ?`Open ☰ More and tap "Results" — connect FA Full-Time to pull in your league table and results automatically.`
          : club.sport==="Hockey"
          ?`Open ☰ More and tap "Results" — connect England Hockey GMS to sync fixtures, results and league standings.`
          :`Open ☰ More and tap "Results" — log your results and keep your season record up to date here.`,
        panel:"results", target:"nav-more-results", beforeNav:()=>setMenuOpen(true), noScroll:true },
      { key:"complete",
        title:"You're all set! 🎉",
        desc:"That's the tour! Explore at your own pace. Tap '❓ Take the app tour' in Club Settings to run through this again any time. Good luck this season!",
        panel:null, target:null },
    ],[club.sport]);

    const [stepIdx, setStepIdx] = React.useState(()=>{
      const s = parseInt(localStorage.getItem("p4m_tour_step")||"0");
      return isNaN(s)||s>=STEPS.length ? 0 : s;
    });
    const [visible, setVisible] = React.useState(false);
    const [rect,    setRect]    = React.useState(null);
    const [ttPos,   setTtPos]   = React.useState("bottom");

    // Auto-start for first admin visit
    React.useEffect(()=>{
      if(!isAdmin) return;
      if(!localStorage.getItem("p4m_tour_complete")){
        const t = setTimeout(()=>setVisible(true), 1400);
        return ()=>clearTimeout(t);
      }
    },[]);

    // Expose retrigger for "Take the tour" button
    React.useEffect(()=>{
      window._p4mStartTour = ()=>{
        localStorage.removeItem("p4m_tour_complete");
        localStorage.setItem("p4m_tour_step","0");
        setMenuOpen(false);
        setStepIdx(0); setRect(null); setVisible(true);
      };
    },[]);

    const step    = STEPS[stepIdx];
    const isFirst = stepIdx===0;
    const isLast  = stepIdx===STEPS.length-1;

    const spotTarget = React.useCallback((target, noScroll)=>{
      if(!target){ setRect(null); return; }
      const attempt = (tries)=>{
        const el = document.querySelector(`[data-tour="${target}"]`);
        if(el){
          if(!noScroll) el.scrollIntoView({behavior:"smooth",block:"center"});
          setTimeout(()=>{
            const r = el.getBoundingClientRect();
            setRect({top:r.top,left:r.left,width:r.width,height:r.height});
            // Nav items are at the bottom — always show tooltip above them
            setTtPos(noScroll ? "top" : (r.top > window.innerHeight*0.55 ? "top" : "bottom"));
          }, noScroll ? 80 : 320);
        } else if(tries>0){
          setTimeout(()=>attempt(tries-1), 250);
        }
      };
      attempt(6);
    },[]);

    const goTo = React.useCallback((idx)=>{
      if(idx<0||idx>=STEPS.length) return;
      const s = STEPS[idx];
      localStorage.setItem("p4m_tour_step", String(idx));
      setStepIdx(idx); setRect(null);
      // Close the More drawer unless this step needs it open
      if(!s.beforeNav) setMenuOpen(false);
      if(s.panel) navigate(s.panel);
      if(s.beforeNav) setTimeout(s.beforeNav, s.panel?300:50);
      if(s.target) setTimeout(()=>spotTarget(s.target, s.noScroll), s.beforeNav?550:(s.panel?420:80));
    },[STEPS, spotTarget]);

    const next     = ()=>{ if(isLast){complete();}else{goTo(stepIdx+1);} };
    const back     = ()=>{ goTo(stepIdx-1); };
    const complete = ()=>{
      localStorage.setItem("p4m_tour_complete","1");
      localStorage.removeItem("p4m_tour_step");
      setMenuOpen(false);
      setVisible(false); setRect(null);
    };

    // Keep spotlight in sync on resize
    React.useEffect(()=>{
      if(!visible||!step.target) return;
      const refresh = ()=>spotTarget(step.target, step.noScroll);
      window.addEventListener("resize", refresh);
      return ()=>window.removeEventListener("resize", refresh);
    },[visible, step.target, step.noScroll, stepIdx, spotTarget]);

    if(!visible) return null;

    const PAD    = 10;
    const hasSpot = !!rect && !!step.target;
    const BG     = "rgba(0,0,0,0.78)";
    const CARD_W = Math.min(296, window.innerWidth-28);

    // ── Full-screen modal (welcome / complete) ──────────────────────────────
    if(!hasSpot){
      return (
        <div style={{position:"fixed",inset:0,zIndex:9500,background:"rgba(0,0,0,0.88)",display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(3px)"}}>
          <div style={{background:"#16162a",border:`1px solid ${primaryCol}55`,borderRadius:22,padding:"40px 28px",maxWidth:360,width:"100%",textAlign:"center",boxShadow:`0 0 80px ${primaryCol}22`}}>
            <div style={{fontSize:52,marginBottom:18}}>{isLast?"🎉":"👋"}</div>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:30,fontWeight:900,color:"#fff",marginBottom:12,letterSpacing:0.5,lineHeight:1.1}}>{step.title}</div>
            <div style={{fontSize:14,color:"rgba(255,255,255,0.6)",lineHeight:1.75,marginBottom:30}}>{step.desc}</div>
            <div style={{display:"flex",gap:10,justifyContent:"center",alignItems:"center",flexWrap:"wrap"}}>
              {!isFirst&&!isLast&&(
                <button onClick={back} style={{padding:"10px 20px",background:"rgba(255,255,255,0.07)",border:"1px solid rgba(255,255,255,0.14)",borderRadius:10,color:"rgba(255,255,255,0.7)",fontSize:13,fontWeight:600,cursor:"pointer"}}>← Back</button>
              )}
              {!isLast&&(
                <button onClick={complete} style={{padding:"10px 18px",background:"transparent",border:"none",color:"rgba(255,255,255,0.3)",fontSize:12,cursor:"pointer"}}>Skip tour</button>
              )}
              <button onClick={next} style={{padding:"11px 26px",background:primaryCol,border:"none",borderRadius:10,color:"#fff",fontSize:14,fontWeight:900,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:0.5}}>
                {isLast?"Let's go! →":isFirst?"Start Tour →":"Next →"}
              </button>
            </div>
            <div style={{display:"flex",gap:5,justifyContent:"center",marginTop:22}}>
              {STEPS.map((_,i)=>(
                <div key={i} style={{width:i===stepIdx?18:7,height:7,borderRadius:4,background:i===stepIdx?primaryCol:"rgba(255,255,255,0.18)",transition:"width 0.25s"}}/>
              ))}
            </div>
          </div>
        </div>
      );
    }

    // ── Spotlight overlay ───────────────────────────────────────────────────
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const sT = Math.max(0, rect.top  - PAD);
    const sL = Math.max(0, rect.left - PAD);
    const sR = Math.min(vw, rect.left + rect.width  + PAD);
    const sB = Math.min(vh, rect.top  + rect.height + PAD);
    const sW = sR - sL;
    const sH = sB - sT;

    // Tooltip always above nav items; otherwise above or below based on position
    let ttTop=undefined, ttBottom=undefined;
    if(ttPos==="top"){ ttBottom = vh - sT + 14; }
    else             { ttTop    = Math.min(sB + 14, vh - 190); }
    let ttLeft = sL + sW/2 - CARD_W/2;
    ttLeft = Math.max(12, Math.min(ttLeft, vw-CARD_W-12));

    return (
      <div style={{position:"fixed",inset:0,zIndex:9500,pointerEvents:"none"}}>
        {/* 4 dim strips around spotlight */}
        <div style={{position:"absolute",top:0,left:0,right:0,height:sT,background:BG,pointerEvents:"auto"}}/>
        <div style={{position:"absolute",top:sB,left:0,right:0,bottom:0,background:BG,pointerEvents:"auto"}}/>
        <div style={{position:"absolute",top:sT,left:0,width:sL,height:sH,background:BG,pointerEvents:"auto"}}/>
        <div style={{position:"absolute",top:sT,left:sR,right:0,height:sH,background:BG,pointerEvents:"auto"}}/>
        {/* Glow ring around spotlight */}
        <div style={{position:"absolute",top:sT,left:sL,width:sW,height:sH,borderRadius:12,border:`2px solid ${primaryCol}`,boxShadow:`0 0 0 1px ${primaryCol}44, 0 0 28px ${primaryCol}66`,pointerEvents:"none"}}/>
        {/* Tooltip card */}
        <div style={{position:"absolute",left:ttLeft,top:ttTop,bottom:ttBottom,width:CARD_W,background:"#16162a",border:`1px solid ${primaryCol}55`,borderRadius:16,padding:"16px 18px",boxShadow:`0 12px 48px rgba(0,0,0,0.8), 0 0 24px ${primaryCol}22`,pointerEvents:"auto",zIndex:9501}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:19,fontWeight:900,color:"#fff",letterSpacing:0.3,lineHeight:1.2,flex:1}}>{step.title}</div>
            <button onClick={complete} style={{background:"none",border:"none",color:"rgba(255,255,255,0.3)",fontSize:20,cursor:"pointer",lineHeight:1,padding:"0 0 0 8px",flexShrink:0}}>✕</button>
          </div>
          <div style={{fontSize:13,color:"rgba(255,255,255,0.6)",lineHeight:1.65,marginBottom:14}}>{step.desc}</div>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
            <div style={{display:"flex",gap:4,flex:1}}>
              {STEPS.map((_,i)=>(
                <div key={i} style={{height:5,borderRadius:3,background:i===stepIdx?primaryCol:"rgba(255,255,255,0.18)",flex:i===stepIdx?2:1,transition:"flex 0.25s"}}/>
              ))}
            </div>
            <div style={{display:"flex",gap:7,flexShrink:0}}>
              {!isFirst&&(
                <button onClick={back} style={{padding:"7px 13px",background:"rgba(255,255,255,0.07)",border:"1px solid rgba(255,255,255,0.12)",borderRadius:8,color:"rgba(255,255,255,0.7)",fontSize:12,fontWeight:600,cursor:"pointer"}}>← Back</button>
              )}
              <button onClick={next} style={{padding:"7px 15px",background:primaryCol,border:"none",borderRadius:8,color:"#fff",fontSize:12,fontWeight:900,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif"}}>
                {isLast?"Finish →":"Next →"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };
  // ── END PRODUCT TOUR ─────────────────────────────────────────────────────────

  const myTeam = currentUser?.team;
  const myTeams = isAdmin ? "all" : myTeam;

  const visibleFixtures = isAdmin ? fixtures : fixtures.filter(f => f.team === myTeams || f.team === "all" || (Array.isArray(f.teams) && f.teams.includes(myTeams)));
  const visiblePlayers = isAdmin ? players
    : isPlayer ? players.filter(p => p.userId === currentUser?.id || p.name === currentUser?.name)
    : players.filter(p => p.team === myTeams);
  const pendingUsers = users.filter(u => !u.approved && !u.isArchived);
  const activeUsers = users.filter(u => !u.isArchived);

  const teams = (() => {
    // If club has custom team names set, use those directly
    if ((club.customTeams||[]).length > 0) return club.customTeams;
    const t = [];
    if (club.sport === "Swimming") {
      (club.swimGroups||[]).forEach(g => t.push(g.startsWith("Age ") ? g : `Age ${g}`));
      return t.length ? t : ["Age 10/11","Age 12","Age 13","Age 14","Age 15","Age 16 & Over"];
    }
    const { mens=0, ladies=0 } = club;
    for (let i=1;i<=mens;i++) t.push(`Men's ${i === 1?"1s":i === 2?"2s":i === 3?"3s":i+"s"}`);
    for (let i=1;i<=ladies;i++) t.push(`Ladies' ${i === 1?"1s":i === 2?"2s":i === 3?"3s":i === 4?"4s":i+"s"}`);
    (club.juniorAges||[]).forEach(age => t.push(`Juniors ${age}`));
    return t.length ? t : ["Men's 1s","Men's 2s","Ladies' 1s","Ladies' 2s"];
  })();

  // ═══════════════════════════════
  // ONBOARDING
  // ═══════════════════════════════
  const extractEthosPoints = (ethos) => {
    const sentences = ethos.split(/[.!?]+/).map(s=>s.trim()).filter(s=>s.length > 20);
    return sentences.slice(0, 5);
  };

  const obContent = () => {
    switch(obStep) {
      case 1: return (
        <div style={{ ...G.center, flex:1, padding:"20px 24px", gap:0 }}>
          <div style={{ fontSize:36, marginBottom:16 }}>🏆</div>
          <div style={{ ...G.h1, fontSize:30, textAlign:"center", marginBottom:8 }}>Welcome to P4M</div>
          <div style={{ fontSize:14, color:"rgba(255,255,255,0.45)", textAlign:"center", marginBottom:32, lineHeight:1.6, maxWidth:300 }}>The all-in-one sports club management platform. Let's get your club set up.</div>
          <input style={{ ...G.input, textAlign:"center", fontSize:16, maxWidth:360, borderColor: club.name.length>1?"rgba(255,255,255,0.2)":"rgba(255,255,255,0.08)" }}
            placeholder="Your club name" value={club.name} onChange={e=>setClub(c=>({...c,name:e.target.value}))} autoFocus />
        </div>
      );
      case 2: return (
        <div style={{ ...G.center, flex:1, padding:"20px 24px", gap:0 }}>
          <div style={{ fontSize:36, marginBottom:16 }}>⚽</div>
          <div style={{ ...G.h1, fontSize:30, textAlign:"center", marginBottom:8 }}>What sport?</div>
          <div style={{ fontSize:14, color:"rgba(255,255,255,0.45)", textAlign:"center", marginBottom:28, maxWidth:300 }}>P4M works across all sports. More being added all the time.</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, width:"100%", maxWidth:360 }}>
            {SPORTS.map(s=>(
              <div key={s.n} onClick={()=>setClub(c=>({...c,sport:s.n}))}
                style={{ padding:"14px 12px", background: club.sport===s.n ? `${primaryCol}18` : "rgba(255,255,255,0.03)", border:`1.5px solid ${club.sport===s.n ? primaryCol : "rgba(255,255,255,0.08)"}`, borderRadius:12, cursor:"pointer", textAlign:"center", transition:"all 0.15s" }}>
                <div style={{ fontSize:24, marginBottom:6 }}>{s.e}</div>
                <div style={{ fontSize:13, fontWeight:600, color: club.sport===s.n ? "#fff" : "rgba(255,255,255,0.5)" }}>{s.n}</div>
              </div>
            ))}
          </div>
        </div>
      );
      case 3: return (
        <div style={{ ...G.center, flex:1, padding:"20px 24px", gap:0, overflowY:"auto" }}>
          <div style={{ fontSize:36, marginBottom:12 }}>👥</div>
          <div style={{ ...G.h1, fontSize:28, textAlign:"center", marginBottom:6 }}>
            {club.sport==="Swimming" ? "Set Up Your Groups" : "How many teams?"}
          </div>
          <div style={{ fontSize:13, color:"rgba(255,255,255,0.4)", textAlign:"center", marginBottom:24 }}>You can always change this later in settings.</div>
          <div style={{ display:"flex", flexDirection:"column", gap:10, width:"100%", maxWidth:360 }}>
            {club.sport==="Swimming" ? (
              <>
                <div style={{fontSize:13,color:"rgba(255,255,255,0.7)",marginBottom:8,fontWeight:600}}>Select your squads</div>
                <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",marginBottom:12,lineHeight:1.5}}>These match the training framework levels — each squad gets its own tailored programme.</div>
                {[
                  {id:"Starters",    emoji:"🌊", desc:"Fun, water confidence, learning to compete"},
                  {id:"Development", emoji:"📈", desc:"Aerobic base, technique, first galas"},
                  {id:"Performance", emoji:"🎯", desc:"PBs, regional qualifying, race strategy"},
                  {id:"Elite",       emoji:"🏆", desc:"National times, full periodisation"},
                  {id:"Masters",     emoji:"🏅", desc:"Fitness, personal goals, masters events"},
                ].map(sq=>{
                  const sel=(club.swimGroups||[]).includes(sq.id);
                  return (
                    <div key={sq.id} onClick={()=>setClub(c=>({...c,swimGroups:sel?(c.swimGroups||[]).filter(x=>x!==sq.id):[...(c.swimGroups||[]),sq.id]}))}
                      style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",background:sel?`${club.primary}18`:"rgba(255,255,255,0.03)",border:`1.5px solid ${sel?club.primary:"rgba(255,255,255,0.08)"}`,borderRadius:10,cursor:"pointer",marginBottom:6}}>
                      <span style={{fontSize:20,flexShrink:0}}>{sq.emoji}</span>
                      <div style={{flex:1}}>
                        <div style={{fontSize:13,fontWeight:sel?600:500,color:sel?"#fff":"rgba(255,255,255,0.75)"}}>{sq.id}</div>
                        <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",marginTop:1}}>{sq.desc}</div>
                      </div>
                      <div style={{width:20,height:20,borderRadius:4,border:`2px solid ${sel?club.primary:"rgba(255,255,255,0.2)"}`,background:sel?club.primary:"transparent",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:"#fff",flexShrink:0}}>{sel?"✓":""}</div>
                    </div>
                  );
                })}
                <div style={{marginTop:10}}>
                  <div style={{fontSize:12,color:"rgba(255,255,255,0.45)",marginBottom:6}}>Add a custom group (e.g. age-specific sub-groups)</div>
                  <div style={{display:"flex",gap:8}}>
                    <input id="cg-swim" style={{...G.input,flex:1,marginBottom:0}} placeholder="e.g. Age 12, Junior Elite"/>
                    <button style={G.btnSm} onClick={()=>{const el=document.getElementById("cg-swim");const v=el?.value?.trim();if(v){setClub(c=>({...c,swimGroups:[...(c.swimGroups||[]),v]}));el.value=""}}}>+ Add</button>
                  </div>
                </div>
              </>
            ) : (
              <>
                {[{k:"mens",l:"Men's Teams"},{k:"ladies",l:"Ladies' Teams"}].map(r=>(
                  <div key={r.k} style={{ display:"flex", alignItems:"center", background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:12, padding:"14px 16px" }}>
                    <div style={{ flex:1, fontSize:14, fontWeight:500, color:"rgba(255,255,255,0.85)" }}>{r.l}</div>
                    <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                      <button onClick={()=>setClub(c=>({...c,[r.k]:Math.max(0,(c[r.k]||0)-1)}))} style={{ width:30, height:30, borderRadius:"50%", background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.1)", color:"#fff", fontSize:18, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1 }}>−</button>
                      <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:22, fontWeight:800, color:"#fff", minWidth:22, textAlign:"center" }}>{club[r.k]||0}</div>
                      <button onClick={()=>setClub(c=>({...c,[r.k]:Math.min(10,(c[r.k]||0)+1)}))} style={{ width:30, height:30, borderRadius:"50%", background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.1)", color:"#fff", fontSize:18, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1 }}>+</button>
                    </div>
                  </div>
                ))}
              </>
            )}
            {club.sport!=="Swimming"&&<div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:12, padding:"14px 16px" }}>
              <div style={{ fontSize:14, fontWeight:500, color:"rgba(255,255,255,0.85)", marginBottom:10 }}>Junior Age Groups</div>
              <div style={{ fontSize:12, color:"rgba(255,255,255,0.4)", marginBottom:10 }}>Select standard groups or add your own:</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:10 }}>
                {[...JUNIOR_AGES,...(club.customAgeGroups||[])].map(age=>{
                  const sel = (club.juniorAges||[]).includes(age);
                  const isCustom = (club.customAgeGroups||[]).includes(age);
                  return (
                    <div key={age} style={{display:"flex",gap:4,alignItems:"center"}}>
                      <div onClick={()=>setClub(c=>({...c,juniorAges:sel?(c.juniorAges||[]).filter(a=>a!==age):[...(c.juniorAges||[]),age]}))}
                        style={{ flex:1, padding:"8px 12px", background:sel?`${primaryCol}18`:"rgba(255,255,255,0.02)", border:`1.5px solid ${sel?primaryCol:"rgba(255,255,255,0.08)"}`, borderRadius:8, cursor:"pointer", fontSize:13, color:sel?"#fff":"rgba(255,255,255,0.5)", textAlign:"center", fontWeight:sel?600:400 }}>
                        {age}
                      </div>
                      {isCustom&&<button onClick={()=>setClub(c=>({...c,customAgeGroups:(c.customAgeGroups||[]).filter(a=>a!==age),juniorAges:(c.juniorAges||[]).filter(a=>a!==age)}))}
                        style={{background:"none",border:"none",color:"rgba(255,255,255,0.3)",cursor:"pointer",fontSize:14,padding:"2px 4px"}}>✕</button>}
                    </div>
                  );
                })}
              </div>
              {/* Add custom age group */}
              <div style={{display:"flex",gap:8}}>
                <input
                  style={{...G.input,marginBottom:0,flex:1,fontSize:13}}
                  placeholder="e.g. U7, Colts, Mini Hockey"
                  onKeyDown={e=>{
                    if(e.key==="Enter"){
                      const val=e.target.value.trim();
                      if(!val) return;
                      if([...JUNIOR_AGES,...(club.customAgeGroups||[])].includes(val)){showToast("Already exists");return;}
                      setClub(c=>({...c,customAgeGroups:[...(c.customAgeGroups||[]),val],juniorAges:[...(c.juniorAges||[]),val]}));
                      e.target.value="";
                    }
                  }}
                  onBlur={()=>{}}
                  id="ob-custom-age-input"
                />
                <button onClick={()=>{
                  const inp=document.getElementById("ob-custom-age-input");
                  const val=(inp?.value||"").trim();
                  if(!val) return;
                  if([...JUNIOR_AGES,...(club.customAgeGroups||[])].includes(val)){showToast("Already exists");return;}
                  setClub(c=>({...c,customAgeGroups:[...(c.customAgeGroups||[]),val],juniorAges:[...(c.juniorAges||[]),val]}));
                  if(inp) inp.value="";
                }} style={{padding:"8px 14px",background:primaryCol,border:"none",borderRadius:9,color:contrastText(primaryCol),fontSize:12,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
                  + Add
                </button>
              </div>
            </div>}

            {/* Custom team names */}
            {club.sport!=="Swimming"&&<div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:12, padding:"14px 16px" }}>
              <div style={{ fontSize:14, fontWeight:500, color:"rgba(255,255,255,0.85)", marginBottom:4 }}>Custom Team Names</div>
              <div style={{ fontSize:12, color:"rgba(255,255,255,0.4)", marginBottom:10, lineHeight:1.5 }}>Override default names — e.g. "First Team", "Reserves", "Craven Heifer FC"</div>
              {(club.customTeams||[]).map((t,i)=>(
                <div key={i} style={{display:"flex",gap:8,marginBottom:8}}>
                  <input
                    style={{...G.input,marginBottom:0,flex:1,fontSize:13}}
                    defaultValue={t}
                    onBlur={e=>{
                      const val=e.target.value.trim();
                      if(!val) return;
                      const updated=[...(club.customTeams||[])];
                      updated[i]=val;
                      setClub(c=>({...c,customTeams:updated}));
                    }}
                  />
                  <button onClick={()=>setClub(c=>({...c,customTeams:(c.customTeams||[]).filter((_,j)=>j!==i)}))}
                    style={{padding:"8px 12px",background:"rgba(230,57,70,0.15)",border:"1px solid rgba(230,57,70,0.3)",borderRadius:8,color:"#E63946",cursor:"pointer",fontSize:14}}>✕</button>
                </div>
              ))}
              <div style={{display:"flex",gap:8}}>
                <input
                  id="ob-custom-team-input"
                  style={{...G.input,marginBottom:0,flex:1,fontSize:13}}
                  placeholder="e.g. First Team, Reserves"
                  onKeyDown={e=>{
                    if(e.key==="Enter"){
                      const val=e.target.value.trim();
                      if(!val) return;
                      setClub(c=>({...c,customTeams:[...(c.customTeams||[]),val]}));
                      e.target.value="";
                    }
                  }}
                />
                <button onClick={()=>{
                  const inp=document.getElementById("ob-custom-team-input");
                  const val=(inp?.value||"").trim();
                  if(!val) return;
                  setClub(c=>({...c,customTeams:[...(c.customTeams||[]),val]}));
                  if(inp) inp.value="";
                }} style={{padding:"8px 14px",background:primaryCol,border:"none",borderRadius:9,color:contrastText(primaryCol),fontSize:12,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
                  + Add
                </button>
              </div>
              {(club.customTeams||[]).length>0&&(
                <button onClick={()=>setClub(c=>({...c,customTeams:[]}))}
                  style={{marginTop:8,background:"none",border:"none",color:"rgba(255,255,255,0.3)",fontSize:11,cursor:"pointer"}}>
                  Clear all (revert to defaults)
                </button>
              )}
            </div>}
          </div>
        </div>
      );
      
      case 4: return (
        <div style={{ ...G.center, flex:1, padding:"20px 24px", gap:0, overflowY:"auto" }}>
          <div style={{ ...G.h1, fontSize:26, textAlign:"center", marginBottom:6 }}>Club Colours</div>
          <div style={{ fontSize:13, color:"rgba(255,255,255,0.4)", textAlign:"center", marginBottom:20 }}>Tap a box to select it, then choose a colour below.</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, width:"100%", maxWidth:360, marginBottom:20 }}>
            {[{key:"primary",label:"Primary"},{key:"secondary",label:"Secondary"}].map(({key,label})=>{
              const col = club[key];
              const isWhite = col === "#f1f5f9" || col === "#ffffff";
              const isActive = club._picking === key;
              return (
                <div key={key} onClick={()=>setClub(c=>({...c,_picking:isActive?null:key}))}
                  style={{ background:isActive?"rgba(255,255,255,0.08)":"rgba(255,255,255,0.03)", border:`2px solid ${isActive?primaryCol:"rgba(255,255,255,0.1)"}`, borderRadius:12, padding:14, textAlign:"center", cursor:"pointer", transition:"all 0.15s" }}>
                  <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:2, color:"rgba(255,255,255,0.38)", marginBottom:10 }}>{label}</div>
                  <div style={{ width:44, height:44, borderRadius:"50%", background:col, margin:"0 auto 8px", border:`3px solid ${isWhite?"#333":"rgba(255,255,255,0.3)"}`, display:"flex", alignItems:"center", justifyContent:"center" }}>
                    {isActive && <div style={{ width:16, height:16, borderRadius:"50%", background:isWhite?"#000":"#fff", opacity:0.7 }}/>}
                  </div>
                  <div style={{ fontSize:12, color:isWhite?"rgba(255,255,255,0.8)":"rgba(255,255,255,0.6)", fontWeight:isActive?700:400 }}>{COLOURS.find(c=>c.h===col)?.n||"Custom"}</div>
                  {isActive && <div style={{ fontSize:10, color:primaryCol, marginTop:4, fontWeight:700 }}>← pick below</div>}
                </div>
              );
            })}
          </div>
          {club._picking && (
            <div style={{ width:"100%", maxWidth:360 }}>
              <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:2, color:"rgba(255,255,255,0.38)", marginBottom:10 }}>
                Choose {club._picking === "primary" ? "Primary" : "Secondary"} Colour
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:8 }}>
                {COLOURS.map(c=>{
                  const isSelected = club[club._picking] === c.h;
                  const isWhite = c.h === "#f1f5f9";
                  return (
                    <div key={c.h} onClick={()=>setClub(cl=>({...cl,[cl._picking]:c.h}))} style={{ position:"relative" }}>
                      <div style={{ aspectRatio:1, borderRadius:8, background:c.h, cursor:"pointer", border:`3px solid ${isSelected?(isWhite?"#333":primaryCol):"transparent"}`, boxShadow:isSelected?`0 0 0 1px ${isWhite?"#555":primaryCol}`:"none", transition:"all 0.15s" }}>
                        {isSelected && <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, color:isWhite?"#000":"#fff", fontWeight:900 }}>✓</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {!club._picking && (
            <div style={{ fontSize:12, color:"rgba(255,255,255,0.35)", marginTop:8 }}>Tap a colour box above to change it</div>
          )}
        </div>
      );
      case 5: return (
        <div style={{ ...G.center, flex:1, padding:"20px 24px", gap:0 }}>
          <div style={{ fontSize:36, marginBottom:12 }}>🛡️</div>
          <div style={{ ...G.h1, fontSize:28, textAlign:"center", marginBottom:6 }}>Club Badge</div>
          <div style={{ fontSize:13, color:"rgba(255,255,255,0.4)", textAlign:"center", marginBottom:24, maxWidth:280 }}>Upload your club crest. It appears on player cards throughout the app.</div>
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:10}}>
            <label style={{ width:120, height:120, borderRadius:"50%", background:`${primaryCol}18`, border:`2px dashed ${primaryCol}66`, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", cursor:"pointer", overflow:"hidden", flexShrink:0 }}>
              {club.logo ? <img src={club.logo} alt="" style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:club.logoPos||"center center" }} /> : <><div style={{ fontSize:30, opacity:0.5 }}>🛡️</div><div style={{ fontSize:11, color:"rgba(255,255,255,0.35)", marginTop:6 }}>Tap to upload</div></>}
              <input type="file" accept="image/*" style={{ display:"none" }} onChange={e=>{const f=e.target.files[0];if(!f)return;const r=new FileReader();r.onload=ev=>setClub(c=>({...c,logo:ev.target.result,logoPos:"center center"}));r.readAsDataURL(f);}}/>
            </label>
            {club.logo&&(
              <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:6,width:240}}>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.45)"}}>Drag to recentre</div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:4,width:120}}>
                  {[["top left","↖"],["top center","↑"],["top right","↗"],["center left","←"],["center center","·"],["center right","→"],["bottom left","↙"],["bottom center","↓"],["bottom right","↘"]].map(([pos,icon])=>(
                    <button key={pos} onClick={()=>setClub(c=>({...c,logoPos:pos}))}
                      style={{padding:6,background:club.logoPos===pos?primaryCol:"rgba(255,255,255,0.06)",border:`1px solid ${club.logoPos===pos?primaryCol:"rgba(255,255,255,0.1)"}`,borderRadius:6,color:"#fff",cursor:"pointer",fontSize:14,lineHeight:1}}>
                      {icon}
                    </button>
                  ))}
                </div>
                <button onClick={()=>setClub(c=>({...c,logo:null,logoPos:"center center"}))} style={{fontSize:11,color:"rgba(255,255,255,0.35)",background:"none",border:"none",cursor:"pointer",textDecoration:"underline"}}>Remove photo</button>
              </div>
            )}
          </div>
          <div style={{ fontSize:12, color:"rgba(255,255,255,0.35)", cursor:"pointer", textDecoration:"underline",marginTop:4 }} onClick={()=>setObStep(s=>s+1)}>Skip for now →</div>
        </div>
      );
      case 6: return (
        <div style={{ ...G.center, flex:1, padding:"20px 24px", gap:0 }}>
          <div style={{ fontSize:36, marginBottom:12 }}>📝</div>
          <div style={{ ...G.h1, fontSize:28, textAlign:"center", marginBottom:6 }}>Club Ethos</div>
          <div style={{ fontSize:13, color:"rgba(255,255,255,0.4)", textAlign:"center", marginBottom:20, maxWidth:300, lineHeight:1.6 }}>Paste your club's values or ethos. P4M uses this to personalise your coaching framework.</div>
          <textarea style={{ ...G.textarea, minHeight:130, maxWidth:360 }} placeholder="e.g. We believe in developing players as people first. Our values are respect, commitment and enjoyment at every level..."
            value={club.ethos} onChange={e=>setClub(c=>({...c,ethos:e.target.value}))}/>
          <div style={{ fontSize:12, color:"rgba(255,255,255,0.35)", cursor:"pointer", textDecoration:"underline", marginTop:4 }} onClick={()=>setObStep(s=>s+1)}>Skip for now →</div>
        </div>
      );
      case 7: return (
        <div style={{ ...G.center, flex:1, padding:"20px 24px", gap:0, overflowY:"auto" }}>
          <div style={{ fontSize:36, marginBottom:12 }}>✅</div>
          <div style={{ ...G.h1, fontSize:28, textAlign:"center", marginBottom:6 }}>All Set!</div>
          <div style={{ fontSize:13, color:"rgba(255,255,255,0.4)", textAlign:"center", marginBottom:20 }}>Here's your club summary.</div>
          <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:14, padding:18, width:"100%", maxWidth:360 }}>
            <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:16, paddingBottom:14, borderBottom:"1px solid rgba(255,255,255,0.07)" }}>
              <div style={{ width:50, height:50, borderRadius:"50%", background:`linear-gradient(135deg,${primaryCol},${secondaryCol})`, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Barlow Condensed',sans-serif", fontSize:14, fontWeight:800, color:"#fff", overflow:"hidden", flexShrink:0 }}>
                {club.logo ? <img src={club.logo} alt="" style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:club.logoPos||"center center" }}/> : inits(club.name)}
              </div>
              <div>
                <div style={{ ...G.h2, fontSize:20 }}>{club.name}</div>
                <div style={{ fontSize:12, color:"rgba(255,255,255,0.4)" }}>{club.sport}</div>
              </div>
            </div>
            {(club.sport==="Swimming"
              ? [["Age group squads",(club.swimGroups||[]).length]]
              : [["Men's teams",club.mens||0],["Ladies' teams",club.ladies||0],["Junior age groups",(club.juniorAges||[]).length]]
            ).map(([l,v])=>(
              <div key={l} style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:"1px solid rgba(255,255,255,0.05)", fontSize:13 }}>
                <span style={{ color:"rgba(255,255,255,0.4)" }}>{l}</span>
                <span style={{ fontWeight:600, color:"#fff" }}>{v}</span>
              </div>
            ))}
            {club.sport==="Swimming" && (club.swimGroups||[]).length > 0 && (
              <div style={{ padding:"8px 0", fontSize:12, color:"rgba(255,255,255,0.4)" }}>
                {(club.swimGroups||[]).join(" · ")}
              </div>
            )}
            {club.sport!=="Swimming" && (club.juniorAges||[]).length > 0 && (
              <div style={{ padding:"8px 0", fontSize:12, color:"rgba(255,255,255,0.4)" }}>
                {(club.juniorAges||[]).join(" · ")}
              </div>
            )}
            {club.ethos && (
              <div style={{ marginTop:12, paddingTop:12, borderTop:"1px solid rgba(255,255,255,0.07)" }}>
                <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:2, color:"rgba(255,255,255,0.38)", marginBottom:8 }}>Club Values</div>
                {extractEthosPoints(club.ethos).map((pt,i)=>(
                  <div key={i} style={{ display:"flex", gap:8, marginBottom:6, fontSize:12, color:"rgba(255,255,255,0.7)", lineHeight:1.5 }}>
                    <span style={{ color:primaryCol, flexShrink:0 }}>•</span>{pt}
                  </div>
                ))}
              </div>
            )}
            <div style={{ display:"flex", gap:8, marginTop:12, paddingTop:12, borderTop:"1px solid rgba(255,255,255,0.07)" }}>
              <div style={{ width:20, height:20, borderRadius:"50%", background:primaryCol }}/>
              <div style={{ width:20, height:20, borderRadius:"50%", background:secondaryCol }}/>
              <span style={{ fontSize:12, color:"rgba(255,255,255,0.4)", marginLeft:4 }}>Club colours</span>
            </div>
          </div>
        </div>
      );
      default: return null;
    }
  };

  // ═══════════════════════════════
  // AUTH
  // ═══════════════════════════════
  // Search clubs by name and sport
  const searchClubs = async (query, sport) => {
    setClubSearching(true);
    try {
      let q = supabase.from("clubs")
        .select("id, name, sport, logo_url, mens, ladies, junior_ages, swim_groups");
      if(sport) q = q.eq("sport", sport);
      if(query && query.length >= 2) q = q.ilike("name", `%${query}%`);
      q = q.limit(12);
      const { data } = await q;
      setClubResults(data || []);
    } catch(e) { setClubResults([]); }
    setClubSearching(false);
  };

  // Get teams list from a club record
  const getClubTeams = (club) => {
    if (!club) return [];
    const t = [];
    if (club.sport === "Swimming") {
      (club.swim_groups || []).forEach(g => t.push(g.startsWith("Age ") ? g : `Age ${g}`));
      return t.length ? t : ["General Squad"];
    }
    for (let i=1; i<=( club.mens||0); i++) t.push(`Men's ${i===1?"1s":i===2?"2s":i===3?"3s":i+"s"}`);
    for (let i=1; i<=(club.ladies||0); i++) t.push(`Ladies' ${i===1?"1s":i===2?"2s":i===3?"3s":i+"s"}`);
    (club.junior_ages||[]).forEach(a => t.push(`Juniors ${a}`));
    return t.length ? t : ["Men's 1s"];
  };

  // ── PUSH NOTIFICATIONS ──────────────────────────────────────
  const registerPushNotifications = async () => {
    try {
      if(!("serviceWorker" in navigator) || !("PushManager" in window)) return;
      const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      if(!vapidKey) return;

      // Check current permission state
      const currentPermission = Notification.permission;
      
      // Already denied — show in-app banner instead of toast
      if(currentPermission === "denied") {
        setShowNotifBanner(true);
        return;
      }

      // Ask permission
      const permission = await Notification.requestPermission();
      if(permission !== "granted") {
        setShowNotifBanner(true);
        return;
      }

      const reg = await navigator.serviceWorker.ready;
      const padding = "=".repeat((4 - vapidKey.length % 4) % 4);
      const base64 = (vapidKey + padding).replace(/-/g,"+").replace(/_/g,"/");
      const rawData = atob(base64);
      const keyBytes = new Uint8Array(rawData.length);
      for(let i=0;i<rawData.length;i++) keyBytes[i]=rawData.charCodeAt(i);
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: keyBytes,
      });
      if(clubId && currentUser?.id) {
        const {error} = await supabase.from("push_subscriptions").upsert({
          user_id: currentUser.id,
          club_id: clubId,
          subscription: subscription.toJSON(),
        }, { onConflict: "user_id,club_id" });
        if(!error) showToast("🔔 Notifications enabled ✓");
      }
    } catch(e) {
      console.error("Push registration failed:", e);
    }
  };

  const sendNotification = async (title, body, teamFilter="all", url="/", tag="p4m") => {
    if(!clubId) return;
    try {
      await supabase.functions.invoke("send-notification", {
        body: { clubId, teamFilter, title, body, url, tag }
      });
    } catch(e) { console.error("Notification failed:", e); }
  };

  const handleLogin = async () => {
    setAuthError("");
    if (!authForm.email || !authForm.password) { setAuthError("Please enter your email and password"); return; }
    setAuthLoading(true);
    try {
      await db.signIn({ email: authForm.email, password: authForm.password });
      showToast("Auth OK — loading profile...");
      const member = await db.getCurrentMember();
      if (!member) { 
        setAuthLoading(false);
        setAuthError("Profile not found — your account may need to be re-linked. Contact support."); 
        return; 
      }
      showToast("Profile found — loading club...");
      if (!member.approved) { setAuthLoading(false); setAuthMode("pending"); return; }
      setCurrentUser({ ...member, id: member.id });
      setClubId(member.club_id);
      const clubData = await db.getClub(member.club_id);
      if (clubData) {
        setClub({
          name: clubData.name, sport: clubData.sport,
          primary: clubData.primary_col, secondary: clubData.secondary_col,
          logo: clubData.logo_url, logoPos: clubData.logo_pos,
          ethos: clubData.ethos, mens: clubData.mens, ladies: clubData.ladies,
          juniorAges: clubData.junior_ages || [], swimGroups: clubData.swim_groups || [],
          gmsClubId: clubData.gms_club_id||"",
            gmsTeams: clubData.gms_teams||[],
            xeroConnected: clubData.xero_connected||false,
            xeroTenantName: clubData.xero_tenant_name||"",
            subscriptionStatus: localStorage.getItem("p4m_sub_override")||clubData.subscription_status||"free",
            stripeCustomerId: clubData.stripe_customer_id||"",
        });
      }
      showToast("Club loaded — loading data...");
      await loadAllData(member.club_id, member.id);
      setAuthLoading(false);
      // Check for Stripe payment success
      const urlParamsLogin = new URLSearchParams(window.location.search);
      if(urlParamsLogin.get("payment")==="success"){
        window.history.replaceState({},"",window.location.pathname);
        // Persist Pro status locally so it survives navigation/refresh while webhook catches up
        localStorage.setItem("p4m_sub_override","active");
        setClub(c=>({...c,subscriptionStatus:"active"}));
        setScreen("app"); setPanel("home");
        showToast("🎉 Subscription activated — welcome to P4M Pro!");
        // Also write directly to DB — don't rely solely on webhook
        supabase.from("clubs").update({
          subscription_status:"active",
          subscription_plan: urlParamsLogin.get("plan")||"pro",
        }).eq("id",member.club_id).then(({error})=>{
          if(!error) localStorage.removeItem("p4m_sub_override");
        });
      } else {
        setScreen("app"); setPanel("home");
        showToast("Welcome back!");
      }
      // Only show if not already decided
      setTimeout(() => {
        const dismissed = localStorage.getItem("p4m_notif_dismissed");
        if(Notification.permission === "default" && !dismissed) setShowNotifPrompt(true);
        else if(Notification.permission === "granted") registerPushNotifications();
      }, 3000);
    } catch(e) {
      setAuthLoading(false);
      const msg = e.message || "";
      if (msg.includes("Invalid login") || msg.includes("invalid_credentials")) setAuthError("Incorrect email or password — please try again");
      else if (msg.includes("Email not confirmed")) { setAuthMode("confirm-email"); setAuthError(""); }
      else if (msg.includes("Too many requests")) setAuthError("Too many attempts — please wait a few minutes");
      else setAuthError(msg || "Login failed — please try again");
    }
  };

  const handleSignup = async () => {
    setAuthError("");
    if (!authForm.name || !authForm.email || !authForm.password) { setAuthError("Please fill in all fields"); return; }
    if (authForm.password !== authForm.confirm) { setAuthError("Passwords don't match"); return; }
    if (authForm.password.length < 6) { setAuthError("Password must be at least 6 characters"); return; }
    if (authForm.role !== "admin" && !authForm.clubId) { setAuthError("Please search for and select your club"); return; }
    if (authForm.role !== "admin" && !authForm.team) { setAuthError("Please select your team or squad"); return; }
    if (!authForm.termsAgreed) { setAuthError("Please agree to the Terms of Service and Privacy Policy"); return; }
    if (authForm.role === "admin" && !authForm.clubName?.trim()) { setAuthError("Please enter your club name"); return; }
    if (authForm.role === "admin" && !authForm.clubRole) { setAuthError("Please select your role at the club"); return; }
    try {
      // Step 1: Create auth user first (establishes session)
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: authForm.email, password: authForm.password,
      });
      if (authError) throw authError;
      const authUserId = authData.user?.id;
      if (!authUserId) throw new Error("Sign up failed — please try again");

      if (authForm.role === "admin") {
        // Step 2: Create club (now authenticated)
        const newClub = await db.createClub(club);
        const newClubId = newClub.id;
        setClubId(newClubId);

        // Step 3: Create member record linked to club
        const { data: memberData, error: memberError } = await supabase.from("members").insert({
          club_id: newClubId,
          auth_id: authUserId,
          name: authForm.name,
          email: authForm.email,
          role: "admin",
          team: "all",
          approved: true,
          also_player: authForm.alsoPlayer||false,
          is_player_coach: authForm.isPlayerCoach||false,
        }).select().single();
        if (memberError) throw memberError;

        const adminStaff = {id:memberData.id,name:authForm.name,role:"admin",position:"Club Admin",team:"all",approved:true,photo:null,email:authForm.email};
        setUsers(us=>[...us,adminStaff]);
        setCurrentUser({ ...memberData, id: memberData.id, also_player:authForm.alsoPlayer });
        // Admin also a player — create player profile
        if(authForm.alsoPlayer) {
          await supabase.from("players").insert({
            club_id: newClubId,
            member_id: memberData.id,
            name: authForm.name,
            team: authForm.alsoPlayerTeam||"",
            position: authForm.alsoPlayerPosition||"",
            scores: {}, fitness: {},
          });
        }
        setScreen("onboard");
        showToast("Account created! Set up your club ✓");
      } else {
        // Non-admin: join existing club
        const joinClubId = authForm.clubId;
        const { data: memberData, error: memberError } = await supabase.from("members").insert({
          club_id: joinClubId,
          auth_id: authUserId,
          name: authForm.name,
          email: authForm.email,
          role: authForm.role,
          team: authForm.team,
          approved: false,
        }).select().single();
        if (memberError) throw memberError;

        // Auto-create player profile for players
        if (authForm.role === "player") {
          await supabase.from("players").insert({
            club_id: joinClubId,
            member_id: memberData.id,
            name: authForm.name,
            team: authForm.team,
            scores: {}, fitness: {},
          });
        }

        setAuthMode("pending");
        showToast("Request sent — your club admin will approve you shortly");
      }
    } catch(e) {
      console.error("Signup error:", e);
      const msg = e.message || "";
      if (msg.includes("already registered") || msg.includes("already been registered")) setAuthError("An account with this email already exists — try logging in");
      else if (msg.includes("valid email")) setAuthError("Please enter a valid email address");
      else if (msg.includes("Password should")) setAuthError("Password must be at least 6 characters");
      else setAuthError(msg || "Sign up failed — please try again");
    }
  };

  // ═══════════════════════════════
  // COACH WIZARD / AI
  // ═══════════════════════════════
  const generateFrameworkWithAI = async () => {
    setWizardLoading(true);
    try {
      // Sport-specific role definition and governing body alignment
      const sportRole = {
        Hockey: `You are an elite-level FIELD HOCKEY coach and long-term athlete development specialist with deep knowledge of England Hockey coaching principles and international best practice. This is FIELD HOCKEY played on artificial grass (astroturf) or natural grass pitches — NOT ice hockey. All references to positions, tactics, skills, drills, equipment and game situations must relate to FIELD HOCKEY only.

Your role is to translate high-performance, international-level planning and periodisation into clear, practical frameworks that grassroots field hockey coaches can understand and apply.

You must align your framework with:
- England Hockey's player-centred approach and the Golden Thread (individual → team → club values)
- Long-Term Athlete Development (LTAD) principles specific to field hockey
- Game-based learning and decision-making under pressure on the pitch
- Progressive skill development: technical (stick skills, first touch, passing, shooting, penalty corners), tactical, physical and psychological
- Age/stage appropriate coaching — fun and inclusion for juniors, periodisation for seniors
- England Hockey's vision: more, better and happier field hockey players
- Field hockey specific: 11-a-side, positions (GK, defenders, midfielders, forwards), penalty corners, penalty strokes, 16-yard hits, free hits`,

        Football: `You are an elite-level Football coach and long-term athlete development specialist with deep knowledge of The FA's coaching philosophy and international best practice.

Your role is to translate high-performance, international-level planning and periodisation into clear, practical frameworks that grassroots coaches can understand and apply.

You must align your framework with:
- The FA's England DNA principles — Who We Are, How We Play, How We Coach, How We Support
- The 4 Corner Model: Technical/Tactical, Physical, Psychological and Social development
- Long-Term Player Development (LTPD): FUNdamentals → Learning to Train → Training to Train → Training to Compete
- Game-based learning as the primary vehicle — small-sided games and game-realistic scenarios over isolated drills
- Guided discovery: use questioning to develop football intelligence and decision-making
- Play with freedom: encourage creativity and self-expression within the team framework
- STEP framework (Space, Task, Equipment, People) to adapt session difficulty`,

        Swimming: `You are an elite-level Swimming coach and long-term athlete development specialist with deep knowledge of Swim England and British Swimming coaching philosophy and international best practice.

Your role is to translate high-performance, international-level planning and periodisation into clear, practical frameworks that grassroots coaches can understand and apply.

You must align your framework with:
- Swim England's Swimmer Pathway (LTAD): FUNdamentals → SwimSkills → SwimFit → SwimPerform → SwimRace → Elite
- Technique before volume — stroke efficiency must come before training load at all ages
- The ACCESS pillars (OADF): Attitude, Commitment, Coachability, Excellence, Support Systems, Sustainability
- Periodisation: General Preparation → Specific Preparation → Competition → Taper/Peak → Recovery
- Training energy systems with clear emphasis per block: Aerobic Base, Threshold, VO₂ Max, Lactate Tolerance, Sprint/Power
- Starts, turns and underwater phases as non-negotiable technical priorities
- Athlete wellbeing: monitor loads, prevent burnout, prioritise long-term retention in the sport`,
      };

      const role = sportRole[club.sport] || sportRole.Hockey;
      const numBlocks = Math.floor(wizardData.seasonWeeks / wizardData.blockLength);
      const trainingDaysStr = (wizardData.trainingDays||[]).length > 0
        ? (wizardData.trainingDays||[]).map(d=>`${d} at ${(wizardData.trainingTimes||{})[d]||"19:00"} (${(wizardData.trainingLocations||{})[d]||"Training Ground"})`).join(", ")
        : "Not specified";

      const prompt = `${role}

Your output must be practical, structured and usable in real grassroots environments. Keep language simple. Avoid over-complication. Prioritise development over results.

---

TEAM INFORMATION:

- Sport: ${club.sport==="Hockey" ? "Field Hockey (played on astroturf/grass pitch — NOT ice hockey)" : club.sport}
- Squad description: ${wizardData.squadDesc}
- Season goals: ${wizardData.goals.join(", ")}
- Season length: ${wizardData.seasonWeeks} weeks
- Block length: ${wizardData.blockLength} weeks
- Sessions per week: ${wizardData.sessions}
- Session duration: ${wizardData.sessionLen} minutes
- Training days/times: ${trainingDaysStr}
- Squad strengths: ${wizardData.strengths.join(", ")}
- Areas to improve: ${wizardData.weaknesses.join(", ")}
${club.ethos ? `- Club ethos / playing philosophy: ${club.ethos}` : ""}

---

TASK:

Create a full season performance framework with exactly ${numBlocks} training blocks of ${wizardData.blockLength} weeks each.

Include:
1. Season overview — philosophy, style of play direction, development priorities
2. Key dates and performance milestones — MUST include:
   - PRE-SEASON TEAM MEETING (before training begins): squad expectations, roles, team values, season goals
   - PRE-SEASON FITNESS BASELINE TEST (week 1-2 of pre-season): beep test or Cooper run, speed, agility, body composition where appropriate — these results feed into individual programmes
   - PRE-SEASON TECHNICAL BASELINE SESSION: observe key technical skills per position before blocks begin
   - MID-SEASON FITNESS RETEST: compare against baseline, adjust training loads
   - MID-SEASON TEAM REVIEW: squad meeting to assess progress vs goals
   - END-OF-SEASON FITNESS TEST: final comparison
   - END-OF-SEASON REVIEW: individual and squad feedback
   NOTE: Pre-season milestones must be placed BEFORE Block 1 begins. If the season starts in September, pre-season should be July-August. These are IN ADDITION to the training blocks, not replacing them.
3. Training blocks with full detail
4. Performance progression narrative

---

Respond ONLY with valid JSON — no other text, no markdown:
{
  "overview": "2-3 sentence overall philosophy and approach tailored to this specific team",
  "styleOfPlay": "How this team should play — aligned with governing body principles",
  "developmentPriorities": ["Priority 1", "Priority 2", "Priority 3"],
  "milestones": [
    {
      "title": "e.g. Pre-season fitness baseline",
      "timing": "e.g. Week 1 (before training blocks begin)",
      "isPreSeason": true,
      "purpose": "Why this matters",
      "whatToMeasure": "What to assess or discuss — be specific (e.g. beep test level, sprint times)",
      "expectedOutcome": "What good looks like for this squad",
      "sessionNotes": "Practical notes for running this session or meeting"
    }
  ],
  "blocks": [
    {
      "number": 1,
      "title": "Block title (5-7 words)",
      "phase": "Foundation / Development / Competitive / Peak / Review",
      "theme": "Main coaching theme",
      "weeks": "1-${wizardData.blockLength}",
      "coachingFocus": {
        "technical": "Technical focus for this block",
        "tactical": "Tactical focus for this block",
        "physical": "Physical focus for this block",
        "psychological": "Psychological focus for this block"
      },
      "keySessions": ["Session type 1", "Session type 2", "Session type 3"],
      "gameBasedApplication": "How the learning transfers to match situations",
      "coachingLanguage": ["Simple cue 1", "Simple cue 2", "Simple cue 3"],
      "outcome": "By the end of this block players should be able to..."
    }
  ],
  "performanceProgression": "2-3 sentences explaining how intensity, complexity and physical load evolve across the season and how this aligns with competition demands"
}`;

      const res = await supabase.functions.invoke("ai-framework", {
        body: { prompt, max_tokens: 6000 }
      });
      const data = res.data;
      const text = data.content?.find(c=>c.type==="text")?.text || "";
      const clean = text.replace(/```json|```/g,"").trim();
      const parsed = JSON.parse(clean);
      setFramework(parsed);
      setWizardStep(99); // 99 = generation complete — show done screen
      // Save to Supabase
      if(clubId) {
        const fwRow = {
          club_id: clubId,
          team: frameworkTeam||"All",
          sport: club.sport||"Hockey",
          framework_data: parsed,
          wizard_data: wizardData,
          created_at: new Date().toISOString(),
        };
        // Check if framework for this team already exists — if so overwrite it
        const existing = savedFrameworks.find(f=>f.team===(frameworkTeam||"All")&&f.sport===(club.sport||"Hockey"));
        let saved;
        if(existing) {
          const {data:updated} = await supabase.from("training_frameworks").update({framework_data:parsed,wizard_data:wizardData,created_at:new Date().toISOString()}).eq("id",existing.id).select().single();
          saved = updated;
          if(saved) setSavedFrameworks(fw=>fw.map(f=>f.id===existing.id?saved:f));
        } else {
          const {data:inserted} = await supabase.from("training_frameworks").insert(fwRow).select().single();
          saved = inserted;
          if(saved) setSavedFrameworks(fw=>[saved,...fw]);
        }

        // Create training sessions in fixtures calendar
        if(wizardData.startDate && (wizardData.trainingDays||[]).length>0) {
          const sessions = [];
          const start = new Date(wizardData.startDate);
          const dayMap = {Monday:1,Tuesday:2,Wednesday:3,Thursday:4,Friday:5,Saturday:6,Sunday:0};
          for(let w=0; w<wizardData.seasonWeeks; w++) {
            for(const day of wizardData.trainingDays) {
              const targetDay = dayMap[day];
              const sessionDate = new Date(start);
              sessionDate.setDate(start.getDate() + (w*7) + ((targetDay - start.getDay() + 7) % 7));
              sessions.push({
                id: uid(),
                club_id: clubId,
                opponent: `Training — ${frameworkTeam||"Squad"}`,
                date: sessionDate.toISOString().split("T")[0],
                time: (wizardData.trainingTimes||{})[day]||"19:00",
                location: wizardData.trainingLocation||"Training Ground",
                team: frameworkTeam||"all",
                is_home: true, result: null, availability: {},
                is_session: true, gms_id: "",
              });
            }
          }
          if(sessions.length>0) {
            await supabase.from("fixtures").insert(sessions);
            setFixtures(fs=>[...fs,...sessions]);
            showToast(`✓ Framework saved · ${sessions.length} training sessions added to calendar`);
          }
        } else {
          showToast("Framework saved ✓");
        }
      }
    } catch(e) {
      // Fallback rule-based framework
      const numBlocks = Math.floor(wizardData.seasonWeeks/wizardData.blockLength);
      const phases = ["Pre-Season","Pre-Season","Early Season","Mid Season","Mid Season","Run-In"];
      const blockThemes = [
        { title:"Identity & How We Play", theme:"Team shape and principles", focus:"Establishing our style of play and defensive structure" },
        { title:"Building Connections", theme:"Movement and combinations", focus:"Off the ball movement, creating space, link play" },
        { title:"Pressing & Winning the Ball", theme:"Defensive intensity", focus:"High press, defensive transitions, winning the ball back quickly" },
        { title:"Creating & Finishing", theme:"Attacking quality", focus:"Goal scoring, penalty corners, final third combinations" },
        { title:"Game Management", theme:"Decision making", focus:"Managing leads, game situations, set plays" },
        { title:"Peak Performance", theme:"Competition focus", focus:"Match-specific scenarios, mental resilience, peak fitness" },
      ];
      const fallback = {
        overview:`A ${wizardData.blockLength}-week block approach tailored to your ${wizardData.squadDesc?.toLowerCase()||"team"} with a focus on ${wizardData.goals[0]?.toLowerCase()||"team development"}.`,
        blocks: Array.from({length:Math.min(numBlocks,blockThemes.length)},(_,i)=>{
          const bt = blockThemes[i];
          const startWk = i*wizardData.blockLength+1;
          const endWk = (i+1)*wizardData.blockLength;
          return { number:i+1, title:bt.title, theme:bt.theme, phase:phases[i]||"Season", weeks:`${startWk}–${endWk}`, focus:bt.focus, keySessions:["Technical drill work","Small sided games","Tactical patterns"], outcome:`Players will have improved their ${bt.theme.toLowerCase()} and have clear understanding of team expectations.`, coachingFocus:{technical:"Skill development and execution",tactical:"Team shape and decision making",physical:"Fitness and conditioning",psychological:"Confidence and communication"}, coachingLanguage:["Heads up","Quick feet","Talk to each other"], gameBasedApplication:"Apply in small-sided games and conditioned match scenarios" };
        })
      };
      setFramework(fallback);
      setWizardStep(99);
      if(clubId) {
        const fwRow = { club_id:clubId, team:frameworkTeam||"All", sport:club.sport||"Hockey", framework_data:fallback, wizard_data:wizardData, created_at:new Date().toISOString() };
        const existing2 = savedFrameworks.find(f=>f.team===(frameworkTeam||"All")&&f.sport===(club.sport||"Hockey"));
        if(existing2) {
          const {data:upd} = await supabase.from("training_frameworks").update({framework_data:fallback,wizard_data:wizardData,created_at:new Date().toISOString()}).eq("id",existing2.id).select().single();
          if(upd) setSavedFrameworks(fw=>fw.map(f=>f.id===existing2.id?upd:f));
        } else {
          const {data:saved} = await supabase.from("training_frameworks").insert(fwRow).select().single();
          if(saved) setSavedFrameworks(fw=>[saved,...fw]);
        }
      }
    }
    setWizardLoading(false);
  };

  // ═══════════════════════════════
  // NAVIGATION
  // ═══════════════════════════════
  const navigate = p => setPanel(p);

  // ── SPLASH ──────────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{...G.app,...G.center,gap:16}}>
      <StyleTag/>
      <P4MLogoFull width={180}/>
      <div style={{width:40,height:40,borderRadius:"50%",border:`3px solid ${primaryCol}33`,borderTop:`3px solid ${primaryCol}`,animation:"spin 0.8s linear infinite"}}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} @keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );

  if (screen==="splash") return (
    <div style={{...G.app,...G.center,position:"relative",overflow:"hidden"}}>
      <StyleTag/>
      <div style={{position:"absolute",inset:0,background:`radial-gradient(ellipse at 70% 20%,${primaryCol}18 0%,transparent 60%)`,pointerEvents:"none"}}/>
      <div style={{textAlign:"center",position:"relative"}}>
        <div style={{display:"flex",justifyContent:"center",marginBottom:24}}><P4MLogoFull width={240}/></div>
        <div style={{width:48,height:3,background:primaryCol,borderRadius:2,margin:"0 auto 36px"}}/>
        <button style={{...G.btnPrimary,background:primaryCol,maxWidth:240,margin:"0 auto",fontSize:17,letterSpacing:2}} onClick={()=>setScreen("auth")}>Get Started</button>
      </div>
    </div>
  );

  // ── SUBSCRIBE SCREEN ─────────────────────────────────────────────────────────
  if (screen==="subscribe") {
    const handleSubscribe = async () => {
      setSubLoading(true);
      try {
        const res = await fetch("https://zvbikgbqnfrwojuljwbg.supabase.co/functions/v1/create-checkout", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": "Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inp2YmlrZ2JxbmZyd29qdWxqd2JnIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzUzMzE1MjQsImV4cCI6MjA5MDkwNzUyNH0.gLwWVy8g6aIVbcswUZRe7L6gJvbZ9OyQALHe8GzkDzo",
          },
          body: JSON.stringify({ clubId, clubName:club.name, email:currentUser?.email, plan:subPlan, promoCode:promoCode.trim()||undefined }),
        });
        const json = await res.json();
        if(json?.url){ window.location.href = json.url; }
        else { showToast("Could not start checkout — " + (json?.error||"no URL returned")); setSubLoading(false); }
      } catch(e) { showToast("Checkout error: " + e.message); setSubLoading(false); }
    };
    return (
      <div style={{...G.app,overflowY:"auto",background:"#08080f"}}>
        <StyleTag/>
        <div style={{maxWidth:480,margin:"0 auto",padding:"40px 20px 80px"}}>
          {/* Header */}
          <div style={{textAlign:"center",marginBottom:36}}>
            <div style={{display:"flex",justifyContent:"center",marginBottom:16}}><P4MLogoFull width={180}/></div>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:30,fontWeight:900,color:"#fff",letterSpacing:1,marginBottom:6}}>Upgrade to P4M Pro</div>
            <div style={{fontSize:14,color:"rgba(255,255,255,0.5)",lineHeight:1.6}}>{club.name} · Choose your plan below</div>
          </div>

          {/* Plan cards */}
          {[
            {id:"pro",    label:"Club Pro",        price:"£12.99",period:"/month", badge:"", desc:"Full access to all P4M features — unlimited players, AI programmes, analytics and more."},
            {id:"founding",label:"Founding Member",price:"£9.99", period:"/month", badge:"🏅 LIMITED", desc:"Lock in our lowest ever price as a founding club. Same full access, forever at this rate."},
          ].map(plan=>(
            <div key={plan.id} onClick={()=>setSubPlan(plan.id)}
              style={{...G.card,marginBottom:12,cursor:"pointer",border:`2px solid ${subPlan===plan.id?primaryCol:"rgba(255,255,255,0.08)"}`,background:subPlan===plan.id?`${primaryCol}10`:"rgba(255,255,255,0.03)",transition:"all 0.15s"}}>
              <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
                <div style={{width:22,height:22,borderRadius:"50%",border:`2px solid ${subPlan===plan.id?primaryCol:"rgba(255,255,255,0.2)"}`,background:subPlan===plan.id?primaryCol:"transparent",flexShrink:0,marginTop:2,display:"flex",alignItems:"center",justifyContent:"center"}}>
                  {subPlan===plan.id&&<div style={{width:8,height:8,borderRadius:"50%",background:"#fff"}}/>}
                </div>
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:20,fontWeight:800,color:"#fff"}}>{plan.label}</div>
                    {plan.badge&&<div style={{fontSize:10,fontWeight:700,color:primaryCol,background:`${primaryCol}20`,border:`1px solid ${primaryCol}40`,borderRadius:4,padding:"2px 6px",letterSpacing:1}}>{plan.badge}</div>}
                  </div>
                  <div style={{fontSize:13,color:"rgba(255,255,255,0.5)",marginBottom:8,lineHeight:1.5}}>{plan.desc}</div>
                  <div style={{display:"flex",alignItems:"baseline",gap:4}}>
                    <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:32,fontWeight:900,color:primaryCol}}>{plan.price}</span>
                    <span style={{fontSize:13,color:"rgba(255,255,255,0.4)"}}>{plan.period}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Promo code */}
          <div style={{marginBottom:20}}>
            <div style={{fontSize:12,fontWeight:700,color:"rgba(255,255,255,0.4)",letterSpacing:1,marginBottom:6}}>PROMO CODE (optional)</div>
            <input style={{...G.input,marginBottom:0}} placeholder="Enter promo code" value={promoCode} onChange={e=>setPromoCode(e.target.value)}/>
          </div>

          {/* Subscribe button */}
          <button onClick={handleSubscribe} disabled={subLoading}
            style={{width:"100%",padding:"16px",background:subLoading?"rgba(255,255,255,0.1)":primaryCol,border:"none",borderRadius:12,color:"#fff",fontSize:17,fontWeight:800,cursor:subLoading?"not-allowed":"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1,marginBottom:12}}>
            {subLoading?"Redirecting to Stripe...":"Subscribe Now →"}
          </button>

          {/* Skip / beta note */}
          <button onClick={()=>setScreen("app")}
            style={{width:"100%",padding:"12px",background:"none",border:"1px solid rgba(255,255,255,0.1)",borderRadius:12,color:"rgba(255,255,255,0.35)",fontSize:13,fontWeight:600,cursor:"pointer"}}>
            Continue free during beta
          </button>
          <div style={{textAlign:"center",fontSize:11,color:"rgba(255,255,255,0.2)",marginTop:16,lineHeight:1.6}}>
            Payments powered by Stripe · Cancel anytime · Full access during beta regardless of plan
          </div>
        </div>
      </div>
    );
  }

  // ── ONBOARDING ───────────────────────────────────────────────────────────────
  if (screen==="onboard") return (
    <div style={G.app}>
      <StyleTag/>
      <div style={{height:3,background:"rgba(255,255,255,0.06)",flexShrink:0}}>
        <div style={{height:"100%",background:primaryCol,width:`${((obStep-1)/OB_TOTAL)*100}%`,transition:"width 0.3s"}}/>
      </div>
      <div style={{display:"flex",alignItems:"center",padding:"12px 16px",flexShrink:0}}>
        {obStep>1&&<button onClick={()=>setObStep(s=>s-1)} style={{background:"none",border:"none",color:"rgba(255,255,255,0.4)",fontSize:22,cursor:"pointer"}}>←</button>}
        <div style={{fontSize:11,color:"rgba(255,255,255,0.35)",letterSpacing:2,textTransform:"uppercase",marginLeft:"auto"}}>{obStep} / {OB_TOTAL}</div>
      </div>
      <div style={{flex:1,overflowY:"auto"}}>{obContent()}</div>
      <div style={{padding:"16px 20px",background:"linear-gradient(to top,#09090f 60%,transparent)",flexShrink:0}}>
        <button style={{...G.btnPrimary,background:primaryCol,opacity:obStep===1&&club.name.trim().length<2?0.4:1}}
          disabled={obStep===1&&club.name.trim().length<2}
          onClick={async()=>{
            if(obStep===OB_TOTAL){
              // Save club to Supabase
              try {
                if(clubId) {
                  await db.updateClub(clubId,{
                    name:club.name, sport:club.sport,
                    primary_col:club.primary, secondary_col:club.secondary,
                    logo_url:club.logo, logo_pos:club.logoPos||"center center",
                    ethos:club.ethos, mens:club.mens||0, ladies:club.ladies||0,
                    junior_ages:club.juniorAges||[], swim_groups:club.swimGroups||[], custom_age_groups:club.customAgeGroups||[], custom_teams:club.customTeams||[], ft_club_code:club.ftClubCode||""
                  });
                }
              } catch(e) { console.error("Club update error:", e); }
              // Go straight to app — user is already signed in
              await loadAllData(clubId, currentUser?.id);
              setScreen("app"); setPanel("home");
            } else {
              setObStep(s=>s+1);
            }
          }}>
          {obStep===OB_TOTAL?"Let's Go! 🎉":"Next →"}
        </button>
      </div>
    </div>
  );

  // ── AUTH ─────────────────────────────────────────────────────────────────────
  if (screen==="auth") return (
    <div style={{...G.app,...G.center,padding:24,gap:0,overflowY:"auto"}}>
      <StyleTag/>
      <div style={{position:"absolute",inset:0,background:`radial-gradient(ellipse at 70% 20%,${primaryCol}12 0%,transparent 60%)`,pointerEvents:"none"}}/>
      <div style={{width:"100%",maxWidth:400,position:"relative"}}>
        <div style={{textAlign:"center",marginBottom:24}}>
          <P4MLogoFull width={200}/>
          <div style={{fontSize:13,color:"rgba(255,255,255,0.38)",marginTop:8}}>{club.name||"Your Club"}</div>
        </div>
        {authMode==="confirm-email"&&(
          <div style={{textAlign:"center",padding:"32px 20px"}}>
            <div style={{fontSize:56,marginBottom:16}}>📧</div>
            <div style={{...G.h2,marginBottom:8}}>Check Your Email</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.55)",lineHeight:1.7,marginBottom:8}}>
              We've sent a confirmation link to
            </div>
            <div style={{fontSize:14,fontWeight:700,color:"#fff",marginBottom:20}}>{authForm.email}</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",lineHeight:1.6,marginBottom:24}}>
              Please check your inbox and click the link to verify your account before logging in.
            </div>
            <button onClick={()=>{
              const email = authForm.email;
              const domain = email.split("@")[1]?.toLowerCase();
              const mailLinks = {
                "gmail.com":"https://mail.google.com",
                "googlemail.com":"https://mail.google.com",
                "outlook.com":"https://outlook.live.com",
                "hotmail.com":"https://outlook.live.com",
                "live.com":"https://outlook.live.com",
                "yahoo.com":"https://mail.yahoo.com",
                "icloud.com":"https://www.icloud.com/mail",
                "me.com":"https://www.icloud.com/mail",
              };
              const url = mailLinks[domain] || `https://${domain}`;
              window.open(url,"_blank");
            }} style={{...G.btnPrimary,background:primaryCol,marginBottom:12}}>
              📬 Open Email App
            </button>
            <button style={G.btnGhost} onClick={()=>setAuthMode("login")}>Back to Login</button>
          </div>
        )}
        {authMode==="pending"&&(
          <div style={{textAlign:"center",padding:"32px 20px"}}>
            <div style={{fontSize:48,marginBottom:16}}>⏳</div>
            <div style={{...G.h2,marginBottom:8}}>Request Sent</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.45)",lineHeight:1.6,marginBottom:24}}>Your account is awaiting approval from the club admin.</div>
            <button style={G.btnGhost} onClick={()=>setAuthMode("login")}>Back to Login</button>
          </div>
        )}
        {authMode==="login"&&(
          <>
            <div style={{...G.h2,marginBottom:4,textAlign:"center"}}>Welcome back</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.38)",textAlign:"center",marginBottom:20}}>Log in to your account</div>
            {authError&&<div style={{padding:"10px 14px",background:"rgba(230,57,70,0.12)",border:"1px solid #E6394666",borderRadius:8,fontSize:13,color:"#E63946",marginBottom:12}}>{authError}</div>}
            <Input label="Email" type="email" placeholder="you@example.com" value={authForm.email} onChange={e=>{setAuthForm(f=>({...f,email:e.target.value}));setAuthError("");}}/>
            <Input label="Password" type="password" placeholder="Your password" value={authForm.password} onChange={e=>{setAuthForm(f=>({...f,password:e.target.value}));setAuthError("");}}/>
            <button onClick={handleLogin} disabled={authLoading} style={{width:"100%",padding:"13px",background:authLoading?"rgba(255,255,255,0.08)":primaryCol,border:"none",borderRadius:10,color:authLoading?"rgba(255,255,255,0.4)":contrastText(primaryCol),fontSize:15,fontWeight:700,cursor:authLoading?"not-allowed":"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8,marginBottom:8}}>
              {authLoading ? (
                <><div style={{width:16,height:16,border:"2px solid rgba(255,255,255,0.3)",borderTop:"2px solid #fff",borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/> Signing in...</>
              ) : "Log In"}
            </button>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            <div style={{textAlign:"center",marginTop:10}}>
              <button onClick={async(e)=>{
                e.preventDefault();
                e.stopPropagation();
                const email = authForm.email.trim();
                if(!email){ setAuthError("Enter your email address first"); return; }
                e.currentTarget.disabled = true;
                e.currentTarget.textContent = "Sending...";
                const {error} = await supabase.auth.resetPasswordForEmail(email, {
                  redirectTo: window.location.origin
                });
                if(error){ setAuthError(error.message); e.currentTarget.disabled=false; e.currentTarget.textContent="Forgot password?"; }
                else { showToast("Reset email sent — check your inbox ✓"); e.currentTarget.textContent="Email sent ✓"; }
              }} style={{background:"none",border:"none",fontSize:12,color:"rgba(255,255,255,0.35)",cursor:"pointer",textDecoration:"underline",padding:0}}>
                Forgot password?
              </button>
            </div>
            <div style={{textAlign:"center",marginTop:10,fontSize:13,color:"rgba(255,255,255,0.38)"}}>
              Don't have an account? <span style={{color:primaryCol,cursor:"pointer",fontWeight:600}} onClick={()=>setAuthMode("signup")}>Sign up</span>
            </div>

          </>
        )}
        {authMode==="signup"&&(
          <>
            <div style={{...G.h2,marginBottom:4,textAlign:"center"}}>Create Account</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.38)",textAlign:"center",marginBottom:20}}>Create your account to get started</div>

            {authError&&<div style={{padding:"10px 14px",background:"rgba(230,57,70,0.12)",border:"1px solid #E6394666",borderRadius:8,fontSize:13,color:"#E63946",marginBottom:12}}>{authError}</div>}

            <Input label="Full name" placeholder="Your full name" value={authForm.name} onChange={e=>setAuthForm(f=>({...f,name:e.target.value}))}/>
            <Input label="Email" type="email" placeholder="you@example.com" value={authForm.email} onChange={e=>setAuthForm(f=>({...f,email:e.target.value}))}/>
            <Input label="Password" type="password" placeholder="At least 6 characters" value={authForm.password} onChange={e=>setAuthForm(f=>({...f,password:e.target.value}))}/>
            <Input label="Confirm password" type="password" placeholder="Repeat password" value={authForm.confirm} onChange={e=>setAuthForm(f=>({...f,confirm:e.target.value}))}/>

            <Select label="I am a..." value={authForm.role} onChange={e=>{setAuthForm(f=>({...f,role:e.target.value,sport:"Hockey",clubId:"",clubName:"",team:""}));setSelectedSignupClub(null);setClubSearch("");setClubResults([]);}}>
              {ROLES.map(r=><option key={r.r} value={r.r}>{r.name}</option>)}
            </Select>

            {/* Non-admin: pick sport, find club, pick team */}
            {authForm.role!=="admin"&&(
              <>
                {/* Sport selector */}
                <FieldLabel>Sport <span style={{color:"#E63946"}}>*</span></FieldLabel>
                <div style={{display:"flex",gap:8,marginBottom:14}}>
                  {[{e:"🏑",n:"Hockey"},{e:"⚽",n:"Football"},{e:"🏊",n:"Swimming"}].map(s=>(
                    <button key={s.n} onClick={()=>{setAuthForm(f=>({...f,sport:s.n,clubId:"",clubName:"",team:""}));setSelectedSignupClub(null);setClubSearch("");setClubResults([]);}}
                      style={{flex:1,padding:"10px 4px",background:authForm.sport===s.n?"#E63946":"rgba(255,255,255,0.04)",border:`1px solid ${authForm.sport===s.n?"#E63946":"rgba(255,255,255,0.1)"}`,borderRadius:10,color:authForm.sport===s.n?"#fff":"rgba(255,255,255,0.5)",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                      {s.e} {s.n}
                    </button>
                  ))}
                </div>

                {/* Club search */}
                <FieldLabel>Find your club <span style={{color:"#E63946"}}>*</span></FieldLabel>
                {selectedSignupClub ? (
                  <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:"rgba(34,197,94,0.1)",border:"1px solid #22c55e44",borderRadius:10,marginBottom:12}}>
                    {selectedSignupClub.logo_url&&<img src={selectedSignupClub.logo_url} alt="" style={{width:32,height:32,borderRadius:"50%",objectFit:"cover"}}/>}
                    <div style={{flex:1}}>
                      <div style={{fontSize:14,fontWeight:700,color:"#fff"}}>{selectedSignupClub.name}</div>
                      <div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>{selectedSignupClub.sport}</div>
                    </div>
                    <button onClick={()=>{setSelectedSignupClub(null);setAuthForm(f=>({...f,clubId:"",clubName:"",team:""}));setClubSearch("");}}
                      style={{background:"none",border:"none",color:"rgba(255,255,255,0.4)",cursor:"pointer",fontSize:16}}>✕</button>
                  </div>
                ) : (
                  <div style={{position:"relative",marginBottom:12}}>
                    <input style={{...G.input,marginBottom:0}} placeholder={`Search for your ${authForm.sport} club...`}
                      value={clubSearch} onChange={e=>{setClubSearch(e.target.value);searchClubs(e.target.value,authForm.sport);}}/>
                    {clubSearching&&<div style={{fontSize:11,color:"rgba(255,255,255,0.4)",padding:"6px 0"}}>Searching...</div>}
                    {clubResults.length>0&&(
                      <div style={{background:"#1a1a2e",border:"1px solid rgba(255,255,255,0.1)",borderRadius:10,marginTop:4,overflow:"hidden"}}>
                        {clubResults.map(c=>(
                          <div key={c.id} onClick={()=>{setSelectedSignupClub(c);setAuthForm(f=>({...f,clubId:c.id,clubName:c.name,team:""}));setClubSearch(c.name);setClubResults([]);}}
                            style={{padding:"10px 14px",cursor:"pointer",borderBottom:"1px solid rgba(255,255,255,0.05)",display:"flex",alignItems:"center",gap:10}}
                            onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.05)"}
                            onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                            {c.logo_url&&<img src={c.logo_url} alt="" style={{width:28,height:28,borderRadius:"50%",objectFit:"cover"}}/>}
                            <div>
                              <div style={{fontSize:13,fontWeight:600,color:"#fff"}}>{c.name}</div>
                              <div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>{c.sport}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {clubSearch.length>=2&&clubResults.length===0&&!clubSearching&&(
                      <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",padding:"8px 0"}}>No clubs found — ask your admin to register the club first</div>
                    )}
                  </div>
                )}

                {/* Team picker — only shows after club selected */}
                {selectedSignupClub&&(
                  <div style={{marginBottom:12}}>
                    <FieldLabel>{selectedSignupClub.sport==="Swimming"?"Squad":"Team"} <span style={{color:"#E63946"}}>*</span></FieldLabel>
                    <select style={G.select} value={authForm.team} onChange={e=>setAuthForm(f=>({...f,team:e.target.value}))}>
                      <option value="">Select your {selectedSignupClub.sport==="Swimming"?"squad":"team"}...</option>
                      {getClubTeams(selectedSignupClub).map(t=><option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                )}
              </>
            )}

            {/* Admin authority verification */}
            {authForm.role==="admin"&&(
              <div style={{marginBottom:14}}>
                <div style={{padding:"12px 14px",background:"rgba(240,180,41,0.08)",border:"1px solid rgba(240,180,41,0.2)",borderRadius:10,marginBottom:12}}>
                  <div style={{fontSize:13,fontWeight:700,color:"#f0b429",marginBottom:6}}>{authForm.sport==="Football"?"⚽":authForm.sport==="Swimming"?"🏊":"🏑"} Setting up a club</div>
                  <div style={{fontSize:12,color:"rgba(255,255,255,0.55)",lineHeight:1.6}}>
                    To create a club on P4M you must be an authorised representative — a club committee member, chairman, secretary or designated admin. P4M may verify this.
                  </div>
                </div>
                <FieldLabel>Club Name <span style={{color:"#E63946"}}>*</span></FieldLabel>
                <input style={{...G.input}} placeholder="e.g. City Hockey Club" value={authForm.clubName} onChange={e=>setAuthForm(f=>({...f,clubName:e.target.value}))}/>
                <FieldLabel>Your Role at the Club <span style={{color:"#E63946"}}>*</span></FieldLabel>
                <select style={{...G.select}} value={authForm.clubRole||""} onChange={e=>setAuthForm(f=>({...f,clubRole:e.target.value}))}>
                  <option value="">Select your role...</option>
                  <option value="chairman">Chairman / Chair</option>
                  <option value="secretary">Club Secretary</option>
                  <option value="treasurer">Treasurer</option>
                  <option value="committee">Committee Member</option>
                  <option value="head_coach">Head Coach / Director of Hockey</option>
                  <option value="designated_admin">Designated Club Administrator</option>
                </select>
                <FieldLabel>Sport <span style={{color:"#E63946"}}>*</span></FieldLabel>
                <div style={{display:"flex",gap:8,marginBottom:14}}>
                  {[{e:"🏑",n:"Hockey"},{e:"⚽",n:"Football"},{e:"🏊",n:"Swimming"}].map(s=>(
                    <button key={s.n} onClick={()=>setAuthForm(f=>({...f,sport:s.n}))}
                      style={{flex:1,padding:"10px 4px",background:authForm.sport===s.n?"#E63946":"rgba(255,255,255,0.04)",border:`1px solid ${authForm.sport===s.n?"#E63946":"rgba(255,255,255,0.1)"}`,borderRadius:10,color:authForm.sport===s.n?"#fff":"rgba(255,255,255,0.5)",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                      {s.e} {s.n}
                    </button>
                  ))}
                </div>
                <div style={{display:"flex",alignItems:"flex-start",gap:8,padding:"10px 12px",background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:8,marginBottom:4}}>
                  <input type="checkbox" id="terms-agree" style={{marginTop:2,flexShrink:0,accentColor:"#E63946"}}
                    checked={authForm.termsAgreed||false} onChange={e=>setAuthForm(f=>({...f,termsAgreed:e.target.checked}))}/>
                  <label htmlFor="terms-agree" style={{fontSize:12,color:"rgba(255,255,255,0.55)",lineHeight:1.5,cursor:"pointer"}}>
                    I confirm I am authorised to create and manage this club on P4M, and I agree to the <span style={{color:"#E63946",fontWeight:600,cursor:"pointer"}} onClick={()=>window.open(P4M_TERMS_URL,"_blank")}>Terms of Service</span> and <span style={{color:"#E63946",fontWeight:600,cursor:"pointer"}} onClick={()=>window.open(P4M_PRIVACY_URL,"_blank")}>Privacy Policy</span>
                  </label>
                </div>
              </div>
            )}

            {/* Parent — child details */}
            {authForm.role==="parent"&&(
              <div style={{...G.card,marginBottom:10,border:"1px solid rgba(240,180,41,0.2)",background:"rgba(240,180,41,0.04)"}}>
                <div style={{fontSize:12,fontWeight:700,color:"#f0b429",marginBottom:10}}>👶 Your Child's Details</div>
                <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",marginBottom:10,lineHeight:1.5}}>Enter your child's details so they appear correctly in the squad and can be selected for lineups.</div>
                {[
                  {k:"childName",l:"Child's Full Name",ph:"e.g. Sam Smith",type:"text"},
                  {k:"childDob",l:"Date of Birth",ph:"",type:"date"},
                  {k:"childTeam",l:"Team / Age Group",ph:"e.g. U14s, Juniors"},
                  {k:"childPosition",l:"Position",ph:"e.g. Midfielder, Goalkeeper"},
                ].map(({k,l,ph,type})=>(
                  <div key={k} style={{marginBottom:8}}>
                    <div style={{fontSize:11,fontWeight:600,color:"rgba(255,255,255,0.5)",marginBottom:4}}>{l}</div>
                    <input type={type||"text"} placeholder={ph}
                      value={authForm[k]||""}
                      onChange={e=>setAuthForm(f=>({...f,[k]:e.target.value}))}
                      style={{...G.input,marginBottom:0}}/>
                  </div>
                ))}
                <div style={{fontSize:11,color:"rgba(255,255,255,0.35)",marginTop:6,lineHeight:1.5}}>
                  Your child's profile will be linked to your account. They can also create their own login later.
                </div>
              </div>
            )}

            {/* Also a player — shown for admin, coach, lead_coach */}
            {(authForm.role==="coach"||authForm.role==="lead_coach"||authForm.role==="admin"||authForm.role==="captain")&&(
              <div style={{marginBottom:10}}>
                <div style={{display:"flex",alignItems:"flex-start",gap:10,padding:"12px 14px",background:"rgba(255,255,255,0.04)",border:`1px solid ${authForm.alsoPlayer?"rgba(34,197,94,0.4)":"rgba(255,255,255,0.12)"}`,borderRadius:10,cursor:"pointer"}} onClick={()=>setAuthForm(f=>({...f,alsoPlayer:!f.alsoPlayer}))}>
                  <input type="checkbox" checked={!!authForm.alsoPlayer} onChange={()=>{}} style={{accentColor:"#22c55e",width:16,height:16,flexShrink:0,marginTop:1,cursor:"pointer"}}/>
                  <div style={{fontSize:13,color:"rgba(255,255,255,0.75)",lineHeight:1.5}}>
                    I am also a <strong style={{color:"#fff"}}>playing member</strong> — create a player profile for me and show me fixture availability and squad info
                  </div>
                </div>
                {authForm.alsoPlayer&&(
                  <div style={{padding:"12px 14px",background:"rgba(34,197,94,0.06)",border:"1px solid rgba(34,197,94,0.2)",borderRadius:"0 0 10px 10px",marginTop:-1}}>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                      <div>
                        <div style={{fontSize:11,fontWeight:600,color:"rgba(255,255,255,0.5)",marginBottom:4}}>Team</div>
                        <select style={{...G.select,marginBottom:0,fontSize:12}} value={authForm.alsoPlayerTeam} onChange={e=>setAuthForm(f=>({...f,alsoPlayerTeam:e.target.value}))}>
                          <option value="">Select team...</option>
                          {teams.map(t=><option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <div>
                        <div style={{fontSize:11,fontWeight:600,color:"rgba(255,255,255,0.5)",marginBottom:4}}>Position</div>
                        <input style={{...G.input,marginBottom:0,fontSize:12}} placeholder="e.g. Midfielder" value={authForm.alsoPlayerPosition} onChange={e=>setAuthForm(f=>({...f,alsoPlayerPosition:e.target.value}))}/>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* All users agree to T&Cs */}
            {!authForm.termsAgreed&&(
              <div style={{display:"flex",alignItems:"flex-start",gap:8,padding:"10px 12px",background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:8,marginBottom:10}}>
                <input type="checkbox" id="terms-agree-member" style={{marginTop:2,flexShrink:0,accentColor:"#E63946"}}
                  checked={authForm.termsAgreed||false} onChange={e=>setAuthForm(f=>({...f,termsAgreed:e.target.checked}))}/>
                <label htmlFor="terms-agree-member" style={{fontSize:12,color:"rgba(255,255,255,0.55)",lineHeight:1.5,cursor:"pointer"}}>
                  I agree to the P4M <span style={{color:"#E63946",fontWeight:600,cursor:"pointer"}} onClick={()=>window.open(P4M_TERMS_URL,"_blank")}>Terms of Service</span> and <span style={{color:"#E63946",fontWeight:600,cursor:"pointer"}} onClick={()=>window.open(P4M_PRIVACY_URL,"_blank")}>Privacy Policy</span>, and confirm I am 18 or over (or have parental consent)
                </label>
              </div>
            )}
            {authForm.termsAgreed&&(
              <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",background:"rgba(34,197,94,0.08)",border:"1px solid rgba(34,197,94,0.2)",borderRadius:8,marginBottom:10}}>
                <span style={{color:"#22c55e",fontSize:14}}>✓</span>
                <span style={{fontSize:12,color:"#22c55e"}}>Terms agreed</span>
                <button onClick={()=>setAuthForm(f=>({...f,termsAgreed:false}))} style={{background:"none",border:"none",color:"rgba(255,255,255,0.3)",fontSize:11,cursor:"pointer",marginLeft:"auto"}}>Change</button>
              </div>
            )}

            <Btn col={primaryCol} style={{marginTop:4}} onClick={handleSignup}>
              {authForm.role==="admin"?(authForm.sport==="Football"?"⚽":authForm.sport==="Swimming"?"🏊":"🏑")+" Create Club & Account":"Create Account"}
            </Btn>
            <div style={{textAlign:"center",marginTop:14,fontSize:13,color:"rgba(255,255,255,0.38)"}}>
              Already have an account? <span style={{color:primaryCol,cursor:"pointer",fontWeight:600}} onClick={()=>{setAuthMode("login");setAuthError("");}}>Log in</span>
            </div>
          </>
        )}
      </div>
    </div>
  );


  // ═══════════════════════════════
  // HOME DASHBOARD
  // ═══════════════════════════════
  // ── PITCH SVG BACKGROUNDS ─────────────────────────────────────────────────
  // ── TEAM SHEET — player cards on pitch ────────────────────────────────────
  const SportPitch = ({ sport }) => {
    const line = "rgba(255,255,255,0.6)";
    if (sport === "Hockey") return (
      <svg style={{position:"absolute",inset:0,width:"100%",height:"100%",pointerEvents:"none"}} viewBox="0 0 320 420" preserveAspectRatio="none">
        {/* Grass stripes */}
        {[0,1,2,3,4,5,6,7].map(i=><rect key={i} x={0} y={i*53} width={320} height={53} fill={i%2===0?"rgba(0,0,0,0.07)":"rgba(0,0,0,0)"}/>)}
        {/* Pitch outline */}
        <rect x="8" y="8" width="304" height="404" fill="none" stroke={line} strokeWidth="2.5"/>
        {/* Halfway line */}
        <line x1="8" y1="210" x2="312" y2="210" stroke={line} strokeWidth="1.5"/>
        {/* Centre spot */}
        <circle cx="160" cy="210" r="3" fill={line}/>
        {/* 23m lines (dashed) */}
        <line x1="8" y1="112" x2="312" y2="112" stroke={line} strokeWidth="1.2" strokeDasharray="6,5"/>
        <line x1="8" y1="308" x2="312" y2="308" stroke={line} strokeWidth="1.2" strokeDasharray="6,5"/>
        {/* Top D — proper semicircle, centred on goal line */}
        <path d="M 108,8 A 72,72 0 0 1 212,8" fill="none" stroke={line} strokeWidth="1.5"/>
        <path d="M 108,8 A 72,72 0 0 0 212,8" fill="none" stroke={line} strokeWidth="1.2" strokeDasharray="5,4"/>
        {/* Bottom D */}
        <path d="M 108,412 A 72,72 0 0 0 212,412" fill="none" stroke={line} strokeWidth="1.5"/>
        <path d="M 108,412 A 72,72 0 0 1 212,412" fill="none" stroke={line} strokeWidth="1.2" strokeDasharray="5,4"/>
        {/* Top goal */}
        <rect x="130" y="4" width="60" height="14" fill="none" stroke={line} strokeWidth="2"/>
        {/* Bottom goal */}
        <rect x="130" y="402" width="60" height="14" fill="none" stroke={line} strokeWidth="2"/>
        {/* Penalty spots */}
        <circle cx="160" cy="56" r="2.5" fill={line}/>
        <circle cx="160" cy="364" r="2.5" fill={line}/>
        {/* Side tick marks at 23m */}
        {[0,1].map(side=>[112,308].map((y,i)=>(
          <line key={side+"-"+i} x1={side===0?8:312} y1={y-6} x2={side===0?8:312} y2={y+6} stroke={line} strokeWidth="2"/>
        )))}
      </svg>
    );
    if (sport === "Football") return (
      <svg style={{position:"absolute",inset:0,width:"100%",height:"100%",pointerEvents:"none"}} viewBox="0 0 320 420" preserveAspectRatio="none">
        {[0,1,2,3,4,5,6,7].map(i=><rect key={i} x={0} y={i*53} width={320} height={53} fill={i%2===0?"rgba(0,0,0,0.07)":"rgba(0,0,0,0)"}/>)}
        <rect x="8" y="8" width="304" height="404" fill="none" stroke={line} strokeWidth="2.5"/>
        <line x1="8" y1="210" x2="312" y2="210" stroke={line} strokeWidth="1.5"/>
        <circle cx="160" cy="210" r="40" fill="none" stroke={line} strokeWidth="1.5"/>
        <circle cx="160" cy="210" r="3" fill={line}/>
        {/* Top box */}
        <rect x="88" y="8" width="144" height="68" fill="none" stroke={line} strokeWidth="1.5"/>
        <rect x="120" y="8" width="80" height="26" fill="none" stroke={line} strokeWidth="1.5"/>
        <circle cx="160" cy="60" r="2.5" fill={line}/>
        {/* Bottom box */}
        <rect x="88" y="344" width="144" height="68" fill="none" stroke={line} strokeWidth="1.5"/>
        <rect x="120" y="386" width="80" height="26" fill="none" stroke={line} strokeWidth="1.5"/>
        <circle cx="160" cy="360" r="2.5" fill={line}/>
      </svg>
    );
    if (sport === "Rugby") return (
      <svg style={{position:"absolute",inset:0,width:"100%",height:"100%",pointerEvents:"none"}} viewBox="0 0 320 420" preserveAspectRatio="none">
        {[0,1,2,3,4,5,6,7].map(i=><rect key={i} x={0} y={i*53} width={320} height={53} fill={i%2===0?"rgba(0,0,0,0.07)":"rgba(0,0,0,0)"}/>)}
        <rect x="8" y="8" width="304" height="404" fill="none" stroke={line} strokeWidth="2.5"/>
        {/* 10m, 22m, halfway lines */}
        {[70,115,210,305,350].map((y,i)=><line key={i} x1="8" y1={y} x2="312" y2={y} stroke={line} strokeWidth={y===210?"2":"1"} strokeDasharray={y===210?"none":"6,4"}/>)}
        {/* Try lines at top and bottom */}
        <line x1="8" y1="28" x2="312" y2="28" stroke={line} strokeWidth="2"/>
        <line x1="8" y1="392" x2="312" y2="392" stroke={line} strokeWidth="2"/>
        {/* Posts */}
        <line x1="145" y1="8" x2="145" y2="35" stroke={line} strokeWidth="2"/>
        <line x1="175" y1="8" x2="175" y2="35" stroke={line} strokeWidth="2"/>
        <line x1="140" y1="22" x2="180" y2="22" stroke={line} strokeWidth="2"/>
        <line x1="145" y1="385" x2="145" y2="412" stroke={line} strokeWidth="2"/>
        <line x1="175" y1="385" x2="175" y2="412" stroke={line} strokeWidth="2"/>
        <line x1="140" y1="398" x2="180" y2="398" stroke={line} strokeWidth="2"/>
      </svg>
    );
    if (sport === "Tennis") return (
      <svg style={{position:"absolute",inset:0,width:"100%",height:"100%",pointerEvents:"none"}} viewBox="0 0 320 420" preserveAspectRatio="none">
        <rect x="30" y="20" width="260" height="380" fill="none" stroke={line} strokeWidth="2.5"/>
        <line x1="30" y1="210" x2="290" y2="210" stroke={line} strokeWidth="2"/>
        <line x1="160" y1="20" x2="160" y2="420" stroke={line} strokeWidth="1.5"/>
        {/* Service boxes */}
        <line x1="30" y1="100" x2="290" y2="100" stroke={line} strokeWidth="1.5"/>
        <line x1="30" y1="320" x2="290" y2="320" stroke={line} strokeWidth="1.5"/>
        {/* Doubles alleys */}
        <line x1="60" y1="20" x2="60" y2="400" stroke={line} strokeWidth="1"/>
        <line x1="260" y1="20" x2="260" y2="400" stroke={line} strokeWidth="1"/>
      </svg>
    );
    // Default — generic pitch
    return (
      <svg style={{position:"absolute",inset:0,width:"100%",height:"100%",pointerEvents:"none"}} viewBox="0 0 320 420" preserveAspectRatio="none">
        {[0,1,2,3,4,5,6,7].map(i=><rect key={i} x={0} y={i*53} width={320} height={53} fill={i%2===0?"rgba(0,0,0,0.07)":"rgba(0,0,0,0)"}/>)}
        <rect x="8" y="8" width="304" height="404" fill="none" stroke={line} strokeWidth="2.5"/>
        <line x1="8" y1="210" x2="312" y2="210" stroke={line} strokeWidth="1.5"/>
        <circle cx="160" cy="210" r="40" fill="none" stroke={line} strokeWidth="1.5"/>
        <circle cx="160" cy="210" r="3" fill={line}/>
      </svg>
    );
  };

  const TeamSheetView = ({ team, fixtureId=null }) => {
    const teamPlayers = players.filter(p=>p.team===team);
    // Swimming: show athlete list, not a pitch
    if (club.sport === "Swimming") return (
      <div style={{marginTop:10}}>
        {teamPlayers.length===0
          ? <div style={{fontSize:13,color:"rgba(255,255,255,0.35)",fontStyle:"italic",padding:"12px 0"}}>No athletes in this squad yet</div>
          : teamPlayers.map(p=>{
              const ov=p.scores&&Object.keys(p.scores).length?Math.round(["spd","sta","att","pos","str","def","sho","wkr"].map(k=>to99(calcCardStat(p.scores,CARD_COMPOSITE?.Swimming?.[k]||[Object.values(CARD_MAP_BY_SPORT.Swimming)[["spd","sta","att","pos","str","def","sho","wkr"].indexOf(k)]]))).reduce((a,b)=>a+b,0)/8):null;
              return (
                <div key={p.id} onClick={()=>{setCurrentPlayer({...p});navigate("detail");}}
                  style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:"1px solid rgba(255,255,255,0.05)",cursor:"pointer"}}>
                  <Avatar name={p.name} photo={p.photo} size={36} colour={avCol(p.name)}/>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:600,color:"#fff"}}>{p.name}</div>
                    <div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>{p.position||"—"}{p.events?.length?` · ${p.events.slice(0,2).join(", ")}`:""}</div>
                  </div>
                  {ov&&<div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:20,fontWeight:800,color:ov>=84?"#e8e8e8":ov>=75?"#d4af37":ov>=65?"#9aa3b0":"#cd7f32"}}>{ov}</div>}
                </div>
              );
            })}
      </div>
    );
    const nextFix = fixtures
      .filter(f=>f.team===team&&new Date(f.date)>=new Date())
      .sort((a,b)=>new Date(a.date)-new Date(b.date))[0];
    const publishedLineup = fixtureId
      ? lineups[fixtureId]
      : nextFix ? lineups[nextFix.id] : null;

    const FORMATIONS = FORMATIONS_BY_SPORT[club.sport] || FORMATIONS_BY_SPORT.default;
    const formationName = publishedLineup?.formation || FORMATIONS[0]?.name || "4-3-3";
    const formationDef = FORMATIONS.find(f=>f.name===formationName) || FORMATIONS[0];
    const rows = formationDef?.rows || [];

    // Use slotMap if available (preserves exact positions), otherwise fall back to ordered starters
    const slotMapData = publishedLineup?.slotMap || null;
    let starters;
    if (slotMapData) {
      // Reconstruct from slotMap — exact positions preserved
      const maxSlot = Math.max(...Object.keys(slotMapData).map(Number));
      starters = Array.from({length: maxSlot + 1}, (_, i) => slotMapData[i] || null);
    } else if (publishedLineup?.starters?.length) {
      starters = publishedLineup.starters;
    } else {
      starters = teamPlayers.slice(0, 11);
    }
    const subs = publishedLineup?.subs?.filter(Boolean) || teamPlayers.slice(11, 16);

    // Build rows from formation — assign players to exact slots
    // Build in FORWARD order (matching how slotMap flatIdx is assigned in LineupPanel)
    // Then reverse for display so GK appears at bottom of pitch
    let idx = 0;
    const playerRowsForward = rows.map(row => {
      const rowPlayers = Array.from({length: row.count}, (_, i) => slotMapData ? (slotMapData[idx + i] || null) : (starters[idx + i] || null));
      idx += row.count;
      return { pos: row.pos, players: rowPlayers };
    });
    const playerRows = [...playerRowsForward].reverse();

    const pitchBg = club.sport === "Tennis"
      ? "linear-gradient(180deg,#1565c0 0%,#1976d2 50%,#1565c0 100%)"
      : "linear-gradient(180deg,#1a6b1a 0%,#1e7a1e 25%,#1a6b1a 50%,#1e7a1e 75%,#1a6b1a 100%)";

    const PitchCard = ({ player }) => (
      <div onClick={()=>{if(player){setCurrentPlayer({...player});navigate("detail");}}}
        style={{
          display:"flex",flexDirection:"column",alignItems:"center",gap:2,
          cursor:player?"pointer":"default",
        }}>
        <div style={{
          width:46,height:46,borderRadius:"50%",
          background:player?`linear-gradient(135deg,${secondaryCol},${primaryCol})`:"rgba(0,0,0,0.35)",
          border:`2px solid ${player?primaryCol:"rgba(255,255,255,0.25)"}`,
          overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center",
          boxShadow:player?`0 0 10px ${primaryCol}88`:"none",
        }}>
          {player?.photo
            ? <img src={player.photo} alt="" style={{width:"100%",height:"100%",objectFit:"cover",objectPosition:"top"}}/>
            : <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:player?13:18,fontWeight:900,color:player?"#fff":"rgba(255,255,255,0.25)"}}>
                {player ? inits(player.name) : "+"}
              </div>}
        </div>
        <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:8,fontWeight:700,color:"#fff",textShadow:"0 1px 4px rgba(0,0,0,0.9)",maxWidth:50,textAlign:"center",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",lineHeight:1.2}}>
          {player ? player.name.split(" ").pop().toUpperCase() : ""}
        </div>
      </div>
    );

    if (!publishedLineup && teamPlayers.length === 0) return (
      <div style={{textAlign:"center",padding:"20px 0",color:"rgba(255,255,255,0.35)",fontSize:13}}>
        No players added yet
      </div>
    );

    return (
      <div style={{width:"100%"}}>
        <div style={{
          position:"relative", width:"100%", borderRadius:10, overflow:"hidden",
          background:pitchBg,
          boxShadow:"inset 0 0 0 2px rgba(255,255,255,0.15), 0 4px 20px rgba(0,0,0,0.5)",
          padding:"10px 6px 8px",
        }}>
          <SportPitch sport={club.sport}/>
          <div style={{position:"relative",zIndex:2,display:"flex",flexDirection:"column",justifyContent:"space-evenly",height:"100%",minHeight:Math.max(380, playerRows.length * 85)}}>
            {playerRows.map((row,ri)=>(
              <div key={ri} style={{display:"flex",justifyContent:"space-evenly",alignItems:"center"}}>
                {row.players.map((p,pi)=><PitchCard key={pi} player={p}/>)}
              </div>
            ))}
          </div>
          {!publishedLineup && isCoach && (
            <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:10,borderRadius:10}}>
              <div style={{textAlign:"center",padding:20}}>
                <div style={{fontSize:13,color:"rgba(255,255,255,0.7)",marginBottom:10}}>No lineup published yet</div>
                {nextFix&&<button onClick={()=>navigate(`lineup-${nextFix.id}`)}
                  style={{...G.btnPrimary,background:primaryCol,fontSize:13,padding:"10px 20px"}}>
                  🏃 Build Lineup
                </button>}
              </div>
            </div>
          )}
        </div>
        {publishedLineup&&<div style={{fontSize:10,color:"rgba(255,255,255,0.35)",marginTop:6,textAlign:"center"}}>{formationName} formation</div>}
        <div style={{marginTop:10,paddingTop:10,borderTop:"1px solid rgba(255,255,255,0.07)"}}>
          <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:2,color:"rgba(255,255,255,0.38)",marginBottom:8}}>Substitutes</div>
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            {Array.from({length:5},(_,i)=>{
              const p = subs[i]||null;
              return (
                <div key={i} onClick={()=>{if(p){setCurrentPlayer({...p});navigate("detail");}}}
                  style={{display:"flex",flexDirection:"column",alignItems:"center",gap:3,cursor:p?"pointer":"default"}}>
                  <div style={{width:44,height:44,borderRadius:"50%",background:p?`linear-gradient(135deg,${secondaryCol},${primaryCol})`:"rgba(255,255,255,0.06)",border:`2px solid ${p?primaryCol:"rgba(255,255,255,0.15)"}`,overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:p?`0 0 8px ${primaryCol}66`:"none"}}>
                    {p?.photo
                      ? <img src={p.photo} alt="" style={{width:"100%",height:"100%",objectFit:"cover",objectPosition:"top"}}/>
                      : <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:p?12:16,fontWeight:900,color:p?"#fff":"rgba(255,255,255,0.15)"}}>{p?inits(p.name):"+"}</div>}
                  </div>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:8,fontWeight:700,color:p?"rgba(255,255,255,0.5)":"rgba(255,255,255,0.2)",maxWidth:44,textAlign:"center",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                    {p?p.name.split(" ").pop().toUpperCase():"SUB"}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };


  const TeamProfilePanel = () => {
    const teamName = panel.startsWith("team-") ? panel.replace("team-","").replace(/-/g," ") : "";
    const [editingName, setEditingName] = useState(false);
    const [newName, setNewName] = useState(teamName);
    const teamPlayers = players.filter(p=>p.team===teamName||p.team==="all");
    const teamFixtures = fixtures.filter(f=>f.team===teamName||f.team==="all").sort((a,b)=>new Date(b.date)-new Date(a.date));
    const teamResults  = teamFixtures.filter(f=>f.result);
    const upcoming     = teamFixtures.filter(f=>new Date(f.date)>=new Date()).sort((a,b)=>new Date(a.date)-new Date(b.date));

    // Find current formation from most recent lineup
    const teamLineups  = Object.values(lineups||{}).filter(l=>l.team===teamName||teamFixtures.find(f=>f.id===l.fixtureId));
    const latestLineup = teamLineups.sort((a,b)=>(b.updatedAt||0)-(a.updatedAt||0))[0];

    const renameTeam = async() => {
      const trimmed = newName.trim();
      if(!trimmed || trimmed===teamName) { setEditingName(false); return; }
      // Update customTeams
      const existing = club.customTeams||[];
      let updated;
      if(existing.includes(teamName)) {
        updated = existing.map(t=>t===teamName?trimmed:t);
      } else {
        // Replace in auto-generated by adding custom override
        updated = [...existing.filter(t=>t!==teamName), trimmed];
      }
      setClub(c=>({...c,customTeams:updated}));
      // Update all fixtures, players with old team name
      setFixtures(fs=>fs.map(f=>f.team===teamName?{...f,team:trimmed}:f));
      setPlayers(ps=>ps.map(p=>p.team===teamName?{...p,team:trimmed}:p));
      if(clubId) {
        await supabase.from("clubs").update({custom_teams:updated}).eq("id",clubId);
        await supabase.from("fixtures").update({team:trimmed}).eq("club_id",clubId).eq("team",teamName);
        await supabase.from("players").update({team:trimmed}).eq("club_id",clubId).eq("team",teamName);
      }
      setEditingName(false);
      navigate(`team-${trimmed.replace(/ /g,"-")}`);
      showToast(`Team renamed to ${trimmed} ✓`);
    };

    const wins   = teamResults.filter(f=>f.result==="W").length;
    const draws  = teamResults.filter(f=>f.result==="D").length;
    const losses = teamResults.filter(f=>f.result==="L").length;

    return (
      <div style={{...G.panel,paddingBottom:100}}>
        {/* Back */}
        <button onClick={()=>navigate("home")} style={{background:"none",border:"none",color:"rgba(255,255,255,0.4)",fontSize:22,cursor:"pointer",marginBottom:8,padding:0}}>←</button>

        {/* Team name header */}
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
          {editingName ? (
            <div style={{display:"flex",gap:8,flex:1,alignItems:"center"}}>
              <input value={newName} onChange={e=>setNewName(e.target.value)}
                style={{...G.input,marginBottom:0,flex:1,fontSize:18,fontWeight:800}}
                onKeyDown={e=>e.key==="Enter"&&renameTeam()}
                autoFocus/>
              <button onClick={renameTeam} style={{padding:"8px 14px",background:primaryCol,border:"none",borderRadius:9,color:contrastText(primaryCol),fontWeight:700,cursor:"pointer"}}>Save</button>
              <button onClick={()=>{setEditingName(false);setNewName(teamName);}} style={{padding:"8px 12px",background:"rgba(255,255,255,0.05)",border:"none",borderRadius:9,color:"rgba(255,255,255,0.4)",cursor:"pointer"}}>Cancel</button>
            </div>
          ) : (
            <>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:26,fontWeight:900,color:"#fff",flex:1,lineHeight:1}}>{teamName}</div>
              {isAdmin&&<button onClick={()=>setEditingName(true)}
                style={{padding:"6px 12px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:8,color:"rgba(255,255,255,0.5)",fontSize:11,cursor:"pointer"}}>
                ✏️ Rename
              </button>}
            </>
          )}
        </div>

        {/* Season stats */}
        {teamResults.length>0&&(
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:8,marginBottom:16}}>
            {[[teamResults.length,"Played","#fff"],[wins,"Won","#22c55e"],[draws,"Drawn","#f0b429"],[losses,"Lost","#E63946"]].map(([val,label,col])=>(
              <div key={label} style={{...G.card,textAlign:"center",padding:"10px 6px"}}>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,fontWeight:900,color:col}}>{val}</div>
                <div style={{fontSize:10,color:"rgba(255,255,255,0.4)",fontWeight:600}}>{label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Current formation */}
        {latestLineup?.formation&&(
          <div style={{...G.card,marginBottom:12,display:"flex",alignItems:"center",gap:10}}>
            <div style={{fontSize:20}}>📐</div>
            <div>
              <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",letterSpacing:1,fontWeight:600}}>CURRENT FORMATION</div>
              <div style={{fontSize:16,fontWeight:800,color:"#fff",fontFamily:"'Barlow Condensed',sans-serif"}}>{latestLineup.formation}</div>
            </div>
          </div>
        )}

        {/* Next fixture */}
        {upcoming[0]&&(
          <div style={{...G.card,marginBottom:12,borderLeft:`3px solid ${primaryCol}`}}>
            <div style={{fontSize:10,fontWeight:700,color:primaryCol,letterSpacing:1,marginBottom:4}}>📅 NEXT FIXTURE</div>
            <div style={{fontSize:15,fontWeight:700,color:"#fff",marginBottom:2}}>{club.name} vs {upcoming[0].opponent}</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.4)"}}>{fmtDate(upcoming[0].date)}{upcoming[0].time?` · ${upcoming[0].time}`:""}</div>
          </div>
        )}

        {/* Squad */}
        <div style={{fontSize:12,fontWeight:700,color:"rgba(255,255,255,0.4)",letterSpacing:1,marginBottom:8}}>SQUAD ({teamPlayers.length})</div>
        <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:16}}>
          {teamPlayers.length===0
            ? <div style={{fontSize:13,color:"rgba(255,255,255,0.3)",textAlign:"center",padding:"16px 0"}}>No players assigned to this team yet</div>
            : teamPlayers.map(p=>(
              <div key={p.id} onClick={()=>{setCurrentPlayer(p);navigate("detail");}}
                style={{...G.card,display:"flex",alignItems:"center",gap:10,cursor:"pointer",padding:"10px 14px"}}>
                <div style={{width:36,height:36,borderRadius:"50%",overflow:"hidden",background:`linear-gradient(135deg,${primaryCol},${secondaryCol})`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  {p.photo?<img src={p.photo} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>:<span style={{fontSize:12,fontWeight:700,color:"#fff"}}>{inits(p.name)}</span>}
                </div>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:600,color:"#fff"}}>{p.name}</div>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>{p.position||"No position"}</div>
                </div>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.3)"}}>›</div>
              </div>
            ))
          }
        </div>

        {/* Recent results */}
        {teamResults.length>0&&(
          <>
            <div style={{fontSize:12,fontWeight:700,color:"rgba(255,255,255,0.4)",letterSpacing:1,marginBottom:8}}>RECENT RESULTS</div>
            {teamResults.slice(0,5).map(f=>{
              const sessionCol = "#0ea5e9"; // blue for training sessions
              const col = f.result==="W"?"#22c55e":f.result==="D"?"#f0b429":f.result==="cancelled"?"#888":f.result==="postponed"?"#f0b429":"#E63946";
              return (
                <div key={f.id} style={{...G.card,display:"flex",alignItems:"center",gap:10,padding:"10px 14px",marginBottom:6,borderLeft:`3px solid ${col}`}}>
                  <div style={{width:26,height:26,borderRadius:6,background:`${col}22`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    <span style={{fontSize:12,fontWeight:800,color:col}}>{f.result==="cancelled"?"CANC":f.result==="postponed"?"PPD":f.result}</span>
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:600,color:"#fff"}}>vs {f.opponent}</div>
                    <div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>{fmtDate(f.date)}</div>
                  </div>
                  {f.goals_for!==null&&f.goals_for!==undefined&&(
                    <div style={{fontSize:15,fontWeight:900,color:"#fff",fontFamily:"'Barlow Condensed',sans-serif"}}>{f.goals_for}–{f.goals_against}</div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
    );
  };

  const HomePanel = () => {
    const hr = new Date().getHours();
    const greeting = hr<12?"Good morning":hr<18?"Good afternoon":"Good evening";
    const today = new Date();
    today.setHours(0,0,0,0);
    const upcoming = visibleFixtures
      .filter(f=>new Date(f.date)>=today && f.result!=="cancelled" && f.result!=="postponed")
      .sort((a,b)=>new Date(a.date)-new Date(b.date));
    const nextFix = upcoming[0];
    const pinnedPosts = posts.filter(p=>p.pinned);
    const [expandedTeams, setExpandedTeams] = useState({});
    const [teamTab, setTeamTab] = useState({});
    const toggleTeam = (team) => setExpandedTeams(e=>({...e,[team]:!e[team]}));
    const totalOutstanding = payments.filter(p=>p.status!=="paid").reduce((s,p)=>s+p.amount,0);

    const adminCards = [
      { icon:"👥", val:users.filter(u=>u.approved).length, title:"Members", sub:"registered", col:"#3b82f6", nav:"players" },
      { icon:"⏳", val:pendingUsers.length, title:"Pending", sub:"approvals", col:pendingUsers.length>0?"#f0b429":"#22c55e", nav:"pending" },
      { icon:"📅", val:upcoming.length, title:FIXTURES_TERM, sub:"upcoming", col:primaryCol, nav:"fixtures" },
      { icon:"💸", val:`£${totalOutstanding.toFixed(0)}`, title:"Outstanding", sub:"fees due", col:totalOutstanding>0?"#E63946":"#22c55e", nav:"payments" },
      { icon:"✉️", val:(()=>{const myId2=currentUser?.id||"";return Math.max(unreadCount,conversations.filter(c=>c.last_message&&c.last_sender&&c.last_sender!==myId2&&!c._readByMe).length);})()||0, title:"Messages", sub:"unread", col:(()=>{const myId2=currentUser?.id||"";return Math.max(unreadCount,conversations.filter(c=>c.last_message&&c.last_sender&&c.last_sender!==myId2&&!c._readByMe).length)>0?"#E63946":"#22c55e";})(), nav:"messages" },
    ];

    // Player-specific data
    const myPlayer = players.find(p=>p.userId===currentUser?.id||p.auth_id===currentUser?.id||p.name===currentUser?.name);
    const myProgramme = myPlayer ? athletePrograms[myPlayer.id] : null;
    const myPayments = payments.filter(p=>p.playerId===myPlayer?.id||p.playerId===currentUser?.id);
    const myOutstanding = myPayments.filter(p=>p.status!=="paid");
    const recentPost = posts.filter(p=>p.team==="all"||p.team===myTeams).sort((a,b)=>b.time-a.time)[0];

    if(isPlayer && !isCoach) return (
      <div style={{...G.panel,paddingBottom:100}}>
        {/* Player greeting */}
        <div style={{marginBottom:16}}>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:26,fontWeight:900,color:"#fff"}}>{greeting}{currentUser?.name?`, ${currentUser.name.split(" ")[0]}`:""} 👋</div>
          <div style={{fontSize:12,color:"rgba(255,255,255,0.38)"}}>{new Date().toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}</div>
        </div>

        {/* Latest club post — full width */}
        {recentPost&&(
          <div onClick={()=>navigate("clubhub")} style={{...G.card,marginBottom:14,cursor:"pointer",borderLeft:`3px solid ${primaryCol}`,background:`${primaryCol}08`}}>
            <div style={{fontSize:10,fontWeight:700,color:primaryCol,letterSpacing:1.5,marginBottom:6}}>LATEST FROM THE CLUB</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.8)",lineHeight:1.5,overflow:"hidden",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical"}}>{recentPost.body}</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",marginTop:6}}>{recentPost.author} · {new Date(recentPost.time).toLocaleDateString("en-GB",{day:"numeric",month:"short"})}</div>
          </div>
        )}

        {/* Quick access grid */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
          {/* Next fixture */}
          <div onClick={()=>navigate("fixtures")} style={{...G.card,cursor:"pointer",background:upcoming[0]?`${primaryCol}08`:"rgba(255,255,255,0.04)"}}>
            <div style={{fontSize:10,fontWeight:700,color:primaryCol,letterSpacing:1,marginBottom:8}}>NEXT FIXTURE</div>
            {upcoming.filter(f=>!f.isSession&&!f.is_session)[0] ? (()=>{
              const nf = upcoming.filter(f=>!f.isSession&&!f.is_session)[0];
              return (<>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:16,fontWeight:800,color:"#fff",marginBottom:2}}>vs {nf.opponent}</div>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.45)"}}>{fmtDate(nf.date)} · {nf.time||"TBC"}</div>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.35)",marginTop:2}}>{nf.isHome?"🏠 Home":"✈️ Away"}</div>
              </>);
            })() : <div style={{fontSize:12,color:"rgba(255,255,255,0.3)"}}>No upcoming fixtures</div>}
          </div>

          {/* Next training */}
          <div onClick={()=>navigate("fixtures")} style={{...G.card,cursor:"pointer",background:"rgba(14,165,233,0.06)"}}>
            <div style={{fontSize:10,fontWeight:700,color:"#0ea5e9",letterSpacing:1,marginBottom:8}}>NEXT TRAINING</div>
            {upcoming.filter(f=>f.isSession||f.is_session)[0] ? (()=>{
              const nt = upcoming.filter(f=>f.isSession||f.is_session)[0];
              return (<>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:15,fontWeight:800,color:"#fff",marginBottom:2}}>Training Session</div>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.45)"}}>{fmtDate(nt.date)} · {nt.time||"TBC"}</div>
              </>);
            })() : <div style={{fontSize:12,color:"rgba(255,255,255,0.3)"}}>No sessions scheduled</div>}
          </div>

          {/* My profile */}
          <div onClick={()=>{if(myPlayer){setCurrentPlayer({...myPlayer});navigate("detail");}else navigate("players");}} style={{...G.card,cursor:"pointer"}}>
            <div style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.4)",letterSpacing:1,marginBottom:8}}>MY PROFILE</div>
            {myPlayer ? (<>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <Avatar name={myPlayer.name} photo={myPlayer.photo} size={32} colour={avCol(myPlayer.name)}/>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:"#fff"}}>{myPlayer.position||"Player"}</div>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>{myPlayer.team||"—"}</div>
                </div>
              </div>
            </>) : <div style={{fontSize:12,color:"rgba(255,255,255,0.3)"}}>Tap to view profile</div>}
          </div>

          {/* Payments */}
          <div onClick={()=>navigate("payments")} style={{...G.card,cursor:"pointer",background:myOutstanding.length>0?"rgba(230,57,70,0.06)":"rgba(255,255,255,0.04)"}}>
            <div style={{fontSize:10,fontWeight:700,color:myOutstanding.length>0?"#E63946":"rgba(255,255,255,0.4)",letterSpacing:1,marginBottom:8}}>PAYMENTS</div>
            {myOutstanding.length>0 ? (<>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:900,color:"#E63946"}}>£{myOutstanding.reduce((s,p)=>s+p.amount,0).toFixed(0)}</div>
              <div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>{myOutstanding.length} outstanding</div>
            </>) : <div style={{fontSize:12,color:"#22c55e",fontWeight:600}}>✓ All paid up</div>}
          </div>
        </div>

        {/* Messages box */}
        <div onClick={()=>navigate("messages")} style={{...G.card,marginBottom:14,cursor:"pointer",borderLeft:`3px solid ${unreadCount>0?"#E63946":"rgba(255,255,255,0.1)"}`,background:unreadCount>0?"rgba(230,57,70,0.06)":"rgba(255,255,255,0.04)"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div style={{fontSize:10,fontWeight:700,color:unreadCount>0?"#E63946":"rgba(255,255,255,0.3)",letterSpacing:1}}>MESSAGES</div>
            {unreadCount>0&&<span style={{background:"#E63946",color:"#fff",borderRadius:10,padding:"2px 8px",fontSize:11,fontWeight:800}}>{unreadCount} new</span>}
          </div>
          {(()=>{
            // Derive unread from conversations state directly as source of truth
            const myId2 = currentUser?.id||"";
            const derived = conversations.filter(c=>
              c.last_message &&
              c.last_sender &&
              c.last_sender!==myId2 &&
              !c._readByMe
            ).length;
            const count = Math.max(unreadCount, derived);
            return count>0
              ? <div style={{fontSize:13,fontWeight:600,color:"#fff",marginTop:6}}>You have {count} unread message{count!==1?"s":""}</div>
              : <div style={{fontSize:12,color:"rgba(255,255,255,0.35)",marginTop:6}}>No new messages</div>;
          })()}
          <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",marginTop:4}}>Tap to open messages →</div>
        </div>

        {/* My training programme */}
        <div onClick={()=>{if(myPlayer){setCurrentPlayer({...myPlayer});navigate("detail");}}} style={{...G.card,marginBottom:14,cursor:"pointer",borderLeft:`3px solid ${myProgramme?"#22c55e":"rgba(255,255,255,0.1)"}`}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div style={{fontSize:10,fontWeight:700,color:myProgramme?"#22c55e":"rgba(255,255,255,0.3)",letterSpacing:1}}>MY TRAINING PROGRAMME</div>
            <span style={{fontSize:11,color:primaryCol}}>View →</span>
          </div>
          {myProgramme ? (<>
            <div style={{fontSize:13,fontWeight:600,color:"#fff",marginTop:6}}>{myProgramme.overview?.slice(0,80)}...</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.35)",marginTop:4}}>{myProgramme.weeks?.length||6} weeks · {myProgramme.createdAt?`Created ${new Date(myProgramme.createdAt).toLocaleDateString("en-GB",{day:"numeric",month:"short"})}`:""}</div>
          </>) : <div style={{fontSize:12,color:"rgba(255,255,255,0.35)",marginTop:6}}>No programme yet — your coach will create one for you</div>}
        </div>
      </div>
    );

    return (
      <div style={G.panel}>
        <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:24, fontWeight:800, color:"#fff", marginBottom:2 }}>{greeting}</div>
        <div style={{ fontSize:12, color:"rgba(255,255,255,0.38)", marginBottom:16 }}>{new Date().toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}</div>



        {/* ── Setup cards — show for admin until each task is done ─────── */}
        {isAdmin&&(()=>{
          const dismissed = JSON.parse(localStorage.getItem("p4m_setup_dismissed")||"[]");
          const dismiss = (key) => {
            const d=[...dismissed,key];
            localStorage.setItem("p4m_setup_dismissed",JSON.stringify(d));
            // force re-render
            setPanel(p=>p);
          };
          const setupCards = [
            !club.stripeOnboarded && !dismissed.includes("stripe") && {
              key:"stripe", icon:"💳", title:"Set up payments",
              desc:"Connect Stripe to collect match fees and memberships online.",
              action:()=>navigate("payments"), btnLabel:"Set up Stripe", btnCol:"#635BFF"
            },
            !club.xeroConnected && !dismissed.includes("xero") && {
              key:"xero", icon:"🔗", title:"Connect Xero",
              desc:"Sync your club payments with Xero accounting automatically.",
              action:()=>navigate("payments"), btnLabel:"Connect Xero", btnCol:"#1AB4D7"
            },
            players.length===0 && !dismissed.includes("members") && {
              key:"members", icon:"👥", title:"Import your members",
              desc:"Upload a CSV of your existing members to get started quickly.",
              action:()=>navigate("club-settings"), btnLabel:"Import Members", btnCol:"#22c55e"
            },
            club.sport==="Hockey" && !club.gmsClubId && !dismissed.includes("gms") && {
              key:"gms", icon:"🏑", title:"Connect England Hockey GMS",
              desc:"Link your club to automatically sync results, fixtures and league tables.",
              action:()=>navigate("club-settings"), btnLabel:"Connect GMS", btnCol:primaryCol
            },
            club.sport==="Football" && !club.ftClubCode && !dismissed.includes("fulltime") && {
              key:"fulltime", icon:"⚽", title:"Connect FA Full-Time",
              desc:"Link your FA Full-Time code to sync results and league tables.",
              action:()=>navigate("club-settings"), btnLabel:"Connect Full-Time", btnCol:primaryCol
            },
          ].filter(Boolean);

          if(!setupCards.length) return null;
          return (
            <div style={{marginBottom:16}}>
              <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.3)",letterSpacing:1.5,marginBottom:8}}>GETTING STARTED</div>
              {setupCards.map(card=>(
                <div key={card.key} style={{background:"rgba(255,255,255,0.04)",border:`1px solid ${card.btnCol}33`,borderLeft:`3px solid ${card.btnCol}`,borderRadius:12,padding:"12px 14px",marginBottom:8,display:"flex",alignItems:"center",gap:12}}>
                  <div style={{fontSize:24,flexShrink:0}}>{card.icon}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:700,color:"#fff",marginBottom:2}}>{card.title}</div>
                    <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",lineHeight:1.4}}>{card.desc}</div>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:4,flexShrink:0}}>
                    <button onClick={card.action}
                      style={{padding:"7px 12px",background:card.btnCol,border:"none",borderRadius:8,color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
                      {card.btnLabel}
                    </button>
                    <button onClick={()=>dismiss(card.key)}
                      style={{padding:"4px 8px",background:"none",border:"none",color:"rgba(255,255,255,0.25)",fontSize:10,cursor:"pointer",textAlign:"center"}}>
                      Dismiss
                    </button>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

        {/* Next fixture */}
        {nextFix && (
          <div style={{ ...G.cardAccent(primaryCol), marginBottom:14 }}>
            <div style={{ fontSize:10, textTransform:"uppercase", letterSpacing:2, color:"rgba(255,255,255,0.38)", marginBottom:6, fontWeight:700 }}>📅 Next {FIXTURE_TERM}</div>
            <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:8}}>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:18, fontWeight:800, color:"#fff", marginBottom:4 }}>{isSwimming?nextFix.opponent:`${club.name||"CBN"} vs ${nextFix.opponent}`}</div>
              {isCoach&&(
                <div style={{position:"relative"}}>
                  <button onClick={e=>{e.stopPropagation();const menu=document.getElementById("fix-menu-"+nextFix.id);if(menu)menu.style.display=menu.style.display==="block"?"none":"block";}}
                    style={{background:"none",border:"none",color:"rgba(255,255,255,0.4)",fontSize:18,cursor:"pointer",padding:"0 4px",lineHeight:1}}>⋯</button>
                  <div id={"fix-menu-"+nextFix.id} style={{display:"none",position:"absolute",right:0,top:"100%",background:"#1a1a2e",border:"1px solid rgba(255,255,255,0.1)",borderRadius:10,padding:6,zIndex:50,minWidth:160,boxShadow:"0 8px 24px rgba(0,0,0,0.4)"}}>
                    {["cancelled","postponed"].map(status=>(
                      <button key={status} onClick={async e=>{
                        e.stopPropagation();
                        const m=document.getElementById("fix-menu-"+nextFix.id);if(m)m.style.display="none";
                        const updated={...nextFix,result:status};
                        setFixtures(fs=>fs.map(f=>f.id===nextFix.id?updated:f));
                        if(clubId) await supabase.from("fixtures").update({result:status}).eq("id",nextFix.id);
                        showToast(status==="cancelled"?"Fixture cancelled":"Fixture postponed");
                      }} style={{display:"block",width:"100%",textAlign:"left",padding:"8px 12px",background:"none",border:"none",color:"rgba(255,255,255,0.7)",fontSize:13,cursor:"pointer",borderRadius:6}}>
                        {status==="cancelled"?"❌ Cancel fixture":"⏸ Postpone fixture"}
                      </button>
                    ))}
                    {isAdmin&&<button onClick={async e=>{
                      e.stopPropagation();
                      if(!window.confirm("Delete this fixture?")) return;
                      setFixtures(fs=>fs.filter(f=>f.id!==nextFix.id));
                      if(clubId) await supabase.from("fixtures").delete().eq("id",nextFix.id);
                    }} style={{display:"block",width:"100%",textAlign:"left",padding:"8px 12px",background:"none",border:"none",color:"#E63946",fontSize:13,cursor:"pointer",borderRadius:6}}>
                      🗑 Delete fixture
                    </button>}
                  </div>
                </div>
              )}
            </div>
            <div style={{ fontSize:12, color:"rgba(255,255,255,0.45)", marginBottom:10 }}>{fmtDate(nextFix.date)}{nextFix.time?` · ${nextFix.time}`:""} · {nextFix.team}</div>
            <div style={{ display:"flex", gap:8 }}>
              {[{s:"yes",l:"Available",col:"#22c55e"},{s:"maybe",l:"Maybe",col:"#f0b429"},{s:"no",l:"Unavailable",col:"#E63946"}].map(a=>{
                const active = nextFix.availability?.me === a.s;
                return (
                  <button key={a.s} onClick={async()=>{setFixtures(fs=>fs.map(f=>f.id===nextFix.id?{...f,availability:{...f.availability,me:a.s}}:f));if(clubId)db.setAvailability(nextFix.id,currentUser?.id||"me",a.s).catch(console.error);}}
                    style={{ flex:1, padding:"8px 4px", borderRadius:9, border:`1.5px solid ${a.col}`, background:active?a.col:"transparent", cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:2, fontSize:11, fontWeight:700, color:active?"#0d0d14":a.col }}>
                    <span style={{fontSize:14}}>{a.s==="yes"?"✓":a.s==="maybe"?"?":"✗"}</span>{a.l}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Admin overview */}
        {isAdmin && (
          <div style={{marginBottom:16}}>
            <div style={G.secHdr}><div style={G.h3}>Club Overview</div></div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:12 }}>
              {adminCards.map((c,i)=>(
                <div key={i} onClick={()=>c.nav&&navigate(c.nav)}
                  style={{ background:"#14141f", border:"1px solid rgba(255,255,255,0.07)", borderRadius:12, padding:12, borderTop:`3px solid ${c.col}`, cursor:"pointer" }}>
                  <div style={{ fontSize:20, marginBottom:4 }}>{c.icon}</div>
                  <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:26, fontWeight:800, color:"#fff", lineHeight:1, marginBottom:2 }}>{c.val}</div>
                  <div style={{ fontSize:12, fontWeight:600, color:"rgba(255,255,255,0.8)" }}>{c.title}</div>
                  <div style={{ fontSize:10, color:"rgba(255,255,255,0.38)", marginTop:1 }}>{c.sub}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Senior / Junior group cards for admin */}
        {isAdmin&&(()=>{
          const seniorTeams = teams.filter(t=>!t.startsWith("Age ")&&!t.toLowerCase().includes("junior")&&!t.toLowerCase().includes("u1")&&!t.toLowerCase().includes("u8")&&!t.toLowerCase().includes("u10")&&!t.toLowerCase().includes("u12")&&!t.toLowerCase().includes("u14")&&!t.toLowerCase().includes("u16")&&!t.toLowerCase().includes("u18"));
          const juniorTeams = teams.filter(t=>t.startsWith("Age ")||t.toLowerCase().includes("junior")||t.toLowerCase().includes("u8")||t.toLowerCase().includes("u10")||t.toLowerCase().includes("u12")||t.toLowerCase().includes("u14")||t.toLowerCase().includes("u16")||t.toLowerCase().includes("u18")||t.toLowerCase().includes("u1"));
          return (
            <div data-tour="teams-section" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
              {seniorTeams.length>0&&(
                <div onClick={()=>{ if(seniorTeams.length===1){navigate(`team-${seniorTeams[0].replace(/ /g,"-")}`)}else{setExpandedTeams(e=>({...e,__senior:!e.__senior}))} }}
                  style={{background:"#14141f",border:`1px solid ${expandedTeams.__senior?primaryCol:"rgba(255,255,255,0.07)"}`,borderRadius:12,padding:12,borderTop:`3px solid ${primaryCol}`,cursor:"pointer"}}>
                  <div style={{fontSize:20,marginBottom:4}}>{club.sport==="Football"?"⚽":club.sport==="Swimming"?"🏊":"🏑"}</div>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:800,color:"#fff",lineHeight:1,marginBottom:2}}>{seniorTeams.length}</div>
                  <div style={{fontSize:12,fontWeight:600,color:"rgba(255,255,255,0.8)"}}>Senior Teams</div>
                  <div style={{fontSize:10,color:"rgba(255,255,255,0.38)",marginTop:1}}>{expandedTeams.__senior?"tap to hide":"tap to view"}</div>
                </div>
              )}
              {juniorTeams.length>0&&(
                <div onClick={()=>setExpandedTeams(e=>({...e,__junior:!e.__junior}))}
                  style={{background:"#14141f",border:`1px solid ${expandedTeams.__junior?"#22c55e":"rgba(255,255,255,0.07)"}`,borderRadius:12,padding:12,borderTop:"3px solid #22c55e",cursor:"pointer"}}>
                  <div style={{fontSize:20,marginBottom:4}}>⭐</div>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:800,color:"#fff",lineHeight:1,marginBottom:2}}>{juniorTeams.length}</div>
                  <div style={{fontSize:12,fontWeight:600,color:"rgba(255,255,255,0.8)"}}>Junior Teams</div>
                  <div style={{fontSize:10,color:"rgba(255,255,255,0.38)",marginTop:1}}>{expandedTeams.__junior?"tap to hide":"tap to view"}</div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Per-team collapsible sections — shown when group is expanded */}
        {(isAdmin ? teams : [myTeams]).filter(t=>{
          if(!isAdmin) return true;
          const isSenior = !t.startsWith("Age ")&&!t.toLowerCase().includes("junior")&&!t.toLowerCase().includes("u8")&&!t.toLowerCase().includes("u10")&&!t.toLowerCase().includes("u12")&&!t.toLowerCase().includes("u14")&&!t.toLowerCase().includes("u16")&&!t.toLowerCase().includes("u18")&&!t.toLowerCase().includes("u1");
          return isSenior ? expandedTeams.__senior : expandedTeams.__junior;
        }).filter(Boolean).map(team=>{
          const isMens = team.toLowerCase().includes("men");
          const isLadies = team.toLowerCase().includes("ladies")||team.toLowerCase().includes("women");
          const isAge = team.startsWith("Age ");
          const teamCol = isMens ? primaryCol : isLadies ? secondaryCol : isAge ? "#0ea5e9" : "#a855f7";
          const teamDisplayName = isSwimming && isAge ? team.replace("Age ","") + " Age Group" : team;
          const teamPlayers = players.filter(p=>p.team===team);
          const teamFix = fixtures.filter(f=>f.team===team&&new Date(f.date)>=new Date()).sort((a,b)=>new Date(a.date)-new Date(b.date));
          const nextTeamFix = teamFix[0];
          const yesCount = Object.values(nextTeamFix?.availability||{}).filter(v=>v==="yes").length;
          const expanded = expandedTeams[team];
          const tab = teamTab[team]||"teamsheet";
          const assessed = teamPlayers.filter(p=>p.scores&&Object.keys(p.scores).length>0);
          const outstanding = payments.filter(p=>p.status!=="paid"&&(p.team===team||p.team==="all")).reduce((s,p)=>s+p.amount,0);

          return (
            <div key={team} style={{ marginBottom:10 }}>
              {/* Team header — tap to expand */}
              <div onClick={()=>toggleTeam(team)}
                style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 14px", background:expanded?"rgba(255,255,255,0.07)":"rgba(255,255,255,0.04)", border:`1px solid ${expanded?teamCol:"rgba(255,255,255,0.08)"}`, borderRadius:expanded?"12px 12px 0 0":12, cursor:"pointer", transition:"all 0.2s" }}>
                <div style={{ width:4, height:20, borderRadius:2, background:teamCol, flexShrink:0 }}/>
                <div onClick={()=>navigate(`team-${team.replace(/ /g,"-")}`)} style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:17, fontWeight:800, color:"#fff", flex:1, cursor:"pointer" }}>{teamDisplayName||team} <span style={{fontSize:12,opacity:0.4}}>›</span></div>
                <div style={{ fontSize:11, color:"rgba(255,255,255,0.4)", marginRight:6 }}>{teamPlayers.length} {isSwimming?"athletes":"players"}</div>
                {outstanding>0&&<div style={{ fontSize:11, color:"#E63946", fontWeight:700 }}>£{outstanding.toFixed(0)} due</div>}
                <div style={{ color:"rgba(255,255,255,0.4)", fontSize:14, transform:expanded?"rotate(180deg)":"rotate(0deg)", transition:"transform 0.2s" }}>▼</div>
              </div>

              {/* Expanded content */}
              {expanded&&(
                <div style={{ background:"rgba(255,255,255,0.03)", border:`1px solid ${teamCol}`, borderTop:"none", borderRadius:"0 0 12px 12px", padding:14 }}>

                  {/* Sub-tabs */}
                  <div style={{ display:"flex", gap:6, marginBottom:14, overflowX:"auto", paddingBottom:4 }}>
                    {[{k:"teamsheet",l:isSwimming?"👥 Squad":"🏟 Team Sheet"},{k:"players",l:isSwimming?"🏊 Athletes":"👥 Players"},{k:"fixtures",l:isSwimming?"📅 Galas":"📅 Fixtures"},{k:"training",l:"🏆 Training"},{k:"finance",l:"💸 Finance"}].map(t=>(
                      <button key={t.k} onClick={()=>setTeamTab(tt=>({...tt,[team]:t.k}))}
                        style={{ padding:"6px 12px", background:tab===t.k?teamCol:"rgba(255,255,255,0.04)", border:`1px solid ${tab===t.k?teamCol:"rgba(255,255,255,0.08)"}`, borderRadius:16, color:tab===t.k?"#fff":"rgba(255,255,255,0.45)", fontSize:11, fontWeight:600, cursor:"pointer", whiteSpace:"nowrap", flexShrink:0 }}>
                        {t.l}
                      </button>
                    ))}
                  </div>

                  {/* TEAM SHEET TAB — pitch with players */}
                  {tab==="teamsheet"&&(
                    <div>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
                        <div style={{ fontSize:12, color:"rgba(255,255,255,0.45)" }}>Tap a player to view their profile</div>
                        {isCoach&&(
                          <button onClick={()=>{
                            if(nextTeamFix) navigate(`lineup-${nextTeamFix.id}`);
                            else { navigate("fixtures"); showToast(`Add a fixture for ${team} first`); }
                          }} style={{fontSize:11,color:primaryCol,background:"none",border:`1px solid ${primaryCol}55`,borderRadius:6,padding:"4px 10px",cursor:"pointer",fontWeight:600}}>
                            {nextTeamFix?(lineups[nextTeamFix.id]?"✏️ Edit Lineup":"🏃 Build Lineup"):"+ Add Fixture"}
                          </button>
                        )}
                      </div>
                      <TeamSheetView team={team}/>
                    </div>
                  )}

                  {/* PLAYERS TAB */}
                  {tab==="players"&&(
                    <div>
                      {teamPlayers.length===0
                        ? <div style={{ fontSize:13, color:"rgba(255,255,255,0.35)", fontStyle:"italic" }}>No {isSwimming?"athletes":"players"} in this {isSwimming?"squad":"team"} yet</div>
                        : teamPlayers.slice(0,8).map(p=>{
                          const sportComp=CARD_COMPOSITE?.[club.sport]; const ov=p.scores&&Object.keys(p.scores).length?Math.round(["spd","sta","att","pos","str","def","sho","wkr"].map(k=>to99(sportComp?.[k]?calcCardStat(p.scores,sportComp[k]):(p.scores[(CARD_MAP_BY_SPORT?.[club.sport]||CARD_MAP)[k]]||50))).reduce((a,b)=>a+b,0)/8):null;
                          return (
                            <div key={p.id} onClick={()=>{setCurrentPlayer({...p});navigate("detail");}}
                              style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:"1px solid rgba(255,255,255,0.05)", cursor:"pointer" }}>
                              <Avatar name={p.name} photo={p.photo} size={32} colour={avCol(p.name)}/>
                              <div style={{ flex:1 }}>
                                <div style={{ fontSize:13, fontWeight:600, color:"#fff" }}>{p.name}</div>
                                <div style={{ fontSize:11, color:"rgba(255,255,255,0.38)" }}>{p.position||"—"}</div>
                              </div>
                              {ov&&<div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:18, fontWeight:800, color:ov>=84?"#e8e8e8":ov>=75?"#d4af37":ov>=65?"#9aa3b0":"#cd7f32" }}>{ov}</div>}
                            </div>
                          );
                        })}
                      {teamPlayers.length>8&&<div style={{ fontSize:12, color:teamCol, marginTop:8, cursor:"pointer" }} onClick={()=>{setTeamFilter(team);navigate("players");}}>View all {teamPlayers.length} players →</div>}
                    </div>
                  )}

                  {/* FIXTURES TAB */}
                  {tab==="fixtures"&&(
                    <div>
                      {teamFix.length===0
                        ? <div style={{ fontSize:13, color:"rgba(255,255,255,0.35)", fontStyle:"italic" }}>No upcoming {isSwimming?"galas":"fixtures"}</div>
                        : teamFix.slice(0,4).map(f=>{
                          const yes=Object.values(f.availability||{}).filter(v=>v==="yes").length;
                          const maybe=Object.values(f.availability||{}).filter(v=>v==="maybe").length;
                          return (
                            <div key={f.id} style={{ padding:"10px 0", borderBottom:"1px solid rgba(255,255,255,0.05)" }}>
                              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:15, fontWeight:700, color:"#fff", marginBottom:2 }}>vs {f.opponent}</div>
                              <div style={{ fontSize:11, color:"rgba(255,255,255,0.4)", marginBottom:6 }}>{fmtDate(f.date)}{f.time?` · ${f.time}`:""} · {f.isHome?"Home":"Away"}</div>
                              <div style={{ display:"flex", gap:10, fontSize:11 }}>
                                <span style={{ color:"#22c55e", fontWeight:700 }}>✓ {yes}</span>
                                <span style={{ color:"#f0b429", fontWeight:700 }}>? {maybe}</span>
                                <span style={{ color:"#E63946", fontWeight:700 }}>✗ {Object.values(f.availability||{}).filter(v=>v==="no").length}</span>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}

                  {/* TRAINING TAB */}
                  {tab==="training"&&(
                    <div>
                      {framework
                        ? <>
                            <div style={{ fontSize:12, color:"rgba(255,255,255,0.45)", marginBottom:10 }}>Current block for {frameworkTeam||"team"}</div>
                            <div style={{ background:"rgba(255,255,255,0.04)", borderRadius:10, padding:12, marginBottom:10 }}>
                              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:15, fontWeight:800, color:"#fff", marginBottom:4 }}>{framework.blocks?.[selectedBlock]?.title||"Block 1"}</div>
                              <div style={{ fontSize:12, color:"rgba(255,255,255,0.45)", marginBottom:6 }}>{framework.blocks?.[selectedBlock]?.phase} · Weeks {framework.blocks?.[selectedBlock]?.weeks}</div>
                              <div style={{ fontSize:13, color:"rgba(255,255,255,0.7)", lineHeight:1.6 }}>{framework.blocks?.[selectedBlock]?.focus}</div>
                            </div>
                            <button style={{ ...G.btnGhost, fontSize:12 }} onClick={()=>navigate("coach")}>View full framework →</button>
                          </>
                        : <div>
                            <div style={{ fontSize:13, color:"rgba(255,255,255,0.35)", marginBottom:10 }}>No training framework set up yet</div>
                            <button style={{ ...G.btnPrimary, background:primaryCol, fontSize:13 }} onClick={()=>navigate("coach")}>Set up framework</button>
                          </div>}
                    </div>
                  )}

                  {/* FINANCE TAB */}
                  {tab==="finance"&&(
                    <div>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:10 }}>
                        <div style={{ background:"rgba(230,57,70,0.1)", border:"1px solid rgba(230,57,70,0.2)", borderRadius:10, padding:12 }}>
                          <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:1.5, color:"rgba(255,255,255,0.38)", marginBottom:4 }}>Outstanding</div>
                          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:24, fontWeight:800, color:"#E63946" }}>£{outstanding.toFixed(2)}</div>
                        </div>
                        <div style={{ background:"rgba(34,197,94,0.1)", border:"1px solid rgba(34,197,94,0.2)", borderRadius:10, padding:12 }}>
                          <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:1.5, color:"rgba(255,255,255,0.38)", marginBottom:4 }}>Collected</div>
                          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:24, fontWeight:800, color:"#22c55e" }}>£{payments.filter(p=>p.status==="paid"&&(p.team===team||p.team==="all")).reduce((s,p)=>s+p.amount,0).toFixed(2)}</div>
                        </div>
                      </div>
                      {payments.filter(p=>p.status!=="paid"&&(p.team===team||p.team==="all")).slice(0,3).map(p=>(
                        <div key={p.id} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:"1px solid rgba(255,255,255,0.05)", fontSize:12 }}>
                          <span style={{ color:"rgba(255,255,255,0.7)" }}>{p.title}</span>
                          <span style={{ color:"#E63946", fontWeight:700 }}>£{p.amount.toFixed(2)}</span>
                        </div>
                      ))}
                      <button style={{ ...G.btnGhost, width:"100%", marginTop:8, fontSize:12 }} onClick={()=>navigate("payments")}>View all payments →</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Pending approvals */}
        {/* Pinned posts */}
        {posts.filter(p=>p.pinned&&(p.team==="all"||p.team===myTeams)).slice(0,3).map(p=>(
          <div key={p.id} style={{...G.card,marginBottom:10,borderLeft:`3px solid ${primaryCol}`,padding:0,overflow:"hidden"}}>
            {p.photo&&(
              <img src={p.photo} alt="" style={{width:"100%",maxHeight:200,objectFit:"cover",display:"block"}}/>
            )}
            <div style={{padding:"10px 14px"}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                <div style={{fontSize:11,fontWeight:700,color:primaryCol}}>📌 PINNED</div>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.35)",flex:1}}>{p.author}</div>
                {isAdmin&&<button onClick={()=>setPosts(ps=>ps.map(x=>x.id===p.id?{...x,pinned:false}:x))} style={{background:"none",border:"none",color:"rgba(255,255,255,0.25)",fontSize:11,cursor:"pointer"}}>Unpin</button>}
              </div>
              {p.body&&<div style={{fontSize:13,color:"rgba(255,255,255,0.75)",lineHeight:1.5}}>{p.body}</div>}
            </div>
          </div>
        ))}

        {isAdmin && pendingUsers.length > 0 && (
          <div style={{ background:"rgba(240,180,41,0.08)", border:"1px solid rgba(240,180,41,0.2)", borderRadius:12, padding:14, marginTop:8 }}>
            <div style={{ fontSize:12, fontWeight:700, color:"#f0b429", marginBottom:10 }}>⏳ {pendingUsers.length} awaiting approval</div>
            {pendingUsers.map(u=>(
              <div key={u.id} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
                <Avatar name={u.name} size={30}/>
                <div style={{ flex:1 }}><div style={{ fontSize:13, fontWeight:600, color:"#fff" }}>{u.name}</div><div style={{ fontSize:11, color:"rgba(255,255,255,0.38)" }}>{u.role} · {u.team}</div></div>
                <button onClick={()=>{
                  setUsers(us=>us.map(usr=>usr.id===u.id?{...usr,approved:true}:usr));
                  supabase.from("members").update({approved:true}).eq("id",u.id).then(()=>{}).catch(console.error);
                  showToast(`${u.name} approved ✓`);
                }} style={{ padding:"6px 12px", background:primaryCol, border:"none", borderRadius:7, color:"#fff", fontSize:12, fontWeight:600, cursor:"pointer" }}>Approve</button>
                <button onClick={()=>{setUsers(us=>us.filter(usr=>usr.id!==u.id));showToast("Declined");}} style={{ padding:"6px 10px", background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:7, color:"rgba(255,255,255,0.5)", fontSize:12, cursor:"pointer" }}>✕</button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };


  // ═══════════════════════════════
  // FIXTURES
  // ═══════════════════════════════
  const FixturesPanel = () => {
    const [fixtureTeamFilter, setFixtureTeamFilter] = useState("all");
    const sorted = [...visibleFixtures].sort((a,b)=>new Date(a.date)-new Date(b.date));
    const teamFiltered = fixtureTeamFilter==="all" ? sorted : sorted.filter(f=>f.team===fixtureTeamFilter);
    const fixtureTeams = [...new Set(visibleFixtures.map(f=>f.team).filter(Boolean))].sort();
    return (
    <div style={{...G.panel,paddingBottom:100}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
        <div style={G.h3}>{FIXTURES_TERM}</div>
        <div style={{display:"flex",gap:6}}>

          {isCoach&&<button style={{...G.secAct}} onClick={()=>setModal("fixture")}>+ Add</button>}
        </div>
      </div>
      {fixtureTeams.length>1&&(
        <div style={{display:"flex",gap:6,marginBottom:14,overflowX:"auto",paddingBottom:4,flexWrap:"wrap"}}>
          {["all",...fixtureTeams].map(t=>(
            <button key={t} onClick={()=>setFixtureTeamFilter(t)} style={{padding:"6px 14px",background:fixtureTeamFilter===t?primaryCol:"rgba(255,255,255,0.05)",border:`1px solid ${fixtureTeamFilter===t?primaryCol:"rgba(255,255,255,0.1)"}`,borderRadius:20,color:fixtureTeamFilter===t?contrastText(primaryCol):"rgba(255,255,255,0.5)",fontSize:11,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>{t==="all"?"All":t}</button>
          ))}
        </div>
      )}
      {teamFiltered.length === 0 ? (
        <div style={{ ...G.center, padding:"48px 20px", gap:8 }}>
          <div style={{ fontSize:36, opacity:0.3 }}>📅</div>
          <div style={{ ...G.h3, color:"rgba(255,255,255,0.25)" }}>No fixtures yet</div>
          {isCoach && <div style={{ fontSize:13, color:"rgba(255,255,255,0.35)" }}>Add your first fixture above</div>}
        </div>
      ) : teamFiltered.map(f=>{
        const players2 = players.filter(p=>p.team===f.team);
        const avail = f.availability||{};
        const yes = Object.values(avail).filter(v=>v==="yes").length;
        const myA = avail.me;
        const past = new Date(f.date) < new Date();
        return (
          <div key={f.id} style={{ ...G.cardAccent(primaryCol), marginBottom:10 }}>
            {isCoach && <button onClick={()=>setFixtures(fs=>fs.filter(fx=>fx.id!==f.id))} style={{ position:"absolute", top:12, right:12, background:"none", border:"none", color:"rgba(255,255,255,0.2)", cursor:"pointer", fontSize:15 }}>✕</button>}
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:18, fontWeight:800, color:"#fff" }}>{club.name||"CBN"} vs {f.opponent}</div>
              {f.result && <span style={{ padding:"2px 8px", borderRadius:4, fontFamily:"'Barlow Condensed',sans-serif", fontSize:12, fontWeight:700, background:f.result==="W"?"rgba(34,197,94,0.2)":f.result==="D"?"rgba(240,180,41,0.2)":"rgba(230,57,70,0.2)", color:f.result==="W"?"#22c55e":f.result==="D"?"#f0b429":"#E63946" }}>{f.result}{f.goalsFor!=null?` ${f.goalsFor}–${f.goalsAgainst}`:""}</span>}
            </div>
            <div style={{ fontSize:12, color:"rgba(255,255,255,0.4)", marginBottom:past?8:12 }}>{fmtDate(f.date)}{f.time?` · ${f.time}`:""}{f.location?` · ${f.location}`:""} · {f.team}</div>
            {!past && (
              <>
                <div style={{display:"flex",gap:14,marginBottom:10}}>
                  <div style={{fontSize:12,color:"#22c55e",fontWeight:700}}>✓ {yes} available</div>
                  <div style={{fontSize:12,color:"#f0b429",fontWeight:700}}>? {Object.values(f.availability||{}).filter(v=>v==="maybe").length} maybe</div>
                  <div style={{fontSize:12,color:"#E63946",fontWeight:700}}>✗ {Object.values(f.availability||{}).filter(v=>v==="no").length} no</div>
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  {[{s:"yes",l:"Available",col:"#22c55e"},{s:"maybe",l:"Maybe",col:"#f0b429"},{s:"no",l:"Unavailable",col:"#E63946"}].map(a=>{
                    const active = myA===a.s;
                    return (
                      <button key={a.s} onClick={()=>{setFixtures(fs=>fs.map(fx=>fx.id===f.id?{...fx,availability:{...fx.availability,me:a.s}}:fx));showToast(a.s==="yes"?"✓ You're in!":a.s==="maybe"?"Marked as maybe":"Marked as unavailable");}}
                        style={{ flex:1, padding:"8px 6px", borderRadius:9, border:`1.5px solid ${a.col}`, background:active?a.col:"transparent", cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:2, fontSize:11, fontWeight:700, color:active?"#09090f":a.col, transition:"all 0.15s" }}>
                        <span style={{fontSize:15}}>{a.s==="yes"?"✓":a.s==="maybe"?"?":"✗"}</span>{a.l}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
            {!past&&isCoach&&(
              <button onClick={()=>navigate(`lineup-${f.id}`)}
                style={{width:"100%",padding:"8px",border:`1px solid ${primaryCol}55`,borderRadius:8,background:`${primaryCol}12`,color:primaryCol,fontFamily:"'Barlow Condensed',sans-serif",fontSize:13,fontWeight:700,cursor:"pointer",marginTop:4}}>
                {lineups[f.id]?"✏️ Edit Lineup":"🏃 Build Lineup"}
              </button>
            )}
            {past && isCoach && !f.result && (
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {["W","D","L"].map(r=>(
                  <button key={r} onClick={()=>{setModal("result"); setFixtures(fs=>fs.map(fx=>fx.id===f.id?{...fx,_editing:true}:fx));}}
                    style={{ flex:1, padding:"8px", border:"none", borderRadius:8, fontFamily:"'Barlow Condensed',sans-serif", fontSize:14, fontWeight:800, cursor:"pointer", background:r==="W"?"rgba(34,197,94,0.15)":r==="D"?"rgba(240,180,41,0.15)":"rgba(230,57,70,0.15)", color:r==="W"?"#22c55e":r==="D"?"#f0b429":"#E63946" }}>
                    {r==="W"?"Win":r==="D"?"Draw":"Loss"}
                  </button>
                ))}
              </div>
            )}
            {isCoach && (f.isSession||f.is_session) && (
              <div style={{display:"flex",gap:6,marginTop:4,flexWrap:"wrap"}}>
                {f.result==="cancelled"||f.result==="postponed" ? (
                  <button onClick={async()=>{
                    const updated={...f,result:null};
                    setFixtures(fs=>fs.map(fx=>fx.id===f.id?updated:fx));
                    if(clubId) await supabase.from("fixtures").update({result:null}).eq("id",f.id);
                    showToast("Session restored");
                  }} style={{padding:"6px 12px",background:"rgba(34,197,94,0.1)",border:"1px solid rgba(34,197,94,0.3)",borderRadius:8,color:"#22c55e",fontSize:11,fontWeight:600,cursor:"pointer"}}>
                    ↩ Restore
                  </button>
                ) : (
                  <>
                    {["cancelled","postponed"].map(status=>(
                      <button key={status} onClick={async()=>{
                        const updated={...f,result:status};
                        setFixtures(fs=>fs.map(fx=>fx.id===f.id?updated:fx));
                        if(clubId) await supabase.from("fixtures").update({result:status}).eq("id",f.id);
                        showToast(status==="cancelled"?"Session cancelled":"Session postponed");
                      }} style={{padding:"6px 12px",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:8,color:"rgba(255,255,255,0.4)",fontSize:11,fontWeight:600,cursor:"pointer"}}>
                        {status==="cancelled"?"❌ Cancel":"⏸ Postpone"}
                      </button>
                    ))}
                    <button onClick={async()=>{
                      if(!window.confirm("Delete this training session?")) return;
                      setFixtures(fs=>fs.filter(fx=>fx.id!==f.id));
                      if(clubId) await supabase.from("fixtures").delete().eq("id",f.id);
                      showToast("Session deleted");
                    }} style={{padding:"6px 12px",background:"rgba(230,57,70,0.08)",border:"1px solid rgba(230,57,70,0.2)",borderRadius:8,color:"#E63946",fontSize:11,fontWeight:600,cursor:"pointer"}}>
                      🗑 Delete
                    </button>
                  </>
                )}
              </div>
            )}
            {isCoach && !(f.isSession||f.is_session) && (
              <div style={{display:"flex",gap:6,marginTop:4,flexWrap:"wrap"}}>
                {f.result==="cancelled"||f.result==="postponed" ? (
                  <button onClick={async()=>{
                    const updated={...f,result:null};
                    setFixtures(fs=>fs.map(fx=>fx.id===f.id?updated:fx));
                    if(clubId) await supabase.from("fixtures").update({result:null}).eq("id",f.id);
                    showToast("Fixture restored");
                  }} style={{padding:"6px 12px",background:"rgba(34,197,94,0.1)",border:"1px solid rgba(34,197,94,0.3)",borderRadius:8,color:"#22c55e",fontSize:11,fontWeight:600,cursor:"pointer"}}>
                    ↩ Restore Fixture
                  </button>
                ) : (
                  <>
                    {["cancelled","postponed"].map(status=>(
                      <button key={status} onClick={async()=>{
                        const updated={...f,result:status};
                        setFixtures(fs=>fs.map(fx=>fx.id===f.id?updated:fx));
                        if(clubId) await supabase.from("fixtures").update({result:status}).eq("id",f.id);
                        showToast(status==="cancelled"?"Fixture cancelled":"Fixture postponed");
                      }} style={{padding:"6px 12px",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:8,color:"rgba(255,255,255,0.4)",fontSize:11,fontWeight:600,cursor:"pointer"}}>
                        {status==="cancelled"?"❌ Cancel":"⏸ Postpone"}
                      </button>
                    ))}
                    {isAdmin&&<button onClick={async()=>{
                      if(!window.confirm("Delete this fixture?")) return;
                      setFixtures(fs=>fs.filter(fx=>fx.id!==f.id));
                      if(clubId) await supabase.from("fixtures").delete().eq("id",f.id);
                      showToast("Fixture deleted");
                    }} style={{padding:"6px 12px",background:"rgba(230,57,70,0.08)",border:"1px solid rgba(230,57,70,0.2)",borderRadius:8,color:"#E63946",fontSize:11,fontWeight:600,cursor:"pointer"}}>
                      🗑 Delete
                    </button>}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
    );
  };

  // ═══════════════════════════════
  // CLUBHUB (feed)
  // ═══════════════════════════════
  const MessagesPanel = ({startConvWith=null}) => {
    const myId   = currentUser?.id||"";
    const myName = currentUser?.name||"Me";
    const myPhoto = players.find(pl=>pl.userId===myId||pl.auth_id===myId)?.photo||null;
    const canCreate = isAdmin||isCoach||isCaptain;
    const [msgSearch, setMsgSearch]         = React.useState("");
    const [msgTeamFilter, setMsgTeamFilter] = React.useState("all");
    const [msgDraft, setMsgDraft]           = React.useState("");
    const [loading, setLoading]             = React.useState(false);
    const msgEndRef = React.useRef(null);

    // ── Scroll to bottom on new messages ────────────────────────────────────
    const msgCount = (convMessages[activeConvId]||[]).length;
    React.useEffect(()=>{
      if(msgCount > 0) setTimeout(()=>msgEndRef.current?.scrollIntoView({behavior:"smooth"}), 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[msgCount, activeConvId]);

    // ── Auto-start DM ─────────────────────────────────────────────────────────
    React.useEffect(()=>{
      if(!startConvWith) return;
      openOrCreateDM(startConvWith.id, startConvWith.name);
    },[startConvWith?.id]);

    // ── Load messages — fetches from all duplicate conv IDs merged into one thread
    const loadMessages = async(convId, force=false)=>{
      if(!convId) return;
      if(!force && convMessages[convId]?.length > 0) {
        setConversations(cs=>cs.map(c=>c.id===convId?{...c,_readByMe:true}:c));
        setUnreadCount(u=>Math.max(0,u-1));
        return;
      }
      setLoading(true);
      try {
        // Include messages from any duplicate conversations for this pair
        const allIds = convGroupMap[convId] || [convId];
        const {data, error} = await supabase
          .from("messages")
          .select("*")
          .in("conversation_id", allIds)
          .order("created_at", {ascending:true})
          .limit(500);
        if(error) throw error;
        if(data !== null) {
          setConvMessages(cm=>({...cm,[convId]:data}));
        }
        setConversations(cs=>cs.map(c=>c.id===convId?{...c,_readByMe:true}:c));
        setUnreadCount(u=>Math.max(0,u-1));
      } catch(e){
        console.error("Load messages failed:", e);
      }
      setLoading(false);
    };

    // ── Open conversation ─────────────────────────────────────────────────────
    const openConv = (conv)=>{
      setActiveConvId(conv.id);
      loadMessages(conv.id);
    };

    // ── Open or create DM ─────────────────────────────────────────────────────
    const openOrCreateDM = async(otherId, otherName)=>{
      const existing = conversations.find(c=>
        c.type==="direct" &&
        (c.member_ids||[]).includes(myId) &&
        (c.member_ids||[]).includes(otherId)
      );
      if(existing){ openConv(existing); return; }
      try {
        const {data, error} = await supabase.from("conversations").insert({
          club_id:clubId, type:"direct", name:null,
          member_ids:[myId, otherId],
          member_names:[myName, otherName],
          created_by:myId,
          last_message_at:new Date().toISOString(),
        }).select().single();
        if(error) throw error;
        if(data){
          setConversations(cs=>[data,...cs]);
          openConv(data);
        }
      } catch(e){ showToast("Could not start conversation"); }
    };

    // ── Send message ──────────────────────────────────────────────────────────
    const sendMsg = async(body, photoUrl=null, fileUrl=null, fileName=null)=>{
      const trimmed = body?.trim()||"";
      if(!activeConvId||(!trimmed&&!photoUrl&&!fileUrl)) return;
      const now = new Date().toISOString();
      const msgRow = {
        conversation_id: activeConvId,
        club_id: clubId,
        sender_id: myId,
        sender_name: myName,
        sender_photo: myPhoto,
        body: trimmed||null,
        photo_url: photoUrl||null,
        file_url: fileUrl||null,
        file_name: fileName||null,
        created_at: now,
        read_by: [myId],
      };
      setMsgDraft("");
      try {
        const {data, error} = await supabase.from("messages").insert(msgRow).select().single();
        if(error) throw error;
        if(data){
          // Add to local state
          setConvMessages(cm=>({
            ...cm,
            [activeConvId]: [...(cm[activeConvId]||[]), data]
          }));
          // Update conversation preview
          await supabase.from("conversations").update({
            last_message: trimmed||"📎 Attachment",
            last_message_at: data.created_at,
            last_sender: myId,
          }).eq("id", activeConvId);
          setConversations(cs=>cs.map(c=>c.id===activeConvId
            ? {...c, last_message:trimmed||"📎 Attachment", last_message_at:data.created_at, last_sender:myId}
            : c
          ).sort((a,b)=>new Date(b.last_message_at)-new Date(a.last_message_at)));
        }
      } catch(e){
        console.error("Send failed:", e);
        showToast("Message failed to send — check your connection");
        setMsgDraft(trimmed); // restore draft
      }
    };

    // ── Upload file/photo ─────────────────────────────────────────────────────
    const handleFileUpload = async(file, isPhoto)=>{
      if(!file) return;
      showToast("Uploading...");
      const path = `${clubId}/messages/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g,"")}`;
      const {data,error} = await supabase.storage.from("club-files").upload(path,file,{upsert:true});
      if(error){ showToast("Upload failed"); return; }
      const {data:urlData} = supabase.storage.from("club-files").getPublicUrl(path);
      if(isPhoto) await sendMsg(null,urlData.publicUrl,null,null);
      else await sendMsg(null,null,urlData.publicUrl,file.name);
    };

    // ── Create group ──────────────────────────────────────────────────────────
    const createGroup = async()=>{
      if(!newGroupName.trim()||newGroupMembers.length===0){
        showToast("Add a group name and at least one member");
        return;
      }
      try {
        const {data, error} = await supabase.from("conversations").insert({
          club_id:clubId, type:"group", name:newGroupName.trim(),
          member_ids:[myId,...newGroupMembers.map(m=>m.id)],
          member_names:[myName,...newGroupMembers.map(m=>m.name)],
          created_by:myId,
          last_message_at:new Date().toISOString(),
        }).select().single();
        if(error) throw error;
        if(data){
          setConversations(cs=>[data,...cs]);
          setNewGroupName(""); setNewGroupMembers([]); setMsgTab("convs");
          openConv(data);
          showToast(`Group "${data.name}" created ✓`);
        }
      } catch(e){ showToast("Could not create group"); }
    };

    // ── Helpers ───────────────────────────────────────────────────────────────
    const activeConv = conversations.find(c=>c.id===activeConvId);
    const activeConvName = activeConv
      ? (activeConv.type==="direct"
          ? (activeConv.member_names||[]).find(n=>n!==myName)||"Direct Message"
          : activeConv.name||"Group")
      : "";
    const msgs = convMessages[activeConvId]||[];
    const myConvs = conversations.filter(c=>(c.member_ids||[]).includes(myId));
    const filteredConvs = msgSearch
      ? myConvs.filter(c=>(c.name||c.member_names?.join("")||"").toLowerCase().includes(msgSearch.toLowerCase()))
      : myConvs;

    const msgTimeLabel = (ts)=>{
      if(!ts) return "";
      const d = new Date(ts);
      const now = new Date();
      const diff = now-d;
      if(diff<60000) return "now";
      if(diff<3600000) return Math.floor(diff/60000)+"m";
      if(diff<86400000) return d.toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"});
      return d.toLocaleDateString("en-GB",{day:"numeric",month:"short"});
    };


    // ── If in thread view ─────────────────────────────────────────────────────
    if(activeConvId && activeConv) return (
      <div style={{...G.panel,paddingBottom:0,display:"flex",flexDirection:"column",height:"calc(100vh - 128px)"}}>
        {/* Thread header */}
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12,flexShrink:0}}>
          <button onClick={()=>setActiveConvId(null)} style={{background:"none",border:"none",color:"rgba(255,255,255,0.4)",fontSize:22,cursor:"pointer",padding:0}}>←</button>
          <div style={{width:36,height:36,borderRadius:"50%",background:`linear-gradient(135deg,${primaryCol},${secondaryCol})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0,fontWeight:700,color:"#fff"}}>
            {activeConv.type==="group"?"👥":inits(activeConvName)}
          </div>
          <div style={{flex:1}}>
            <div style={{fontSize:14,fontWeight:700,color:"#fff"}}>{activeConvName}</div>
            {activeConv.type==="group"&&<div style={{fontSize:11,color:"rgba(255,255,255,0.35)"}}>{(activeConv.member_ids||[]).length} members</div>}
          </div>
        </div>

        {/* Messages */}
        <div style={{flex:1,overflowY:"auto",display:"flex",flexDirection:"column",gap:4,paddingBottom:8}}
          ref={el=>{
            if(el){
              const isNearBottom = el.scrollHeight-el.scrollTop-el.clientHeight < 100;
              if(isNearBottom||msgs[msgs.length-1]?.sender_id===myId) el.scrollTop=el.scrollHeight;
            }
          }}>
          {loading&&<div style={{textAlign:"center",padding:"16px 0",color:"rgba(255,255,255,0.3)",fontSize:12}}>Loading...</div>}
          {!loading&&msgs.length===0&&<div style={{textAlign:"center",padding:"32px 0",color:"rgba(255,255,255,0.3)",fontSize:13}}>No messages yet — say hello 👋</div>}
          {msgs.map((m,i)=>{
            const isMe = m.sender_id===myId;
            const showName = !isMe && activeConv.type==="group" && (i===0||msgs[i-1]?.sender_id!==m.sender_id);
            return (
              <div key={m.id||i} style={{display:"flex",flexDirection:"column",alignItems:isMe?"flex-end":"flex-start",paddingLeft:isMe?40:0,paddingRight:isMe?0:40}}>
                {showName&&<div style={{fontSize:10,color:"rgba(255,255,255,0.35)",marginBottom:2,paddingLeft:8}}>{m.sender_name}</div>}
                <div style={{background:isMe?primaryCol:"rgba(255,255,255,0.08)",borderRadius:isMe?"14px 14px 4px 14px":"14px 14px 14px 4px",padding:m.photo_url?"6px":"10px 14px",maxWidth:"100%",position:"relative"}}>
                  {m.photo_url&&<img src={m.photo_url} alt="" style={{maxWidth:"100%",maxHeight:260,borderRadius:8,display:"block"}}/>}
                  {m.file_url&&(
                    <a href={m.file_url} target="_blank" rel="noreferrer" style={{display:"flex",alignItems:"center",gap:8,color:isMe?"rgba(255,255,255,0.9)":"rgba(255,255,255,0.75)",textDecoration:"none",padding:"4px 0"}}>
                      <span style={{fontSize:18}}>📎</span>
                      <span style={{fontSize:13,fontWeight:600}}>{m.file_name||"File"}</span>
                    </a>
                  )}
                  {m.body&&<div style={{fontSize:14,color:isMe?"#fff":"rgba(255,255,255,0.88)",lineHeight:1.5,wordBreak:"break-word"}}>{m.body}</div>}
                </div>
                <div style={{fontSize:10,color:"rgba(255,255,255,0.25)",marginTop:2,paddingLeft:isMe?0:8,paddingRight:isMe?8:0}}>{msgTimeLabel(m.created_at)}</div>
              </div>
            );
          })}
          <div ref={msgEndRef}/>
        </div>

        {/* Input bar */}
        <div style={{flexShrink:0,padding:"10px 0 16px",borderTop:"1px solid rgba(255,255,255,0.07)",display:"flex",gap:8,alignItems:"flex-end"}}>
          {/* Photo upload */}
          <label style={{width:36,height:36,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(255,255,255,0.06)",borderRadius:"50%",cursor:"pointer",flexShrink:0}}>
            📷
            <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>handleFileUpload(e.target.files[0],true)}/>
          </label>
          {/* File upload */}
          <label style={{width:36,height:36,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(255,255,255,0.06)",borderRadius:"50%",cursor:"pointer",flexShrink:0}}>
            📎
            <input type="file" style={{display:"none"}} onChange={e=>handleFileUpload(e.target.files[0],false)}/>
          </label>
          <textarea value={msgDraft} onChange={e=>setMsgDraft(e.target.value)}
            onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendMsg(msgDraft);}}}
            placeholder="Message..." rows={1}
            style={{flex:1,background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:20,padding:"10px 16px",color:"#fff",fontSize:14,resize:"none",outline:"none",fontFamily:"inherit",lineHeight:1.4,maxHeight:100,overflowY:"auto"}}/>
          <button onClick={()=>sendMsg(msgDraft)} disabled={!msgDraft.trim()}
            style={{width:36,height:36,borderRadius:"50%",background:msgDraft.trim()?primaryCol:"rgba(255,255,255,0.06)",border:"none",color:msgDraft.trim()?"#fff":"rgba(255,255,255,0.3)",cursor:msgDraft.trim()?"pointer":"default",fontSize:16,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
            ↑
          </button>
        </div>
      </div>
    );

    // ── Conversations list / New group ────────────────────────────────────────
    return (
      <div style={{...G.panel,paddingBottom:100}}>
        {/* Header */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
          <div style={G.h2}>Messages</div>
          <div style={{display:"flex",gap:6}}>
            {canCreate&&(
              <button onClick={()=>setMsgTab(msgTab==="new"?"convs":"new")}
                style={{padding:"7px 14px",background:msgTab==="new"?primaryCol:"rgba(255,255,255,0.05)",border:`1px solid ${msgTab==="new"?primaryCol:"rgba(255,255,255,0.1)"}`,borderRadius:8,color:msgTab==="new"?contrastText(primaryCol):"rgba(255,255,255,0.6)",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                {msgTab==="new"?"← Back":"+ New Group"}
              </button>
            )}
          </div>
        </div>

        {/* New group form */}
        {msgTab==="new"&&canCreate&&(
          <div style={{...G.card,marginBottom:14,border:`1px solid ${primaryCol}33`}}>
            <div style={{fontSize:13,fontWeight:700,color:"#fff",marginBottom:10}}>Create Group Chat</div>
            <input defaultValue={newGroupName}
              onChange={e=>setNewGroupName(e.target.value)}
              onBlur={e=>setNewGroupName(e.target.value)}
              style={{...G.input}} placeholder="Group name e.g. Men's 1s Match Day"/>
            <FieldLabel>Add members</FieldLabel>
            <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
              {newGroupMembers.map(m=>(
                <div key={m.id} style={{display:"flex",alignItems:"center",gap:6,padding:"4px 10px",background:`${primaryCol}18`,border:`1px solid ${primaryCol}33`,borderRadius:20}}>
                  <span style={{fontSize:12,color:"#fff"}}>{m.name}</span>
                  <button onClick={()=>setNewGroupMembers(gm=>gm.filter(x=>x.id!==m.id))} style={{background:"none",border:"none",color:"rgba(255,255,255,0.4)",cursor:"pointer",fontSize:14,padding:0}}>✕</button>
                </div>
              ))}
            </div>
            <div style={{maxHeight:180,overflowY:"auto",background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:10,padding:6,marginBottom:10}}>
              {users.filter(u=>u.id!==myId&&!newGroupMembers.find(m=>m.id===u.id)).map(u=>(
                <div key={u.id} onClick={()=>setNewGroupMembers(gm=>[...gm,{id:u.id,name:u.name||u.email}])}
                  style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",borderRadius:8,cursor:"pointer"}}
                  onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.06)"}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <Avatar name={u.name||u.email} size={28}/>
                  <div style={{fontSize:13,color:"rgba(255,255,255,0.8)"}}>{u.name||u.email}</div>
                </div>
              ))}
            </div>
            <button onClick={createGroup} style={{...G.btnPrimary,background:primaryCol,width:"100%"}}>Create Group</button>
          </div>
        )}

        {/* Member search + team filter */}
        {msgTab==="convs"&&(
          <div style={{marginBottom:14}}>
            {/* Search input */}
            <input value={msgSearch} onChange={e=>setMsgSearch(e.target.value)}
              style={{...G.input,marginBottom:10}} placeholder="🔍  Search by name..."/>

            {/* Team filter pills */}
            <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:6,marginBottom:10}}>
              <button onClick={()=>setMsgTeamFilter("all")}
                style={{padding:"5px 14px",background:msgTeamFilter==="all"?primaryCol:"rgba(255,255,255,0.05)",border:`1px solid ${msgTeamFilter==="all"?primaryCol:"rgba(255,255,255,0.1)"}`,borderRadius:20,color:msgTeamFilter==="all"?contrastText(primaryCol):"rgba(255,255,255,0.5)",fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>
                All
              </button>
              {teams.map(t=>(
                <button key={t} onClick={()=>setMsgTeamFilter(t)}
                  style={{padding:"5px 14px",background:msgTeamFilter===t?primaryCol:"rgba(255,255,255,0.05)",border:`1px solid ${msgTeamFilter===t?primaryCol:"rgba(255,255,255,0.1)"}`,borderRadius:20,color:msgTeamFilter===t?contrastText(primaryCol):"rgba(255,255,255,0.5)",fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>
                  {t}
                </button>
              ))}
            </div>

            {/* Filtered member list */}
            {(()=>{
              const searchLower = msgSearch.toLowerCase();
              const filteredUsers = users.filter(u=>{
                if(u.id===myId) return false;
                // Name filter
                if(searchLower&&!(u.name||u.email||"").toLowerCase().includes(searchLower)) return false;
                // Team filter
                if(msgTeamFilter!=="all"){
                  const uPlayer = players.find(pl=>pl.userId===u.id||pl.auth_id===u.id);
                  if(!uPlayer||uPlayer.team!==msgTeamFilter) return false;
                }
                return true;
              });

              if(filteredUsers.length===0) return (
                <div style={{textAlign:"center",padding:"12px 0",color:"rgba(255,255,255,0.3)",fontSize:13}}>
                  No members found
                </div>
              );

              return (
                <div style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:12,overflow:"hidden"}}>
                  {filteredUsers.map((u,i)=>{
                    const uPlayer = players.find(pl=>pl.userId===u.id||pl.auth_id===u.id);
                    const photo = uPlayer?.photo||null;
                    const team  = uPlayer?.team||u.role||"";
                    const isLast = i===filteredUsers.length-1;
                    return (
                      <div key={u.id} onClick={()=>openOrCreateDM(u.id,u.name||u.email)}
                        style={{display:"flex",alignItems:"center",gap:12,padding:"11px 14px",borderBottom:isLast?"none":"1px solid rgba(255,255,255,0.05)",cursor:"pointer",transition:"background .15s"}}
                        onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.06)"}
                        onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        <div style={{width:38,height:38,borderRadius:"50%",overflow:"hidden",background:`linear-gradient(135deg,${primaryCol},${secondaryCol})`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                          {photo?<img src={photo} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>:<span style={{fontSize:13,fontWeight:700,color:"#fff"}}>{inits(u.name||u.email||"")}</span>}
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:14,fontWeight:600,color:"#fff",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{u.name||u.email}</div>
                          {team&&<div style={{fontSize:11,color:"rgba(255,255,255,0.4)",marginTop:1}}>{team}</div>}
                        </div>
                        <div style={{fontSize:12,color:primaryCol,fontWeight:600,flexShrink:0}}>Message →</div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}

        {/* Conversation list */}
        {msgTab==="convs"&&(
          <div>
            <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.3)",letterSpacing:1.5,marginBottom:8}}>CONVERSATIONS</div>
            {filteredConvs.length===0&&(
              <div style={{textAlign:"center",padding:"24px 0",color:"rgba(255,255,255,0.3)",fontSize:13}}>
                No messages yet — tap a member above to start a conversation
              </div>
            )}
            {filteredConvs.map(c=>{
              const isGroup = c.type==="group";
              const name = isGroup ? c.name : (c.member_names||[]).find(n=>n!==myName)||"Direct Message";
              const isUnread = c.last_sender&&c.last_sender!==myId&&c.last_message;
              const otherMember = !isGroup&&users.find(u=>u.id===(c.member_ids||[]).find(id=>id!==myId));
              const photo = otherMember ? players.find(pl=>pl.userId===otherMember.id||pl.auth_id===otherMember.id)?.photo : null;
              return (
                <div key={c.id} onClick={()=>openConv(c)}
                  style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:12,marginBottom:6,cursor:"pointer",transition:"background .15s"}}
                  onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.08)"}
                  onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,0.04)"}>
                  <div style={{width:44,height:44,borderRadius:"50%",background:`linear-gradient(135deg,${primaryCol},${secondaryCol})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:isGroup?20:14,fontWeight:700,color:"#fff",flexShrink:0,overflow:"hidden"}}>
                    {isGroup?"👥":photo?<img src={photo} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>:inits(name)}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                      <div style={{fontSize:14,fontWeight:isUnread?700:500,color:"#fff",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{name}</div>
                      {isGroup&&<div style={{fontSize:10,background:`${primaryCol}18`,border:`1px solid ${primaryCol}33`,borderRadius:4,padding:"1px 6px",color:primaryCol,fontWeight:700,flexShrink:0}}>GROUP</div>}
                    </div>
                    <div style={{fontSize:12,color:isUnread?"rgba(255,255,255,0.6)":"rgba(255,255,255,0.3)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontWeight:isUnread?600:400}}>
                      {c.last_message||"No messages yet"}
                    </div>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4,flexShrink:0}}>
                    <div style={{fontSize:10,color:"rgba(255,255,255,0.3)"}}>{c.last_message_at?msgTimeLabel(c.last_message_at):""}</div>
                    {isUnread&&<div style={{width:8,height:8,borderRadius:"50%",background:primaryCol}}/>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const ClubHubPanel = () => {
    const [hubTab, setHubTab] = useState("feed");
    const [openComments, setOpenComments] = useState({});
    const [commentDraft, setCommentDraft] = useState({});

    const addComment = (postId) => {
      const text = (commentDraft[postId]||"").trim();
      if (!text) return;
      const filtered = filterSwears(text);
      setPosts(ps => ps.map(p => p.id===postId ? {
        ...p,
        comments:[...(p.comments||[]),{
          id:uid(), author:currentUser?.name||"Member",
          authorId:currentUser?.id, text:filtered, time:Date.now()
        }]
      } : p));
      setCommentDraft(d => ({...d,[postId]:""}));
    };

    const teamPosts = posts.filter(p=>{
      if(isAdmin||isCaptain) return true;
      if(p.team==="all") return true;
      if(p.teams&&p.teams.length>0) return p.teams.includes(myTeams);
      return p.team===myTeams||p.team==="all";
    });
    const filtered = feedFilter==="all" ? teamPosts :
      teamPosts.filter(p=>p.tag===feedFilter);

    if(hubTab==="messages") return (
      <div style={{...G.panel,paddingBottom:100}}>
        <div style={{display:"flex",gap:0,marginBottom:14,background:"rgba(255,255,255,0.04)",borderRadius:10,padding:3}}>
          <button onClick={()=>setHubTab("feed")} style={{flex:1,padding:"9px",background:"transparent",border:"none",borderRadius:8,color:"rgba(255,255,255,0.45)",fontSize:13,fontWeight:700,cursor:"pointer"}}>💬 Feed</button>
          <button onClick={()=>setHubTab("messages")} style={{flex:1,padding:"9px",background:primaryCol,border:"none",borderRadius:8,color:contrastText(primaryCol),fontSize:13,fontWeight:700,cursor:"pointer"}}>✉️ Messages</button>
        </div>
        <MessagesPanel/>
      </div>
    );

    return (
      <div style={{...G.panel,maxWidth:640,margin:"0 auto"}}>
        {/* Feed / Messages tabs */}
        <div style={{display:"flex",gap:0,marginBottom:14,background:"rgba(255,255,255,0.04)",borderRadius:10,padding:3}}>
          <button onClick={()=>setHubTab("feed")} style={{flex:1,padding:"9px",background:primaryCol,border:"none",borderRadius:8,color:contrastText(primaryCol),fontSize:13,fontWeight:700,cursor:"pointer"}}>💬 Feed</button>
          <button onClick={()=>setHubTab("messages")} style={{flex:1,padding:"9px",background:"transparent",border:"none",borderRadius:8,color:"rgba(255,255,255,0.45)",fontSize:13,fontWeight:700,cursor:"pointer",position:"relative"}}>
            ✉️ Messages{unreadCount>0&&<span style={{position:"absolute",top:3,right:6,minWidth:15,height:15,borderRadius:8,background:"#E63946",fontSize:9,fontWeight:800,color:"#fff",display:"inline-flex",alignItems:"center",justifyContent:"center",padding:"0 3px"}}>{unreadCount}</span>}
          </button>
        </div>
        {/* Filter tabs */}
        <div style={{display:"flex",gap:8,marginBottom:14,overflowX:"auto",paddingBottom:4}}>
          {[{v:"all",l:"All"},{v:"announce",l:"📢 Announcements"},{v:"team",l:isSwimming?"🏊 Squad":"👥 Team"}].map(f=>(
            <button key={f.v} onClick={()=>setFeedFilter(f.v)}
              style={{padding:"7px 14px",background:feedFilter===f.v?primaryCol:"rgba(255,255,255,0.04)",border:`1px solid ${feedFilter===f.v?primaryCol:"rgba(255,255,255,0.08)"}`,borderRadius:20,color:feedFilter===f.v?"#fff":"rgba(255,255,255,0.45)",fontSize:12,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>
              {f.l}
            </button>
          ))}
        </div>

        {/* Post composer */}
        <div style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:14,padding:14,marginBottom:16}}>
          <div style={{display:"flex",gap:10,marginBottom:10}}>
            <Avatar name={currentUser?.name||""} size={36} colour={primaryCol}/>
            <select style={{...G.select,flex:1,marginBottom:0}} value={postForm.tag} onChange={e=>setPostForm(f=>({...f,tag:e.target.value}))}>
              <option value="team">{isSwimming?"🏊 Squad post":"👥 Club post"}</option>
              <option value="announce">📢 Announcement</option>
            </select>
            <div style={{position:"relative",minWidth:130}}>
              {postForm.tag==="announce" ? (
                <div>
                  <div style={{fontSize:10,color:"rgba(255,255,255,0.4)",marginBottom:4}}>Tag teams (tap to toggle)</div>
                  <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                    <button onClick={()=>setPostForm(f=>({...f,teams:[]}))} style={{padding:"4px 10px",background:postForm.teams.length===0?primaryCol:"rgba(255,255,255,0.06)",border:`1px solid ${postForm.teams.length===0?primaryCol:"rgba(255,255,255,0.1)"}`,borderRadius:20,color:postForm.teams.length===0?contrastText(primaryCol):"rgba(255,255,255,0.5)",fontSize:11,fontWeight:600,cursor:"pointer"}}>
                      All
                    </button>
                    {teams.map(t=>{
                      const sel=postForm.teams.includes(t);
                      return <button key={t} onClick={()=>setPostForm(f=>({...f,teams:sel?f.teams.filter(x=>x!==t):[...f.teams,t]}))} style={{padding:"4px 10px",background:sel?primaryCol:"rgba(255,255,255,0.06)",border:`1px solid ${sel?primaryCol:"rgba(255,255,255,0.1)"}`,borderRadius:20,color:sel?contrastText(primaryCol):"rgba(255,255,255,0.5)",fontSize:11,fontWeight:600,cursor:"pointer"}}>{t}</button>;
                    })}
                  </div>
                </div>
              ) : (
                <select style={{...G.select,marginBottom:0}} value={postForm.team} onChange={e=>setPostForm(f=>({...f,team:e.target.value}))}>
                  <option value="all">{isSwimming?"All squads":"All teams"}</option>
                  {teams.map(t=><option key={t} value={t}>{t}</option>)}
                </select>
              )}
            </div>
          </div>
          <textarea
            id="clubhub-post-body"
            style={{...G.textarea,minHeight:100,marginBottom:10}}
            placeholder="Share something with the club..."
          />
          {postForm.photo&&(
            <div style={{position:"relative",marginBottom:10}}>
              <img src={postForm.photo} alt="" style={{width:"100%",maxHeight:200,objectFit:"cover",borderRadius:8}}/>
              <button onClick={()=>setPostForm(f=>({...f,photo:null}))} style={{position:"absolute",top:6,right:6,background:"rgba(0,0,0,0.7)",border:"none",borderRadius:"50%",width:24,height:24,color:"#fff",cursor:"pointer",fontSize:14}}>✕</button>
            </div>
          )}
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <label style={{display:"flex",alignItems:"center",gap:6,padding:"8px 14px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:8,cursor:"pointer",fontSize:12,color:"rgba(255,255,255,0.6)"}}>
              🖼️ Upload Image
              <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>{const f=e.target.files[0];if(!f)return;const r=new FileReader();r.onload=ev=>setPostForm(pf=>({...pf,photo:ev.target.result}));r.readAsDataURL(f);}}/>
            </label>
            <label style={{display:"flex",alignItems:"center",gap:6,padding:"8px 14px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:8,cursor:"pointer",fontSize:12,color:"rgba(255,255,255,0.6)"}}>
              📷 Camera
              <input type="file" accept="image/*" capture="environment" style={{display:"none"}} onChange={e=>{const f=e.target.files[0];if(!f)return;const r=new FileReader();r.onload=ev=>setPostForm(pf=>({...pf,photo:ev.target.result}));r.readAsDataURL(f);}}/>
            </label>
            <button onClick={()=>{
              const bodyText = (document.getElementById("clubhub-post-body")?.value||"").trim();
              if(!bodyText&&!postForm.photo){showToast("Write something or add a photo");return;}
              const np={
                id:uid(),
                authorId:currentUser?.id,
                author:currentUser?.name||"Member",
                body:filterSwears(bodyText),
                tag:postForm.tag,
                team:postForm.tag==="announce"&&postForm.teams.length>0 ? postForm.teams.join(",") : (postForm.team||"all"),
                teams:postForm.tag==="announce" ? postForm.teams : [],
                time:Date.now(),
                likes:0,liked:false,
                pinned:postForm.pinned||false,
                photo:postForm.photo||null,
                reactions:{},comments:[]
              };
              setPosts(ps=>[np,...ps]);
              if(clubId)db.createPost(clubId,currentUser?.id,np).catch(console.error);
              const ta=document.getElementById("clubhub-post-body");
              if(ta) ta.value="";
              setPostForm(f=>({...f,body:"",photo:null,pinned:false}));
              showToast(np.pinned?"Posted & pinned to home ✓":"Posted ✓");
            }} style={{...G.btnPrimary,background:primaryCol,flex:1,padding:"10px",fontSize:13}}>Post</button>
          </div>
        </div>

        {/* Posts */}
        {filtered.map(p=>{
          const commentsOpen = openComments[p.id];
          const draft = commentDraft[p.id]||"";
          return (
            <div key={p.id} style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.09)",borderRadius:14,overflow:"hidden",marginBottom:12}}>
              {/* Post header */}
              <div style={{padding:"12px 14px 0"}}>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                  {(()=>{
                    const postAuthor = players.find(pl=>pl.userId===p.authorId||pl.auth_id===p.authorId)||players.find(pl=>pl.name===p.author);
                    const photo = p.authorPhoto || postAuthor?.photo || null;
                    return photo
                      ? <div style={{width:36,height:36,borderRadius:"50%",overflow:"hidden",flexShrink:0,border:"2px solid rgba(255,255,255,0.1)"}}><img src={photo} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/></div>
                      : <Avatar name={p.author} size={36} colour={avCol(p.author)}/>;
                  })()}
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:600,color:"#fff"}}>{p.author}</div>
                    <div style={{fontSize:11,color:"rgba(255,255,255,0.35)",marginTop:1}}>
                      {timeSince(p.time)}{p.team&&p.team!=="all"?` · ${p.team}`:""}
                      {p.pinned&&<span style={{color:primaryCol,fontWeight:700}}> · 📌 Pinned</span>}
                    </div>
                  </div>
                  <div style={{display:"flex",gap:6,alignItems:"center"}}>
                    {p.tag==="announce"&&<span style={{padding:"2px 8px",borderRadius:4,background:"rgba(240,180,41,0.15)",color:"#f0b429",fontSize:10,fontWeight:700}}>📢 Announcement</span>}
                    {(isAdmin||isCaptain)&&<button onClick={()=>{
                      const newPinned=!p.pinned;
                      setPosts(ps=>ps.map(px=>px.id===p.id?{...px,pinned:newPinned}:px));
                      supabase.from("posts").update({pinned:newPinned}).eq("id",p.id).catch(console.error);
                      showToast(newPinned?"📌 Pinned to home":"Unpinned");
                    }} style={{background:"none",border:"none",color:p.pinned?primaryCol:"rgba(255,255,255,0.25)",cursor:"pointer",fontSize:16,padding:"4px"}}>📌</button>}
                    {(isAdmin||currentUser?.id===p.authorId)&&<button onClick={()=>setPosts(ps=>ps.filter(px=>px.id!==p.id))} style={{background:"none",border:"none",color:"rgba(255,255,255,0.2)",cursor:"pointer",fontSize:14}}>✕</button>}
                  </div>
                </div>
                {p.body&&<div style={{fontSize:13,color:"rgba(255,255,255,0.85)",lineHeight:1.68,marginBottom:p.photo?10:0}}>{p.body}</div>}
              </div>
              {/* Photo */}
              {p.photo&&<img src={p.photo} alt="" onClick={()=>setModal("lightbox-"+p.id)}
                style={{width:"100%",maxHeight:320,objectFit:"contain",display:"block",background:"#000",cursor:"pointer"}}/>}
              {/* Actions */}
              {/* Emoji reactions */}
              <div style={{padding:"8px 14px",borderTop:"1px solid rgba(255,255,255,0.06)"}}>
                <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:6}}>
                  {["👍","❤️","😂","😮","😢","🔥","👏",club.sport==="Football"?"⚽":club.sport==="Swimming"?"🏊":"🏑"].map(emoji=>{
                    const count=(p.reactions||{})[emoji]?.length||0;
                    const mine=(p.reactions||{})[emoji]?.includes(currentUser?.id||"me");
                    return (
                      <button key={emoji} onClick={()=>setPosts(ps=>ps.map(px=>{
                        if(px.id!==p.id)return px;
                        const uid=currentUser?.id||"me";
                        const cur=(px.reactions||{})[emoji]||[];
                        const next=mine?cur.filter(x=>x!==uid):[...cur,uid];
                        return {...px,reactions:{...(px.reactions||{}),[emoji]:next}};
                      }))}
                        style={{display:"flex",alignItems:"center",gap:3,padding:"4px 8px",background:mine?`${primaryCol}22`:"rgba(255,255,255,0.05)",border:`1px solid ${mine?primaryCol:"rgba(255,255,255,0.08)"}`,borderRadius:20,cursor:"pointer",fontSize:14,color:mine?"#fff":"rgba(255,255,255,0.7)"}}>
                        {emoji}{count>0&&<span style={{fontSize:11,fontWeight:700}}>{count}</span>}
                      </button>
                    );
                  })}
                </div>
                <button onClick={()=>setOpenComments(o=>({...o,[p.id]:!o[p.id]}))}
                  style={{background:"none",border:"none",display:"flex",alignItems:"center",gap:5,fontSize:12,color:"rgba(255,255,255,0.45)",cursor:"pointer"}}>
                  💬 {(p.comments||[]).length} comment{(p.comments||[]).length!==1?"s":""}
                </button>
              </div>
              {/* Comments section */}
              {commentsOpen&&(
                <div style={{borderTop:"1px solid rgba(255,255,255,0.06)",padding:"10px 14px"}}>
                  {(p.comments||[]).map(c=>(
                    <div key={c.id} style={{display:"flex",gap:8,marginBottom:10}}>
                      <Avatar name={c.author} size={28} colour={avCol(c.author)}/>
                      <div style={{flex:1,background:"rgba(255,255,255,0.04)",borderRadius:10,padding:"7px 11px"}}>
                        <div style={{fontSize:12,fontWeight:600,color:"#fff",marginBottom:2}}>{c.author} <span style={{color:"rgba(255,255,255,0.35)",fontWeight:400,fontSize:10}}>{timeSince(c.time)}</span></div>
                        <div style={{fontSize:13,color:"rgba(255,255,255,0.8)",lineHeight:1.5}}>{c.text}</div>
                      </div>
                      {(isAdmin||currentUser?.id===c.authorId)&&<button onClick={()=>setPosts(ps=>ps.map(px=>px.id===p.id?{...px,comments:px.comments.filter(cx=>cx.id!==c.id)}:px))} style={{background:"none",border:"none",color:"rgba(255,255,255,0.2)",cursor:"pointer",alignSelf:"flex-start",fontSize:12}}>✕</button>}
                    </div>
                  ))}
                  <div style={{display:"flex",gap:8,marginTop:6}}>
                    <Avatar name={currentUser?.name||""} size={28} colour={primaryCol}/>
                    <div style={{flex:1,display:"flex",gap:6}}>
                      <input value={draft} onChange={e=>setCommentDraft(d=>({...d,[p.id]:e.target.value}))}
                        onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();addComment(p.id);}}}
                        style={{...G.input,flex:1,padding:"7px 12px",fontSize:13,marginBottom:0}} placeholder="Write a comment..."/>
                      <button onClick={()=>addComment(p.id)} style={{...G.btnPrimary,background:primaryCol,padding:"7px 14px",fontSize:13,width:"auto"}}>↑</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };



  // ── AI MESOCYCLE GENERATOR ─────────────────────────────────────────────────
  const generateMesocycle = async () => {
    if (!framework) return;
    setMesoLoading(true);
    showToast("Generating week-by-week plan...");
    try {
      const block = framework.blocks?.[selectedBlock];
      if (!block) { setMesoLoading(false); return; }
      const sportStr = club.sport==="Hockey" ? "Field Hockey (played on astroturf/grass — NOT ice hockey. Use sticks, penalty corners, 16-yard hits. All drills must be field hockey specific)" : club.sport;
      const prompt = `You are an elite ${sportStr} coach and periodisation specialist. Generate a detailed week-by-week training plan for this block.

SPORT: ${sportStr}
TEAM: ${frameworkTeam||"Squad"}
SQUAD DESCRIPTION: ${wizardData.squadDesc||"Mixed ability"}
BLOCK: ${block.title}
THEME: ${block.theme}
PHASE: ${block.phase}
COACHING FOCUS - Technical: ${block.coachingFocus?.technical||block.focus||""}
COACHING FOCUS - Tactical: ${block.coachingFocus?.tactical||""}
COACHING FOCUS - Physical: ${block.coachingFocus?.physical||""}
COACHING FOCUS - Psychological: ${block.coachingFocus?.psychological||""}
SESSIONS PER WEEK: ${wizardData.sessions}
SESSION LENGTH: ${wizardData.sessionLen} minutes
BLOCK LENGTH: ${wizardData.blockLength} weeks
GAME APPLICATION: ${block.gameBasedApplication||""}
COACHING LANGUAGE: ${(block.coachingLanguage||[]).join(", ")||""}
SEASON GOALS: ${(wizardData.goals||[]).join(", ")||""}

Generate exactly ${wizardData.blockLength} weeks of sessions. Each session must include field-hockey-specific drills, warmup, main session and cool down. Coaching points must reference the block theme directly.

Respond ONLY with valid JSON:
{"weeks":[{"week":1,"theme":"Week theme","objective":"What players will achieve by end of week","sessions":[{"day":"Tuesday","title":"Session title","duration":${wizardData.sessionLen},"warmup":"Dynamic warmup with ball work (10 mins)","main":"Main session description (${wizardData.sessionLen-25} mins)","cooldown":"Cool down and debrief (10 mins)","drills":["Sport-specific drill 1","Sport-specific drill 2","Sport-specific drill 3"],"drillShape":"rondo","coachingPoints":["Specific point 1 referencing block theme","Specific point 2","Specific point 3"],"_diagramDetail":{"shape":"rondo","playerCount":8,"zones":["circle","middle"],"movements":["pass around","central pressure"],"ballStart":"outside","setup":"Short description of exact player positions for diagram generation"}}]}]}

drillShape must be ONE of: penalty_corner, rondo, one_v_one, press, channels, small_sided, passing_pattern, shooting, defensive_shape, transition, penalty_stroke.

_diagramDetail is used ONLY for diagram generation — not shown to the user. Be specific: describe exact positions (e.g. "4 defenders in a 4-4-2 shape, ball on left flank, pressing trigger set"), player counts, movement directions, zones used on the pitch. This enables accurate visual diagrams to be generated from your session description.`;
      const res = await supabase.functions.invoke("ai-framework", {
        body: { prompt, max_tokens: 4000 }
      });
      const data = res.data;
      const text = data.content?.find(c=>c.type==="text")?.text||"";
      const parsed = JSON.parse(text.replace(/```json|```/g,"").trim());
      setMesocycle(prev=>({...prev,[selectedBlock]:parsed}));
      showToast("Week-by-week plan ready ✓");
    } catch(e) {
      const block = framework.blocks?.[selectedBlock];
      const days = ["Tuesday","Thursday"].slice(0,wizardData.sessions||2);
      const fallback = {weeks:Array.from({length:wizardData.blockLength||6},(_,wi)=>({
        week:wi+1, theme:`Week ${wi+1} — ${block?.title||"Training"}`,
        objective:`Build on ${block?.focus||"core skills"} through progressive work`,
        sessions:days.map((day,si)=>({
          day, title:si===0?"Technical Session":"Tactical Session",
          duration:wizardData.sessionLen||75,
          warmup:"Dynamic warm-up and ball work (10 mins)",
          main:`${block?.keySessions?.[si%3]||"Drill-based training"} (${(wizardData.sessionLen||75)-25} mins)`,
          cooldown:"Cool-down and debrief (10-15 mins)",
          drills:["Passing patterns","Small-sided game","Tactical shape"],
          coachingPoints:["Communication","Movement","Decision making"]
        }))
      }))};
      setMesocycle(prev=>({...prev,[selectedBlock]:fallback}));
      showToast("Plan generated ✓");
    }
    setMesoLoading(false);
  };

  // ── SWIMMING FRAMEWORK ────────────────────────────────────────────────────
  // Squad levels with distinct aims and training approaches
  const SWIM_SQUAD_LEVELS = {
    starters: {
      label:"Starters", emoji:"🌊",
      desc:"Fun, water confidence, learning to compete",
      color:"#38bdf8",
      defaultGoals:["Enjoy swimming","Develop water confidence","Learn all four strokes","Complete first gala","Progress through club stages"],
      defaultFocus:["Freestyle technique","Backstroke technique","Water confidence","Fun activities","Learning starts & turns"],
      phaseNames:["Foundation","Skill Building","First Competition","Consolidation"],
      sessionFocus:"Technique drills and fun games — keep it enjoyable",
      zonesNote:"Mostly Zone 1-2 — low intensity, high fun factor",
    },
    development: {
      label:"Development", emoji:"📈",
      desc:"Aerobic base, technique refinement, first galas",
      color:"#22c55e",
      defaultGoals:["Build aerobic base","Improve stroke technique","Achieve first PBs","Compete regularly","Develop race awareness"],
      defaultFocus:["Stroke technique","Turns & walls","Aerobic endurance","Pacing awareness","Starts"],
      phaseNames:["Base Building","Technique Focus","Gala Preparation","End of Season"],
      sessionFocus:"Long aerobic sets with technique emphasis — quality over quantity",
      zonesNote:"Primarily Zone 2 with technique at Zone 1 — build the engine first",
    },
    performance: {
      label:"Performance", emoji:"🎯",
      desc:"PBs, regional qualifying times, race strategy",
      color:"#f0b429",
      defaultGoals:["Achieve PBs in all events","Hit regional qualifying times","Develop race strategy","Improve race turns","Compete at regional championships"],
      defaultFocus:["Race pace training","Threshold sets","Turns & underwater phases","Race strategy","Event-specific sets"],
      phaseNames:["Base","Build","Pre-Competition","Taper","Competition","Recovery"],
      sessionFocus:"CSS and threshold sets — race pace with tactical awareness",
      zonesNote:"Progressive zone loading — Z2 base with increasing Z3-Z4 as competition approaches",
    },
    elite: {
      label:"Elite", emoji:"🏆",
      desc:"National times, full BPM periodisation, peak performance",
      color:"#E63946",
      defaultGoals:["Hit national qualifying times","Compete at national championships","Achieve podium finishes","Develop race-specific fitness","Peak for target competition"],
      defaultFocus:["VO2 max sets","Race-specific intervals","Anaerobic capacity","Mental performance","Taper management"],
      phaseNames:["Base","Build","Specific","Peak","Taper","Competition","Recovery"],
      sessionFocus:"Full periodised programme — BPM zones drive every session",
      zonesNote:"All five zones used strategically — full periodisation model",
    },
    masters: {
      label:"Masters", emoji:"🏅",
      desc:"Fitness, personal goals, event-specific training",
      color:"#a855f7",
      defaultGoals:["Maintain fitness","Compete at masters events","Achieve age-group PBs","Enjoy the sport","Stay injury free"],
      defaultFocus:["Aerobic maintenance","Technique refinement","Event-specific sets","Recovery focus","Flexibility"],
      phaseNames:["Fitness Base","Event Focus","Competition Prep","Recovery"],
      sessionFocus:"Varied sessions balancing fitness and enjoyment — recovery prioritised",
      zonesNote:"Z1-Z3 dominant — intensity adjusted to individual fitness levels",
    },
  };

  const generateSwimFramework = async () => {
    setWizardLoading(true);
    showToast("Building your swimming framework...");
    const d = swimWizardData;
    const level = SWIM_SQUAD_LEVELS[d.level] || SWIM_SQUAD_LEVELS.performance;
    const maxHR = d.maxHR || 190;
    const zones = [
      {z:1,name:"Recovery",    pct:"60-70%",bpm:`${Math.round(maxHR*0.6)}-${Math.round(maxHR*0.7)}`,col:"#38bdf8",desc:"Easy aerobic, technique focus"},
      {z:2,name:"Aerobic Base",pct:"70-80%",bpm:`${Math.round(maxHR*0.7)}-${Math.round(maxHR*0.8)}`,col:"#22c55e",desc:"Main aerobic development"},
      {z:3,name:"Threshold",   pct:"80-90%",bpm:`${Math.round(maxHR*0.8)}-${Math.round(maxHR*0.9)}`,col:"#f0b429",desc:"Lactate threshold, CSS pace"},
      {z:4,name:"VO₂ Max",     pct:"90-95%",bpm:`${Math.round(maxHR*0.9)}-${Math.round(maxHR*0.95)}`,col:"#e85d04",desc:"High-intensity intervals"},
      {z:5,name:"Anaerobic",   pct:"95-100%",bpm:`${Math.round(maxHR*0.95)}-${maxHR}`,col:"#E63946",desc:"Sprint work, max effort"},
    ];
    try {
      const prompt = `You are an elite swimming coach. Build a periodised training framework.

SQUAD LEVEL: ${level.label} — ${level.desc}
SQUAD: ${d.ageGroup||"General squad"}
TARGET COMPETITION: ${d.targetComp||"Club championships"}
TARGET DATE: ${d.targetDate||"End of season"}
TARGET EVENTS: ${d.targetEvents.join(", ")||"Various"}
SESSIONS PER WEEK: ${d.sessionsPerWeek}
SESSION LENGTH: ${d.sessionLength} mins
MAX HR: ${maxHR} BPM
GOALS: ${d.annualGoals.join(", ")||level.defaultGoals.join(", ")}
FOCUS: ${d.focusAreas.join(", ")||level.defaultFocus.join(", ")}

Training zones: Z1 ${zones[0].bpm} | Z2 ${zones[1].bpm} | Z3 ${zones[2].bpm} | Z4 ${zones[3].bpm} | Z5 ${zones[4].bpm} BPM

Create a periodised annual plan appropriate for the ${level.label} squad level. 
For ${level.label}: ${level.sessionFocus}. ${level.zonesNote}.

Respond ONLY with valid JSON:
{
  "overview": "2-3 sentence summary tailored to this squad level",
  "phases": [
    {
      "number": 1,
      "name": "Phase name",
      "duration": "X weeks",
      "focus": "What this phase develops",
      "zonesSplit": {"z1":20,"z2":60,"z3":15,"z4":4,"z5":1},
      "keySession": "Example session description with distances and effort levels",
      "weeklyVolume": "XKm",
      "coachingPoints": ["Point 1","Point 2","Point 3"]
    }
  ]
}`;
      const res = await supabase.functions.invoke("ai-framework", {
        body: { prompt, max_tokens: 4000 }
      });
      const data = res.data;
      const text = data.content?.find(c=>c.type==="text")?.text||"";
      const parsed = JSON.parse(text.replace(/```json|```/g,"").trim());
      setSwimPlan({...parsed,zones,level:d.level,wizardData:d});
      if(clubId) db.saveSwimFramework(clubId,d.ageGroup||"General",d.level,{...parsed,zones,wizardData:d}).catch(console.error);
      setSwimWizardStep(0);showToast("Framework ready ✓");
    } catch(e) {
      // Level-appropriate fallback
      const fallbacks = {
        starters:{phases:[
          {number:1,name:"Water Confidence",duration:"8 weeks",focus:"Fun and technique foundations",zonesSplit:{z1:70,z2:25,z3:5,z4:0,z5:0},keySession:"Stroke drills and fun relay games — 25m repeats focusing on technique",weeklyVolume:"4-6km",coachingPoints:["Make it fun","Celebrate small wins","Correct technique early"]},
          {number:2,name:"First Gala Prep",duration:"4 weeks",focus:"Race awareness and competition confidence",zonesSplit:{z1:60,z2:30,z3:10,z4:0,z5:0},keySession:"Mock race situations — practice starts and finishes in a fun format",weeklyVolume:"5-7km",coachingPoints:["Race day routine","Positive self-talk","Enjoy the experience"]},
        ]},
        development:{phases:[
          {number:1,name:"Aerobic Foundation",duration:"10 weeks",focus:"Build the aerobic engine",zonesSplit:{z1:20,z2:65,z3:12,z4:3,z5:0},keySession:"400m-600m aerobic sets at steady Zone 2 pace — focus on stroke efficiency",weeklyVolume:"12-16km",coachingPoints:["Technique over speed","Consistent pace","Build volume gradually"]},
          {number:2,name:"Technique & Speed",duration:"6 weeks",focus:"Refine strokes and introduce race pace",zonesSplit:{z1:15,z2:55,z3:22,z4:7,z5:1},keySession:"CSS sets and 200m repeats — threshold pace with technique focus",weeklyVolume:"14-18km",coachingPoints:["Race pace awareness","Turn improvement","Underwater phases"]},
          {number:3,name:"Competition",duration:"4 weeks",focus:"Peak and race",zonesSplit:{z1:20,z2:45,z3:22,z4:10,z5:3},keySession:"Race simulation sets — target event distances at race pace",weeklyVolume:"12-14km",coachingPoints:["Trust the training","Race strategy","PB focus"]},
        ]},
        performance:{phases:[
          {number:1,name:"Base Phase",duration:"8 weeks",focus:"High volume aerobic foundation",zonesSplit:{z1:15,z2:65,z3:15,z4:4,z5:1},keySession:"800m-1500m aerobic sets at Zone 2 — quality over intensity",weeklyVolume:"18-24km",coachingPoints:["Build volume","Stroke efficiency","Establish habits"]},
          {number:2,name:"Build Phase",duration:"8 weeks",focus:"Threshold development",zonesSplit:{z1:10,z2:50,z3:28,z4:10,z5:2},keySession:"CSS sets and threshold intervals — 200m repeats at Zone 3",weeklyVolume:"20-26km",coachingPoints:["Race pace introduction","Lactate tolerance","Push threshold"]},
          {number:3,name:"Peak Phase",duration:"4 weeks",focus:"Race-specific conditioning",zonesSplit:{z1:10,z2:35,z3:25,z4:22,z5:8},keySession:"Race-pace intervals — target event distances at Zone 4-5",weeklyVolume:"22km",coachingPoints:["Race simulation","Event-specific pace","Confidence"]},
          {number:4,name:"Taper",duration:"2 weeks",focus:"Freshness and race readiness",zonesSplit:{z1:20,z2:40,z3:22,z4:14,z5:4},keySession:"Short sharp race-pace efforts — 50m sprints at Zone 5",weeklyVolume:"12km",coachingPoints:["Reduce volume","Maintain intensity","Mental prep"]},
          {number:5,name:"Recovery",duration:"2 weeks",focus:"Rebuild for next cycle",zonesSplit:{z1:60,z2:35,z3:5,z4:0,z5:0},keySession:"Easy technique swimming",weeklyVolume:"10km",coachingPoints:["Active recovery","Reflect on season","Plan next cycle"]},
        ]},
        elite:{phases:[
          {number:1,name:"General Prep",duration:"6 weeks",focus:"Aerobic base and volume",zonesSplit:{z1:10,z2:65,z3:18,z4:6,z5:1},keySession:"High volume aerobic sets — 1500m-3000m continuous at Zone 2",weeklyVolume:"25-35km",coachingPoints:["Volume over intensity","Technique under fatigue","Build the engine"]},
          {number:2,name:"Specific Prep",duration:"8 weeks",focus:"Race-specific conditioning and VO₂",zonesSplit:{z1:8,z2:45,z3:28,z4:16,z5:3},keySession:"VO₂ max intervals and CSS sets — 400m repeats at Zone 3-4",weeklyVolume:"28-35km",coachingPoints:["Race-pace integration","Anaerobic capacity","Tactical development"]},
          {number:3,name:"Competition Prep",duration:"4 weeks",focus:"Peak fitness and race sharpness",zonesSplit:{z1:8,z2:35,z3:27,z4:22,z5:8},keySession:"Race-simulation sets at target pace — 50m-200m at Zone 5",weeklyVolume:"25km",coachingPoints:["Race strategy","Mental conditioning","Confidence building"]},
          {number:4,name:"Taper",duration:"2-3 weeks",focus:"Peak for competition",zonesSplit:{z1:20,z2:38,z3:22,z4:16,z5:4},keySession:"Short activation sets — 6x50m at race pace with full recovery",weeklyVolume:"14km",coachingPoints:["Trust the taper","Sleep and nutrition","Visualisation"]},
          {number:5,name:"Competition",duration:"1-2 weeks",focus:"Race performance",zonesSplit:{z1:35,z2:40,z3:18,z4:5,z5:2},keySession:"Activation only — warm up and race-pace priming",weeklyVolume:"8km",coachingPoints:["Race execution","Trust training","PB mindset"]},
          {number:6,name:"Recovery",duration:"2 weeks",focus:"Physical and mental reset",zonesSplit:{z1:65,z2:30,z3:5,z4:0,z5:0},keySession:"Easy technique and fun sets",weeklyVolume:"10km",coachingPoints:["Full recovery","Reflect on performance","Plan next macrocycle"]},
        ]},
        masters:{phases:[
          {number:1,name:"Fitness Base",duration:"8 weeks",focus:"Aerobic maintenance and technique",zonesSplit:{z1:30,z2:55,z3:12,z4:3,z5:0},keySession:"Mixed stroke aerobic sets — 300m-400m repeats with technique focus",weeklyVolume:"10-15km",coachingPoints:["Listen to your body","Technique is your friend","Consistency over intensity"]},
          {number:2,name:"Event Focus",duration:"8 weeks",focus:"Event-specific preparation",zonesSplit:{z1:20,z2:48,z3:24,z4:7,z5:1},keySession:"Event-specific sets — race-pace intervals at controlled effort",weeklyVolume:"12-16km",coachingPoints:["Specific event training","Race pace awareness","Recovery between sets"]},
          {number:3,name:"Competition",duration:"4 weeks",focus:"Race readiness",zonesSplit:{z1:25,z2:42,z3:22,z4:9,z5:2},keySession:"Race simulation and tapering sets",weeklyVolume:"10km",coachingPoints:["Enjoy it","Trust your fitness","Age-group PB focus"]},
        ]},
      };
      const fb = fallbacks[d.level]||fallbacks.performance;
      setSwimPlan({...fb,zones,level:d.level,overview:`${level.label} framework for ${d.ageGroup||"the squad"}. ${level.sessionFocus}.`,targetComp:d.targetComp,targetDate:d.targetDate,wizardData:d});
      setSwimWizardStep(0);showToast("Framework built ✓");
    }
    setWizardLoading(false);
  };

  // ── INDIVIDUAL ATHLETE PROGRAMME ──────────────────────────────────────────
  const generateAthleteProgram = async (athlete, programData) => {
    if (!athlete) return null;
    const maxHR = programData.maxHR || 190;
    try {
      const prompt = `You are a swimming coach creating a personalised programme.

ATHLETE: ${athlete.name}
DISCIPLINE: ${athlete.position||"General"}
SPECIALIST EVENTS: ${(athlete.events||[]).join(", ")||"Not specified"}
CURRENT PBs: ${JSON.stringify(programData.currentPBs||{})}
GOALS: ${(programData.goals||[]).join(", ")}
SESSIONS PER WEEK: ${programData.sessionsPerWeek||4}
SESSION LENGTH: ${programData.sessionLength||75} mins
MAX HR: ${maxHR} BPM
FOCUS: ${(programData.focusAreas||[]).join(", ")||"General improvement"}

Create a personal 6-week programme. Include:
- Daily session plans (only on training days)
- BPM zones for each set
- Specific distances and reps
- Individual coaching points for this athlete

Respond ONLY with valid JSON:
{
  "overview": "Personal overview for this athlete",
  "weeks": [
    {
      "week": 1,
      "theme": "Week theme",
      "sessions": [
        {
          "day": "Monday",
          "title": "Session title",
          "duration": 75,
          "sets": [
            {"desc": "400m warm up easy","zone": 1,"bpm": "${Math.round(maxHR*0.65)}"},
            {"desc": "8x50m on 60s at threshold","zone": 3,"bpm": "${Math.round(maxHR*0.85)}"}
          ],
          "totalDistance": "3.2km",
          "coachingPoints": ["Point for this athlete specifically"]
        }
      ]
    }
  ]
}`;
      const res = await supabase.functions.invoke("ai-framework", {
        body: { prompt, max_tokens: 4000 }
      });
      const data = res.data;
      const text = data.content?.find(c=>c.type==="text")?.text||"";
      return JSON.parse(text.replace(/```json|```/g,"").trim());
    } catch(e) { return null; }
  };

  // ── SWIM WIZARD VIEW ──────────────────────────────────────────────────────
  const SwimWizardView = () => {
    const d = swimWizardData;
    const set = (key,val) => setSwimWizardData(s=>({...s,[key]:val}));
    const ftype = d.frameworkType || null;
    const level = SWIM_SQUAD_LEVELS[d.level] || SWIM_SQUAD_LEVELS.performance;
    const totalSteps = ftype==="individual" ? 1 : ftype==="group" ? 4 : 1;
    const SWIM_EVENTS = ["50m Freestyle","100m Freestyle","200m Freestyle","400m Freestyle","800m Freestyle","1500m Freestyle","50m Backstroke","100m Backstroke","200m Backstroke","50m Breaststroke","100m Breaststroke","200m Breaststroke","50m Butterfly","100m Butterfly","200m Butterfly","200m IM","400m IM","4x100m Relay","4x200m Relay"];

    return (
      <div key={`swim-wiz-${swimWizardStep}-${d.frameworkType||"none"}`} style={{display:"flex",flexDirection:"column",padding:"0 4px"}}>
        <div style={{height:3,background:"rgba(255,255,255,0.06)",marginBottom:0}}>
          <div style={{height:"100%",background:primaryCol,width:`${(swimWizardStep/totalSteps)*100}%`,transition:"width 0.3s"}}/>
        </div>
        <div style={{display:"flex",alignItems:"center",padding:"10px 0 14px"}}>
          <button onClick={()=>setSwimWizardStep(s=>Math.max(0,s-1))} style={{background:"none",border:"none",color:"rgba(255,255,255,0.4)",fontSize:20,cursor:"pointer",visibility:swimWizardStep===0?"hidden":"visible"}}>←</button>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.38)",letterSpacing:2,textTransform:"uppercase",marginLeft:"auto"}}>
            {d.frameworkType ? `Step ${swimWizardStep} of ${ftype==="group"?4:1}` : "Choose type"}
          </div>
        </div>
        <div style={{overflowY:"auto"}}>

          {/* TYPE SELECTOR — shown first if not yet chosen */}
          {swimWizardStep===1&&!d.frameworkType&&(
            <>
              <div style={G.h2}>What are you creating?</div>
              <div style={{fontSize:13,color:"rgba(255,255,255,0.4)",margin:"6px 0 16px",lineHeight:1.6}}>Choose whether this framework is for a whole squad or a single athlete.</div>
              {[
                {k:"group",     emoji:"👥", title:"Squad Framework",     desc:"Annual plan for an entire squad — Starters, Development, Performance, Elite or Masters"},
                {k:"individual",emoji:"👤", title:"Individual Programme", desc:"Personalised 6-week programme for a specific athlete, based on their events and PBs"},
              ].map(opt=>(
                <div key={opt.k} onClick={()=>set("frameworkType",opt.k)}
                  style={{display:"flex",alignItems:"center",gap:14,padding:"16px 14px",background:"rgba(255,255,255,0.04)",border:`2px solid rgba(255,255,255,0.08)`,borderRadius:12,cursor:"pointer",marginBottom:10,transition:"all 0.15s"}}>
                  <span style={{fontSize:28,flexShrink:0}}>{opt.emoji}</span>
                  <div>
                    <div style={{fontSize:15,fontWeight:700,color:"#fff",marginBottom:3}}>{opt.title}</div>
                    <div style={{fontSize:12,color:"rgba(255,255,255,0.45)",lineHeight:1.5}}>{opt.desc}</div>
                  </div>
                </div>
              ))}
            </>
          )}

          {/* INDIVIDUAL ATHLETE FAST-TRACK */}
          {swimWizardStep===1&&ftype==="individual"&&(
            <>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
                <button onClick={()=>set("frameworkType",null)} style={{background:"none",border:"none",color:"rgba(255,255,255,0.4)",fontSize:20,cursor:"pointer"}}>←</button>
                <div style={G.h2}>Individual Programme</div>
              </div>
              <FieldLabel>Select Athlete</FieldLabel>
              <select style={{...G.select,marginBottom:12}} value={d.athleteId||""} onChange={e=>{
                const pl=players.find(p=>p.id===e.target.value);
                set("athleteId",e.target.value);
                set("athleteName",pl?.name||"");
                set("targetEvents",pl?.events||[]);
              }}>
                <option value="">Choose athlete...</option>
                {players.map(p=><option key={p.id} value={p.id}>{p.name} {p.team?`(${p.team})`:""}</option>)}
              </select>
              {d.athleteId&&players.find(p=>p.id===d.athleteId)&&(()=>{
                const pl=players.find(p=>p.id===d.athleteId);
                return (
                  <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:`${primaryCol}12`,border:`1px solid ${primaryCol}33`,borderRadius:10,marginBottom:14}}>
                    <Avatar name={pl.name} photo={pl.photo} size={36} colour={avCol(pl.name)}/>
                    <div>
                      <div style={{fontSize:13,fontWeight:600,color:"#fff"}}>{pl.name}</div>
                      <div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>{pl.position||"—"}{pl.events?.length?` · ${pl.events.slice(0,2).join(", ")}`:""}</div>
                    </div>
                  </div>
                );
              })()}
              <FieldLabel>Programme goals</FieldLabel>
              <div style={{display:"flex",gap:8,marginBottom:10}}>
                <input id="ind-goal" style={{...G.input,flex:1,marginBottom:0}} placeholder="e.g. Sub 1:00 100m Freestyle"/>
                <button style={G.btnSm} onClick={()=>{const el=document.getElementById("ind-goal");const v=el?.value?.trim();if(v){set("annualGoals",[...(d.annualGoals||[]),v]);el.value=""}}}>+ Add</button>
              </div>
              {(d.annualGoals||[]).map((g,i)=>(
                <div key={i} style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:"rgba(255,255,255,0.7)",marginBottom:5}}>
                  <span style={{color:primaryCol}}>→</span><span style={{flex:1}}>{g}</span>
                  <button onClick={()=>set("annualGoals",d.annualGoals.filter((_,j)=>j!==i))} style={{background:"none",border:"none",color:"rgba(255,255,255,0.3)",cursor:"pointer",fontSize:11}}>✕</button>
                </div>
              ))}
              <div style={{marginTop:14}}>
                <FieldLabel>Sessions per week</FieldLabel>
                <div style={{display:"flex",gap:5,marginBottom:12}}>
                  {[2,3,4,5,6].map(n=>(
                    <button key={n} onClick={()=>set("sessionsPerWeek",n)} style={{flex:1,padding:"8px 0",background:d.sessionsPerWeek===n?primaryCol:"rgba(255,255,255,0.04)",border:`1px solid ${d.sessionsPerWeek===n?primaryCol:"rgba(255,255,255,0.08)"}`,borderRadius:6,color:d.sessionsPerWeek===n?"#fff":"rgba(255,255,255,0.5)",fontWeight:700,fontSize:13,cursor:"pointer"}}>{n}</button>
                  ))}
                </div>
              </div>
              {wizardLoading
                ? <div style={{textAlign:"center",padding:"20px 0"}}><div style={{fontSize:28,marginBottom:8}}>⏳</div><div style={{fontSize:13,color:"rgba(255,255,255,0.4)"}}>Building personal programme...</div></div>
                : <Btn col={primaryCol} onClick={async()=>{
                    if(!d.athleteId){showToast("Please select an athlete");return;}
                    setWizardLoading(true);
                    const pl=players.find(p=>p.id===d.athleteId);
                    const prog = await generateAthleteProgram(pl,{goals:d.annualGoals||[],sessionsPerWeek:d.sessionsPerWeek||4,sessionLength:d.sessionLength||75,maxHR:d.maxHR||190,currentPBs:d.currentPBs||{}});
                    if(prog){
                      setAthletePrograms(ap=>({...ap,[d.athleteId]:prog}));
                      if(clubId) db.saveProgram(clubId,d.athleteId,"Swimming",prog).catch(console.error);
                      showToast(`Personal programme created for ${pl?.name} ✓`);
                      setSwimWizardStep(0);
                      set("frameworkType",null);
                    } else {
                      showToast("Could not generate — try again");
                    }
                    setWizardLoading(false);
                  }}>🏊 Generate Personal Programme</Btn>}
            </>
          )}

          {/* STEP 1 — Squad level + group */}
          {swimWizardStep===1&&ftype==="group"&&(
            <>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
                <button onClick={()=>set("frameworkType",null)} style={{background:"none",border:"none",color:"rgba(255,255,255,0.4)",fontSize:20,cursor:"pointer"}}>←</button>
                <div style={G.h2}>Squad Level & Goals</div>
              </div>
              <div style={{fontSize:13,color:"rgba(255,255,255,0.4)",margin:"6px 0 14px"}}>Select the squad level — each has different aims and training approaches.</div>
              <FieldLabel>Squad level</FieldLabel>
              <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:16}}>
                {Object.entries(SWIM_SQUAD_LEVELS).map(([k,v])=>{
                  const sel=d.level===k;
                  return (
                    <div key={k} onClick={()=>set("level",k)}
                      style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",background:sel?`${v.color}18`:"rgba(255,255,255,0.03)",border:`1.5px solid ${sel?v.color:"rgba(255,255,255,0.08)"}`,borderRadius:10,cursor:"pointer"}}>
                      <span style={{fontSize:20}}>{v.emoji}</span>
                      <div style={{flex:1}}>
                        <div style={{fontSize:14,fontWeight:sel?700:500,color:sel?"#fff":"rgba(255,255,255,0.8)"}}>{v.label}</div>
                        <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",marginTop:1}}>{v.desc}</div>
                      </div>
                      {sel&&<div style={{width:18,height:18,borderRadius:"50%",background:v.color,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:"#fff"}}>✓</div>}
                    </div>
                  );
                })}
              </div>
              <FieldLabel>Which squad / age group?</FieldLabel>
              <select style={{...G.select,marginBottom:14}} value={d.ageGroup} onChange={e=>set("ageGroup",e.target.value)}>
                <option value="">Select squad...</option>
                {teams.map(t=><option key={t} value={t}>{t}</option>)}
              </select>
              {d.level&&(
                <>
                  <FieldLabel>Annual goals</FieldLabel>
                  <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:10}}>
                    {level.defaultGoals.map(g=>{
                      const sel=d.annualGoals.includes(g);
                      return <div key={g} onClick={()=>set("annualGoals",sel?d.annualGoals.filter(x=>x!==g):[...d.annualGoals,g])}
                        style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",background:sel?`${primaryCol}18`:"rgba(255,255,255,0.03)",border:`1.5px solid ${sel?primaryCol:"rgba(255,255,255,0.08)"}`,borderRadius:9,cursor:"pointer"}}>
                        <div style={{width:16,height:16,borderRadius:3,border:`2px solid ${sel?primaryCol:"rgba(255,255,255,0.25)"}`,background:sel?primaryCol:"transparent",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:"#fff",flexShrink:0}}>{sel?"✓":""}</div>
                        <div style={{fontSize:13,color:sel?"#fff":"rgba(255,255,255,0.7)",fontWeight:sel?600:400}}>{g}</div>
                      </div>;
                    })}
                  </div>
                  <div style={{display:"flex",gap:8}}>
                    <input id="sg" style={{...G.input,flex:1,marginBottom:0}} placeholder="Add your own goal..."/>
                    <button style={G.btnSm} onClick={()=>{const el=document.getElementById("sg");const v=el?.value?.trim();if(v){set("annualGoals",[...d.annualGoals,v]);el.value=""}}}>+ Add</button>
                  </div>
                </>
              )}

            </>
          )}

          {/* STEP 2 — Target events + competition */}
          {ftype==="group"&&swimWizardStep===5&&(
            <>
              <div style={G.h2}>Competition & Events</div>
              <div style={{fontSize:13,color:"rgba(255,255,255,0.4)",margin:"6px 0 14px"}}>What are you building towards?</div>
              <Input label="Primary competition target" placeholder="e.g. NW Regional Championships" value={d.targetComp} onChange={e=>set("targetComp",e.target.value)}/>
              <Input label="Target competition date" type="date" value={d.targetDate} onChange={e=>set("targetDate",e.target.value)}/>
              <FieldLabel>Target events</FieldLabel>
              <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:14}}>
                {SWIM_EVENTS.map(ev=>{
                  const sel=d.targetEvents.includes(ev);
                  return <div key={ev} onClick={()=>set("targetEvents",sel?d.targetEvents.filter(x=>x!==ev):[...d.targetEvents,ev])}
                    style={{padding:"5px 12px",background:sel?`${primaryCol}22`:"rgba(255,255,255,0.04)",border:`1px solid ${sel?primaryCol:"rgba(255,255,255,0.1)"}`,borderRadius:20,fontSize:12,color:sel?primaryCol:"rgba(255,255,255,0.6)",cursor:"pointer",fontWeight:sel?600:400}}>
                    {ev}
                  </div>;
                })}
              </div>
              {d.targetEvents.length>0&&(
                <>
                  <FieldLabel>Squad benchmark PBs (optional)</FieldLabel>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",marginBottom:8}}>Helps AI set realistic targets</div>
                  {d.targetEvents.slice(0,4).map(ev=>(
                    <div key={ev} style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                      <div style={{flex:1,fontSize:12,color:"rgba(255,255,255,0.7)"}}>{ev}</div>
                      <input placeholder="e.g. 1:02.34" style={{width:90,padding:"6px 10px",background:"rgba(0,0,0,0.3)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:7,color:"#fff",fontSize:12}} value={d.currentPBs[ev]||""} onChange={e=>set("currentPBs",{...d.currentPBs,[ev]:e.target.value})}/>
                    </div>
                  ))}
                </>
              )}
            </>
          )}

          {/* STEP 3 — Training setup + BPM zones */}
          {ftype==="group"&&swimWizardStep===2&&(
            <>
              <div style={G.h2}>Training Setup</div>
              <div style={{fontSize:13,color:"rgba(255,255,255,0.4)",margin:"6px 0 14px"}}>Sessions and BPM zones for this squad.</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
                <div>
                  <FieldLabel>Sessions per week</FieldLabel>
                  <div style={{display:"flex",gap:4}}>
                    {[2,3,4,5,6,7,8].map(n=>(
                      <button key={n} onClick={()=>set("sessionsPerWeek",n)} style={{flex:1,padding:"7px 0",background:d.sessionsPerWeek===n?primaryCol:"rgba(255,255,255,0.04)",border:`1px solid ${d.sessionsPerWeek===n?primaryCol:"rgba(255,255,255,0.08)"}`,borderRadius:6,color:d.sessionsPerWeek===n?"#fff":"rgba(255,255,255,0.5)",fontWeight:700,fontSize:12,cursor:"pointer"}}>{n}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <FieldLabel>Session length</FieldLabel>
                  <select style={{...G.select,marginBottom:0}} value={d.sessionLength} onChange={e=>set("sessionLength",parseInt(e.target.value))}>
                    {[45,60,75,90,105,120].map(l=><option key={l} value={l}>{l} mins</option>)}
                  </select>
                </div>
              </div>
              <FieldLabel>Estimated max heart rate (BPM)</FieldLabel>
              <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",marginBottom:8}}>Use 220 minus average age for this squad</div>
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
                <input type="range" min={160} max={210} value={d.maxHR} onChange={e=>set("maxHR",parseInt(e.target.value))} style={{flex:1,accentColor:primaryCol}}/>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:800,color:primaryCol,minWidth:48}}>{d.maxHR}</div>
              </div>
              <div style={{...G.card,marginBottom:14}}>
                <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:2,color:"rgba(255,255,255,0.38)",marginBottom:10}}>Training Zones for This Squad</div>
                {[{z:"Z1",n:"Recovery",lo:0.6,hi:0.7,col:"#38bdf8"},{z:"Z2",n:"Aerobic",lo:0.7,hi:0.8,col:"#22c55e"},{z:"Z3",n:"Threshold",lo:0.8,hi:0.9,col:"#f0b429"},{z:"Z4",n:"VO₂ Max",lo:0.9,hi:0.95,col:"#e85d04"},{z:"Z5",n:"Anaerobic",lo:0.95,hi:1.0,col:"#E63946"}].map(z=>(
                  <div key={z.z} style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:13,fontWeight:800,color:z.col,width:24}}>{z.z}</div>
                    <div style={{fontSize:12,color:"rgba(255,255,255,0.7)",flex:1}}>{z.n}</div>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:14,fontWeight:700,color:z.col}}>{Math.round(d.maxHR*z.lo)}-{Math.round(d.maxHR*z.hi)} BPM</div>
                  </div>
                ))}
              </div>
              <FieldLabel>Focus areas for this squad</FieldLabel>
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                {(d.level?SWIM_SQUAD_LEVELS[d.level]?.defaultFocus||[]:["Freestyle technique","Backstroke","Turns","Starts","Aerobic endurance","Race strategy"]).concat(["Butterfly technique","Breaststroke technique","IM","Underwater phases","Mental performance"]).map(f=>{
                  const sel=d.focusAreas.includes(f);
                  return <div key={f} onClick={()=>set("focusAreas",sel?d.focusAreas.filter(x=>x!==f):[...d.focusAreas,f])}
                    style={{padding:"5px 12px",background:sel?`${primaryCol}22`:"rgba(255,255,255,0.04)",border:`1px solid ${sel?primaryCol:"rgba(255,255,255,0.1)"}`,borderRadius:20,fontSize:12,color:sel?primaryCol:"rgba(255,255,255,0.6)",cursor:"pointer",fontWeight:sel?600:400}}>
                    {f}
                  </div>;
                })}
              </div>
            </>
          )}

          {/* STEP 4 — Review */}
          {ftype==="group"&&swimWizardStep===3&&(
            <>
              <div style={G.h2}>Ready to Build</div>
              <div style={{fontSize:13,color:"rgba(255,255,255,0.4)",margin:"6px 0 14px",lineHeight:1.6}}>AI will build a periodised framework tailored to your {level.label} squad.</div>
              {d.level&&<div style={{display:"flex",alignItems:"center",gap:10,padding:"12px 14px",background:`${level.color}18`,border:`1px solid ${level.color}44`,borderRadius:10,marginBottom:14}}>
                <span style={{fontSize:24}}>{level.emoji}</span>
                <div><div style={{fontSize:14,fontWeight:700,color:"#fff"}}>{level.label}</div><div style={{fontSize:12,color:"rgba(255,255,255,0.5)"}}>{level.desc}</div></div>
              </div>}
              <div style={{...G.card,marginBottom:14}}>
                {[["Squad",d.ageGroup||"Not set"],["Target",d.targetComp||"Not set"],["Date",d.targetDate?new Date(d.targetDate).toLocaleDateString("en-GB"):"Not set"],["Events",d.targetEvents.slice(0,2).join(", ")+(d.targetEvents.length>2?" +more":"")||"Not set"],["Sessions",`${d.sessionsPerWeek}x/week · ${d.sessionLength}mins`],["Max HR",`${d.maxHR} BPM`]].map(([l,v])=>(
                  <div key={l} style={{display:"flex",gap:12,padding:"6px 0",borderBottom:"1px solid rgba(255,255,255,0.05)",fontSize:13}}>
                    <div style={{color:"rgba(255,255,255,0.38)",minWidth:80}}>{l}</div>
                    <div style={{color:"rgba(255,255,255,0.8)",flex:1}}>{v}</div>
                  </div>
                ))}
              </div>
              {wizardLoading
                ? <div style={{textAlign:"center",padding:"24px 0"}}><div style={{fontSize:32,marginBottom:10}}>⏳</div><div style={{fontSize:13,color:"rgba(255,255,255,0.4)"}}>Building your swimming framework...</div></div>
                : <Btn col={primaryCol} onClick={generateSwimFramework}>🏊 Generate Framework</Btn>}
            </>
          )}
        </div>
        {ftype==="group"&&swimWizardStep>=1&&swimWizardStep<5&&<div style={{paddingTop:14}}><Btn col={primaryCol} onClick={()=>setSwimWizardStep(s=>s+1)}>Next →</Btn></div>}
      </div>
    );
  };

  // ── SWIM PLAN VIEW ────────────────────────────────────────────────────────
  const SwimPlanView = () => {
    const [selPhase, setSelPhase] = useState(0);
    const [showIndividual, setShowIndividual] = useState(false);
    const [selAthlete, setSelAthlete] = useState(null);
    const [athleteProgramData, setAthleteProgramData] = useState({goals:[],focusAreas:[],sessionsPerWeek:4,sessionLength:75,maxHR:190,currentPBs:{}});
    const [athleteProgram, setAthleteProgram] = useState(null);
    const [generatingAthlete, setGeneratingAthlete] = useState(false);
    if (!swimPlan) return null;
    const phase = swimPlan.phases?.[selPhase];
    const zones = swimPlan.zones||[];
    const ZONE_COLS = ["#38bdf8","#22c55e","#f0b429","#e85d04","#E63946"];
    const levelData = SWIM_SQUAD_LEVELS[swimPlan.level]||SWIM_SQUAD_LEVELS.performance;

    const ZoneBar = ({split}) => {
      const total = Object.values(split||{}).reduce((a,b)=>a+b,0)||100;
      return (
        <div style={{display:"flex",height:8,borderRadius:4,overflow:"hidden",marginBottom:4}}>
          {Object.entries(split||{}).map(([k,v],i)=>(
            <div key={k} style={{width:`${(v/total)*100}%`,background:ZONE_COLS[i]||"#fff",opacity:v>0?1:0}}/>
          ))}
        </div>
      );
    };

    return (
      <div style={G.panel}>
        {/* Header */}
        <div style={{...G.cardAccent(levelData.color),marginBottom:14}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
            <span style={{fontSize:20}}>{levelData.emoji}</span>
            <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:2,color:"rgba(255,255,255,0.5)"}}>{levelData.label} Framework · {swimPlan.wizardData?.ageGroup||""}</div>
          </div>
          <div style={{fontSize:13,color:"rgba(255,255,255,0.75)",lineHeight:1.6,marginBottom:8}}>{swimPlan.overview}</div>
          <div style={{display:"flex",gap:12,fontSize:12,flexWrap:"wrap"}}>
            {swimPlan.targetComp&&<span style={{color:levelData.color,fontWeight:700}}>🎯 {swimPlan.targetComp}</span>}
            {swimPlan.targetDate&&<span style={{color:"rgba(255,255,255,0.5)"}}>📅 {new Date(swimPlan.targetDate).toLocaleDateString("en-GB")}</span>}
          </div>
          <div style={{display:"flex",gap:8,marginTop:10}}>
            <button style={G.btnSm} onClick={()=>setSwimWizardStep(1)}>Rebuild</button>
            <button style={{...G.btnSm,background:showIndividual?`${primaryCol}22`:"none",border:`1px solid ${showIndividual?primaryCol:"rgba(255,255,255,0.2)"}`}} onClick={()=>setShowIndividual(!showIndividual)}>👤 Individual Programmes</button>
          </div>
        </div>

        {/* Individual athlete programme builder */}
        {showIndividual&&(
          <div style={{...G.card,marginBottom:14,border:`1px solid ${primaryCol}44`}}>
            <div style={{...G.h3,marginBottom:10}}>Individual Athlete Programme</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.45)",marginBottom:10,lineHeight:1.6}}>Create a personalised 6-week programme for a specific athlete, built around their events and PBs.</div>
            <FieldLabel>Select Athlete</FieldLabel>
            <select style={{...G.select,marginBottom:10}} value={selAthlete?.id||""} onChange={e=>{const p=players.find(pl=>pl.id===e.target.value);setSelAthlete(p||null);setAthleteProgram(null);}}>
              <option value="">Choose athlete...</option>
              {players.filter(p=>!swimPlan.wizardData?.ageGroup||p.team===swimPlan.wizardData.ageGroup).map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            {selAthlete&&(
              <>
                <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:"rgba(255,255,255,0.04)",borderRadius:8,marginBottom:12}}>
                  <Avatar name={selAthlete.name} photo={selAthlete.photo} size={36} colour={avCol(selAthlete.name)}/>
                  <div>
                    <div style={{fontSize:13,fontWeight:600,color:"#fff"}}>{selAthlete.name}</div>
                    <div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>{selAthlete.position||"—"}{selAthlete.events?.length?` · ${selAthlete.events.slice(0,2).join(", ")}`:""}</div>
                  </div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                  <div>
                    <FieldLabel>Sessions/week</FieldLabel>
                    <select style={{...G.select,marginBottom:0}} value={athleteProgramData.sessionsPerWeek} onChange={e=>setAthleteProgramData(d=>({...d,sessionsPerWeek:parseInt(e.target.value)}))}>
                      {[2,3,4,5,6].map(n=><option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div>
                    <FieldLabel>Session length</FieldLabel>
                    <select style={{...G.select,marginBottom:0}} value={athleteProgramData.sessionLength} onChange={e=>setAthleteProgramData(d=>({...d,sessionLength:parseInt(e.target.value)}))}>
                      {[45,60,75,90,120].map(l=><option key={l} value={l}>{l} mins</option>)}
                    </select>
                  </div>
                </div>
                <FieldLabel>Individual goals</FieldLabel>
                <div style={{display:"flex",gap:8,marginBottom:10}}>
                  <input id="ag-goal" style={{...G.input,flex:1,marginBottom:0}} placeholder="e.g. Sub 1:00 100m Freestyle"/>
                  <button style={G.btnSm} onClick={()=>{const el=document.getElementById("ag-goal");const v=el?.value?.trim();if(v){setAthleteProgramData(d=>({...d,goals:[...d.goals,v]}));el.value=""}}}>+ Add</button>
                </div>
                {athleteProgramData.goals.map((g,i)=>(
                  <div key={i} style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,fontSize:12,color:"rgba(255,255,255,0.7)"}}>
                    <span style={{color:primaryCol}}>→</span>{g}
                    <button onClick={()=>setAthleteProgramData(d=>({...d,goals:d.goals.filter((_,j)=>j!==i)}))} style={{background:"none",border:"none",color:"rgba(255,255,255,0.3)",cursor:"pointer",marginLeft:"auto",fontSize:11}}>✕</button>
                  </div>
                ))}
                <div style={{marginTop:10}}>
                  {generatingAthlete
                    ? <div style={{textAlign:"center",padding:"16px 0",fontSize:13,color:"rgba(255,255,255,0.4)"}}>⏳ Building personal programme...</div>
                    : <button style={{...G.btnPrimary,background:primaryCol,fontSize:13}} onClick={async()=>{
                        setGeneratingAthlete(true);
                        const prog = await generateAthleteProgram(selAthlete,athleteProgramData);
                        setAthleteProgram(prog);
                        setGeneratingAthlete(false);
                        if(!prog)showToast("Could not generate — try again");
                      }}>🏊 Generate Personal Programme</button>}
                </div>
                {/* Show athlete programme */}
                {athleteProgram&&(
                  <div style={{marginTop:14}}>
                    <div style={{fontSize:13,color:"rgba(255,255,255,0.75)",lineHeight:1.6,marginBottom:10,fontStyle:"italic"}}>{athleteProgram.overview}</div>
                    {athleteProgram.weeks?.map((week,wi)=>(
                      <div key={wi} style={{...G.card,marginBottom:8}}>
                        <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:15,fontWeight:800,color:"#fff",marginBottom:2}}>Week {week.week} — {week.theme}</div>
                        {week.sessions?.map((s,si)=>(
                          <div key={si} style={{marginBottom:8,paddingBottom:8,borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
                            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:12,fontWeight:800,color:primaryCol,minWidth:70}}>{s.day}</div>
                              <div style={{fontSize:13,fontWeight:600,color:"#fff",flex:1}}>{s.title}</div>
                              <div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>{s.totalDistance}</div>
                            </div>
                            {s.sets?.map((set,seti)=>(
                              <div key={seti} style={{display:"flex",gap:8,padding:"4px 8px",background:"rgba(255,255,255,0.03)",borderRadius:6,marginBottom:3,alignItems:"center"}}>
                                <div style={{width:6,height:6,borderRadius:"50%",background:ZONE_COLS[set.zone-1]||"#fff",flexShrink:0}}/>
                                <div style={{fontSize:11,color:"rgba(255,255,255,0.75)",flex:1}}>{set.desc}</div>
                                <div style={{fontSize:10,color:ZONE_COLS[set.zone-1]||"#fff",fontWeight:700,flexShrink:0}}>Z{set.zone} · {set.bpm}bpm</div>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* BPM zones */}
        <div style={{...G.card,marginBottom:14}}>
          <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:2,color:"rgba(255,255,255,0.38)",marginBottom:8}}>Training Zones</div>
          {zones.map((z,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:8,marginBottom:5}}>
              <div style={{width:6,height:6,borderRadius:"50%",background:ZONE_COLS[i],flexShrink:0}}/>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:12,fontWeight:800,color:ZONE_COLS[i],width:16}}>Z{z.z}</div>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.6)",flex:1}}>{z.name}</div>
              <div style={{fontSize:12,fontWeight:700,color:ZONE_COLS[i]}}>{z.bpm} BPM</div>
            </div>
          ))}
        </div>

        {/* Phase tabs */}
        <FieldLabel>Training Phase</FieldLabel>
        <div style={{display:"flex",gap:5,overflowX:"auto",paddingBottom:8,marginBottom:12}}>
          {swimPlan.phases?.map((p,i)=>(
            <button key={i} onClick={()=>setSelPhase(i)}
              style={{padding:"6px 12px",background:selPhase===i?primaryCol:"rgba(255,255,255,0.04)",border:`1px solid ${selPhase===i?primaryCol:"rgba(255,255,255,0.08)"}`,borderRadius:16,color:selPhase===i?"#fff":"rgba(255,255,255,0.45)",fontSize:11,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>
              {p.name}
            </button>
          ))}
        </div>

        {phase&&(
          <div style={G.card}>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:18,fontWeight:800,color:"#fff",marginBottom:2}}>{phase.name}</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",marginBottom:10}}>{phase.duration}{phase.weeklyVolume?` · ${phase.weeklyVolume}`:""}</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.75)",lineHeight:1.6,marginBottom:12}}>{phase.focus}</div>
            <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:1.5,color:"rgba(255,255,255,0.38)",marginBottom:6}}>Zone Split</div>
            <ZoneBar split={phase.zonesSplit}/>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:14}}>
              {Object.entries(phase.zonesSplit||{}).filter(([,v])=>v>0).map(([k,v],i)=>(
                <span key={k} style={{fontSize:11,color:ZONE_COLS[parseInt(k.replace("z",""))-1],fontWeight:700}}>Z{k.replace("z","")}: {v}%</span>
              ))}
            </div>
            <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:1.5,color:"rgba(255,255,255,0.38)",marginBottom:6}}>Key Session</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.8)",lineHeight:1.6,marginBottom:12,padding:"10px 12px",background:"rgba(255,255,255,0.04)",borderRadius:8}}>{phase.keySession}</div>
            <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:1.5,color:"rgba(255,255,255,0.38)",marginBottom:8}}>Coaching Points</div>
            {(phase.coachingPoints||[]).map((cp,i)=>(
              <div key={i} style={{display:"flex",gap:8,marginBottom:5,fontSize:13,color:"rgba(255,255,255,0.7)"}}>
                <span style={{color:primaryCol,flexShrink:0}}>→</span>{cp}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };


  const CoachPanel = () => {
    const blockMeso = mesocycle?.[selectedBlock];
    const weekData  = blockMeso?.weeks?.[selectedWeek ?? 0];

    const addSession = () => {
      const date = prompt("Session date (YYYY-MM-DD):");
      const title = prompt("Session title:");
      const time  = prompt("Time (e.g. 19:00):");
      if (!date || !title) return;
      setExtraSessions(es => [...es, {id:Date.now().toString(), date, title, time, type:"training", team: myTeams||"All"}]);
      setFixtures(fs => [...fs, {id:Date.now().toString()+1, opponent: title, date, time, location:"Training", team: myTeams||teams[0]||"All", result:null, availability:{}, isHome:true, isSession:true}]);
      showToast("Session added to calendar ✓");
    };

    return (
      <div style={G.panel}>
        {/* Tab bar */}
        <div style={{display:"flex",gap:6,marginBottom:18,overflowX:"auto",paddingBottom:4}}>
          {[{k:"macro",l:"📅 Macrocycle"},{k:"meso",l:"📋 Mesocycle"},{k:"micro",l:"🗓 Microcycle"}].map(t=>(
            <button key={t.k} onClick={()=>setCoachTab(t.k)}
              style={{padding:"8px 16px",background:coachTab===t.k?primaryCol:"rgba(255,255,255,0.04)",border:`1px solid ${coachTab===t.k?primaryCol:"rgba(255,255,255,0.08)"}`,borderRadius:20,color:coachTab===t.k?"#fff":"rgba(255,255,255,0.45)",fontSize:12,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>
              {t.l}
            </button>
          ))}
          <button onClick={addSession} style={{...G.btnSm,marginLeft:"auto",whiteSpace:"nowrap",flexShrink:0}}>+ Add Session</button>
        </div>

        {/* ── SWIMMING COACH PANEL ── */}
        {isSwimming&&(
          <>
            {swimWizardStep>0
              ? <SwimWizardView/>
              : swimPlan
                ? <SwimPlanView/>
                : <div style={{...G.center,padding:"40px 20px",gap:16}}>
                    <div style={{fontSize:40}}>🏊</div>
                    <div style={G.h2}>Set Up Swimming Framework</div>
                    <div style={{fontSize:13,color:"rgba(255,255,255,0.45)",textAlign:"center",maxWidth:300,lineHeight:1.6}}>
                      Create a squad framework or individual athlete programme with BPM training zones.
                    </div>
                    {isPro
                      ? <button style={{...G.btnPrimary,background:primaryCol,maxWidth:280}} onClick={()=>{setSwimWizardStep(1);setSwimWizardData(d=>({...d,frameworkType:null,annualGoals:[],targetEvents:[],focusAreas:[]}));}}>🏊 Start Framework Wizard</button>
                      : <button style={{...G.btnPrimary,background:primaryCol,maxWidth:280}} onClick={()=>setScreen("subscribe")}>⚡ Upgrade to unlock AI Wizard</button>
                    }
                  </div>}
          </>
        )}

        {/* ── WIZARD (overlays all tabs when active) ── */}
        {!isSwimming&&wizardStep>0&&wizardStep<99&&wizardStep!==98&&(isPro?<WizardView/>:<div style={{padding:4}}><ProGate feature="AI squad framework wizard"/></div>)}
        {!isSwimming&&wizardStep===98&&(
          <div key="indiv-prog" style={{padding:"4px",paddingBottom:100}}>
          {!isPro&&<ProGate feature="AI individual player programmes"/>}
          {isPro&&<>
            {/* Loading overlay */}
            {indivGenerating&&(
              <div style={{position:"fixed",inset:0,background:"rgba(8,8,16,0.97)",zIndex:999,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:32}}>
                <div style={{marginBottom:32,position:"relative",width:56,height:56}}>
                  <div style={{width:80,height:80,border:`3px solid ${primaryCol}22`,borderTop:`3px solid ${primaryCol}`,borderRadius:"50%",animation:"spin 1s linear infinite",position:"absolute",inset:-12}}/>
                  <div style={{width:56,height:56,display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,position:"relative"}}>🎯</div>
                </div>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:36,fontWeight:900,color:"#fff",letterSpacing:2,marginBottom:6,marginTop:16}}>P4M NEXUS</div>
                <div style={{fontSize:12,fontWeight:700,color:primaryCol,letterSpacing:3,textTransform:"uppercase",marginBottom:24}}>Building personal programme</div>
                {["Analysing player attributes...","Identifying key development areas...","Building sport-specific sessions...","Adding coaching points and drills..."].map((msg,i)=>(
                  <div key={i} style={{fontSize:13,color:"rgba(255,255,255,0.4)",marginBottom:8,animation:`fadeIn 0.5s ease ${i*0.7}s both`}}>{msg}</div>
                ))}
              </div>
            )}
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
              <button onClick={()=>setWizardStep(0)} style={{background:"none",border:"none",color:"rgba(255,255,255,0.4)",fontSize:22,cursor:"pointer"}}>←</button>
              <div>
                <div style={G.h2}>Individual Programme</div>
                <div style={{fontSize:12,color:"rgba(255,255,255,0.4)"}}>P4M Nexus · Personalised development plan</div>
              </div>
            </div>

            {/* Step 1: Select player */}
            <div style={{...G.card,marginBottom:12}}>
              <div style={{fontSize:11,fontWeight:700,color:primaryCol,letterSpacing:1.5,marginBottom:10}}>SELECT PLAYER</div>
              <select style={{...G.select,marginBottom:0}} value={indivAthleteId} onChange={e=>setIndivAthleteId(e.target.value)}>
                <option value="">Choose player...</option>
                {players.map(p=><option key={p.id} value={p.id}>{p.name} {p.team?`(${p.team})`:""}</option>)}
              </select>
            </div>

            {indivAthleteId&&(()=>{
              const pl=players.find(p=>p.id===indivAthleteId);
              if(!pl)return null;
              const scores=pl.scores||{};
              const allAttrs=Object.keys(scores).filter(k=>!k.startsWith("_")&&typeof scores[k]==="number");
              const rated=allAttrs.map(a=>({a,v:Math.round((scores[a]/255)*99)})).sort((a,b)=>a.v-b.v);
              const weakest=rated.slice(0,5);
              const strongest=rated.slice(-3).reverse();
              const fitnessKeys=Object.keys(pl.fitness||{});
              const existing=athletePrograms[pl.id];
              return (
                <>
                  {/* Player overview card */}
                  <div style={{...G.card,marginBottom:12,borderLeft:`3px solid ${primaryCol}`}}>
                    <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:10}}>
                      <Avatar name={pl.name} photo={pl.photo} size={44} colour={avCol(pl.name)}/>
                      <div style={{flex:1}}>
                        <div style={{fontSize:15,fontWeight:700,color:"#fff"}}>{pl.name}</div>
                        <div style={{fontSize:12,color:"rgba(255,255,255,0.45)"}}>{pl.position||"Position not set"} · {pl.team||"No team"}</div>
                        {rated.length>0&&<div style={{fontSize:12,color:primaryCol,fontWeight:700,marginTop:2}}>Overall: {Math.round(rated.reduce((s,r)=>s+r.v,0)/rated.length)}/99</div>}
                      </div>
                    </div>
                    {/* Strengths and weaknesses */}
                    {rated.length>0&&(
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                        <div style={{background:"rgba(34,197,94,0.08)",border:"1px solid rgba(34,197,94,0.2)",borderRadius:8,padding:"8px 10px"}}>
                          <div style={{fontSize:10,fontWeight:700,color:"#22c55e",letterSpacing:1,marginBottom:4}}>STRENGTHS</div>
                          {strongest.map(({a,v})=><div key={a} style={{fontSize:11,color:"rgba(255,255,255,0.7)",marginBottom:2}}>→ {a} ({v}/99)</div>)}
                        </div>
                        <div style={{background:"rgba(230,57,70,0.08)",border:"1px solid rgba(230,57,70,0.2)",borderRadius:8,padding:"8px 10px"}}>
                          <div style={{fontSize:10,fontWeight:700,color:"#E63946",letterSpacing:1,marginBottom:4}}>NEEDS WORK</div>
                          {weakest.slice(0,3).map(({a,v})=><div key={a} style={{fontSize:11,color:"rgba(255,255,255,0.7)",marginBottom:2}}>↑ {a} ({v}/99)</div>)}
                        </div>
                      </div>
                    )}
                    {rated.length===0&&<div style={{fontSize:12,color:"rgba(255,255,255,0.3)",fontStyle:"italic"}}>No attribute ratings yet — programme will focus on general development</div>}
                  </div>

                  {/* Fitness data */}
                  {fitnessKeys.length>0&&(
                    <div style={{...G.card,marginBottom:12}}>
                      <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.4)",letterSpacing:1.5,marginBottom:8}}>FITNESS TEST DATA</div>
                      {fitnessKeys.map(k=>{
                        const entries=pl.fitness[k]||[];
                        const latest=Array.isArray(entries)?entries.slice(-1)[0]:null;
                        return latest?<div key={k} style={{display:"flex",justifyContent:"space-between",padding:"5px 0",borderBottom:"1px solid rgba(255,255,255,0.05)",fontSize:12}}>
                          <span style={{color:"rgba(255,255,255,0.5)"}}>{k}</span>
                          <span style={{color:"#fff",fontWeight:600}}>{latest.val}</span>
                        </div>:null;
                      })}
                    </div>
                  )}

                  {/* Sessions per week */}
                  <div style={{...G.card,marginBottom:12}}>
                    <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.4)",letterSpacing:1.5,marginBottom:10}}>PROGRAMME SETTINGS</div>
                    <FieldLabel>Sessions per week</FieldLabel>
                    <div style={{display:"flex",gap:8,marginBottom:12}}>
                      {[1,2,3,4].map(n=>(
                        <button key={n} onClick={()=>setIndivGoals(g=>[...g.filter(x=>!x.startsWith("__spw:")),`__spw:${n}`])}
                          style={{flex:1,padding:"10px 0",background:indivGoals.find(x=>x===`__spw:${n}`)?primaryCol:"rgba(255,255,255,0.05)",border:`1px solid ${indivGoals.find(x=>x===`__spw:${n}`)?primaryCol:"rgba(255,255,255,0.1)"}`,borderRadius:8,color:indivGoals.find(x=>x===`__spw:${n}`)?contrastText(primaryCol):"rgba(255,255,255,0.5)",fontSize:14,fontWeight:700,cursor:"pointer"}}>{n}x</button>
                      ))}
                    </div>
                    <FieldLabel>Focus area (optional)</FieldLabel>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:12}}>
                      {["Technical","Tactical","Physical","Mental"].map(f=>(
                        <button key={f} onClick={()=>setIndivGoals(g=>g.includes(`__focus:${f}`)?g.filter(x=>x!==`__focus:${f}`):[...g,`__focus:${f}`])}
                          style={{padding:"6px 14px",background:indivGoals.includes(`__focus:${f}`)?`${primaryCol}22`:"rgba(255,255,255,0.04)",border:`1px solid ${indivGoals.includes(`__focus:${f}`)?primaryCol:"rgba(255,255,255,0.1)"}`,borderRadius:20,color:indivGoals.includes(`__focus:${f}`)?primaryCol:"rgba(255,255,255,0.5)",fontSize:12,fontWeight:600,cursor:"pointer"}}>{f}</button>
                      ))}
                    </div>
                  </div>

                  {/* Coaching goals */}
                  <div style={{...G.card,marginBottom:12}}>
                    <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.4)",letterSpacing:1.5,marginBottom:10}}>COACHING GOALS (optional)</div>
                    {indivGoals.filter(g=>!g.startsWith("__")).map((g,i)=>(
                      <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 0",borderBottom:"1px solid rgba(255,255,255,0.05)"}}>
                        <span style={{color:primaryCol,fontSize:12}}>→</span>
                        <span style={{flex:1,fontSize:13,color:"rgba(255,255,255,0.75)"}}>{g}</span>
                        <button onClick={()=>setIndivGoals(gs=>gs.filter(x=>x!==g))} style={{background:"none",border:"none",color:"rgba(255,255,255,0.3)",cursor:"pointer",fontSize:16,padding:0}}>✕</button>
                      </div>
                    ))}
                    {(()=>{
                      const GI=()=>{const [v,setV]=React.useState("");const add=()=>{if(v.trim()){setIndivGoals(g=>[...g,v.trim()]);setV("");}};return(
                        <div style={{display:"flex",gap:8,marginTop:8}}>
                          <input style={{...G.input,flex:1,marginBottom:0}} placeholder="e.g. Improve first touch under pressure" value={v} onChange={e=>setV(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")add();}}/>
                          <button style={G.btnSm} onClick={add}>+ Add</button>
                        </div>
                      );};return <GI/>;
                    })()}
                  </div>

                  {existing&&(
                    <div style={{...G.card,marginBottom:12,background:"rgba(240,180,41,0.05)",border:"1px solid rgba(240,180,41,0.2)"}}>
                      <div style={{fontSize:12,color:"#f0b429"}}>⚠️ A programme already exists for {pl.name}. Generating a new one will replace it.</div>
                    </div>
                  )}

                  <button onClick={async()=>{
                    setIndivGenerating(true);
                    const spw=parseInt((indivGoals.find(x=>x.startsWith("__spw:"))||"__spw:3").split(":")[1]);
                    const cleanGoals=indivGoals.filter(g=>!g.startsWith("__"));
                    try {
                      const prog=await generateIndividualPlan(pl,cleanGoals,spw);
                      if(prog){
                        const progWithMeta={...prog,createdAt:new Date().toISOString()};
                        setAthletePrograms(ap=>({...ap,[pl.id]:progWithMeta}));
                        if(clubId){
                          const {error:saveErr} = await supabase.from("training_programs").upsert({club_id:clubId,player_id:pl.id,sport:club.sport,program_data:progWithMeta,created_at:new Date().toISOString()},{onConflict:"club_id,player_id"});
                          if(saveErr) console.error("[P4M] Programme save error:", saveErr);
                        }
                        showToast(`Programme created for ${pl.name} ✓`);
                        setIndivGoals([]);setIndivAthleteId("");setWizardStep(0);
                        setCurrentPlayer(pl);navigate("detail");setDetailTab("programme");
                      } else {
                        showToast("Could not generate — check your connection");
                      }
                    } catch(e) {
                      console.error("[P4M] Generate button error:", e);
                      showToast("Generation failed: " + (e.message||"unknown error"));
                    }
                    setIndivGenerating(false);
                  }} style={{width:"100%",padding:"14px",background:primaryCol,border:"none",borderRadius:12,color:"#fff",fontSize:16,fontWeight:800,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1}}>
                    🧠 Generate Personal Programme
                  </button>
                </>
              );
            })()}
          </>}
          </div>
        )}

        {/* ── MACROCYCLE ── */}
        {!isSwimming&&(wizardStep===0||wizardStep===99)&&coachTab==="macro"&&(
          <>
            {!framework?(
              <div>
                {/* Saved frameworks for this sport */}
                {savedFrameworks.filter(fw=>fw.sport===club.sport).length>0 ? (
                  <div style={{marginBottom:16}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                      <div style={G.h3}>Saved Frameworks</div>
                      <button style={G.btnSm} onClick={()=>{setWizardData({ goals:[], squadDesc:"", sessions:2, sessionLen:75, strengths:[], weaknesses:[], blockLength:6, seasonWeeks:28, startDate:"", trainingDays:[], trainingTimes:{}, trainingLocations:{}, trainingLocation:"", trainingTime:"" });setFrameworkTeam("");setFramework(null);setWizardStep(1);}}>+ New</button>
                    </div>
                    {savedFrameworks.filter(fw=>fw.sport===club.sport).map((fw,i)=>(
                      <div key={fw.id||i} style={{...G.card,marginBottom:8,borderLeft:`3px solid ${primaryCol}44`,position:"relative"}}>
                        <div style={{display:"flex",alignItems:"center",gap:10}}>
                          <div style={{width:42,height:42,borderRadius:10,background:`${primaryCol}18`,border:`1px solid ${primaryCol}33`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0}}>🧠</div>
                          <div style={{flex:1,minWidth:0,cursor:"pointer"}} onClick={()=>{
                            setFramework(fw.framework_data);
                            setFrameworkTeam(fw.team||"");
                            if(fw.wizard_data) setWizardData(wd=>({...wd,...fw.wizard_data}));
                          }}>
                            <div style={{fontSize:14,fontWeight:700,color:"#fff",marginBottom:2}}>{fw.team||"Full Squad"} — Season Framework</div>
                            <div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>
                              {fw.framework_data?.blocks?.length||0} blocks · {new Date(fw.created_at).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}
                            </div>
                          </div>
                          {/* 3-dot menu */}
                          <div style={{position:"relative",flexShrink:0}}>
                            <button onClick={e=>{e.stopPropagation();const m=document.getElementById("fw-menu-"+(fw.id||i));if(m)m.style.display=m.style.display==="block"?"none":"block";}}
                              style={{background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:8,color:"rgba(255,255,255,0.5)",fontSize:16,cursor:"pointer",padding:"4px 10px",lineHeight:1}}>⋯</button>
                            <div id={"fw-menu-"+(fw.id||i)} style={{display:"none",position:"absolute",right:0,top:"100%",marginTop:4,background:"#1a1a2e",border:"1px solid rgba(255,255,255,0.12)",borderRadius:10,padding:6,zIndex:50,minWidth:180,boxShadow:"0 8px 24px rgba(0,0,0,0.5)"}}>
                              <button onClick={()=>{document.getElementById("fw-menu-"+(fw.id||i)).style.display="none";setFramework(fw.framework_data);setFrameworkTeam(fw.team||"");}}
                                style={{display:"block",width:"100%",textAlign:"left",padding:"9px 12px",background:"none",border:"none",color:"rgba(255,255,255,0.8)",fontSize:13,cursor:"pointer",borderRadius:6}}>
                                👁 View framework
                              </button>
                              <button onClick={()=>{document.getElementById("fw-menu-"+(fw.id||i)).style.display="none";setWizardData(fw.wizard_data ? {...fw.wizard_data} : { goals:[], squadDesc:"", sessions:2, sessionLen:75, strengths:[], weaknesses:[], blockLength:6, seasonWeeks:28, startDate:"", trainingDays:[], trainingTimes:{}, trainingLocations:{}, trainingLocation:"", trainingTime:"" });setFrameworkTeam(fw.team||"");setWizardStep(1);}}
                                style={{display:"block",width:"100%",textAlign:"left",padding:"9px 12px",background:"none",border:"none",color:"rgba(255,255,255,0.8)",fontSize:13,cursor:"pointer",borderRadius:6}}>
                                ✏️ Alter / rebuild
                              </button>
                              <button onClick={async e=>{e.stopPropagation();document.getElementById("fw-menu-"+(fw.id||i)).style.display="none";if(!window.confirm(`Delete the framework for ${fw.team||"Full Squad"}?`))return;await supabase.from("training_frameworks").delete().eq("id",fw.id);setSavedFrameworks(s=>s.filter(f=>f.id!==fw.id));showToast("Framework deleted");}}
                                style={{display:"block",width:"100%",textAlign:"left",padding:"9px 12px",background:"none",border:"none",color:"#E63946",fontSize:13,cursor:"pointer",borderRadius:6}}>
                                🗑 Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div style={{display:"flex",gap:8,marginTop:4}}>
                      <button style={{...G.btnGhost,flex:1,fontSize:12}} onClick={()=>{setIndivWizardType("individual");setWizardStep(98);}}>👤 Individual Programme</button>
                    </div>
                  </div>
                ) : (
                  <div style={{...G.center,padding:"40px 20px",gap:14}}>
                    <div style={{fontSize:40}}>🏆</div>
                    <div style={G.h2}>Training Framework</div>
                    <div style={{fontSize:13,color:"rgba(255,255,255,0.45)",textAlign:"center",maxWidth:300,lineHeight:1.6}}>Build a full season framework for a squad, or create a personalised programme for an individual player.</div>
                    <button style={{...G.btnPrimary,background:primaryCol,maxWidth:260}} onClick={()=>setWizardStep(1)}>🧠 P4M Nexus — Build Season Framework</button>
                    <button style={{...G.btnGhost,maxWidth:260,marginTop:4}} onClick={()=>{setIndivWizardType("individual");setWizardStep(98);}}>👤 Individual Player Programme</button>
                  </div>
                )}
              </div>
            ):(
              <>
                <div style={{...G.cardAccent(primaryCol),marginBottom:10}}>
                  <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:2,color:"rgba(255,255,255,0.38)",marginBottom:4}}>Season Overview{frameworkTeam?` · ${frameworkTeam}`:""}</div>
                  <div style={{fontSize:13,color:"rgba(255,255,255,0.75)",lineHeight:1.65,marginBottom:framework.styleOfPlay?10:0}}>{framework.overview}</div>
                  {framework.styleOfPlay&&<div style={{fontSize:12,color:"rgba(255,255,255,0.5)",lineHeight:1.6,borderTop:"1px solid rgba(255,255,255,0.08)",paddingTop:8,marginTop:4,fontStyle:"italic"}}>🎯 {framework.styleOfPlay}</div>}
                </div>

                {/* Development priorities */}
                {framework.developmentPriorities?.length>0&&(
                  <div style={{...G.card,marginBottom:10}}>
                    <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:1.5,color:"rgba(255,255,255,0.38)",marginBottom:8}}>Development Priorities</div>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                      {framework.developmentPriorities.map((p,i)=>(
                        <span key={i} style={{padding:"4px 12px",background:`${primaryCol}15`,border:`1px solid ${primaryCol}33`,borderRadius:20,fontSize:12,color:"rgba(255,255,255,0.75)",fontWeight:500}}>{p}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Key milestones */}
                {framework.milestones?.length>0&&(
                  <div style={{...G.card,marginBottom:10}}>
                    <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:1.5,color:"rgba(255,255,255,0.38)",marginBottom:10}}>Key Season Milestones</div>
                    {framework.milestones.map((m,i)=>(
                      <div key={i} style={{padding:"10px 0",borderBottom:"1px solid rgba(255,255,255,0.05)"}}>
                        <div style={{display:"flex",gap:10,alignItems:"flex-start",marginBottom:m.whatToMeasure||m.expectedOutcome?6:0}}>
                          <div style={{background:`${primaryCol}18`,border:`1px solid ${primaryCol}33`,borderRadius:6,padding:"3px 8px",fontSize:10,fontWeight:700,color:primaryCol,flexShrink:0,whiteSpace:"nowrap"}}>{m.timing}</div>
                          <div style={{flex:1}}>
                            <div style={{fontSize:12,fontWeight:700,color:"#fff",marginBottom:2}}>{m.title}</div>
                            <div style={{fontSize:11,color:"rgba(255,255,255,0.45)",lineHeight:1.5}}>{m.purpose}</div>
                          </div>
                        </div>
                        {m.whatToMeasure&&<div style={{marginLeft:0,padding:"6px 10px",background:"rgba(255,255,255,0.03)",borderRadius:6,marginTop:4}}>
                          <div style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.3)",letterSpacing:1,marginBottom:3}}>MEASURE / DISCUSS</div>
                          <div style={{fontSize:11,color:"rgba(255,255,255,0.55)",lineHeight:1.5}}>{m.whatToMeasure}</div>
                        </div>}
                        {m.expectedOutcome&&<div style={{marginTop:4,padding:"6px 10px",background:`${primaryCol}08`,borderRadius:6}}>
                          <div style={{fontSize:10,fontWeight:700,color:primaryCol,letterSpacing:1,marginBottom:3}}>EXPECTED OUTCOME</div>
                          <div style={{fontSize:11,color:"rgba(255,255,255,0.55)",lineHeight:1.5}}>{m.expectedOutcome}</div>
                        </div>}
                      </div>
                    ))}
                  </div>
                )}
                <div style={G.secHdr}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <button onClick={()=>{setFramework(null);setWizardStep(0);}} style={{background:"none",border:"none",color:"rgba(255,255,255,0.4)",fontSize:18,cursor:"pointer",padding:0}}>←</button>
                    <div style={G.h3}>{frameworkTeam||"Squad"} Framework</div>
                  </div>
                  <div style={{display:"flex",gap:6}}>
                    <button style={G.btnSm} onClick={()=>setWizardStep(1)}>+ New</button>
                    <button style={{...G.btnSm,background:primaryCol,color:"#fff",border:"none"}} onClick={()=>{setFramework(null);setWizardStep(0);}}>Done ✓</button>
                  </div>
                </div>
                {framework.blocks?.map((b,i)=>(
                  <div key={i} onClick={()=>{setSelectedBlock(i);setCoachTab("meso");}}
                    style={{display:"flex",alignItems:"center",gap:12,background:i===selectedBlock?"rgba(255,255,255,0.07)":"rgba(255,255,255,0.03)",border:`1px solid ${i===selectedBlock?primaryCol:"rgba(255,255,255,0.08)"}`,borderRadius:10,padding:"12px 14px",marginBottom:6,cursor:"pointer",transition:"all 0.15s"}}>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:800,color:i===selectedBlock?primaryCol:"rgba(255,255,255,0.4)",minWidth:28}}>{i+1}</div>
                    <div style={{flex:1}}>
                      <div style={{fontSize:13,fontWeight:600,color:"#fff",marginBottom:2}}>{b.title}</div>
                      <div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>{b.phase} · Weeks {b.weeks}</div>
                      {b.theme&&<div style={{fontSize:11,color:"rgba(255,255,255,0.3)",marginTop:2,fontStyle:"italic"}}>{b.theme}</div>}
                    </div>
                    <div style={{fontSize:11,color:primaryCol,fontWeight:600}}>View plan →</div>
                  </div>
                ))}
              </>
            )}
            {framework&&(
              <>
                <div style={{...G.secHdr,marginTop:16}}><div style={G.h3}>Squad</div><button style={G.secAct} onClick={()=>navigate("players")}>All players</button></div>
                {players.slice(0,4).map(p=>{
                  const sportComp=CARD_COMPOSITE?.[club.sport]; const ov=p.scores&&Object.keys(p.scores).length?Math.round(["spd","sta","att","pos","str","def","sho","wkr"].map(k=>to99(sportComp?.[k]?calcCardStat(p.scores,sportComp[k]):(p.scores[(CARD_MAP_BY_SPORT?.[club.sport]||CARD_MAP)[k]]||50))).reduce((a,b)=>a+b,0)/8):null;
                  return(
                    <div key={p.id} onClick={()=>{setCurrentPlayer({...p});navigate("detail");}} style={{display:"flex",alignItems:"center",gap:12,background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:10,padding:"10px 14px",marginBottom:6,cursor:"pointer"}}>
                      <Avatar name={p.name} photo={p.photo} size={34}/>
                      <div style={{flex:1}}><div style={{fontSize:13,fontWeight:600,color:"#fff"}}>{p.name}</div><div style={{fontSize:11,color:"rgba(255,255,255,0.38)"}}>{p.position||"—"} · {p.team||"—"}</div></div>
                      {ov&&<div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:20,fontWeight:800,color:"#f0b429"}}>{ov}</div>}
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}

        {/* ── MESOCYCLE ── */}
        {!isSwimming&&(wizardStep===0||wizardStep===99)&&coachTab==="meso"&&(
          <>
            {!framework?(
              <div style={{...G.center,padding:"32px 0",gap:8}}><div style={{fontSize:13,color:"rgba(255,255,255,0.4)"}}>Set up your framework first in the Macrocycle tab.</div><button style={{...G.btnGhost,marginTop:8}} onClick={()=>setCoachTab("macro")}>Go to Macrocycle</button></div>
            ):(
              <>
                {/* Block selector */}
                <div style={{marginBottom:14}}>
                  <FieldLabel>Training Block</FieldLabel>
                  <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:4}}>
                    {framework.blocks?.map((b,i)=>(
                      <button key={i} onClick={()=>{setSelectedBlock(i);setSelectedWeek(0);}}
                        style={{padding:"7px 14px",background:selectedBlock===i?primaryCol:"rgba(255,255,255,0.04)",border:`1px solid ${selectedBlock===i?primaryCol:"rgba(255,255,255,0.08)"}`,borderRadius:20,color:selectedBlock===i?"#fff":"rgba(255,255,255,0.45)",fontSize:12,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>
                        Block {i+1}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Block summary */}
                {framework.blocks?.[selectedBlock]&&(
                  <div style={{...G.card,marginBottom:14}}>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:16,fontWeight:800,color:"#fff",marginBottom:2}}>{framework.blocks[selectedBlock].title}</div>
                    <div style={{fontSize:12,color:"rgba(255,255,255,0.45)",marginBottom:8}}>Weeks {framework.blocks[selectedBlock].weeks} · {framework.blocks[selectedBlock].phase}</div>
                    {framework.blocks[selectedBlock].focus&&<div style={{fontSize:13,color:"rgba(255,255,255,0.7)",lineHeight:1.6,marginBottom:10}}>{framework.blocks[selectedBlock].focus}</div>}
                    {/* 4 Corners coaching focus */}
                    {framework.blocks[selectedBlock].coachingFocus&&(
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
                        {[["Technical","#38bdf8"],["Tactical","#22c55e"],["Physical","#E63946"],["Psychological","#f0b429"]].map(([corner,col])=>(
                          <div key={corner} style={{background:`${col}10`,border:`1px solid ${col}25`,borderRadius:8,padding:"8px 10px"}}>
                            <div style={{fontSize:10,fontWeight:700,color:col,letterSpacing:1,marginBottom:3}}>{corner.toUpperCase()}</div>
                            <div style={{fontSize:11,color:"rgba(255,255,255,0.65)",lineHeight:1.4}}>{framework.blocks[selectedBlock].coachingFocus[corner.toLowerCase()]||"—"}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Coaching language cues */}
                    {framework.blocks[selectedBlock].coachingLanguage?.length>0&&(
                      <div style={{marginTop:10,paddingTop:10,borderTop:"1px solid rgba(255,255,255,0.07)"}}>
                        <div style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.35)",letterSpacing:1,marginBottom:6}}>COACHING LANGUAGE</div>
                        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                          {framework.blocks[selectedBlock].coachingLanguage.map((cue,i)=>(
                            <span key={i} style={{padding:"3px 10px",background:"rgba(255,255,255,0.06)",borderRadius:20,fontSize:11,color:"rgba(255,255,255,0.6)"}}>{cue}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Game-based application */}
                    {framework.blocks[selectedBlock].gameBasedApplication&&(
                      <div style={{marginTop:10,paddingTop:10,borderTop:"1px solid rgba(255,255,255,0.07)"}}>
                        <div style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.35)",letterSpacing:1,marginBottom:4}}>GAME APPLICATION</div>
                        <div style={{fontSize:12,color:"rgba(255,255,255,0.6)",lineHeight:1.5,fontStyle:"italic"}}>{framework.blocks[selectedBlock].gameBasedApplication}</div>
                      </div>
                    )}
                    {/* Outcome */}
                    {framework.blocks[selectedBlock].outcome&&(
                      <div style={{marginTop:10,paddingTop:10,borderTop:"1px solid rgba(255,255,255,0.07)"}}>
                        <div style={{fontSize:10,fontWeight:700,color:primaryCol,letterSpacing:1,marginBottom:4}}>BLOCK OUTCOME</div>
                        <div style={{fontSize:12,color:"rgba(255,255,255,0.75)",lineHeight:1.5}}>{framework.blocks[selectedBlock].outcome}</div>
                      </div>
                    )}
                  </div>
                )}

                {/* Generate or show meso */}
                {!blockMeso?(
                  <div style={{textAlign:"center",padding:"20px 0"}}>
                    {mesoLoading
                      ? <>
                          <div style={{position:"fixed",inset:0,background:"rgba(8,8,16,0.97)",zIndex:200,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:32}}>
                            <div style={{marginBottom:32,position:"relative",width:56,height:56}}>
                              <div style={{width:80,height:80,border:`3px solid ${primaryCol}22`,borderTop:`3px solid ${primaryCol}`,borderRadius:"50%",animation:"spin 1s linear infinite",position:"absolute",inset:-12}}/>
                              <div style={{width:56,height:56,display:"flex",alignItems:"center",justifyContent:"center",fontSize:28}}>📋</div>
                            </div>
                            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:36,fontWeight:900,color:"#fff",letterSpacing:2,marginBottom:6,marginTop:16}}>P4M NEXUS</div>
                            <div style={{fontSize:13,fontWeight:600,color:primaryCol,letterSpacing:3,textTransform:"uppercase",marginBottom:24}}>Building week-by-week plan</div>
                            <div style={{maxWidth:280,textAlign:"center"}}>
                              {["Structuring each training week...","Adding session content and drills...","Writing coaching points..."].map((msg,i)=>(
                                <div key={i} style={{fontSize:13,color:"rgba(255,255,255,0.4)",marginBottom:8,animation:`fadeIn 0.5s ease ${i*0.6}s both`}}>{msg}</div>
                              ))}
                            </div>
                            <div style={{marginTop:32,fontSize:11,color:"rgba(255,255,255,0.2)",letterSpacing:1}}>THIS USUALLY TAKES 10–15 SECONDS</div>
                          </div>
                        </>
                      : <><div style={{fontSize:13,color:"rgba(255,255,255,0.45)",marginBottom:14}}>No week-by-week plan yet for this block.</div><button style={{...G.btnPrimary,background:primaryCol}} onClick={generateMesocycle}>🤖 Generate Week-by-Week Plan</button></>
                    }
                  </div>
                ):(
                  <>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
                      <div style={G.h3}>Week-by-Week Plan</div>
                      <button style={G.btnSm} onClick={generateMesocycle}>Regenerate</button>
                    </div>
                    {/* Week tabs */}
                    <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:8,marginBottom:14}}>
                      {blockMeso.weeks?.map((w,i)=>(
                        <button key={i} onClick={()=>setSelectedWeek(i)}
                          style={{padding:"7px 14px",background:(selectedWeek??0)===i?primaryCol:"rgba(255,255,255,0.04)",border:`1px solid ${(selectedWeek??0)===i?primaryCol:"rgba(255,255,255,0.08)"}`,borderRadius:20,color:(selectedWeek??0)===i?"#fff":"rgba(255,255,255,0.45)",fontSize:12,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>
                          Wk {w.week}
                        </button>
                      ))}
                    </div>
                    {/* Selected week detail */}
                    {weekData&&(
                      <div>
                        <div style={{...G.cardAccent(primaryCol),marginBottom:10}}>
                          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:16,fontWeight:800,color:"#fff",marginBottom:4}}>Week {weekData.week} — {weekData.theme}</div>
                          <div style={{fontSize:13,color:"rgba(255,255,255,0.7)",lineHeight:1.6,fontStyle:"italic"}}>{weekData.objective}</div>
                        </div>
                        {weekData.sessions?.map((s,si)=>(
                          <div key={si} style={{...G.card,marginBottom:10}}>
                            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:14,fontWeight:800,color:primaryCol,minWidth:80}}>{s.day}</div>
                              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:16,fontWeight:700,color:"#fff",flex:1}}>{s.title}</div>
                              <div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>{s.duration} mins</div>
                            </div>
                            {[["🏃 Warm-up",s.warmup],["⚽ Main Session",s.main],["🧘 Cool-down",s.cooldown]].map(([lbl,val])=>(
                              <div key={lbl} style={{marginBottom:8}}>
                                <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:1.5,color:"rgba(255,255,255,0.38)",marginBottom:4}}>{lbl}</div>
                                <div style={{fontSize:12,color:"rgba(255,255,255,0.75)",lineHeight:1.6}}>{val}</div>
                              </div>
                            ))}
                            {/* Session diagram */}
                            <div style={{marginBottom:12}}>
                              <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:1.5,color:"rgba(255,255,255,0.38)",marginBottom:8}}>Session Diagram</div>
                              <SessionDiagram drillText={s.drills?.join(" ")||""} sessionText={s.title+" "+s.main} drillShape={s.drillShape||s._diagramDetail?.shape||null} diagramDetail={s._diagramDetail||null} col={primaryCol} sport={club.sport}/>
                            </div>
                            <div style={{marginBottom:6}}>
                              <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:1.5,color:"rgba(255,255,255,0.38)",marginBottom:6}}>Key Drills</div>
                              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                                {s.drills?.map((d,di)=>(
                                  <span key={di} style={{padding:"3px 10px",background:`${primaryCol}18`,border:`1px solid ${primaryCol}44`,borderRadius:20,fontSize:11,color:"rgba(255,255,255,0.75)"}}>{d}</span>
                                ))}
                              </div>
                            </div>
                            <div>
                              <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:1.5,color:"rgba(255,255,255,0.38)",marginBottom:6}}>Coaching Points</div>
                              {s.coachingPoints?.map((cp,ci)=>(
                                <div key={ci} style={{display:"flex",gap:8,marginBottom:4,fontSize:12,color:"rgba(255,255,255,0.7)"}}>
                                  <span style={{color:primaryCol,flexShrink:0}}>→</span>{cp}
                                </div>
                              ))}
                            </div>
                            <button onClick={()=>{setFixtures(fs=>[...fs,{id:Date.now().toString(),opponent:`Training: ${s.title}`,date:"",time:s.day,location:"Training Ground",team:myTeams||teams[0]||"All",result:null,availability:{},isHome:true,isSession:true}]);showToast("Session added to calendar ✓");}} style={{...G.btnGhost,width:"100%",marginTop:10,fontSize:12}}>📅 Add to Calendar</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}

        {/* ── MICROCYCLE ── */}
        {!isSwimming&&(wizardStep===0||wizardStep===99)&&coachTab==="micro"&&(
          <>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.45)",marginBottom:16,lineHeight:1.6}}>Day-by-day view. Select a week from the Mesocycle tab first, then drill into individual sessions here.</div>
            {!blockMeso?(
              <div style={{...G.center,padding:"32px 0",gap:8}}><div style={{fontSize:13,color:"rgba(255,255,255,0.4)"}}>Generate a mesocycle plan first.</div><button style={{...G.btnGhost,marginTop:8}} onClick={()=>setCoachTab("meso")}>Go to Mesocycle</button></div>
            ):(
              <>
                <FieldLabel>Select Week</FieldLabel>
                <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:8,marginBottom:14}}>
                  {blockMeso.weeks?.map((w,i)=>(
                    <button key={i} onClick={()=>setSelectedWeek(i)}
                      style={{padding:"7px 14px",background:(selectedWeek??0)===i?primaryCol:"rgba(255,255,255,0.04)",border:`1px solid ${(selectedWeek??0)===i?primaryCol:"rgba(255,255,255,0.08)"}`,borderRadius:20,color:(selectedWeek??0)===i?"#fff":"rgba(255,255,255,0.45)",fontSize:12,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>
                      Week {w.week}
                    </button>
                  ))}
                </div>
                {/* Day grid */}
                {weekData&&(
                  <div>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:16,fontWeight:800,color:"#fff",marginBottom:12}}>Week {weekData.week} — Daily View</div>
                    {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map(day=>{
                      const session = weekData.sessions?.find(s=>s.day===day);
                      return (
                        <div key={day} style={{display:"flex",gap:12,marginBottom:8,alignItems:"stretch"}}>
                          <div style={{width:80,flexShrink:0,display:"flex",alignItems:"center"}}>
                            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:13,fontWeight:700,color:session?primaryCol:"rgba(255,255,255,0.25)",letterSpacing:0.5}}>{day.slice(0,3).toUpperCase()}</div>
                          </div>
                          <div style={{flex:1,background:session?"rgba(255,255,255,0.05)":"rgba(255,255,255,0.02)",border:`1px solid ${session?primaryCol+"44":"rgba(255,255,255,0.06)"}`,borderRadius:10,padding:"10px 14px",minHeight:48,display:"flex",alignItems:"center"}}>
                            {session
                              ? <div style={{width:"100%"}}>
                                  <div style={{fontSize:13,fontWeight:600,color:"#fff",marginBottom:3}}>{session.title}</div>
                                  <div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>{session.duration} mins · {session.drills?.slice(0,2).join(", ")}</div>
                                </div>
                              : <div style={{fontSize:12,color:"rgba(255,255,255,0.2)",fontStyle:"italic"}}>Rest day</div>
                            }
                          </div>
                        </div>
                      );
                    })}
                    <button onClick={addSession} style={{...G.btnGhost,width:"100%",marginTop:8}}>+ Add Extra Session</button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    );
  };


  const SquadCardsPanel = ({ teamFilter:tf="" }) => {
    const squadPlayers = tf ? players.filter(p=>p.team===tf) : visiblePlayers;
    return (
      <div style={G.panel}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
          <button onClick={()=>navigate("home")} style={{background:"none",border:"none",color:"rgba(255,255,255,0.4)",fontSize:22,cursor:"pointer"}}>←</button>
          <div style={{...G.h2}}>{tf||"Squad"} — Player Cards</div>
        </div>
        <ProGate feature="Squad player cards">
          {squadPlayers.length===0
            ? <div style={{...G.center,padding:"48px 0",gap:8}}><div style={{fontSize:36,opacity:0.25}}>🎴</div><div style={{fontSize:13,color:"rgba(255,255,255,0.3)"}}>No players yet</div></div>
            : <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(170px,1fr))",gap:20,justifyItems:"center"}}>
                {squadPlayers.map(p=>(
                  <div key={p.id} onClick={()=>{setCurrentPlayer({...p});navigate("detail");}} style={{cursor:"pointer"}}>
                    <PlayerCard player={p} size="mini" clubLogo={club.logo} primaryCol={primaryCol} secondaryCol={secondaryCol} sport={club.sport}/>
                    <div style={{textAlign:"center",marginTop:8,fontFamily:"'Barlow Condensed',sans-serif",fontSize:12,fontWeight:700,color:"rgba(255,255,255,0.5)",letterSpacing:1}}>{p.name}</div>
                  </div>
                ))}
              </div>}
        </ProGate>
      </div>
    );
  };

  // ── CALENDAR PANEL ─────────────────────────────────────────────────────────
  const CalendarPanel = () => {
    const today = new Date();
    const [calYear, setCalYear] = useState(today.getFullYear());
    const [calMonth, setCalMonth] = useState(today.getMonth());
    const [selectedDay, setSelectedDay] = useState(null);
    const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
    const dayNames = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
    const firstDay = new Date(calYear,calMonth,1);
    const lastDay  = new Date(calYear,calMonth+1,0);
    const startOffset = (firstDay.getDay()+6)%7;
    const rows = Math.ceil((startOffset+lastDay.getDate())/7);
    const fixturesThisMonth = visibleFixtures.filter(f=>{const d=new Date(f.date);return d.getFullYear()===calYear&&d.getMonth()===calMonth;});
    const fixturesOnDay = day => fixturesThisMonth.filter(f=>new Date(f.date).getDate()===day);
    return (
      <div style={G.panel}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16,flexWrap:"wrap"}}>
          <div style={{...G.h2,flex:1}}>{monthNames[calMonth]} {calYear}</div>
          <button style={G.btnSm} onClick={()=>{if(calMonth===0){setCalMonth(11);setCalYear(y=>y-1);}else setCalMonth(m=>m-1);}}>‹</button>
          <button style={G.btnSm} onClick={()=>{if(calMonth===11){setCalMonth(0);setCalYear(y=>y+1);}else setCalMonth(m=>m+1);}}>›</button>
          {isCoach&&<button style={{...G.secAct}} onClick={()=>setModal("fixture")}>+ Add {FIXTURE_TERM}</button>}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:4}}>
          {dayNames.map(d=><div key={d} style={{textAlign:"center",fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.35)",letterSpacing:1,padding:"4px 0"}}>{d}</div>)}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:16}}>
          {Array.from({length:rows*7},(_,i)=>{
            const dayNum=i-startOffset+1;
            const valid=dayNum>=1&&dayNum<=lastDay.getDate();
            const isToday=valid&&dayNum===today.getDate()&&calMonth===today.getMonth()&&calYear===today.getFullYear();
            const hasFix=valid&&fixturesOnDay(dayNum).length>0;
            const isSel=valid&&dayNum===selectedDay;
            return (
              <div key={i} onClick={()=>valid&&setSelectedDay(isSel?null:dayNum)}
                style={{height:56,display:"flex",flexDirection:"column",alignItems:"flex-start",padding:"4px 5px",borderRadius:8,cursor:valid?"pointer":"default",overflow:"hidden",background:isSel?`${primaryCol}22`:isToday?"rgba(255,255,255,0.06)":"transparent",border:`1px solid ${isSel?primaryCol:hasFix?primaryCol+"66":"rgba(255,255,255,0.05)"}`}}>
                {valid&&<>
                  <div style={{fontSize:12,fontWeight:isToday||isSel?700:400,color:isSel?primaryCol:isToday?primaryCol:"rgba(255,255,255,0.6)",lineHeight:1.2}}>{dayNum}</div>
                  {fixturesOnDay(dayNum).slice(0,2).map((fx,fi)=>(
                    <div key={fi} style={{width:"100%",marginTop:2,padding:"1px 4px",background:primaryCol,borderRadius:3,fontSize:9,color:"#fff",fontWeight:600,lineHeight:1.3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                      vs {fx.opponent}
                    </div>
                  ))}
                  {fixturesOnDay(dayNum).length>2&&<div style={{fontSize:8,color:primaryCol,marginTop:1}}>+{fixturesOnDay(dayNum).length-2} more</div>}
                </>}
              </div>
            );
          })}
        </div>
        {selectedDay&&(
          <div>
            <div style={{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:2,color:"rgba(255,255,255,0.38)",marginBottom:10}}>{selectedDay} {monthNames[calMonth]}</div>
            {fixturesOnDay(selectedDay).length===0
              ? <div style={{fontSize:13,color:"rgba(255,255,255,0.3)",fontStyle:"italic"}}>No fixtures on this day</div>
              : fixturesOnDay(selectedDay).map(f=>{
                const avail=f.availability||{};
                const yes=Object.values(avail).filter(v=>v==="yes").length;
                const maybe=Object.values(avail).filter(v=>v==="maybe").length;
                const no=Object.values(avail).filter(v=>v==="no").length;
                return (
                  <div key={f.id} style={{...G.card,marginBottom:10}}>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:18,fontWeight:800,color:"#fff",marginBottom:4}}>{club.name||"CBN"} vs {f.opponent}</div>
                    <div style={{fontSize:12,color:"rgba(255,255,255,0.45)",marginBottom:10}}>{f.time||"TBC"} · {f.location||"TBC"} · {f.team} · <span style={{fontWeight:700,color:f.isHome?"#22c55e":"#3b82f6"}}>{f.isHome?"HOME":"AWAY"}</span></div>
                    <div style={{display:"flex",gap:14}}>
                      <div style={{fontSize:12,color:"#22c55e",fontWeight:700}}>✓ {yes}</div>
                      <div style={{fontSize:12,color:"#f0b429",fontWeight:700}}>? {maybe}</div>
                      <div style={{fontSize:12,color:"#E63946",fontWeight:700}}>✗ {no}</div>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
        {!selectedDay&&fixturesThisMonth.length>0&&(
          <div>
            <div style={{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:2,color:"rgba(255,255,255,0.38)",marginBottom:10}}>All fixtures this month</div>
            {fixturesThisMonth.sort((a,b)=>new Date(a.date)-new Date(b.date)).map(f=>{
              const avail=f.availability||{};
              return (
                <div key={f.id} style={{...G.card,marginBottom:8}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:16,fontWeight:800,color:"#fff",flex:1}}>{club.name||"CBN"} vs {f.opponent}</div>
                    <span style={{padding:"2px 8px",borderRadius:4,background:f.isHome?"rgba(34,197,94,0.15)":"rgba(59,130,246,0.15)",color:f.isHome?"#22c55e":"#3b82f6",fontWeight:700,fontSize:11}}>{f.isHome?"HOME":"AWAY"}</span>
                  </div>
                  <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",marginBottom:6}}>{fmtDate(f.date)}{f.time?` · ${f.time}`:""} · {f.team}</div>
                  <div style={{display:"flex",gap:12}}>
                    <div style={{fontSize:12,color:"#22c55e",fontWeight:700}}>✓ {Object.values(avail).filter(v=>v==="yes").length}</div>
                    <div style={{fontSize:12,color:"#f0b429",fontWeight:700}}>? {Object.values(avail).filter(v=>v==="maybe").length}</div>
                    <div style={{fontSize:12,color:"#E63946",fontWeight:700}}>✗ {Object.values(avail).filter(v=>v==="no").length}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ── RESULTS PANEL ──────────────────────────────────────────────────────────
  // ── LIVE FEED / LEAGUE SYNC COMPONENT ─────────────────────────────────────
  const FeedTab = ({ primaryCol, showToast, setLeagueTable }) => {
    const [loading, setLoading] = useState(false);
    const [fetchedHtml, setFetchedHtml] = useState(null);
    const [fetchError, setFetchError] = useState(null);
    const [parsedTables, setParsedTables] = useState([]);
    const [selectedTable, setSelectedTable] = useState(null);
    const [customUrl, setCustomUrl] = useState("");
    const [activeUrl, setActiveUrl] = useState("");
    const [showIframe, setShowIframe] = useState(false);

    const NW_SITES = [
      { label:"NW Hockey — Leagues", url:"https://northwest.englandhockey.co.uk/leagues" },
      { label:"NW Hockey — Fixtures", url:"https://northwest.englandhockey.co.uk/fixtures" },
      { label:"NW Hockey — Results", url:"https://northwest.englandhockey.co.uk/results" },
    ];

    // Parse HTML tables from fetched content
    const parseHtmlTables = (html) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const tables = doc.querySelectorAll("table");
      const results = [];
      tables.forEach((table, ti) => {
        const rows = [];
        const headers = [];
        table.querySelectorAll("thead tr th, thead tr td").forEach(th => headers.push(th.textContent.trim()));
        table.querySelectorAll("tbody tr").forEach(tr => {
          const cells = [];
          tr.querySelectorAll("td, th").forEach(td => cells.push(td.textContent.trim()));
          if (cells.length > 2) rows.push(cells);
        });
        if (rows.length > 0) {
          results.push({ index: ti, headers, rows, title: headers.join(" | ") || `Table ${ti+1}` });
        }
      });
      return results;
    };

    // Try to fetch via CORS proxy
    const fetchPage = async (url) => {
      setLoading(true);
      setFetchError(null);
      setFetchedHtml(null);
      setParsedTables([]);
      setSelectedTable(null);
      setActiveUrl(url);

      // Try multiple CORS proxies
      const proxies = [
        `https://api.allorigins.win/get?url=${encodeURIComponent(url)}`,
        `https://corsproxy.io/?${encodeURIComponent(url)}`,
      ];

      let success = false;
      for (const proxy of proxies) {
        try {
          const res = await fetch(proxy);
          if (!res.ok) continue;
          const data = await res.json().catch(() => null);
          const html = data?.contents || await res.text();
          if (html && html.length > 100) {
            setFetchedHtml(html);
            const tables = parseHtmlTables(html);
            setParsedTables(tables);
            if (tables.length > 0) {
              setSelectedTable(0);
              showToast(`Found ${tables.length} table${tables.length>1?"s":""} on this page`);
            } else {
              showToast("Page loaded but no tables found");
            }
            success = true;
            break;
          }
        } catch(e) { continue; }
      }
      if (!success) {
        setFetchError("This site blocks automated requests. Use the manual paste method below instead.");
      }
      setLoading(false);
    };

    const importTable = (tableData) => {
      if (!tableData) return;
      const rows = tableData.rows.map(cells => {
        const name = cells[0]?.replace(/^\d+\.?\s*/, "") || "";
        const nums = cells.slice(1).map(c => parseInt(c.replace(/[^\d-]/g,""))||0);
        return { team:name, p:nums[0]||0, w:nums[1]||0, d:nums[2]||0, l:nums[3]||0, gf:nums[4]||0, ga:nums[5]||0 };
      }).filter(r => r.team && r.team.length > 1);
      if (rows.length === 0) { showToast("Could not parse this table"); return; }
      setLeagueTable(rows);
      showToast(`Imported ${rows.length} teams into league table ✓`);
    };

    return (
      <div style={{...G.panel,paddingTop:14}}>
        {/* Site quick-select */}
        <div style={{marginBottom:14}}>
          <FieldLabel>North West Hockey — View Live Site</FieldLabel>
          <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>
            {NW_SITES.map(s=>(
              <button key={s.url} onClick={()=>{setActiveUrl(s.url);setShowIframe(true);}}
                style={{padding:"8px 14px",background:activeUrl===s.url&&showIframe?primaryCol:"rgba(255,255,255,0.05)",border:`1px solid ${activeUrl===s.url&&showIframe?primaryCol:"rgba(255,255,255,0.1)"}`,borderRadius:10,color:activeUrl===s.url&&showIframe?contrastText(primaryCol):"rgba(255,255,255,0.6)",fontSize:12,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}>
                {s.label}
              </button>
            ))}
          </div>
          {showIframe&&activeUrl&&(
            <div style={{marginBottom:14}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>🌐 {activeUrl}</div>
                <div style={{display:"flex",gap:6}}>
                  <button onClick={()=>window.open(activeUrl,"_blank")} style={{padding:"5px 10px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:6,color:"rgba(255,255,255,0.6)",fontSize:11,cursor:"pointer"}}>Open in browser ↗</button>
                  <button onClick={()=>setShowIframe(false)} style={{padding:"5px 10px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:6,color:"rgba(255,255,255,0.6)",fontSize:11,cursor:"pointer"}}>✕</button>
                </div>
              </div>
              <iframe
                src={activeUrl}
                style={{width:"100%",height:500,border:"1px solid rgba(255,255,255,0.1)",borderRadius:10,background:"#fff"}}
                title="NW Hockey"
                onError={()=>setShowIframe(false)}
              />
              <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",marginTop:6,textAlign:"center"}}>
                If the page doesn't load, tap "Open in browser" above — some sites block embedding.
              </div>
            </div>
          )}
          <FieldLabel style={{marginTop:showIframe?12:0}}>Or import table data</FieldLabel>
          <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>
            {NW_SITES.map(s=>(
              <button key={s.url+"_fetch"} onClick={()=>{setShowIframe(false);fetchPage(s.url);}}
                style={{padding:"7px 12px",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:10,color:"rgba(255,255,255,0.5)",fontSize:11,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}>
                📥 {s.label.replace("NW Hockey — ","")}
              </button>
            ))}
          </div>
          <FieldLabel>Or enter a custom URL</FieldLabel>
          <div style={{display:"flex",gap:8}}>
            <input style={{...G.input,flex:1,marginBottom:0,fontSize:13}} type="url"
              placeholder="https://..." value={customUrl}
              onChange={e=>setCustomUrl(e.target.value)}
              onKeyDown={e=>{if(e.key==="Enter"&&customUrl.trim())fetchPage(customUrl.trim());}}/>
            <button style={{...G.btnPrimary,background:primaryCol,width:"auto",padding:"10px 16px",fontSize:13}} onClick={()=>customUrl.trim()&&fetchPage(customUrl.trim())}>Fetch</button>
          </div>
        </div>

        {/* Loading */}
        {loading&&(
          <div style={{textAlign:"center",padding:"40px 0"}}>
            <div style={{fontSize:32,marginBottom:12,opacity:0.6}}>⏳</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.5)"}}>Fetching page...</div>
          </div>
        )}

        {/* Error */}
        {fetchError&&!loading&&(
          <div style={{background:"rgba(230,57,70,0.1)",border:"1px solid rgba(230,57,70,0.25)",borderRadius:12,padding:16,marginBottom:16}}>
            <div style={{fontSize:13,fontWeight:600,color:"#E63946",marginBottom:6}}>⚠️ Could not fetch page</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.6)",lineHeight:1.6,marginBottom:12}}>{fetchError}</div>
            <div style={{fontSize:12,fontWeight:600,color:"rgba(255,255,255,0.8)",marginBottom:8}}>How to import manually:</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.55)",lineHeight:1.7}}>
              1. Open <span style={{color:primaryCol,fontWeight:600}}>northwest.englandhockey.co.uk</span> in your browser<br/>
              2. Find your league table<br/>
              3. Select all the text in the table and copy it<br/>
              4. Paste it in the box below and tap Import
            </div>
          </div>
        )}

        {/* Parsed tables */}
        {parsedTables.length>0&&!loading&&(
          <div style={{marginBottom:16}}>
            <FieldLabel>Tables found on this page</FieldLabel>
            <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:12}}>
              {parsedTables.map((t,i)=>(
                <button key={i} onClick={()=>setSelectedTable(i)}
                  style={{padding:"6px 12px",background:selectedTable===i?primaryCol:"rgba(255,255,255,0.05)",border:`1px solid ${selectedTable===i?primaryCol:"rgba(255,255,255,0.08)"}`,borderRadius:8,color:selectedTable===i?"#fff":"rgba(255,255,255,0.5)",fontSize:11,fontWeight:600,cursor:"pointer"}}>
                  Table {i+1} ({t.rows.length} rows)
                </button>
              ))}
            </div>
            {selectedTable!==null&&parsedTables[selectedTable]&&(
              <div>
                <div style={{background:"#14141f",border:"1px solid rgba(255,255,255,0.07)",borderRadius:10,overflow:"hidden",marginBottom:10}}>
                  {parsedTables[selectedTable].rows.slice(0,8).map((row,ri)=>(
                    <div key={ri} style={{display:"flex",gap:8,padding:"8px 12px",borderBottom:"1px solid rgba(255,255,255,0.05)",fontSize:12}}>
                      <div style={{flex:2,color:"rgba(255,255,255,0.8)",fontWeight:ri===0?600:400}}>{row[0]}</div>
                      {row.slice(1,7).map((cell,ci)=>(
                        <div key={ci} style={{minWidth:28,textAlign:"center",color:"rgba(255,255,255,0.5)"}}>{cell}</div>
                      ))}
                    </div>
                  ))}
                  {parsedTables[selectedTable].rows.length>8&&<div style={{fontSize:11,color:"rgba(255,255,255,0.3)",padding:"6px 12px"}}>+{parsedTables[selectedTable].rows.length-8} more rows</div>}
                </div>
                <button style={{...G.btnPrimary,background:primaryCol}} onClick={()=>importTable(parsedTables[selectedTable])}>
                  Import this table into League Table ✓
                </button>
              </div>
            )}
          </div>
        )}

        {/* Manual paste fallback — always available */}
        <ManualPasteImport primaryCol={primaryCol} showToast={showToast} setLeagueTable={setLeagueTable}/>
      </div>
    );
  };

  const ManualPasteImport = ({ primaryCol, showToast, setLeagueTable }) => {
    const [pasteText, setPasteText] = useState("");
    const [open, setOpen] = useState(false);

    const parseAndImport = () => {
      const lines = pasteText.split("\n").map(l=>l.trim()).filter(l=>l.length>2);
      const rows = [];
      lines.forEach(line => {
        const parts = line.split(/\s{2,}|\t|\|/).map(p=>p.trim()).filter(Boolean);
        if (parts.length >= 3) {
          const name = parts[0].replace(/^\d+\.?\s*/, "").trim();
          const nums = parts.slice(1).map(p=>parseInt(p.replace(/[^\d]/g,""))||0);
          if (name.length > 1 && nums.length >= 3) {
            rows.push({ team:name, p:nums[0]||0, w:nums[1]||0, d:nums[2]||0, l:nums[3]||0, gf:nums[4]||0, ga:nums[5]||0 });
          }
        }
      });
      if (rows.length === 0) { showToast("Couldn't parse — try copying just the table rows"); return; }
      setLeagueTable(rows);
      setPasteText("");
      setOpen(false);
      showToast(`Imported ${rows.length} teams ✓`);
    };

    return (
      <div style={{marginTop:8}}>
        <button style={G.btnGhost} onClick={()=>setOpen(!open)}>
          {open?"▼":"▶"} Manual paste import
        </button>
        {open&&(
          <div style={{marginTop:10}}>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.45)",marginBottom:10,lineHeight:1.6}}>
              Copy the league table text from the NW Hockey website and paste it below. Each team on a new line.
            </div>
            <textarea style={{...G.textarea,minHeight:130,fontFamily:"monospace",fontSize:12,marginBottom:10}}
              placeholder={"CBN 1s    12    9    2    1    34    14\nPendle Forest    12    7..."}
              value={pasteText} onChange={e=>setPasteText(e.target.value)}/>
            <button style={{...G.btnPrimary,background:primaryCol}} onClick={parseAndImport}>Import Table</button>
          </div>
        )}
      </div>
    );
  };


  const FootballResultsPanel = () => {
    const ftCode = club.ftClubCode||"";
    const [view, setView] = useState("results");
    const [results, setResults] = useState([]);
    const [tables, setTables] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastSync, setLastSync] = useState(null);
    const [showAddResult, setShowAddResult] = useState(false);

    const loadData = async () => {
      const [resData, tabData, clubData] = await Promise.all([
        supabase.from("gms_results").select("*").eq("club_id",clubId).order("date",{ascending:false}),
        supabase.from("gms_league_tables").select("*").eq("club_id",clubId).order("position"),
        supabase.from("clubs").select("gms_last_sync").eq("id",clubId).single(),
      ]);
      if(resData.data) setResults(resData.data);
      if(tabData.data) setTables(tabData.data);
      if(clubData.data?.gms_last_sync) setLastSync(clubData.data.gms_last_sync);
      setLoading(false);
    };

    React.useEffect(()=>{ loadData(); },[]);

    const teamNames = [...new Set(results.map(r=>r.team).filter(Boolean))].sort();
    const leagueTeamNames = [...new Set(tables.map(r=>r.team_name).filter(Boolean))].sort();
    const allTeamNames = [...new Set([...teamNames,...leagueTeamNames])].sort();
    const [selTeam, setSelTeam] = useState("all");

    const filteredResults = selTeam==="all" ? results
      : results.filter(r=>r.team===selTeam||(r.home||"").toLowerCase().includes(selTeam.toLowerCase().split(" ")[0])||(r.away||"").toLowerCase().includes(selTeam.toLowerCase().split(" ")[0]));

    const filteredTable = selTeam==="all" ? [] : tables.filter(r=>r.team_name===selTeam).sort((a,b)=>a.position-b.position);

    const resultBadge = (r) => {
      const cn=(club.name||"").split(" ")[0].toLowerCase();
      const clubHome=(r.home||"").toLowerCase().includes(cn);
      if(r.home_goals===r.away_goals) return {l:"D",c:"#f0b429"};
      const won=clubHome?r.home_goals>r.away_goals:r.away_goals>r.home_goals;
      return won?{l:"W",c:"#22c55e"}:{l:"L",c:"#E63946"};
    };

    return (
      <div style={{...G.panel,paddingBottom:100}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
          <div style={G.h2}>Results & Leagues</div>
          {isCoach&&<button onClick={()=>setShowAddResult(!showAddResult)}
            style={{padding:"6px 12px",background:showAddResult?primaryCol:"rgba(255,255,255,0.05)",border:`1px solid ${showAddResult?primaryCol:"rgba(255,255,255,0.1)"}`,borderRadius:8,color:showAddResult?contrastText(primaryCol):"rgba(255,255,255,0.6)",fontSize:11,fontWeight:600,cursor:"pointer"}}>
            + Add Result
          </button>}
        </div>

        {/* FA Full-Time setup prompt */}
        {!ftCode&&isAdmin&&(
          <div style={{padding:"12px 14px",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:10,marginBottom:12,display:"flex",alignItems:"center",gap:10}}>
            <div style={{fontSize:20}}>⚽</div>
            <div style={{flex:1}}>
              <div style={{fontSize:12,fontWeight:700,color:"#fff",marginBottom:2}}>Connect FA Full-Time</div>
              <div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>Add your Full-Time Club Code in Club Settings to sync results automatically</div>
            </div>
            <button onClick={()=>navigate("club-settings")} style={{padding:"6px 12px",background:primaryCol,border:"none",borderRadius:8,color:contrastText(primaryCol),fontSize:11,fontWeight:700,cursor:"pointer"}}>Set up</button>
          </div>
        )}

        {/* FA Full-Time live widget */}
        {ftCode&&(
          <div style={{marginBottom:12}}>
            <div style={{display:"flex",gap:0,marginBottom:10,background:"rgba(255,255,255,0.04)",borderRadius:10,padding:3}}>
              {[{k:"widget",l:"⚽ Live Feed"},{k:"manual",l:"📊 All Results"}].map(t=>(
                <button key={t.k} onClick={()=>setView(t.k)}
                  style={{flex:1,padding:"9px 4px",background:view===t.k?primaryCol:"transparent",border:"none",borderRadius:8,color:view===t.k?contrastText(primaryCol):"rgba(255,255,255,0.45)",fontSize:13,fontWeight:700,cursor:"pointer"}}>
                  {t.l}
                </button>
              ))}
            </div>
            {view==="widget"&&(
              <div style={{background:"#fff",borderRadius:12,overflow:"hidden",padding:8}}>
                <div dangerouslySetInnerHTML={{__html:`
                  <script type="text/javascript" src="https://fulltime.thefa.com/js/widget.js"></script>
                  <div class="ft-widget-ns" data-lrcode="${ftCode}"></div>
                `}}/>
              </div>
            )}
          </div>
        )}

        {/* View tabs for manual results */}
        {(view==="manual"||!ftCode)&&(
          <>
            <div style={{display:"flex",gap:0,marginBottom:12,background:"rgba(255,255,255,0.04)",borderRadius:10,padding:3}}>
              {[{k:"results",l:"📊 Results"},{k:"league",l:"🏆 League Table"}].map(t=>(
                <button key={t.k} onClick={()=>setView(t.k)}
                  style={{flex:1,padding:"10px 4px",background:view===t.k?primaryCol:"transparent",border:"none",borderRadius:8,color:view===t.k?contrastText(primaryCol):"rgba(255,255,255,0.45)",fontSize:13,fontWeight:700,cursor:"pointer"}}>
                  {t.l}
                </button>
              ))}
            </div>

            {allTeamNames.length>0&&(
              <div style={{display:"flex",gap:6,marginBottom:12,overflowX:"auto",paddingBottom:4}}>
                <button onClick={()=>setSelTeam("all")} style={{padding:"6px 14px",background:selTeam==="all"?primaryCol:"rgba(255,255,255,0.05)",border:`1px solid ${selTeam==="all"?primaryCol:"rgba(255,255,255,0.1)"}`,borderRadius:20,color:selTeam==="all"?contrastText(primaryCol):"rgba(255,255,255,0.55)",fontSize:12,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>All Teams</button>
                {allTeamNames.map((name,i)=>(
                  <button key={i} onClick={()=>setSelTeam(name)} style={{padding:"6px 14px",background:selTeam===name?primaryCol:"rgba(255,255,255,0.05)",border:`1px solid ${selTeam===name?primaryCol:"rgba(255,255,255,0.1)"}`,borderRadius:20,color:selTeam===name?contrastText(primaryCol):"rgba(255,255,255,0.55)",fontSize:12,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>{name}</button>
                ))}
              </div>
            )}

            {loading&&<div style={{textAlign:"center",padding:"32px 0"}}><div style={{width:28,height:28,border:`3px solid ${primaryCol}`,borderTopColor:"transparent",borderRadius:"50%",animation:"spin 0.8s linear infinite",margin:"0 auto 10px"}}/><div style={{fontSize:12,color:"rgba(255,255,255,0.3)"}}>Loading...</div></div>}

            {!loading&&view==="results"&&(
              filteredResults.length===0
                ? <div style={{textAlign:"center",padding:"32px 0",color:"rgba(255,255,255,0.3)",fontSize:13}}>
                    {results.length===0?<span>No results yet — add them manually with <strong style={{color:primaryCol}}>+ Add Result</strong></span>:"No results for this team"}
                  </div>
                : Object.entries(filteredResults.reduce((acc,r)=>{const d=r.date||"Unknown";if(!acc[d])acc[d]=[];acc[d].push(r);return acc;},{})).sort(([a],[b])=>b.localeCompare(a)).map(([date,matches])=>(
                    <div key={date} style={{marginBottom:16}}>
                      <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.35)",letterSpacing:1.5,textTransform:"uppercase",marginBottom:8,paddingBottom:6,borderBottom:"1px solid rgba(255,255,255,0.05)"}}>
                        {new Date(date+"T12:00:00").toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}
                      </div>
                      {matches.map((r,i)=>{
                        const {l,c}=resultBadge(r);
                        return (
                          <div key={i} style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.07)",borderLeft:`3px solid ${c}`,borderRadius:10,padding:"12px 14px",marginBottom:8}}>
                            {r.competition&&<div style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.3)",letterSpacing:1,marginBottom:6,textTransform:"uppercase"}}>{r.competition}</div>}
                            <div style={{display:"flex",alignItems:"center",gap:10}}>
                              <div style={{width:28,height:28,borderRadius:6,background:`${c}22`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><span style={{fontSize:13,fontWeight:800,color:c}}>{l}</span></div>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{display:"flex",alignItems:"center",gap:8}}>
                                  <span style={{fontSize:13,fontWeight:700,color:"#fff",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.home}</span>
                                  <span style={{fontSize:17,fontWeight:900,color:"#fff",fontFamily:"'Barlow Condensed',sans-serif",background:"rgba(255,255,255,0.08)",padding:"2px 12px",borderRadius:6,flexShrink:0}}>{r.home_goals} – {r.away_goals}</span>
                                  <span style={{fontSize:13,fontWeight:500,color:"rgba(255,255,255,0.65)",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",textAlign:"right"}}>{r.away}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))
            )}

            {!loading&&view==="league"&&(
              <ProGate feature="League table">
              {selTeam==="all"
                ? <div style={{padding:"10px 14px",background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:10,fontSize:12,color:"rgba(255,255,255,0.4)",textAlign:"center"}}>Select a team above to see their league table</div>
                : filteredTable.length>0
                  ? <div>
                      <div style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:12,overflow:"hidden"}}>
                        <div style={{padding:"10px 14px",borderBottom:"1px solid rgba(255,255,255,0.07)"}}><div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.4)",letterSpacing:1,textTransform:"uppercase"}}>{filteredTable[0]?.comp_name||selTeam}</div></div>
                        <div style={{display:"grid",gridTemplateColumns:"28px 1fr 28px 28px 28px 28px 36px",gap:2,padding:"8px 12px",borderBottom:"1px solid rgba(255,255,255,0.07)"}}>
                          {["#","Team","P","W","D","L","Pts"].map((h,i)=><div key={i} style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.3)",textAlign:i>1?"center":"left"}}>{h}</div>)}
                        </div>
                        {filteredTable.map((row,i)=>(
                          <div key={i} style={{display:"grid",gridTemplateColumns:"28px 1fr 28px 28px 28px 28px 36px",gap:2,padding:"10px 12px",borderBottom:"1px solid rgba(255,255,255,0.04)",background:row.is_our_club?`${primaryCol}12`:"transparent",borderLeft:row.is_our_club?`3px solid ${primaryCol}`:"3px solid transparent"}}>
                            <div style={{fontSize:12,color:row.is_our_club?primaryCol:"rgba(255,255,255,0.35)",fontWeight:row.is_our_club?700:400}}>{row.position}</div>
                            <div style={{fontSize:13,fontWeight:row.is_our_club?700:500,color:row.is_our_club?"#fff":"rgba(255,255,255,0.8)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{row.club}</div>
                            <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",textAlign:"center"}}>{row.played}</div>
                            <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",textAlign:"center"}}>{row.won}</div>
                            <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",textAlign:"center"}}>{row.drawn}</div>
                            <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",textAlign:"center"}}>{row.lost}</div>
                            <div style={{fontSize:14,fontWeight:row.is_our_club?800:600,color:row.is_our_club?primaryCol:"#fff",textAlign:"center",fontFamily:"'Barlow Condensed',sans-serif"}}>{row.points}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  : <div style={{textAlign:"center",padding:"32px 0",color:"rgba(255,255,255,0.3)",fontSize:13}}>No league table for {selTeam} yet</div>
              }
              </ProGate>
            )}
          </>
        )}

        {/* Add result form */}
        {showAddResult&&isCoach&&(
          <div style={{...G.card,marginTop:12,border:`1px solid ${primaryCol}44`}}>
            <div style={{fontSize:13,fontWeight:700,color:"#fff",marginBottom:10}}>Add Result</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
              <div><FieldLabel>Date</FieldLabel><input id="fb-ar-date" type="date" style={{...G.input,marginBottom:0}} defaultValue={new Date().toISOString().split("T")[0]}/></div>
              <div><FieldLabel>Team</FieldLabel><select id="fb-ar-team" style={{...G.select,marginBottom:0}}><option value="">Select</option>{teams.map(t=><option key={t} value={t}>{t}</option>)}</select></div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 56px 56px 1fr",gap:8,alignItems:"end",marginBottom:8}}>
              <div><FieldLabel>Home</FieldLabel><input id="fb-ar-home" style={{...G.input,marginBottom:0}} placeholder="Home team"/></div>
              <div><FieldLabel>Score</FieldLabel><input id="fb-ar-hg" type="number" min="0" max="20" style={{...G.input,marginBottom:0,textAlign:"center"}} placeholder="0"/></div>
              <div><input id="fb-ar-ag" type="number" min="0" max="20" style={{...G.input,marginBottom:0,textAlign:"center",marginTop:20}} placeholder="0"/></div>
              <div><FieldLabel>Away</FieldLabel><input id="fb-ar-away" style={{...G.input,marginBottom:0}} placeholder="Away team"/></div>
            </div>
            <div style={{marginBottom:8}}><FieldLabel>Competition</FieldLabel><input id="fb-ar-comp" style={{...G.input,marginBottom:0}} placeholder="e.g. Premier Division"/></div>
            <button onClick={async()=>{
              const date=document.getElementById("fb-ar-date")?.value;
              const team=document.getElementById("fb-ar-team")?.value;
              const home=document.getElementById("fb-ar-home")?.value?.trim();
              const away=document.getElementById("fb-ar-away")?.value?.trim();
              const hg=parseInt(document.getElementById("fb-ar-hg")?.value||"0");
              const ag=parseInt(document.getElementById("fb-ar-ag")?.value||"0");
              const comp=document.getElementById("fb-ar-comp")?.value?.trim()||"";
              if(!date||!home||!away){showToast("Fill in date, home and away teams");return;}
              const row={id:`manual_${clubId}_${Date.now()}`,club_id:clubId,date,team:team||"all",home,away,home_goals:hg,away_goals:ag,competition:comp,venue:""};
              const {error}=await supabase.from("gms_results").insert(row);
              if(error){showToast("Failed to save");return;}
              setResults(r=>[row,...r]);
              setShowAddResult(false);
              showToast("Result added ✓");
            }} style={{...G.btnPrimary,background:primaryCol,width:"100%"}}>Save Result</button>
          </div>
        )}

        <div style={{fontSize:11,color:"rgba(255,255,255,0.2)",textAlign:"center",marginTop:16}}>
          {ftCode?"FA Full-Time Live Feed + Manual Results":"Enter results manually or connect FA Full-Time in Club Settings"}
        </div>
      </div>
    );
  };

  const ResultsPanel = () => {
    const clubGmsId = club.sport==="Hockey" ? (club.gmsClubId||"") : "";
    const gmsTeams  = club.gmsTeams||[];
    const [view,     setView]    = useState("results");
    const [selTeam,  setSelTeam] = useState("all");
    const [syncing,  setSyncing] = useState(false);
    const [lastSync, setLastSync]= useState(null);
    const [results,  setResults] = useState([]);
    const [tables,   setTables]  = useState([]);
    const [loading,  setLoading] = useState(true);
    const [showAddResult, setShowAddResult] = useState(false);
    const [selMonth, setSelMonth]= useState("all");

    const loadData = async () => {
      const [resData, tabData, clubData] = await Promise.all([
        supabase.from("gms_results").select("*").eq("club_id",clubId).order("date",{ascending:false}),
        supabase.from("gms_league_tables").select("*").eq("club_id",clubId).order("position"),
        supabase.from("clubs").select("gms_last_sync").eq("id",clubId).single(),
      ]);
      if(resData.data)  setResults(resData.data);
      if(tabData.data)  setTables(tabData.data);
      if(clubData.data?.gms_last_sync) setLastSync(clubData.data.gms_last_sync);
      setLoading(false);
    };

    React.useEffect(()=>{
      if(!clubGmsId){ setLoading(false); return; }
      loadData().then(()=>{
        supabase.from("clubs").select("gms_last_sync").eq("id",clubId).single().then(({data})=>{
          const age = data?.gms_last_sync ? Date.now()-new Date(data.gms_last_sync).getTime() : Infinity;
          if(age > 6*60*60*1000) syncGMS(true);
        });
      });
    },[]);

    // Saturday auto-sync: 14:00, 17:00, 19:00
    React.useEffect(()=>{
      if(!clubGmsId) return;
      const iv = setInterval(()=>{
        const now=new Date(); const sat=now.getDay()===6; const h=now.getHours();
        if(sat && [14,17,19].includes(h)) syncGMS(true);
      }, 60*60*1000);
      return ()=>clearInterval(iv);
    },[]);

    const syncGMS = async (silent=false) => {
      if(!clubGmsId) return;
      if(!silent) setSyncing(true);
      try {
        const res = await supabase.functions.invoke("gms-sync",{
          body:{ club_id:clubId, gms_club_id:clubGmsId, gms_teams:gmsTeams }
        });
        if(res.data) {
          await loadData();
          const {data:fx} = await supabase.from("fixtures").select("*").eq("club_id",clubId);
          if(fx) setFixtures(fx.map(f=>({...f,availability:f.availability||{}})));
          // Always log for debugging
          console.log("GMS sync response:", JSON.stringify(res.data));
          if(!silent) {
            if(res.data.ok && (res.data.syncedResults>0||res.data.syncedFixtures>0)) {
              showToast(`✓ ${res.data.syncedResults||0} results · ${res.data.syncedFixtures||0} fixtures`);
            } else if(res.data.log) {
              // Show last log line to help debug
              const lastLog = res.data.log[res.data.log.length-1]||"";
              showToast(`Sync: ${lastLog.slice(0,60)}`);
            } else if(res.data.error) {
              showToast(`Error: ${res.data.error.slice(0,60)}`);
            } else {
              showToast("Sync complete — check console for details");
            }
          }
        } else if(res.error) {
          console.error("Edge function error:", res.error);
          if(!silent) showToast(`Edge function error: ${JSON.stringify(res.error).slice(0,60)}`);
        }
      } catch(e){ if(!silent) showToast("Sync failed"); }
      if(!silent) setSyncing(false);
    };

    // Derive unique teams and months from results
    const teamNames = [...new Set(results.map((r)=>r.team).filter(Boolean))].sort();
    const leagueTeamNames = [...new Set(tables.map((r)=>r.team_name).filter(Boolean))].sort();
    const allTeamNames = [...new Set([...teamNames,...leagueTeamNames])].sort();

    // Available months from results
    const availMonths = React.useMemo(()=>{
      const months = new Set();
      results.forEach((r)=>{
        if(r.date) months.add(r.date.slice(0,7)); // YYYY-MM
      });
      return [...months].sort().reverse();
    },[results]);

    // Filter results
    const filteredResults = results.filter((r)=>{
      const teamOk = selTeam==="all" || r.team===selTeam
        || (r.home||"").toLowerCase().includes((selTeam||"").toLowerCase().split(" ")[0])
        || (r.away||"").toLowerCase().includes((selTeam||"").toLowerCase().split(" ")[0]);
      const monthOk = selMonth==="all" || (r.date||"").startsWith(selMonth);
      return teamOk && monthOk;
    });

    // League table for selected team
    const filteredTable = selTeam==="all" ? []
      : tables.filter((r)=>r.team_name===selTeam).sort((a, b)=>a.position-b.position);

    const resultBadge = (r:any) => {
      const cn=(club.name||"CBN").split(" ")[0].toLowerCase();
      const clubHome=(r.home||"").toLowerCase().includes(cn);
      if(r.home_goals===r.away_goals) return {l:"D",c:"#f0b429"};
      const won=clubHome?r.home_goals>r.away_goals:r.away_goals>r.home_goals;
      return won?{l:"W",c:"#22c55e"}:{l:"L",c:"#E63946"};
    };

    // Football uses its own panel — return after hooks are declared
    if(club.sport==="Football") return <FootballResultsPanel/>;

    if(!clubGmsId) return (
      <div data-tour="results-panel" style={{...G.panel,paddingBottom:100}}>
        <div style={{...G.h2,marginBottom:16}}>Results & Leagues</div>
        <div style={{padding:16,background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:12,textAlign:"center"}}>
          <div style={{fontSize:32,marginBottom:8}}>🏑</div>
          <div style={{fontSize:14,fontWeight:700,color:"#fff",marginBottom:6}}>Live data not set up yet</div>
          <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",lineHeight:1.6}}>Contact P4M to connect your club to England Hockey GMS.</div>
        </div>
      </div>
    );

    const monthLabel = (ym) => {
      const [y,m]=ym.split("-");
      return new Date(Number(y),Number(m)-1,1).toLocaleDateString("en-GB",{month:"long",year:"numeric"});
    };

    return (
      <div data-tour="results-panel" style={{...G.panel,paddingBottom:100}}>

        {/* Header */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
          <div style={G.h2}>Results & Leagues</div>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            {isCoach&&<button onClick={()=>setShowAddResult(!showAddResult)}
              style={{padding:"6px 12px",background:showAddResult?primaryCol:"rgba(255,255,255,0.05)",border:`1px solid ${showAddResult?primaryCol:"rgba(255,255,255,0.1)"}`,borderRadius:8,color:showAddResult?contrastText(primaryCol):"rgba(255,255,255,0.6)",fontSize:11,fontWeight:600,cursor:"pointer"}}>
              + Add Result
            </button>}
            {lastSync&&<div style={{fontSize:10,color:"rgba(255,255,255,0.25)"}}>
              {new Date(lastSync).toLocaleDateString("en-GB",{day:"numeric",month:"short"})}
            </div>}
            <button onClick={()=>syncGMS(false)} disabled={syncing}
              style={{padding:"6px 10px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:8,color:syncing?"rgba(255,255,255,0.25)":"rgba(255,255,255,0.6)",fontSize:11,fontWeight:600,cursor:syncing?"default":"pointer"}}>
              {syncing?"⟳":"⟳"}
            </button>
          </div>
        </div>

        {/* Manual result entry form */}
        {showAddResult&&isCoach&&(
          <div style={{...G.card,marginBottom:14,border:`1px solid ${primaryCol}44`}}>
            <div style={{fontSize:13,fontWeight:700,color:"#fff",marginBottom:10}}>Add Result</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
              <div>
                <FieldLabel>Date</FieldLabel>
                <input id="ar-date" type="date" style={{...G.input,marginBottom:0}}
                  defaultValue={new Date().toISOString().split("T")[0]}/>
              </div>
              <div>
                <FieldLabel>Team</FieldLabel>
                <select id="ar-team" style={{...G.select,marginBottom:0}}>
                  <option value="">Select team</option>
                  {teams.map(t=><option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr auto auto 1fr",gap:8,alignItems:"end",marginBottom:8}}>
              <div>
                <FieldLabel>Home Team</FieldLabel>
                <input id="ar-home" style={{...G.input,marginBottom:0}} placeholder="Home team"/>
              </div>
              <div>
                <FieldLabel>Score</FieldLabel>
                <input id="ar-hg" type="number" min="0" max="30" style={{...G.input,marginBottom:0,width:50,textAlign:"center"}} placeholder="0"/>
              </div>
              <div>
                <input id="ar-ag" type="number" min="0" max="30" style={{...G.input,marginBottom:0,width:50,textAlign:"center",marginTop:20}} placeholder="0"/>
              </div>
              <div>
                <FieldLabel>Away Team</FieldLabel>
                <input id="ar-away" style={{...G.input,marginBottom:0}} placeholder="Away team"/>
              </div>
            </div>
            <div style={{marginBottom:8}}>
              <FieldLabel>Competition <span style={{color:"rgba(255,255,255,0.3)",fontWeight:400}}>(optional)</span></FieldLabel>
              <input id="ar-comp" style={{...G.input,marginBottom:0}} placeholder="e.g. NW Premier Division"/>
            </div>
            <button onClick={async()=>{
              const date = document.getElementById("ar-date")?.value;
              const team = document.getElementById("ar-team")?.value;
              const home = document.getElementById("ar-home")?.value?.trim();
              const away = document.getElementById("ar-away")?.value?.trim();
              const hg   = parseInt(document.getElementById("ar-hg")?.value||"0");
              const ag   = parseInt(document.getElementById("ar-ag")?.value||"0");
              const comp = document.getElementById("ar-comp")?.value?.trim()||"";
              if(!date||!home||!away){ showToast("Fill in date, home and away teams"); return; }
              const row = {
                id: `manual_${clubId}_${Date.now()}`,
                club_id: clubId,
                date, team: team||"all",
                home, away,
                home_goals: hg, away_goals: ag,
                competition: comp,
                venue: "",
              };
              const {error} = await supabase.from("gms_results").insert(row);
              if(error){ showToast("Failed to save"); return; }
              setResults(r=>[row,...r]);
              setShowAddResult(false);
              showToast("Result added ✓");
            }} style={{...G.btnPrimary,background:primaryCol,width:"100%"}}>Save Result</button>
          </div>
        )}

        {/* View tabs */}
        <div style={{display:"flex",gap:0,marginBottom:12,background:"rgba(255,255,255,0.04)",borderRadius:10,padding:3}}>
          {[{k:"results",l:"📊 Results"},{k:"league",l:"🏆 League Table"}].map(t=>(
            <button key={t.k} onClick={()=>setView(t.k)}
              style={{flex:1,padding:"10px 4px",background:view===t.k?primaryCol:"transparent",border:"none",borderRadius:8,color:view===t.k?contrastText(primaryCol):"rgba(255,255,255,0.45)",fontSize:13,fontWeight:700,cursor:"pointer"}}>
              {t.l}
            </button>
          ))}
        </div>

        {/* Team filter */}
        {!loading&&allTeamNames.length>0&&(
          <div style={{display:"flex",gap:6,marginBottom:10,overflowX:"auto",paddingBottom:4}}>
            <button onClick={()=>setSelTeam("all")}
              style={{padding:"6px 14px",background:selTeam==="all"?primaryCol:"rgba(255,255,255,0.05)",border:`1px solid ${selTeam==="all"?primaryCol:"rgba(255,255,255,0.1)"}`,borderRadius:20,color:selTeam==="all"?contrastText(primaryCol):"rgba(255,255,255,0.55)",fontSize:12,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>
              All Teams
            </button>
            {allTeamNames.map((name:string,i:number)=>(
              <button key={i} onClick={()=>setSelTeam(name)}
                style={{padding:"6px 14px",background:selTeam===name?primaryCol:"rgba(255,255,255,0.05)",border:`1px solid ${selTeam===name?primaryCol:"rgba(255,255,255,0.1)"}`,borderRadius:20,color:selTeam===name?contrastText(primaryCol):"rgba(255,255,255,0.55)",fontSize:12,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>
                {name}
              </button>
            ))}
          </div>
        )}

        {/* Month filter — only on Results view */}
        {!loading&&view==="results"&&availMonths.length>0&&(
          <div style={{display:"flex",gap:6,marginBottom:12,overflowX:"auto",paddingBottom:4,alignItems:"center"}}>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",flexShrink:0}}>Month:</div>
            <button onClick={()=>setSelMonth("all")}
              style={{padding:"5px 12px",background:selMonth==="all"?`${primaryCol}22`:"rgba(255,255,255,0.04)",border:`1px solid ${selMonth==="all"?primaryCol:"rgba(255,255,255,0.08)"}`,borderRadius:20,color:selMonth==="all"?primaryCol:"rgba(255,255,255,0.4)",fontSize:11,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>
              All
            </button>
            {availMonths.map((ym)=>(
              <button key={ym} onClick={()=>setSelMonth(ym)}
                style={{padding:"5px 12px",background:selMonth===ym?`${primaryCol}22`:"rgba(255,255,255,0.04)",border:`1px solid ${selMonth===ym?primaryCol:"rgba(255,255,255,0.08)"}`,borderRadius:20,color:selMonth===ym?primaryCol:"rgba(255,255,255,0.4)",fontSize:11,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>
                {monthLabel(ym)}
              </button>
            ))}
          </div>
        )}

        {/* Loading */}
        {loading&&(
          <div style={{textAlign:"center",padding:"40px 0"}}>
            <div style={{width:32,height:32,border:"3px solid rgba(255,255,255,0.1)",borderTop:`3px solid ${primaryCol}`,borderRadius:"50%",animation:"spin 0.8s linear infinite",margin:"0 auto 12px"}}/>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.35)"}}>Loading...</div>
          </div>
        )}

        {/* ── RESULTS ───────────────────────────────────────────────────── */}
        {!loading&&view==="results"&&(
          <div>
            {filteredResults.length===0?(
              <div style={{textAlign:"center",padding:"32px 0",color:"rgba(255,255,255,0.3)",fontSize:13}}>
                {results.length===0
                  ? <span>No data yet — tap <strong style={{color:primaryCol}}>⟳ Sync</strong> to load<br/><span style={{fontSize:11,marginTop:6,display:"block",color:"rgba(255,255,255,0.2)"}}>If season has ended data may load next August</span></span>
                  : "No results for this filter"}
              </div>
            ):(
              Object.entries(
                filteredResults.reduce((acc, r)=>{
                  const d=r.date||"Unknown"; if(!acc[d]) acc[d]=[]; acc[d].push(r); return acc;
                },{})
              ).sort(([a],[b])=>b.localeCompare(a)).map(([date,matches]:any)=>(
                <div key={date} style={{marginBottom:16}}>
                  <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.35)",letterSpacing:1.5,textTransform:"uppercase",marginBottom:8,paddingBottom:6,borderBottom:"1px solid rgba(255,255,255,0.05)"}}>
                    {new Date(date+"T12:00:00").toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}
                  </div>
                  {matches.map((r, i)=>{
                    const {l,c}=resultBadge(r);
                    return (
                      <div key={i} style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.07)",borderLeft:`3px solid ${c}`,borderRadius:10,padding:"12px 14px",marginBottom:8}}>
                        {r.competition&&<div style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.3)",letterSpacing:1,marginBottom:6,textTransform:"uppercase"}}>{r.competition}</div>}
                        <div style={{display:"flex",alignItems:"center",gap:10}}>
                          <div style={{width:28,height:28,borderRadius:6,background:`${c}22`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                            <span style={{fontSize:13,fontWeight:800,color:c}}>{l}</span>
                          </div>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{display:"flex",alignItems:"center",gap:8}}>
                              <span style={{fontSize:13,fontWeight:700,color:"#fff",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.home}</span>
                              <span style={{fontSize:17,fontWeight:900,color:"#fff",fontFamily:"'Barlow Condensed',sans-serif",background:"rgba(255,255,255,0.08)",padding:"2px 12px",borderRadius:6,flexShrink:0}}>
                                {r.home_goals} – {r.away_goals}
                              </span>
                              <span style={{fontSize:13,fontWeight:500,color:"rgba(255,255,255,0.65)",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",textAlign:"right"}}>{r.away}</span>
                            </div>
                          </div>
                        </div>
                        {r.venue&&<div style={{fontSize:11,color:"rgba(255,255,255,0.25)",marginTop:6}}>📍 {r.venue}</div>}
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        )}

        {/* ── LEAGUE TABLE ──────────────────────────────────────────────── */}
        {!loading&&view==="league"&&(
          <div>
            {selTeam==="all"&&(
              <div style={{padding:"10px 14px",background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:10,marginBottom:12,fontSize:12,color:"rgba(255,255,255,0.4)",textAlign:"center"}}>
                {allTeamNames.length>0 ? "↑ Select a team to see their league table" : tables.length===0 ? <span>Tap <strong style={{color:primaryCol}}>⟳ Sync</strong> then contact P4M to add Competition IDs for league tables</span> : "Select a team above"}
              </div>
            )}

            {selTeam!=="all"&&filteredTable.length>0&&(
              <div>
                {/* Show if this is last known / current */}
                {filteredTable[0]?.synced_at&&(
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.25)",marginBottom:8,textAlign:"right"}}>
                    Last updated {new Date(filteredTable[0].synced_at).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}
                  </div>
                )}
                <div style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:12,overflow:"hidden"}}>
                  <div style={{padding:"10px 14px",borderBottom:"1px solid rgba(255,255,255,0.07)",background:"rgba(255,255,255,0.02)"}}>
                    <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.4)",letterSpacing:1,textTransform:"uppercase"}}>{filteredTable[0]?.comp_name||selTeam}</div>
                  </div>
                  {/* Column headers */}
                  <div style={{display:"grid",gridTemplateColumns:"28px 1fr 28px 28px 28px 28px 36px",gap:2,padding:"8px 12px",borderBottom:"1px solid rgba(255,255,255,0.07)"}}>
                    {["#","Team","P","W","D","L","Pts"].map((h,i)=>(
                      <div key={i} style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.3)",textAlign:i>1?"center":"left",letterSpacing:1}}>{h}</div>
                    ))}
                  </div>
                  {filteredTable.map((row, i)=>(
                    <div key={i} style={{display:"grid",gridTemplateColumns:"28px 1fr 28px 28px 28px 28px 36px",gap:2,padding:"10px 12px",borderBottom:"1px solid rgba(255,255,255,0.04)",background:row.is_our_club?`${primaryCol}12`:"transparent",borderLeft:row.is_our_club?`3px solid ${primaryCol}`:"3px solid transparent"}}>
                      <div style={{fontSize:12,color:row.is_our_club?primaryCol:"rgba(255,255,255,0.35)",fontWeight:row.is_our_club?700:400}}>{row.position}</div>
                      <div style={{fontSize:13,fontWeight:row.is_our_club?700:500,color:row.is_our_club?"#fff":"rgba(255,255,255,0.8)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{row.club}</div>
                      <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",textAlign:"center"}}>{row.played}</div>
                      <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",textAlign:"center"}}>{row.won}</div>
                      <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",textAlign:"center"}}>{row.drawn}</div>
                      <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",textAlign:"center"}}>{row.lost}</div>
                      <div style={{fontSize:14,fontWeight:row.is_our_club?800:600,color:row.is_our_club?primaryCol:"#fff",textAlign:"center",fontFamily:"'Barlow Condensed',sans-serif"}}>{row.points}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selTeam!=="all"&&filteredTable.length===0&&(
              <div style={{textAlign:"center",padding:"32px 0",color:"rgba(255,255,255,0.3)",fontSize:13}}>
                No league table for {selTeam}<br/>
                <span style={{fontSize:11,display:"block",marginTop:4,color:"rgba(255,255,255,0.2)"}}>Contact P4M to add this team's Competition ID</span>
              </div>
            )}
          </div>
        )}

        <div style={{fontSize:11,color:"rgba(255,255,255,0.2)",textAlign:"center",marginTop:16}}>
          England Hockey GMS · Auto-syncs Saturdays 14:00, 17:00, 19:00
        </div>
      </div>
    );
  };


  const LeagueTableCard = ({lt, club, primaryCol}:any) => {
    const clubName = (club.name||"").split(" ")[0].toLowerCase();
    if(!lt?.table?.length) return null;
    return (
      <div style={{marginBottom:20}}>
        <div style={{fontSize:12,fontWeight:700,color:"rgba(255,255,255,0.4)",letterSpacing:1,textTransform:"uppercase",marginBottom:10}}>{lt.teamName}</div>
        <div style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:12,overflow:"hidden"}}>
          <div style={{display:"grid",gridTemplateColumns:"28px 1fr 28px 28px 28px 28px 36px",gap:2,padding:"8px 12px",borderBottom:"1px solid rgba(255,255,255,0.07)"}}>
            {["#","Team","P","W","D","L","Pts"].map((h,i)=>(
              <div key={i} style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.3)",textAlign:i>1?"center":"left",letterSpacing:1}}>{h}</div>
            ))}
          </div>
          {lt.table.map((row, i)=>{
            const isClub=(row.team||"").toLowerCase().includes(clubName);
            return (
              <div key={i} style={{display:"grid",gridTemplateColumns:"28px 1fr 28px 28px 28px 28px 36px",gap:2,padding:"10px 12px",borderBottom:"1px solid rgba(255,255,255,0.04)",background:isClub?`${primaryCol}12`:"transparent",borderLeft:isClub?`3px solid ${primaryCol}`:"3px solid transparent"}}>
                <div style={{fontSize:12,color:isClub?primaryCol:"rgba(255,255,255,0.35)",fontWeight:isClub?700:400}}>{row.position}</div>
                <div style={{fontSize:13,fontWeight:isClub?700:500,color:isClub?"#fff":"rgba(255,255,255,0.8)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{row.team}</div>
                <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",textAlign:"center"}}>{row.played}</div>
                <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",textAlign:"center"}}>{row.won}</div>
                <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",textAlign:"center"}}>{row.drawn}</div>
                <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",textAlign:"center"}}>{row.lost}</div>
                <div style={{fontSize:14,fontWeight:isClub?800:600,color:isClub?primaryCol:"#fff",textAlign:"center",fontFamily:"'Barlow Condensed',sans-serif"}}>{row.points}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };



  const ShopPanel = () => {
    const [shopItems, setShopItems] = useState([
      {id:"s1",name:"Club Shirt",price:35,emoji:"👕",stock:true,image:null,description:"Home playing shirt"},
      {id:"s2",name:"Training Top",price:25,emoji:"🏃",stock:true,image:null,description:"Technical training top"},
      {id:"s3",name:"Club Hoodie",price:45,emoji:"🧥",stock:true,image:null,description:"Club branded hoodie"},
      {id:"s4",name:"Beanie Hat",price:15,emoji:"🧢",stock:true,image:null,description:"Warm beanie with club badge"},
      {id:"s5",name:"Socks",price:8,emoji:"🧦",stock:true,image:null,description:"Official club socks"},
      {id:"s6",name:"Kit Bag",price:30,emoji:"🎒",stock:true,image:null,description:"Club holdall bag"},
    ]);
    const [editItem, setEditItem] = useState(null);
    const [cart, setCart] = useState([]);
    if(!isPro) return <div style={G.panel}><div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}><button onClick={()=>navigate("home")} style={{background:"none",border:"none",color:"rgba(255,255,255,0.4)",fontSize:22,cursor:"pointer"}}>←</button><div style={G.h2}>Shop</div></div><ProGate feature="The club shop"/></div>;

    return (
      <div style={G.panel}>
        <SectionHdr title="Club Shop" action={isCoach?"+ Add Item":undefined} onAction={()=>setEditItem({id:uid(),name:"",price:0,emoji:"📦",stock:true,image:null,description:""})}/>

        {cart.length>0&&(
          <div style={{...G.cardAccent(primaryCol),marginBottom:14,display:"flex",alignItems:"center",gap:12}}>
            <div style={{flex:1}}><div style={{fontSize:13,fontWeight:600,color:"#fff"}}>{cart.length} item{cart.length!==1?"s":""} in order</div><div style={{fontSize:11,color:"rgba(255,255,255,0.45)"}}>£{cart.reduce((s,c)=>s+c.price,0).toFixed(2)} total</div></div>
            <button onClick={()=>{showToast("Order submitted — admin will be in touch ✓");setCart([]);}} style={{...G.btnPrimary,background:primaryCol,width:"auto",padding:"10px 18px",fontSize:13}}>Submit Order</button>
          </div>
        )}

        {editItem&&isCoach&&(
          <div style={{...G.card,marginBottom:14,border:`1px solid ${primaryCol}44`}}>
            <div style={{...G.h3,marginBottom:12}}>{editItem.id&&shopItems.find(i=>i.id===editItem.id)?"Edit Item":"New Item"}</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <Input label="Item name" value={editItem.name} onChange={e=>setEditItem(i=>({...i,name:e.target.value}))}/>
              <Input label="Price (£)" type="number" value={editItem.price} onChange={e=>setEditItem(i=>({...i,price:parseFloat(e.target.value)||0}))}/>
            </div>
            <TextArea label="Description" value={editItem.description} onChange={e=>setEditItem(i=>({...i,description:e.target.value}))} style={{minHeight:60}}/>
            <div style={{marginBottom:12}}>
              <FieldLabel>Product photo</FieldLabel>
              <label style={{display:"flex",alignItems:"center",gap:8,padding:"10px 14px",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:10,cursor:"pointer",fontSize:13,color:editItem.image?"#22c55e":"rgba(255,255,255,0.5)"}}>
                {editItem.image?"📷 Photo added ✓":"📷 Upload product photo"}
                <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>{const f=e.target.files[0];if(!f)return;const r=new FileReader();r.onload=ev=>setEditItem(i=>({...i,image:ev.target.result}));r.readAsDataURL(f);}}/>
              </label>
            </div>
            <div style={{display:"flex",gap:8}}>
              <Btn col={primaryCol} onClick={()=>{
                if(!editItem.name){showToast("Enter item name");return;}
                setShopItems(items=>{
                  const exists = items.find(i=>i.id===editItem.id);
                  return exists ? items.map(i=>i.id===editItem.id?editItem:i) : [...items,editItem];
                });
                setEditItem(null);showToast("Item saved ✓");
              }}>Save Item</Btn>
              <button onClick={()=>setEditItem(null)} style={{...G.btnGhost,flex:1}}>Cancel</button>
            </div>
          </div>
        )}

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          {shopItems.map(item=>(
            <div key={item.id} style={{background:"#14141f",border:"1px solid rgba(255,255,255,0.07)",borderRadius:12,overflow:"hidden"}}>
              <div style={{width:"100%",height:110,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(255,255,255,0.03)",position:"relative"}}>
                {item.image
                  ? <img src={item.image} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                  : <span style={{fontSize:44}}>{item.emoji}</span>}
                {isCoach&&(
                  <div style={{position:"absolute",top:6,right:6,display:"flex",gap:4}}>
                    <button onClick={()=>setEditItem({...item})} style={{background:"rgba(0,0,0,0.6)",border:"none",borderRadius:6,padding:"4px 7px",color:"rgba(255,255,255,0.6)",cursor:"pointer",fontSize:11}}>✏️</button>
                    <button onClick={()=>setShopItems(is=>is.filter(i=>i.id!==item.id))} style={{background:"rgba(0,0,0,0.6)",border:"none",borderRadius:6,padding:"4px 7px",color:"#E63946",cursor:"pointer",fontSize:11}}>✕</button>
                  </div>
                )}
              </div>
              <div style={{padding:12}}>
                <div style={{fontSize:13,fontWeight:600,color:"#fff",marginBottom:2}}>{item.name}</div>
                {item.description&&<div style={{fontSize:11,color:"rgba(255,255,255,0.4)",marginBottom:6,lineHeight:1.4}}>{item.description}</div>}
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:20,fontWeight:800,color:primaryCol,marginBottom:8}}>£{item.price.toFixed(2)}</div>
                <button onClick={()=>{setCart(c=>[...c,item]);showToast(`${item.name} added to order`);}}
                  style={{width:"100%",padding:8,background:primaryCol,border:"none",borderRadius:7,color:"#fff",fontFamily:"'Barlow Condensed',sans-serif",fontSize:13,fontWeight:700,letterSpacing:0.5,cursor:"pointer"}}>
                  Add to Order
                </button>
              </div>
            </div>
          ))}
        </div>

        <div style={{marginTop:20,padding:14,background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:12}}>
          <div style={{fontSize:12,fontWeight:600,color:"rgba(255,255,255,0.5)",marginBottom:4}}>💳 Payments</div>
          <div style={{fontSize:12,color:"rgba(255,255,255,0.35)",lineHeight:1.6}}>Orders are submitted to the club admin who will arrange payment. For online payments, the club can integrate Stripe or PayPal — ask your admin for details.</div>
        </div>
      </div>
    );
  };


  const EventsPanel = () => {
    return (
      <div style={G.panel}>
        <SectionHdr title="Events" action={isCoach?"+ Add Event":undefined} onAction={()=>setModal("add-event")}/>
        {events.length===0
          ? <div style={{...G.center,padding:"40px 0",gap:8}}><div style={{fontSize:36,opacity:0.25}}>🎉</div><div style={{fontSize:13,color:"rgba(255,255,255,0.3)"}}>No events yet</div></div>
          : events.map(ev=>{
            const myRsvp = ev.rsvp?.[currentUser?.id];
            const goingCount = Object.values(ev.rsvp||{}).filter(v=>v==="going").length;
            const maybeCount = Object.values(ev.rsvp||{}).filter(v=>v==="maybe").length;
            return (
              <div key={ev.id} style={{...G.card,marginBottom:12}}>
                <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:8}}>
                  <div style={{flex:1}}>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:18,fontWeight:800,color:"#fff",marginBottom:3}}>{ev.title}</div>
                    <div style={{fontSize:12,color:"rgba(255,255,255,0.45)",marginBottom:6}}>{fmtDate(ev.date)}{ev.time?` · ${ev.time}`:""}{ev.location?` · ${ev.location}`:""}</div>
                    {ev.description&&<div style={{fontSize:13,color:"rgba(255,255,255,0.7)",lineHeight:1.6,marginBottom:ev.photo?8:10}}>{ev.description}</div>}
                    {ev.photo&&<img src={ev.photo} alt="" style={{width:"100%",maxHeight:180,objectFit:"cover",borderRadius:8,marginBottom:10}}/>}
                  </div>
                  {isCoach&&<button onClick={()=>setEvents(es=>es.filter(e=>e.id!==ev.id))} style={{background:"none",border:"none",color:"rgba(255,255,255,0.2)",cursor:"pointer",fontSize:14,marginLeft:10}}>✕</button>}
                </div>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.38)",marginBottom:8}}>
                  {goingCount} going · {maybeCount} maybe
                </div>
                <div style={{display:"flex",gap:8}}>
                  {[{v:"going",l:"✓ Going",col:"#22c55e"},{v:"maybe",l:"? Maybe",col:"#f0b429"},{v:"notgoing",l:"✗ Can't go",col:"#E63946"}].map(opt=>{
                    const active = myRsvp===opt.v;
                    return (
                      <button key={opt.v} onClick={()=>setEvents(es=>es.map(e=>e.id===ev.id?{...e,rsvp:{...e.rsvp,[currentUser?.id||"me"]:opt.v}}:e))}
                        style={{flex:1,padding:"8px 4px",borderRadius:9,border:`1.5px solid ${opt.col}`,background:active?opt.col:"transparent",cursor:"pointer",fontSize:11,fontWeight:700,color:active?"#09090f":opt.col,transition:"all 0.15s"}}>
                        {opt.l}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
      </div>
    );
  };

  // ── FESTIVALS PANEL ────────────────────────────────────────────────────────
  const FestivalsPanel = () => {
    return (
      <div style={G.panel}>
        <SectionHdr title="Tournaments & Cups" action={isCoach?"+ Add":undefined} onAction={()=>setModal("add-festival")}/>
        <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",marginBottom:14,lineHeight:1.6}}>
          Junior festivals, adult tournaments and cup competitions. Mark your availability for each role.
        </div>
        {festivals.length===0
          ? <div style={{...G.center,padding:"40px 0",gap:8}}><div style={{fontSize:36,opacity:0.25}}>🏆</div><div style={{fontSize:13,color:"rgba(255,255,255,0.3)"}}>No tournaments listed yet</div></div>
          : festivals.map(fest=>{
            const myAvail = fest.availability?.[currentUser?.id||"me"] || {};
            const roles = ["playing","umpiring","volunteering"];
            const roleCols = {playing:"#22c55e",umpiring:"#3b82f6",volunteering:"#f0b429"};
            const roleIcons = {playing:"🏑",umpiring:"🟡",volunteering:"🤝"};
            const counts = {};
            roles.forEach(r => { counts[r] = Object.values(fest.availability||{}).filter(v=>v?.[r]).length; });
            return (
              <div key={fest.id} style={{...G.cardAccent(primaryCol),marginBottom:14}}>
                {fest.photo&&<img src={fest.photo} alt="" style={{width:"100%",maxHeight:160,objectFit:"cover",borderRadius:8,marginBottom:10,display:"block"}}/>}
                <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:6}}>
                  <div style={{flex:1}}>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:18,fontWeight:800,color:"#fff",marginBottom:3}}>{fest.title}</div>
                    <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:6,flexWrap:"wrap"}}>
                      <span style={{fontSize:12,color:"rgba(255,255,255,0.45)"}}>{fmtDate(fest.date)}</span>
                      {fest.location&&<span style={{fontSize:12,color:"rgba(255,255,255,0.45)"}}>· {fest.location}</span>}
                      {fest.ageGroup&&<span style={{padding:"2px 8px",borderRadius:4,background:`${primaryCol}22`,color:primaryCol,fontSize:11,fontWeight:700}}>{fest.ageGroup}</span>}
                      {fest.type==="adult"&&<span style={{padding:"2px 8px",borderRadius:4,background:"rgba(59,130,246,0.2)",color:"#3b82f6",fontSize:11,fontWeight:700}}>ADULT</span>}
                    </div>
                    {fest.description&&<div style={{fontSize:12,color:"rgba(255,255,255,0.65)",lineHeight:1.6,marginBottom:10}}>{fest.description}</div>}
                  </div>
                  {isCoach&&<button onClick={()=>setFestivals(fs=>fs.filter(f=>f.id!==fest.id))} style={{background:"none",border:"none",color:"rgba(255,255,255,0.2)",cursor:"pointer",fontSize:14,marginLeft:10}}>✕</button>}
                </div>
                <div style={{display:"flex",gap:14,marginBottom:12}}>
                  {roles.map(r=>(
                    <div key={r} style={{display:"flex",alignItems:"center",gap:4,fontSize:12,color:roleCols[r],fontWeight:600}}>
                      <span>{roleIcons[r]}</span><span style={{textTransform:"capitalize"}}>{r}: {counts[r]}</span>
                    </div>
                  ))}
                </div>
                <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:2,color:"rgba(255,255,255,0.38)",marginBottom:8}}>Your availability</div>
                <div style={{display:"flex",gap:8}}>
                  {roles.map(r=>{
                    const active = myAvail[r];
                    return (
                      <button key={r} onClick={()=>{
                        const updated = {...myAvail,[r]:!myAvail[r]};
                        setFestivals(fs=>fs.map(f=>f.id===fest.id?{...f,availability:{...f.availability,[currentUser?.id||"me"]:updated}}:f));
                      }}
                        style={{flex:1,padding:"9px 6px",borderRadius:9,border:`1.5px solid ${roleCols[r]}`,background:active?roleCols[r]:"transparent",cursor:"pointer",fontSize:11,fontWeight:700,color:active?"#09090f":roleCols[r],display:"flex",alignItems:"center",justifyContent:"center",gap:4}}>
                        <span>{roleIcons[r]}</span><span style={{textTransform:"capitalize"}}>{r}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
      </div>
    );
  };


  const PaymentsPanel = () => {
    const [payTab, setPayTab] = useState("outstanding");
    const [xeroSyncing, setXeroSyncing] = useState(false);
    const [xeroSummary, setXeroSummary] = useState(null);
    const [reqForm, setReqForm] = useState({title:"",amount:"",type:"match",dueDate:"",team:"",playerId:"",description:""});
    const [showReqForm, setShowReqForm] = useState(false);

    const myPayments = isAdmin ? payments : payments.filter(p=>p.team===myTeams||p.team==="all"||p.playerId===currentUser?.id);
    const outstanding = myPayments.filter(p=>p.status!=="paid");
    const collected   = myPayments.filter(p=>p.status==="paid");
    const totalOwed   = outstanding.reduce((s,p)=>s+p.amount,0);
    const totalIn     = collected.reduce((s,p)=>s+p.amount,0);

    const paymentTypes = [
      {v:"match",l:"Match Fee",i:club.sport==="Football"?"⚽":club.sport==="Swimming"?"🏊":"🏑"},
      {v:"membership",l:"Membership",i:"🏆"},
      {v:"fine",l:"Fine",i:"⚠️"},
      {v:"kit",l:"Kit",i:"👕"},
      {v:"trip",l:"Trip/Tour",i:"✈️"},
      {v:"other",l:"Other",i:"📌"},
    ];

    const requestFromLineup = () => {
      // Find next fixture and its lineup — create a payment per starting player
      const nextFix = fixtures.filter(f=>new Date(f.date)>=new Date()).sort((a,b)=>new Date(a.date)-new Date(b.date))[0];
      if (!nextFix) { showToast("No upcoming fixtures found"); return; }
      const lineup = lineups[nextFix.id];
      if (!lineup?.starters?.length) { showToast("No lineup published for next fixture"); return; }
      const amount = reqForm.amount ? parseFloat(reqForm.amount) : 5;
      const newPayments = lineup.starters.filter(Boolean).map(p=>({
        id:uid(), title:`Match Fee vs ${nextFix.opponent}`, amount, type:"match",
        dueDate:nextFix.date, team:nextFix.team, playerId:p.id, playerName:p.name,
        description:`Match fee for ${fmtDate(nextFix.date)} vs ${nextFix.opponent}`,
        status:"pending"
      }));
      setPayments(ps=>[...ps,...newPayments]);
      showToast(`${newPayments.length} match fee requests sent ✓`);
      setShowReqForm(false);
    };

    const submitRequest = () => {
      if (!reqForm.title||!reqForm.amount) { showToast("Enter title and amount"); return; }
      const base = {id:uid(),...reqForm,amount:parseFloat(reqForm.amount),status:"pending"};
      if (reqForm.playerId) {
        // Single player
        const player = players.find(p=>p.id===reqForm.playerId);
        setPayments(ps=>[...ps,{...base,playerName:player?.name||""}]);
        showToast("Payment requested ✓");
      } else if (reqForm.team) {
        // Whole team
        const teamPlayers = players.filter(p=>p.team===reqForm.team);
        const newPays = teamPlayers.map(p=>({...base,id:uid(),playerId:p.id,playerName:p.name}));
        setPayments(ps=>[...ps,...newPays]);
        showToast(`${newPays.length} payment requests sent ✓`);
      } else {
        // All members
        setPayments(ps=>[...ps,{...base,team:"all"}]);
        showToast("Payment request sent to all ✓");
      }
      setReqForm({title:"",amount:"",type:"match",dueDate:"",team:"",playerId:"",description:""});
      setShowReqForm(false);
    };

    const PayCard = ({p}) => (
      <div style={{...G.card,display:"flex",alignItems:"center",gap:12,marginBottom:8}}>
        <div style={{width:42,height:42,borderRadius:10,background:`${primaryCol}18`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>
          {paymentTypes.find(t=>t.v===p.type)?.i||"📌"}
        </div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:13,fontWeight:600,color:"#fff",marginBottom:1}}>{p.title}</div>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.38)",marginTop:1}}>
            {p.playerName&&<span style={{color:primaryCol,fontWeight:600}}>{p.playerName} · </span>}
            {p.team&&p.team!=="all"&&!p.playerName&&<span>{p.team} · </span>}
            {p.dueDate?`Due ${fmtDate(p.dueDate)}`:""}
            {p.description&&<span style={{color:"rgba(255,255,255,0.3)"}}> · {p.description}</span>}
          </div>
        </div>
        <div style={{textAlign:"right",flexShrink:0}}>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:20,fontWeight:800,color:p.status==="paid"?"#22c55e":"#E63946"}}>£{p.amount.toFixed(2)}</div>
          {p.status!=="paid"
            ? <div style={{display:"flex",flexDirection:"column",gap:4,marginTop:4,alignItems:"flex-end"}}>
                {/* Player: pay via Stripe */}
                {!isAdmin&&(
                  <button onClick={async()=>{
                    try {
                      showToast("Opening payment...");
                      const { data, error } = await supabase.functions.invoke("create-payment", {
                        body: {
                          paymentId: p.id, amount: Math.round(p.amount*100),
                          title: p.title, clubId, playerName: currentUser?.name,
                          stripeAccountId: club.stripeAccountId,
                        }
                      });
                      if(error) { showToast("Error: " + error.message); return; }
                      if(data?.url) window.open(data.url, "_blank");
                      else showToast(data?.error || "Payment setup not complete");
                    } catch(e) { showToast("Error: " + e.message); console.error(e); }
                  }} style={{padding:"4px 12px",background:primaryCol,border:"none",borderRadius:6,color:"#fff",fontFamily:"'Barlow Condensed',sans-serif",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                    💳 Pay Now
                  </button>
                )}
                {/* Admin: mark paid manually */}
                {isAdmin&&(
                  <button onClick={()=>{
                    setPayments(ps=>ps.map(px=>px.id===p.id?{...px,status:"paid",paidAt:new Date().toISOString()}:px));
                    supabase.from("payments").update({status:"paid",paid_at:new Date().toISOString()}).eq("id",p.id).then(()=>{}).catch(console.error);
                    showToast("Payment recorded ✓");
                  }} style={{padding:"4px 10px",background:"rgba(34,197,94,0.15)",border:"1px solid rgba(34,197,94,0.3)",borderRadius:6,color:"#22c55e",fontFamily:"'Barlow Condensed',sans-serif",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                    ✓ Mark Paid
                  </button>
                )}
              </div>
            : <div style={{fontSize:11,color:"#22c55e",fontWeight:600,marginTop:4}}>✓ Paid</div>}
        </div>
      </div>
    );

    return (
      <div data-tour="payments-panel" style={G.panel}>
        {/* Summary */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
          <div style={{...G.card,marginBottom:0,borderTop:"3px solid #E63946"}}>
            <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:1.5,color:"rgba(255,255,255,0.38)",marginBottom:4}}>Outstanding</div>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:26,fontWeight:800,color:"#E63946"}}>£{totalOwed.toFixed(2)}</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.38)"}}>{outstanding.length} request{outstanding.length!==1?"s":""}</div>
          </div>
          <div style={{...G.card,marginBottom:0,borderTop:"3px solid #22c55e"}}>
            <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:1.5,color:"rgba(255,255,255,0.38)",marginBottom:4}}>Collected</div>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:26,fontWeight:800,color:"#22c55e"}}>£{totalIn.toFixed(2)}</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.38)"}}>{collected.length} payment{collected.length!==1?"s":""}</div>
          </div>
        </div>

        {/* Admin — request payment */}
        {isCoach&&(
          <div style={{marginBottom:14}}>
            {!showReqForm
              ? <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  <button onClick={()=>setShowReqForm(true)} style={{...G.btnPrimary,background:primaryCol,flex:1,fontSize:13}}>+ Request Payment</button>
                  <button onClick={requestFromLineup} style={{...G.btnGhost,fontSize:12,whiteSpace:"nowrap"}}>🏑 Charge lineup (match fee)</button>
                </div>
              : <div style={{...G.card,marginBottom:0}}>
                  <div style={{...G.h3,marginBottom:12}}>New Payment Request</div>
                  <Input label="Title / what it's for" placeholder="e.g. April Match Fee" value={reqForm.title} onChange={e=>setReqForm(f=>({...f,title:e.target.value}))}/>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                    <Input label="Amount (£)" type="number" placeholder="5.00" value={reqForm.amount} onChange={e=>setReqForm(f=>({...f,amount:e.target.value}))}/>
                    <Select label="Type" value={reqForm.type} onChange={e=>setReqForm(f=>({...f,type:e.target.value}))}>
                      {paymentTypes.map(t=><option key={t.v} value={t.v}>{t.l}</option>)}
                    </Select>
                  </div>
                  <TextArea label="Description (optional)" placeholder="e.g. Home game vs Pendle Forest" value={reqForm.description} onChange={e=>setReqForm(f=>({...f,description:e.target.value}))} style={{minHeight:60}}/>
                  <Input label="Due date" type="date" value={reqForm.dueDate} onChange={e=>setReqForm(f=>({...f,dueDate:e.target.value}))}/>
                  <FieldLabel>Send to</FieldLabel>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
                    <Select label="" value={reqForm.team} onChange={e=>setReqForm(f=>({...f,team:e.target.value,playerId:""}))}>
                      <option value="">All members</option>
                      {teams.map(t=><option key={t} value={t}>{t}</option>)}
                    </Select>
                    <Select label="" value={reqForm.playerId} onChange={e=>setReqForm(f=>({...f,playerId:e.target.value,team:""}))}>
                      <option value="">Or individual player...</option>
                      {players.filter(p=>!reqForm.team||p.team===reqForm.team).map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                    </Select>
                  </div>
                  <div style={{display:"flex",gap:8}}>
                    <Btn col={primaryCol} onClick={submitRequest}>Send Request</Btn>
                    <button onClick={()=>setShowReqForm(false)} style={{...G.btnGhost,flex:1}}>Cancel</button>
                  </div>
                </div>}
          </div>
        )}

        {/* Tabs */}
        <div style={{display:"flex",gap:6,marginBottom:14,overflowX:"auto",paddingBottom:4}}>
          {[{k:"outstanding",l:"💸 Unpaid"},{k:"paid",l:"✅ Paid"},...(isAdmin?[{k:"request",l:"+ Request"},{k:"xero",l:"🔗 Xero"}]:[])].map(t=>(
            <button key={t.k} onClick={()=>setPayTab(t.k)}
              style={{padding:"8px 16px",background:payTab===t.k?primaryCol:"rgba(255,255,255,0.04)",border:`1px solid ${payTab===t.k?primaryCol:"rgba(255,255,255,0.08)"}`,borderRadius:20,color:payTab===t.k?"#fff":"rgba(255,255,255,0.45)",fontSize:12,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>
              {t.l}{t.k==="outstanding"?` (${outstanding.length})`:""}
            </button>
          ))}
        </div>

        {payTab==="outstanding"&&(
          outstanding.length===0
            ? <div style={{...G.center,padding:"32px 0",gap:6}}><div style={{fontSize:32,opacity:0.25}}>✅</div><div style={{fontSize:13,color:"rgba(255,255,255,0.3)"}}>All paid up!</div></div>
            : outstanding.map(p=><PayCard key={p.id} p={p}/>)
        )}
        {payTab==="paid"&&(
          collected.length===0
            ? <div style={{...G.center,padding:"32px 0",gap:6}}><div style={{fontSize:32,opacity:0.25}}>💸</div><div style={{fontSize:13,color:"rgba(255,255,255,0.3)"}}>No payments recorded yet</div></div>
            : collected.map(p=><PayCard key={p.id} p={p}/>)
        )}

        {/* ── REQUEST TAB ── */}
        {payTab==="request"&&isAdmin&&(
          <div style={{...G.card}}>
            <div style={{fontSize:13,fontWeight:700,color:"#fff",marginBottom:12}}>Request Payment</div>
            <Input label="Title" placeholder="e.g. Match Fee vs Rivals" value={reqForm.title} onChange={e=>setReqForm(f=>({...f,title:e.target.value}))}/>
            <Input label="Amount (£)" type="number" placeholder="e.g. 8" value={reqForm.amount} onChange={e=>setReqForm(f=>({...f,amount:e.target.value}))}/>
            <FieldLabel>Type</FieldLabel>
            <select style={G.select} value={reqForm.type} onChange={e=>setReqForm(f=>({...f,type:e.target.value}))}>
              {paymentTypes.map(t=><option key={t.v} value={t.v}>{t.i} {t.l}</option>)}
            </select>
            <Input label="Due date" type="date" value={reqForm.dueDate} onChange={e=>setReqForm(f=>({...f,dueDate:e.target.value}))}/>
            <FieldLabel>Send to</FieldLabel>
            <select style={G.select} value={reqForm.team} onChange={e=>setReqForm(f=>({...f,team:e.target.value,playerId:""}))}>
              <option value="">All members</option>
              {teams.map(t=><option key={t} value={t}>{t}</option>)}
            </select>
            <FieldLabel>Or specific player</FieldLabel>
            <select style={G.select} value={reqForm.playerId} onChange={e=>setReqForm(f=>({...f,playerId:e.target.value,team:""}))}>
              <option value="">All / team above</option>
              {players.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <div style={{display:"flex",gap:8,marginTop:4}}>
              <button onClick={submitRequest} style={{...G.btnPrimary,background:primaryCol,flex:1}}>Send Request</button>
              <button onClick={requestFromLineup} style={{...G.btnGhost,flex:1,fontSize:12}}>From Lineup</button>
            </div>
          </div>
        )}

        {/* ── XERO TAB ── */}
        {payTab==="xero"&&isAdmin&&(
          <div>
          <ProGate feature="Xero accounting integration">
            {!club.xeroConnected ? (
              <div style={{...G.card,textAlign:"center",padding:"24px 16px"}}>
                <div style={{fontSize:32,marginBottom:12}}>🔗</div>
                <div style={{fontSize:15,fontWeight:700,color:"#fff",marginBottom:6}}>Connect to Xero</div>
                <div style={{fontSize:12,color:"rgba(255,255,255,0.45)",lineHeight:1.6,marginBottom:16}}>
                  Sync your P4M payments with Xero. Outstanding fees become invoices automatically.
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:16,textAlign:"left"}}>
                  {[["🧾","Auto invoices","Payment requests become Xero invoices"],["💰","Track owed","See outstanding fees in Xero"],["✅","Mark paid","Paying in P4M updates Xero"],["📊","Reports","Full accounting history in Xero"]].map(([i,t,d])=>(
                    <div key={t} style={{padding:"10px 12px",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:10}}>
                      <div style={{fontSize:18,marginBottom:4}}>{i}</div>
                      <div style={{fontSize:12,fontWeight:700,color:"#fff",marginBottom:2}}>{t}</div>
                      <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",lineHeight:1.4}}>{d}</div>
                    </div>
                  ))}
                </div>
                <button onClick={async()=>{
                  const {data} = await supabase.functions.invoke("xero-auth",{body:{action:"connect"}});
                  if(data?.authUrl) window.location.href=data.authUrl;
                  else showToast("Could not connect — check edge function is deployed");
                }} style={{padding:"13px 32px",background:"#1AB4D7",border:"none",borderRadius:12,color:"#fff",fontSize:14,fontWeight:800,cursor:"pointer"}}>
                  🔗 Connect to Xero
                </button>
              </div>
            ) : (
              <div>
                <div style={{...G.card,marginBottom:12,borderLeft:"3px solid #1AB4D7"}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
                    <div style={{width:8,height:8,borderRadius:"50%",background:"#22c55e"}}/>
                    <div style={{fontSize:14,fontWeight:700,color:"#fff",flex:1}}>Connected to Xero</div>
                    <button onClick={async()=>{
                      await supabase.functions.invoke("xero-auth",{body:{action:"disconnect",club_id:clubId}});
                      setClub(c=>({...c,xeroConnected:false,xeroTenantName:""}));
                      showToast("Xero disconnected");
                    }} style={{background:"none",border:"none",color:"rgba(255,255,255,0.3)",fontSize:11,cursor:"pointer"}}>Disconnect</button>
                  </div>
                  {club.xeroTenantName&&<div style={{fontSize:12,color:"rgba(255,255,255,0.4)"}}>{club.xeroTenantName}</div>}
                </div>
                {xeroSummary&&(
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
                    <div style={{...G.card,textAlign:"center"}}><div style={{fontSize:22,fontWeight:800,color:"#E63946",fontFamily:"'Barlow Condensed',sans-serif"}}>£{(xeroSummary.totalOwed||0).toFixed(2)}</div><div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>Outstanding</div></div>
                    <div style={{...G.card,textAlign:"center"}}><div style={{fontSize:22,fontWeight:800,color:"#f0b429",fontFamily:"'Barlow Condensed',sans-serif"}}>{xeroSummary.invoiceCount||0}</div><div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>Open Invoices</div></div>
                  </div>
                )}
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  <button onClick={async()=>{
                    setXeroSyncing(true);
                    const {data}=await supabase.functions.invoke("xero-sync",{body:{action:"summary",club_id:clubId}});
                    if(data?.ok) setXeroSummary(data);
                    setXeroSyncing(false);
                  }} style={{padding:"12px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:10,color:"#fff",fontSize:13,fontWeight:600,cursor:"pointer"}}>
                    📊 Refresh Summary
                  </button>
                  <button onClick={async()=>{
                    if(!window.confirm("Create Xero invoices for all unsynced outstanding payments?")) return;
                    setXeroSyncing(true);
                    const {data}=await supabase.functions.invoke("xero-sync",{body:{action:"sync_all",club_id:clubId}});
                    if(data?.ok) showToast(`✓ ${data.created} invoices created in Xero`);
                    else showToast("Sync failed");
                    setXeroSyncing(false);
                  }} disabled={xeroSyncing} style={{padding:"12px",background:"#1AB4D7",border:"none",borderRadius:10,color:"#fff",fontSize:13,fontWeight:700,cursor:xeroSyncing?"default":"pointer",opacity:xeroSyncing?0.6:1}}>
                    {xeroSyncing?"Syncing...":"⟳ Sync Outstanding to Xero"}
                  </button>
                  <button onClick={()=>window.open("https://go.xero.com","_blank")} style={{padding:"12px",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:10,color:"rgba(255,255,255,0.6)",fontSize:13,cursor:"pointer"}}>
                    Open Xero Dashboard ↗
                  </button>
                </div>
              </div>
            )}
          </ProGate>
          </div>
        )}
      </div>
    );
  };


  // ── SQUAD CARDS PANEL ────────────────────────────────────────────────────

  // ── PLAYERS PANEL ─────────────────────────────────────────────────────────
  const teamDominantCol = (team) => {
    if(team?.toLowerCase().includes("men")) return primaryCol;
    if(team?.toLowerCase().includes("ladies")||team?.toLowerCase().includes("women")) return "#a855f7";
    return "#f0b429";
  };

  const PlayersPanel = () => {
    const [staffView, setStaffView] = useState(false);
    const staffUsers = users.filter(u=>u.approved&&["lead_coach","coach","captain","admin"].includes(u.role));
    const myOwnProfile = players.find(p=>p.name===currentUser?.name||p.userId===currentUser?.id);
    const playerList = isPlayer
      ? (myOwnProfile?[myOwnProfile]:[])
      : (teamFilter?players.filter(p=>p.team===teamFilter):visiblePlayers);
    const teamDominantCol=(team)=>{
      if(team?.toLowerCase().includes("men"))return primaryCol;
      if(team?.toLowerCase().includes("ladies")||team?.toLowerCase().includes("women"))return secondaryCol;
      return "#a855f7";
    };
    return (
      <div data-tour="players-panel" style={G.panel}>
        <div style={{display:"flex",gap:6,marginBottom:14}}>
          <button onClick={()=>setStaffView(false)} style={{padding:"8px 16px",background:!staffView?primaryCol:"rgba(255,255,255,0.04)",border:`1px solid ${!staffView?primaryCol:"rgba(255,255,255,0.08)"}`,borderRadius:20,color:!staffView?"#fff":"rgba(255,255,255,0.45)",fontSize:12,fontWeight:600,cursor:"pointer"}}>
            👤 {MEMBER_TERM[club.sport]||"Player"}s
          </button>
          <button onClick={()=>setStaffView(true)} style={{padding:"8px 16px",background:staffView?primaryCol:"rgba(255,255,255,0.04)",border:`1px solid ${staffView?primaryCol:"rgba(255,255,255,0.08)"}`,borderRadius:20,color:staffView?"#fff":"rgba(255,255,255,0.45)",fontSize:12,fontWeight:600,cursor:"pointer"}}>🏆 Staff</button>
          {!staffView&&isCoach&&<button style={G.secAct} onClick={()=>setModal("add-player")}>+ Add</button>}
          {staffView&&isAdmin&&<button style={G.secAct} onClick={()=>setModal("add-staff")}>+ Add Staff</button>}
          {!staffView&&teams.length>0&&!isPlayer&&<select style={{...G.select,width:"auto",marginBottom:0,fontSize:12,padding:"6px 10px"}} value={teamFilter} onChange={e=>setTeamFilter(e.target.value)}><option value="">All teams</option>{teams.map(t=><option key={t} value={t}>{t}</option>)}</select>}
        </div>
        {staffView?(
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))",gap:16,justifyItems:"center"}}>
            {staffUsers.map(u=>(
              <div key={u.id} onClick={()=>{setCurrentStaff(u);setPanel("staff-detail");}}
                style={{display:"flex",flexDirection:"column",alignItems:"center",gap:6,cursor:"pointer"}}>
                <CoachCard user={u} size="mini" clubLogo={club.logo} primaryCol={primaryCol} secondaryCol={secondaryCol} teamColour={teamDominantCol(u.team)}/>
                <div style={{fontSize:10,color:"rgba(255,255,255,0.35)"}}>Tap to view</div>
              </div>
            ))}
            {staffUsers.length===0&&<div style={{fontSize:13,color:"rgba(255,255,255,0.3)",gridColumn:"1/-1",textAlign:"center",padding:"32px 0"}}>No staff yet</div>}
          </div>
        ):(
          <>
            {playerList.length===0
              ? <div style={{...G.center,padding:"48px 20px",gap:8}}><div style={{fontSize:36,opacity:0.3}}>👤</div><div style={{...G.h3,color:"rgba(255,255,255,0.25)"}}>No {(MEMBER_TERM[club.sport]||"player").toLowerCase()}s yet</div>{isCoach&&<div style={{fontSize:13,color:"rgba(255,255,255,0.35)"}}>Tap + Add above</div>}</div>
              : playerList.map(p=>{
                  const sportComp=CARD_COMPOSITE?.[club.sport]; const ov=p.scores&&Object.keys(p.scores).length?Math.round(["spd","sta","att","pos","str","def","sho","wkr"].map(k=>to99(sportComp?.[k]?calcCardStat(p.scores,sportComp[k]):(p.scores[(CARD_MAP_BY_SPORT?.[club.sport]||CARD_MAP)[k]]||50))).reduce((a,b)=>a+b,0)/8):null;
                  const tierCol=ov?(ov>=84?"#e8e8e8":ov>=75?"#d4af37":ov>=65?"#9aa3b0":"#cd7f32"):null;
                  return (
                    <div key={p.id} onClick={()=>{setCurrentPlayer({...p});setDetailTab("fitness");navigate("detail");}}
                      style={{...G.card,display:"flex",alignItems:"center",gap:12,cursor:"pointer",marginBottom:8}}>
                      <Avatar name={p.name} photo={p.photo} size={42} colour={avCol(p.name)}/>
                      <div style={{flex:1}}>
                        <div style={{fontSize:14,fontWeight:600,color:"#fff"}}>{p.name}</div>
                        <div style={{fontSize:11,color:"rgba(255,255,255,0.38)",marginTop:2}}>{p.position||"—"} · {p.team||"—"}</div>
                      </div>
                      {ov?<div style={{textAlign:"right"}}><div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,fontWeight:800,color:tierCol}}>{ov}</div></div>:<div style={{fontSize:11,color:"rgba(255,255,255,0.3)"}}>Not assessed</div>}
                      <div style={{color:"rgba(255,255,255,0.2)",fontSize:16}}>›</div>
                    </div>
                  );
                })}
          </>
        )}
      </div>
    );
  };

  // ── DETAIL PANEL ──────────────────────────────────────────────────────────
  // ── FITNESS ADD ROW COMPONENT ───────────────────────────────────────────
  const FitnessAddRow = ({ cfg, onAdd }) => {
    const [val, setVal] = React.useState("");
    return (
      <div style={{display:"flex",gap:8,marginBottom:8}}>
        <input
          type="number" step="0.1" placeholder={`Add ${cfg.label}...`}
          value={val} onChange={e=>setVal(e.target.value)}
          style={{...G.input,flex:1,marginBottom:0,fontSize:13}}
        />
        <button onClick={()=>{
          const n=parseFloat(val);
          if(isNaN(n))return;
          onAdd(n);
          setVal("");
        }} style={{...G.btnSm,background:cfg.color,border:"none",color:"#fff",flexShrink:0}}>+ Add</button>
      </div>
    );
  };

  const uploadToStorage = async (dataUrl, folder, id) => {
    if(!clubId) return dataUrl;
    try {
      // Convert base64 directly to blob — no fetch, no re-encoding, no resize
      const parts = dataUrl.split(",");
      const mime = parts[0].match(/:(.*?);/)[1] || "image/jpeg";
      const ext = mime === "image/png" ? "png" : "jpg";
      const byteStr = atob(parts[1]);
      const arr = new Uint8Array(byteStr.length);
      for(let i=0;i<byteStr.length;i++) arr[i]=byteStr.charCodeAt(i);
      const blob = new Blob([arr], {type: mime});
      const path = `${clubId}/${folder}/${id}_${Date.now()}.${ext}`;
      await supabase.storage.from("photos").upload(path, blob, {upsert:true, contentType:mime});
      const { data: urlData } = supabase.storage.from("photos").getPublicUrl(path);
      return urlData.publicUrl;
    } catch(e) { console.error("Upload failed:",e); return dataUrl; }
  };

  const runBgRemoval = async () => {
    if(!photoPreview?.original) return;
    setPhotoPreview(pp=>({...pp, processing:true}));
    showToast("Removing background...");
    try {
      const removeBgKey = process.env.REACT_APP_REMOVEBG_KEY;
      let result = null;
      if(removeBgKey) {
        const base64 = photoPreview.original.split(",")[1];
        const fd = new FormData();
        fd.append("image_file_b64", base64); fd.append("size","auto");
        const res = await fetch("https://api.remove.bg/v1.0/removebg",{method:"POST",headers:{"X-Api-Key":removeBgKey},body:fd});
        if(res.ok){ const blob=await res.blob(); result=await new Promise(r=>{const rd=new FileReader();rd.onload=e=>r(e.target.result);rd.readAsDataURL(blob);}); }
      }
      if(!result) {
        result = await new Promise((resolve,reject)=>{
          const img=new Image(); img.onload=()=>{
            const W=img.width,H=img.height,c=document.createElement("canvas");c.width=W;c.height=H;
            const ctx=c.getContext("2d");ctx.drawImage(img,0,0);
            const id=ctx.getImageData(0,0,W,H),d=id.data;
            const bw=Math.max(4,Math.floor(Math.min(W,H)*0.05)),s=[];
            for(let x=0;x<W;x++)for(let b=0;b<bw;b++){[[x,b],[x,H-1-b]].forEach(([px,py])=>{const i=(py*W+px)*4;s.push([d[i],d[i+1],d[i+2]]);});}
            for(let y=bw;y<H-bw;y++)for(let b=0;b<bw;b++){[[b,y],[W-1-b,y]].forEach(([px,py])=>{const i=(py*W+px)*4;s.push([d[i],d[i+1],d[i+2]]);});}
            const bgR=Math.round(s.reduce((a,v)=>a+v[0],0)/s.length),bgG=Math.round(s.reduce((a,v)=>a+v[1],0)/s.length),bgB=Math.round(s.reduce((a,v)=>a+v[2],0)/s.length);
            for(let i=0;i<d.length;i+=4){const dist=Math.sqrt((d[i]-bgR)**2+(d[i+1]-bgG)**2+(d[i+2]-bgB)**2);if(dist<40)d[i+3]=0;else if(dist<70)d[i+3]=Math.round(255*(dist-40)/30);}
            ctx.putImageData(id,0,0);resolve(c.toDataURL("image/png"));
          };img.onerror=reject;img.src=photoPreview.original;
        });
      }
      setPhotoPreview(pp=>({...pp, processed:result, processing:false}));
      showToast("Done — check the preview ✓");
    } catch(err) {
      setPhotoPreview(pp=>({...pp, processing:false}));
      showToast("Background removal failed");
    }
  };

  const applyPhotoFromPreview = async (dataUrl) => {
    if(!currentPlayer) return;
    const url = await uploadToStorage(dataUrl, "players", currentPlayer.id);
    const updated = {...currentPlayer, photo:url};
    setCurrentPlayer(updated);
    setPlayers(ps=>ps.map(pl=>pl.id===currentPlayer.id ? updated : pl));
    db.updatePlayer(currentPlayer.id, {photo:url}).catch(console.error);
    setPhotoPreview(null);
    setModal(null);
    showToast("Photo saved ✓");
  };

  const DetailPanel = () => {
    const [removing, setRemoving] = useState(false);
    if (!currentPlayer) return null;
    const p = currentPlayer;
    const sportFitTests = FIT_CFG_BY_SPORT[club.sport]||FIT_CFG_BY_SPORT.default||[];

    const savePlayer = async () => {
      const updated={...p,lastAssessed:fmtDate(new Date())};
      setPlayers(ps=>ps.map(pl=>pl.id===p.id?updated:pl));
      setCurrentPlayer(updated);
      if (clubId) {
        db.updatePlayer(p.id, updated).catch(e => console.error("Save failed:", e));
      }
      showToast("Saved ✓");
    };

;

    const handlePhotoUpload = async (file) => {
      if(!file) return;

      // Upload to Supabase Storage


      // Read file
      const reader = new FileReader();
      reader.onload = async (e) => {
        const original = e.target.result;
        // Reset framing to defaults so new photo starts centred
        setCurrentPlayer(cp=>({...cp, photoX:0, photoY:0, photoZoom:120}));
        // Show preview — let user choose original or bg removed
        setPhotoPreview({ original, processed:null, processing:false });
        setModal("photo-preview");
      };
      reader.readAsDataURL(file);
    };

    const applyPhotoToPlayer = async (dataUrl) => {
      const url = await uploadToStorage(dataUrl, "players", p.id);
      const updated = {...p, photo:url};
      setCurrentPlayer(updated);
      setPlayers(ps=>ps.map(pl=>pl.id===p.id ? updated : pl));
      db.updatePlayer(p.id, {photo:url}).catch(console.error);
      setPhotoPreview(null);
      setModal(null);
      showToast("Photo saved ✓");
    };





    return (
      <div style={{...G.panel,paddingBottom:100}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20}}>
          <button onClick={()=>navigate("players")} style={{background:"none",border:"none",color:"rgba(255,255,255,0.4)",fontSize:22,cursor:"pointer"}}>←</button>
          {(isAdmin||currentUser?.id===p.auth_id) ? (
            <input
              key={p.id+"-name"}
              defaultValue={p.name}
              onBlur={e=>{
                const n=e.target.value.trim();
                if(!n||n===p.name) return;
                const updated={...p,name:n};
                setCurrentPlayer(updated);
                setPlayers(ps=>ps.map(pl=>pl.id===p.id?updated:pl));
                supabase.from("players").update({name:n}).eq("id",p.id).catch(console.error);
                supabase.from("members").update({name:n}).eq("id",p.id).catch(console.error);
                showToast("Name updated ✓");
              }}
              style={{flex:1,background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.15)",borderRadius:8,padding:"6px 10px",fontSize:18,fontWeight:800,color:"#fff",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1}}
            />
          ) : (
            <div style={{...G.h2,flex:1}}>{p.name}</div>
          )}
          {isCoach&&<button onClick={savePlayer} style={{background:primaryCol,color:"#fff",border:"none",borderRadius:9,padding:"9px 18px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:14,fontWeight:700,cursor:"pointer"}}>✓ Save</button>}
          {isAdmin&&<button onClick={()=>{if(!window.confirm(`Archive ${p.name}? They will be removed from active views but data is retained.`))return;
            const archived={...p,isArchived:true};
            setPlayers(ps=>ps.filter(pl=>pl.id!==p.id));
            supabase.from("players").update({is_archived:true}).eq("id",p.id).then(()=>showToast(`${p.name} archived`)).catch(console.error);
            supabase.from("members").update({is_archived:true}).eq("id",p.id).catch(console.error);
            navigate("players");
          }} style={{background:"rgba(230,57,70,0.12)",color:"#E63946",border:"1px solid #E6394644",borderRadius:9,padding:"9px 14px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:13,fontWeight:700,cursor:"pointer"}}>🗑 Archive</button>}
        </div>

        <div style={{display:"flex",gap:20,marginBottom:20,alignItems:"flex-start",justifyContent:"center",flexWrap:"wrap"}}>
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:10,flexShrink:0}}>
            <PlayerCard player={p} size="full" clubLogo={club.logo} primaryCol={primaryCol} secondaryCol={secondaryCol} sport={club.sport}/>
            {/* Card display name editor */}
            <div style={{width:"100%",maxWidth:300}}>
              <div style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.3)",letterSpacing:1,marginBottom:6,textTransform:"uppercase"}}>Card Display Name</div>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <input
                  key={p.id+"-displayname"}
                  defaultValue={p.displayName||p.name}
                  placeholder={p.name}
                  onBlur={e=>{
                    const n=e.target.value.trim()||p.name;
                    if(n===(p.displayName||p.name)) return;
                    const updated={...p,displayName:n};
                    setCurrentPlayer(updated);
                    setPlayers(ps=>ps.map(pl=>pl.id===p.id?updated:pl));
                    db.updatePlayer(p.id,{display_name:n}).catch(console.error);
                    showToast("Card name updated ✓");
                  }}
                  style={{flex:1,background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.12)",borderRadius:8,padding:"7px 10px",fontSize:13,color:"#fff",outline:"none"}}
                />
              </div>
              <div style={{fontSize:10,color:"rgba(255,255,255,0.25)",marginTop:4}}>This name appears on the player card — use a nickname if preferred</div>
            </div>

            {/* Stat key — separate box below the card */}
            <div style={{width:"100%",maxWidth:300,padding:"10px 14px",background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:10}}>
              <div style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.3)",letterSpacing:1,marginBottom:7,textTransform:"uppercase"}}>Card Key</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"4px 12px"}}>
                {(p.position==="GK"
                  ? [{l:"REF",f:"Reflexes"},{l:"STA",f:"Stamina"},{l:"DIS",f:"Distribution"},{l:"POS",f:"Positioning"},{l:"PHY",f:"Physical"},{l:"SPG",f:"Shot Stopping"},{l:"KIC",f:"Kicking"},{l:"WRK",f:"Work Rate"}]
                  : club.sport==="Swimming"
                  ? [{l:"SPR",f:"Sprint"},{l:"END",f:"Endurance"},{l:"STK",f:"Stroke"},{l:"PAC",f:"Pacing"},{l:"STR",f:"Strength"},{l:"TRN",f:"Turns"},{l:"STA",f:"Starts"},{l:"CON",f:"Consistency"}]
                  : [{l:"PAC",f:"Pace"},{l:"STA",f:"Stamina"},{l:"DRI",f:"Dribbling"},{l:"PAS",f:"Passing"},{l:"PHY",f:"Physical"},{l:"DEF",f:"Defending"},{l:"SHO",f:"Shooting"},{l:"WRK",f:"Work Rate"}]
                ).map(({l,f})=>(
                  <div key={l} style={{display:"flex",alignItems:"center",gap:6}}>
                    <span style={{fontSize:11,fontWeight:800,color:primaryCol,minWidth:30,letterSpacing:0.5}}>{l}</span>
                    <span style={{fontSize:11,color:"rgba(255,255,255,0.45)"}}>{f}</span>
                  </div>
                ))}
              </div>
            </div>
            {(isCoach||currentUser?.id===p.userId||isPlayer)&&(
              <div style={{display:"flex",gap:8,flexWrap:"wrap",justifyContent:"center"}}>
                <label style={{display:"flex",alignItems:"center",gap:6,padding:"9px 14px",background:`${primaryCol}18`,border:`1.5px solid ${primaryCol}55`,borderRadius:10,cursor:"pointer",fontSize:12,fontWeight:600,color:primaryCol}}>
                  {removing?"⏳ Processing...":"📁 Upload"}
                  <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>handlePhotoUpload(e.target.files[0])}/>
                </label>
                <label style={{display:"flex",alignItems:"center",gap:6,padding:"9px 14px",background:`${primaryCol}18`,border:`1.5px solid ${primaryCol}55`,borderRadius:10,cursor:"pointer",fontSize:12,fontWeight:600,color:primaryCol}}>
                  📷 Camera
                  <input type="file" accept="image/*" capture="user" style={{display:"none"}} onChange={e=>handlePhotoUpload(e.target.files[0])}/>
                </label>
                <button onClick={()=>setModal("photo-tips")} style={{display:"flex",alignItems:"center",gap:6,padding:"9px 14px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:10,cursor:"pointer",fontSize:12,fontWeight:600,color:"rgba(255,255,255,0.5)"}}>
                  💡 Tips
                </button>
              </div>
            )}
            {p.photo&&(photoFrameLocked ? (
              <button onClick={(e)=>{e.stopPropagation();setPhotoFrameLocked(false);}} style={{padding:"8px 18px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.12)",borderRadius:10,color:"rgba(255,255,255,0.5)",fontSize:12,fontWeight:600,cursor:"pointer"}}>
                ✏️ Edit Photo Frame
              </button>
            ) : (
              <PhotoFrameEditor
                p={p}
                primaryCol={primaryCol}
                onNudge={(dx,dy,dz)=>{
                  const px=p.photoX??0,py=p.photoY??0,pz=p.photoZoom??100;
                  const nx=Math.max(-200,Math.min(200,px+dx));
                  const ny=Math.max(-200,Math.min(200,py+dy));
                  const nz=Math.max(80,Math.min(300,pz+dz));
                  const updated={...p,photoX:nx,photoY:ny,photoZoom:nz,scores:{...p.scores,_photoX:nx,_photoY:ny,_photoZoom:nz}};
                  setCurrentPlayer(updated);
                  setPlayers(ps=>ps.map(pl=>pl.id===p.id?updated:pl));
                  db.updatePlayer(p.id,{scores:{...p.scores,_photoX:nx,_photoY:ny,_photoZoom:nz}}).catch(console.error);
                }}
                onLock={()=>setPhotoFrameLocked(true)}
              />
            ))}
            {p.photo&&isCoach&&<button onClick={()=>setCurrentPlayer(cp=>({...cp,photo:null}))} style={{fontSize:12,color:"rgba(255,255,255,0.35)",background:"none",border:"none",cursor:"pointer"}}>Remove photo</button>}
          </div>
          {isCoach&&(
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",flex:1,minWidth:260}}>
              <div style={{...G.h3,marginBottom:10,alignSelf:"flex-start"}}>Radar</div>
              <RadarChart scores={p.scores||{}} size={280} sport={club.sport} position={p.position||""}/>
            </div>
          )}
        </div>

        {isCoach&&(
          <div style={{marginBottom:16}}>
          <ProGate feature="Player performance ratings and attributes">
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
              <div style={G.h3}>Attributes</div>
              <div style={{marginLeft:"auto",display:"flex",gap:8,alignItems:"center"}}>
                <span style={{fontSize:11,color:"rgba(255,255,255,0.38)"}}>Position</span>
                <select value={p.position||"MID"} onChange={e=>setCurrentPlayer(cp=>({...cp,position:e.target.value}))} style={{...G.select,width:"auto",padding:"6px 10px",fontSize:13,marginBottom:0}}>
                  {["GK","DEF","CB","RB","LB","MID","CM","CDM","CAM","RM","LM","RW","LW","FWD","ST","CF"].map(pos=><option key={pos} value={pos}>{pos}</option>)}
                </select>
              </div>
            </div>
            {Object.entries((p.position==="GK"&&ATTRS_BY_SPORT[`${club.sport}-GK`])||ATTRS_BY_SPORT[club.sport]||ATTRS_BY_SPORT.default||{}).map(([cat,attrs])=>(
              <div key={cat} style={{marginBottom:14}}>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:12,fontWeight:800,letterSpacing:2,color:ATTR_COLS[cat],marginBottom:8,textTransform:"uppercase"}}>{cat}</div>
                {attrs.map(attr=>{
                  const val = to99((p.scores&&p.scores[attr])||50);
                  return (
                    <div key={attr} style={{display:"grid",gridTemplateColumns:"130px 1fr 44px",alignItems:"center",gap:10,marginBottom:8}}>
                      <div style={{fontSize:12,color:"rgba(255,255,255,0.6)"}}>{attr}</div>
                      <input type="range" min={1} max={100} value={(p.scores&&p.scores[attr])||50} style={{accentColor:ATTR_COLS[cat],width:"100%"}} onChange={e=>setCurrentPlayer(cp=>({...cp,scores:{...cp.scores,[attr]:parseInt(e.target.value)}}))}/>
                      <input
                        key={`num-${attr}`}
                        type="number" min={1} max={100}
                        defaultValue={(p.scores&&p.scores[attr])||50}
                        onBlur={e=>{
                          const n=Math.max(1,Math.min(100,parseInt(e.target.value)||1));
                          e.target.value=n;
                          setCurrentPlayer(cp=>({...cp,scores:{...cp.scores,[attr]:n}}));
                        }}
                        onKeyDown={e=>{
                          if(e.key==="Enter"){
                            const n=Math.max(1,Math.min(100,parseInt(e.target.value)||1));
                            e.target.value=n;
                            e.target.blur();
                            setCurrentPlayer(cp=>({...cp,scores:{...cp.scores,[attr]:n}}));
                          }
                        }}
                        style={{width:44,background:"none",border:"none",fontFamily:"'Barlow Condensed',sans-serif",fontSize:14,fontWeight:800,color:ATTR_COLS[cat],textAlign:"center",padding:0,outline:"none",MozAppearance:"textfield",WebkitAppearance:"none"}}
                      />
                    </div>
                  );
                })}
              </div>
            ))}
          </ProGate>
          </div>
        )}

        <div style={{display:"flex",gap:6,marginBottom:16,overflowX:"auto",paddingBottom:4}}>
          {["profile","fitness","feedback",...(athletePrograms[p.id]?["programme"]:[])].map(t=>(
            <button key={t} onClick={()=>setDetailTab(t)} style={{padding:"8px 16px",background:detailTab===t?primaryCol:"rgba(255,255,255,0.04)",border:`1px solid ${detailTab===t?primaryCol:"rgba(255,255,255,0.08)"}`,borderRadius:20,color:detailTab===t?"#fff":"rgba(255,255,255,0.45)",fontSize:12,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0,position:"relative"}}>
              {t==="profile"?"👤 Profile":t==="fitness"?"⏱ Fitness":t==="feedback"?"📝 Feedback":"📋 Programme"}
              {(t==="fitness"||t==="programme")&&!isPro&&<span style={{fontSize:9,marginLeft:4,color:primaryCol}}>⚡</span>}
            </button>
          ))}
        </div>

        {detailTab==="profile"&&(
          <div>
            {/* Consent controls */}
            <div style={{...G.card,marginBottom:12,border:"1px solid rgba(240,180,41,0.2)",background:"rgba(240,180,41,0.04)"}}>
              <div style={{fontSize:12,fontWeight:700,color:"#f0b429",marginBottom:10,letterSpacing:1}}>✅ CONSENT & PERMISSIONS</div>
              {[
                {k:"consentMedical", l:"Medical data consent", desc:"I consent to my medical information (conditions, allergies, medication) being stored and visible to club coaches and admins for safety purposes."},
                {k:"consentPhoto",   l:"Photo consent",        desc:"I consent to my photo being used on my player card and visible to club members within the app."},
                {k:"consentContact", l:"Contact data consent", desc:"I consent to my contact details being stored and accessible to club administrators."},
              ].map(({k,l,desc})=>(
                <div key={k} style={{display:"flex",gap:10,alignItems:"flex-start",padding:"10px 0",borderBottom:"1px solid rgba(255,255,255,0.05)"}}>
                  <div style={{paddingTop:2}}>
                    <input type="checkbox"
                      style={{accentColor:primaryCol,width:16,height:16,cursor:"pointer"}}
                      checked={p[k]||false}
                      onChange={async e=>{
                        const val=e.target.checked;
                        const updated={...p,[k]:val};
                        setCurrentPlayer(updated);
                        setPlayers(ps=>ps.map(pl=>pl.id===p.id?updated:pl));
                        await db.updatePlayer(p.id,{[k]:val}).catch(console.error);
                      }}
                      disabled={p.userId!==currentUser?.id&&p.auth_id!==currentUser?.id&&p.member_id!==currentUser?.id&&!isAdmin&&!isCoach}
                    />
                  </div>
                  <div>
                    <div style={{fontSize:12,fontWeight:600,color:"#fff",marginBottom:2}}>{l}</div>
                    <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",lineHeight:1.5}}>{desc}</div>
                    {p[k]&&<div style={{fontSize:10,color:"#22c55e",marginTop:2}}>✓ Consented</div>}
                    {!p[k]&&<div style={{fontSize:10,color:"#f0b429",marginTop:2}}>⚠️ Not yet consented</div>}
                  </div>
                </div>
              ))}
              <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",marginTop:8,lineHeight:1.5}}>
                Only you can change your own consent. Admins cannot override this. You can withdraw consent at any time.
              </div>
            </div>

            {/* Contact details */}
            <div style={{...G.card,marginBottom:12}}>
              <div style={{fontSize:12,fontWeight:700,color:primaryCol,marginBottom:10,letterSpacing:1}}>CONTACT DETAILS</div>
              {[
                {k:"phone",l:"Phone Number",ph:"e.g. 07700 900000",type:"tel"},
                {k:"email",l:"Email Address",ph:"e.g. name@email.com",type:"email"},
                {k:"dob",l:"Date of Birth",ph:"",type:"date"},
                {k:"address",l:"Address",ph:"e.g. 1 Main Street, Manchester"},
              ].map(({k,l,ph,type})=>(
                <div key={k} style={{marginBottom:10}}>
                  <FieldLabel>{l}</FieldLabel>
                  <input
                    type={type||"text"}
                    style={{...G.input,marginBottom:0}}
                    placeholder={ph}
                    defaultValue={p[k]||""}
                    onBlur={async e=>{
                      const val=e.target.value.trim();
                      const updated={...p,[k]:val};
                      setCurrentPlayer(updated);
                      setPlayers(ps=>ps.map(pl=>pl.id===p.id?updated:pl));
                      await db.updatePlayer(p.id,{[k]:val}).catch(console.error);
                    }}
                    readOnly={!isAdmin&&!isCoach&&p.userId!==currentUser?.id&&p.auth_id!==currentUser?.id}
                    style={{...G.input,marginBottom:0,background:(!isAdmin&&!isCoach&&p.userId!==currentUser?.id&&p.auth_id!==currentUser?.id)?"rgba(255,255,255,0.02)":"rgba(255,255,255,0.06)"}}
                  />
                </div>
              ))}
            </div>

            {/* Emergency contact */}
            <div style={{...G.card,marginBottom:12}}>
              <div style={{fontSize:12,fontWeight:700,color:"#f0b429",marginBottom:10,letterSpacing:1}}>🚨 EMERGENCY / NEXT OF KIN</div>
              {[
                {k:"nokName",l:"Name",ph:"e.g. Jane Smith"},
                {k:"nokRelation",l:"Relationship",ph:"e.g. Parent, Spouse"},
                {k:"nokPhone",l:"Phone Number",ph:"e.g. 07700 900000"},
              ].map(({k,l,ph})=>(
                <div key={k} style={{marginBottom:10}}>
                  <FieldLabel>{l}</FieldLabel>
                  <input
                    style={{...G.input,marginBottom:0}}
                    placeholder={ph}
                    defaultValue={p[k]||""}
                    onBlur={async e=>{
                      const val=e.target.value.trim();
                      const updated={...p,[k]:val};
                      setCurrentPlayer(updated);
                      setPlayers(ps=>ps.map(pl=>pl.id===p.id?updated:pl));
                      await db.updatePlayer(p.id,{[k]:val}).catch(console.error);
                    }}
                  />
                </div>
              ))}
            </div>

            {/* Medical — only show if consented */}
            <div style={{...G.card,marginBottom:12,opacity:p.consentMedical?1:0.5}}>
              <div style={{fontSize:12,fontWeight:700,color:"#E63946",marginBottom:6,letterSpacing:1}}>🏥 MEDICAL INFORMATION</div>
              {!p.consentMedical&&(
                <div style={{padding:"8px 12px",background:"rgba(240,180,41,0.08)",border:"1px solid rgba(240,180,41,0.2)",borderRadius:8,marginBottom:10,fontSize:12,color:"#f0b429"}}>
                  ⚠️ Medical consent required — tick the consent box above to enable this section
                </div>
              )}
              <div style={{fontSize:11,color:"rgba(255,255,255,0.35)",marginBottom:10,lineHeight:1.5}}>
                Only visible to club admins and coaches. Stored securely.
              </div>
              {[
                {k:"medConditions",l:"Medical Conditions",ph:"e.g. Asthma, Diabetes — or None"},
                {k:"medAllergies",l:"Allergies",ph:"e.g. Penicillin, Nuts — or None"},
                {k:"medMedication",l:"Current Medication",ph:"e.g. Ventolin inhaler — or None"},
              ].map(({k,l,ph})=>(
                <div key={k} style={{marginBottom:10}}>
                  <FieldLabel>{l}</FieldLabel>
                  <input
                    style={{...G.input,marginBottom:0}}
                    placeholder={ph}
                    defaultValue={p[k]||""}
                    onBlur={async e=>{
                      const val=e.target.value.trim();
                      const updated={...p,[k]:val};
                      setCurrentPlayer(updated);
                      setPlayers(ps=>ps.map(pl=>pl.id===p.id?updated:pl));
                      if(isAdmin||isCoach) await db.updatePlayer(p.id,{[k]:val}).catch(console.error);
                    }}
                  />
                </div>
              ))}
              <div style={{marginBottom:0}}>
                <FieldLabel>Has Own First Aid Kit?</FieldLabel>
                <div style={{display:"flex",gap:8}}>
                  {["Yes","No","Unknown"].map(v=>(
                    <button key={v} onClick={async()=>{
                      const updated={...p,medFirstAid:v};
                      setCurrentPlayer(updated);
                      setPlayers(ps=>ps.map(pl=>pl.id===p.id?updated:pl));
                      await db.updatePlayer(p.id,{medFirstAid:v}).catch(console.error);
                    }} style={{padding:"6px 16px",background:p.medFirstAid===v?primaryCol:"rgba(255,255,255,0.05)",border:`1px solid ${p.medFirstAid===v?primaryCol:"rgba(255,255,255,0.1)"}`,borderRadius:8,color:p.medFirstAid===v?contrastText(primaryCol):"rgba(255,255,255,0.5)",fontSize:12,fontWeight:600,cursor:"pointer"}}>
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Membership */}
            <div style={{...G.card,marginBottom:12}}>
              <div style={{fontSize:12,fontWeight:700,color:"rgba(255,255,255,0.4)",marginBottom:10,letterSpacing:1}}>MEMBERSHIP</div>
              {[
                {k:"englandHockeyId",l:"England Hockey ID",ph:"EH member number"},
              ].map(({k,l,ph,type})=>(
                <div key={k} style={{marginBottom:10}}>
                  <FieldLabel>{l}</FieldLabel>
                  <input
                    type={type||"text"}
                    style={{...G.input,marginBottom:0}}
                    placeholder={ph}
                    defaultValue={p[k]||""}
                    onBlur={async e=>{
                      const val=e.target.value.trim();
                      const updated={...p,[k]:val};
                      setCurrentPlayer(updated);
                      setPlayers(ps=>ps.map(pl=>pl.id===p.id?updated:pl));
                      await db.updatePlayer(p.id,{[k]:val}).catch(console.error);
                    }}
                  />
                </div>
              ))}
            </div>
            {/* My Data / Delete */}
            {p.id===currentUser?.id&&(
              <div style={{...G.card,marginBottom:12,border:"1px solid rgba(230,57,70,0.2)",background:"rgba(230,57,70,0.04)"}}>
                <div style={{fontSize:12,fontWeight:700,color:"#E63946",marginBottom:8,letterSpacing:1}}>🗑 MY DATA</div>
                <div style={{fontSize:12,color:"rgba(255,255,255,0.45)",lineHeight:1.6,marginBottom:12}}>
                  You have the right to request deletion of your personal data from P4M. Your club admin will be notified. Profile data, medical info, photos and contact details will be permanently erased.
                </div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  <button onClick={()=>{
                    window.open("mailto:privacy@p4m.club?subject=Data Deletion Request&body=Please delete all personal data associated with my account.%0A%0AName: "+encodeURIComponent(p.name||"")+"/%0AEmail: "+encodeURIComponent(currentUser?.email||"")+"/%0AClub: "+encodeURIComponent(club.name||""),"_blank");
                  }} style={{padding:"9px 16px",background:"rgba(230,57,70,0.12)",border:"1px solid rgba(230,57,70,0.3)",borderRadius:9,color:"#E63946",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                    📧 Request Data Deletion
                  </button>
                  <button onClick={()=>{
                    window.open("mailto:privacy@p4m.club?subject=Subject Access Request&body=Please provide a copy of all personal data held about me.%0A%0AName: "+encodeURIComponent(p.name||"")+"/%0AEmail: "+encodeURIComponent(currentUser?.email||"")+"/%0AClub: "+encodeURIComponent(club.name||""),"_blank");
                  }} style={{padding:"9px 16px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:9,color:"rgba(255,255,255,0.5)",fontSize:12,fontWeight:600,cursor:"pointer"}}>
                    📋 Request My Data (SAR)
                  </button>
                </div>
                <div style={{fontSize:10,color:"rgba(255,255,255,0.25)",marginTop:8}}>privacy@p4m.club · We respond within 30 days as required by UK GDPR</div>
              </div>
            )}
          </div>
        )}

        {detailTab==="fitness"&&(
          <div>
          <ProGate feature="Fitness testing and analytics">
            {sportFitTests.map(cfg=>{
              const entries=(p.fitness&&p.fitness[cfg.key])||[];
              const latest=entries[entries.length-1];
              const best=entries.length?(cfg.higher?Math.max(...entries.map(e=>e.val)):Math.min(...entries.map(e=>e.val))):null;
              return (
                <div key={cfg.key} style={{...G.card,marginBottom:10}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:14,fontWeight:800,color:cfg.color}}>{cfg.label}</div>
                    <div style={{fontSize:11,color:"rgba(255,255,255,0.35)",marginLeft:"auto"}}>{cfg.unit}</div>
                  </div>
                  {isCoach&&<FitnessAddRow cfg={cfg} onAdd={val=>{const updated={...p,fitness:{...p.fitness,[cfg.key]:[...(p.fitness?.[cfg.key]||[]),{val,date:fmtDate(new Date())}]}};setCurrentPlayer(updated);setPlayers(ps=>ps.map(pl=>pl.id===p.id?updated:pl));db.updatePlayer(p.id,{fitness:updated.fitness}).catch(console.error);}}/>}
                  {entries.length>0&&(
                    <div style={{marginTop:6}}>
                      {entries.slice().reverse().slice(0,5).map((e,i)=>(
                        <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",borderBottom:"1px solid rgba(255,255,255,0.04)",fontSize:12}}>
                          <span style={{color:"rgba(255,255,255,0.5)"}}>{e.date}</span>
                          <span style={{color:"#fff",fontWeight:600}}>{e.val} {cfg.unit}</span>
                        </div>
                      ))}
                      {best!==null&&<div style={{fontSize:11,color:"rgba(255,255,255,0.4)",marginTop:6}}>Best: <span style={{color:"#f0b429",fontWeight:700}}>{best}{cfg.unit&&cfg.unit!=="level"?cfg.unit:""}</span></div>}
                    </div>
                  )}
                </div>
              );
            })}
          </ProGate>
          </div>
        )}

        {detailTab==="feedback"&&(
          <div>
            {isCoach&&<Btn col={primaryCol} onClick={()=>setModal(club.sport==="Swimming"?"swimfeedback":"feedback")} style={{marginBottom:12}}>+ Add {club.sport==="Swimming"?"Meet":"Match"} Feedback</Btn>}
            {(p.feedback||[]).length===0&&<div style={{fontSize:13,color:"rgba(255,255,255,0.3)",fontStyle:"italic"}}>No feedback yet</div>}
            {(p.feedback||[]).map((fb,i)=>(
              <div key={i} style={{...G.card,marginBottom:10}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:15,fontWeight:700,color:"#fff"}}>{fb.match}</div>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.35)"}}>{fb.date}</div>
                </div>
                {fb.result&&<div style={{fontSize:12,color:primaryCol,fontWeight:600,marginBottom:6}}>{fb.result}</div>}
                <div style={{fontSize:13,color:"rgba(255,255,255,0.7)",lineHeight:1.5}}>{fb.note}</div>
                {fb.playerResponse&&<div style={{marginTop:8,padding:"8px 12px",background:"rgba(255,255,255,0.04)",borderRadius:8,fontSize:12,color:"rgba(255,255,255,0.6)",fontStyle:"italic"}}>Player: "{fb.playerResponse}"</div>}
              </div>
            ))}
          </div>
        )}

        {detailTab==="programme"&&(
          <div>
          <ProGate feature="AI individual player programmes">
            {!athletePrograms[p.id] ? (
              <div style={{textAlign:"center",padding:"32px 0"}}>
                <div style={{fontSize:32,marginBottom:12}}>📋</div>
                <div style={{fontSize:14,fontWeight:700,color:"#fff",marginBottom:6}}>No programme yet</div>
                <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",lineHeight:1.6,marginBottom:16,maxWidth:260,margin:"0 auto 16px"}}>
                  {isCoach
                    ? "Go to the Coach tab to generate an individual programme for this player based on their attributes."
                    : "Your coach hasn't created a personal programme for you yet."}
                </div>
                {isCoach&&<button onClick={()=>navigate("coach")} style={{...G.btnPrimary,background:primaryCol}}>Go to Coach →</button>}
              </div>
            ) : (
              <div>
                {/* Programme header */}
                <div style={{...G.card,marginBottom:12,borderLeft:`3px solid ${primaryCol}`,background:`${primaryCol}08`}}>
                  <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:10,marginBottom:8}}>
                    <div>
                      <div style={{fontSize:11,fontWeight:700,color:primaryCol,letterSpacing:1,marginBottom:4}}>PERSONAL PROGRAMME</div>
                      <div style={{fontSize:14,fontWeight:700,color:"#fff"}}>{p.name}</div>
                      <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",marginTop:2}}>{athletePrograms[p.id].weeks?.length||6} weeks · {p.position||"Player"}</div>
                    </div>
                    {isCoach&&(
                      <button onClick={async()=>{
                        if(!window.confirm("Delete this programme and regenerate?")) return;
                        setAthletePrograms(ap=>{const n={...ap};delete n[p.id];return n;});
                        await supabase.from("training_programs").delete().eq("club_id",clubId).eq("player_id",p.id);
                        showToast("Programme deleted — go to Coach to regenerate");
                      }} style={{background:"rgba(230,57,70,0.1)",border:"1px solid rgba(230,57,70,0.2)",borderRadius:8,color:"#E63946",fontSize:11,padding:"6px 10px",cursor:"pointer",flexShrink:0}}>
                        Delete
                      </button>
                    )}
                  </div>
                  <div style={{fontSize:13,color:"rgba(255,255,255,0.75)",lineHeight:1.65,fontStyle:"italic"}}>
                    {athletePrograms[p.id].overview}
                  </div>
                  {athletePrograms[p.id].createdAt&&(
                    <div style={{fontSize:10,color:"rgba(255,255,255,0.25)",marginTop:8}}>
                      Created {new Date(athletePrograms[p.id].createdAt).toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"})}
                    </div>
                  )}
                </div>

                {/* Week selector */}
                {(athletePrograms[p.id].weeks?.length||0)>1&&(
                  <div style={{display:"flex",gap:6,marginBottom:12,overflowX:"auto",paddingBottom:4}}>
                    {(athletePrograms[p.id].weeks||[]).map((w,wi)=>{
                      const progSelWeek = athletePrograms[p.id]._selectedWeek||0;
                      return (
                        <button key={wi} onClick={()=>{
                          setAthletePrograms(ap=>({...ap,[p.id]:{...ap[p.id],_selectedWeek:wi}}));
                        }} style={{padding:"6px 14px",background:progSelWeek===wi?primaryCol:"rgba(255,255,255,0.05)",border:`1px solid ${progSelWeek===wi?primaryCol:"rgba(255,255,255,0.1)"}`,borderRadius:20,color:progSelWeek===wi?"#fff":"rgba(255,255,255,0.45)",fontSize:12,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>
                          Wk {w.week}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Selected week detail */}
                {(()=>{
                  const prog = athletePrograms[p.id];
                  const selWi = prog._selectedWeek||0;
                  const week = prog.weeks?.[selWi];
                  if(!week) return null;
                  return (
                    <div>
                      <div style={{...G.card,marginBottom:10,background:`${primaryCol}06`}}>
                        <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:17,fontWeight:800,color:primaryCol,marginBottom:4}}>
                          Week {week.week} — {week.theme}
                        </div>
                        {week.objective&&<div style={{fontSize:12,color:"rgba(255,255,255,0.55)",lineHeight:1.5,fontStyle:"italic"}}>{week.objective}</div>}
                      </div>
                      {(week.sessions||[]).map((s,si)=>(
                        <div key={si} style={{...G.card,marginBottom:10}}>
                          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                            <div style={{background:`${primaryCol}18`,borderRadius:6,padding:"4px 10px",fontSize:11,fontWeight:700,color:primaryCol,flexShrink:0}}>{s.day}</div>
                            <div style={{fontSize:13,fontWeight:700,color:"#fff",flex:1}}>{s.title}</div>
                            <div style={{fontSize:11,color:"rgba(255,255,255,0.35)"}}>{s.duration} min</div>
                          </div>
                          {[["🏃 Warm-up",s.warmup,"rgba(255,255,255,0.5)"],["⚡ Main Session",s.main,"rgba(255,255,255,0.8)"],["🧘 Cool-down",s.cooldown,"rgba(255,255,255,0.5)"]].filter(([,v])=>v).map(([lbl,val,col])=>(
                            <div key={lbl} style={{marginBottom:8,paddingBottom:8,borderBottom:"1px solid rgba(255,255,255,0.05)"}}>
                              <div style={{fontSize:10,fontWeight:700,letterSpacing:1,color:"rgba(255,255,255,0.3)",marginBottom:3}}>{lbl}</div>
                              <div style={{fontSize:12,color:col,lineHeight:1.55}}>{val}</div>
                            </div>
                          ))}
                          {s.drills?.length>0&&(
                            <div style={{marginBottom:8}}>
                              <div style={{fontSize:10,fontWeight:700,letterSpacing:1,color:"rgba(255,255,255,0.3)",marginBottom:6}}>KEY DRILLS</div>
                              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                                {s.drills.map((d,di)=>(
                                  <span key={di} style={{padding:"3px 10px",background:`${primaryCol}15`,border:`1px solid ${primaryCol}33`,borderRadius:20,fontSize:11,color:"rgba(255,255,255,0.7)"}}>{d}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          {s.coachingPoints?.length>0&&(
                            <div>
                              <div style={{fontSize:10,fontWeight:700,letterSpacing:1,color:"rgba(255,255,255,0.3)",marginBottom:6}}>COACHING POINTS</div>
                              {s.coachingPoints.map((cp,ci)=>(
                                <div key={ci} style={{display:"flex",gap:8,marginBottom:4}}>
                                  <span style={{color:primaryCol,fontWeight:700,flexShrink:0}}>→</span>
                                  <span style={{fontSize:12,color:"rgba(255,255,255,0.7)",lineHeight:1.4}}>{cp}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}
          </ProGate>
          </div>
        )}
      </div>
    );
  };


  // ── NAV ───────────────────────────────────────────────────────────────────────
  const coreNavItems = [
    {p:"home",     icon:"🏠", label:"Home"},
    {p:"fixtures", icon:"📅", label:FIXTURES_TERM},
    {p:"clubhub",  icon:"💬", label:"ClubHub"},
    {p:"messages", icon:"✉️", label:"Messages", badge:unreadCount||null},
    ...(isCoach||isCaptain?[{p:"coach",icon:"🏆",label:"Coach"}]:[]),
    {p:"players",  icon:"👤", label:MEMBER_TERM[club.sport]||"Players"},
  ];
  const moreNavItems = [
    ...(!isPlayer ? [{p:"club-settings", icon:"⚙️", label:"Club"}] : []),
    {p:"results",   icon:"📊", label:"Results"},
    {p:"drills",    icon:"🎯", label:"Drills & Skills"},
    {p:"events",    icon:"🎉", label:"Events"},
    {p:"festivals", icon:"🏆", label:"Tournaments"},
    {p:"payments",  icon:"💸", label:"Pay"},
    ...(isPlayer ? [{p:"profile-settings", icon:"👤", label:"My Profile"}] : []),
  ];
  const panelToNav = {calendar:"fixtures","squad-cards":"players",detail:"players"};
  const activeNav  = panelToNav[panel]||(panel.startsWith("lineup-")?"fixtures":panel);


  // ── CLUB SETTINGS PANEL ──────────────────────────────────────────────────
  const ProfileSettingsPanel = () => {
    const myMember = currentUser;
    const myPlayer = players.find(p=>p.userId===myMember?.id||p.auth_id===myMember?.id||p.member_id===myMember?.id||p.name===myMember?.name);
    const [saving, setSaving] = React.useState(false);
    const [linkSearch, setLinkSearch] = React.useState("");
    const [showLinkSearch, setShowLinkSearch] = React.useState(false);

    // Link to existing player or create new one
    const linkPlayerProfile = async(player) => {
      setSaving(true);
      try {
        await supabase.from("players").update({member_id:myMember.id}).eq("id",player.id);
        await supabase.from("members").update({also_player:true}).eq("id",myMember.id);
        setCurrentUser(u=>({...u,also_player:true}));
        setPlayers(ps=>ps.map(p=>p.id===player.id?{...p,userId:myMember.id,member_id:myMember.id}:p));
        showToast(`Linked to ${player.name}'s profile ✓`);
        setShowLinkSearch(false);
      } catch(e){ showToast("Link failed"); }
      setSaving(false);
    };

    const createPlayerProfile = async(team, position) => {
      setSaving(true);
      try {
        const {data:newPlayer} = await supabase.from("players").insert({
          club_id:clubId, member_id:myMember.id,
          name:myMember.name, team, position, scores:{}, fitness:{},
        }).select().single();
        await supabase.from("members").update({also_player:true}).eq("id",myMember.id);
        setCurrentUser(u=>({...u,also_player:true}));
        if(newPlayer) setPlayers(ps=>[...ps,{...newPlayer,userId:myMember.id}]);
        showToast("Player profile created ✓");
      } catch(e){ showToast("Failed to create profile"); }
      setSaving(false);
    };

    const [newTeam, setNewTeam] = React.useState(myMember?.team||"");
    const [newPos, setNewPos] = React.useState("");

    return (
      <div style={{...G.panel,paddingBottom:100}}>
        <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,fontWeight:900,color:"#fff",marginBottom:4}}>My Account</div>
        <div style={{fontSize:12,color:"rgba(255,255,255,0.38)",marginBottom:20}}>{myMember?.email}</div>

        {/* Account info */}
        <div style={{...G.card,marginBottom:14}}>
          <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.4)",letterSpacing:1.5,marginBottom:12}}>ACCOUNT DETAILS</div>
          <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
            <Avatar name={myMember?.name} size={48} colour={primaryCol}/>
            <div>
              <div style={{fontSize:16,fontWeight:700,color:"#fff"}}>{myMember?.name}</div>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.45)"}}>{ROLES.find(r=>r.r===myMember?.role)?.name||myMember?.role} · {myMember?.team||"All teams"}</div>
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 0",borderTop:"1px solid rgba(255,255,255,0.07)"}}>
            <div style={{flex:1,fontSize:13,color:"rgba(255,255,255,0.5)"}}>Email</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.8)"}}>{myMember?.email}</div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 0",borderTop:"1px solid rgba(255,255,255,0.07)"}}>
            <div style={{flex:1,fontSize:13,color:"rgba(255,255,255,0.5)"}}>Role</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.8)"}}>{ROLES.find(r=>r.r===myMember?.role)?.name||myMember?.role}</div>
          </div>
        </div>

        {/* Player profile section */}
        <div style={{...G.card,marginBottom:14}}>
          <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.4)",letterSpacing:1.5,marginBottom:12}}>PLAYER PROFILE</div>
          {myPlayer ? (
            <div>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12,padding:"10px 12px",background:`${primaryCol}10`,border:`1px solid ${primaryCol}33`,borderRadius:10}}>
                <Avatar name={myPlayer.name} photo={myPlayer.photo} size={40} colour={avCol(myPlayer.name)}/>
                <div style={{flex:1}}>
                  <div style={{fontSize:14,fontWeight:700,color:"#fff"}}>{myPlayer.name}</div>
                  <div style={{fontSize:12,color:"rgba(255,255,255,0.45)"}}>{myPlayer.position||"No position"} · {myPlayer.team||"No team"}</div>
                </div>
                <div style={{fontSize:11,color:"#22c55e",fontWeight:700}}>✓ Linked</div>
              </div>
              <button onClick={()=>{setCurrentPlayer({...myPlayer});navigate("detail");}}
                style={{width:"100%",padding:"10px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:8,color:"rgba(255,255,255,0.7)",fontSize:13,fontWeight:600,cursor:"pointer"}}>
                View my player profile →
              </button>
            </div>
          ) : (
            <div>
              <div style={{fontSize:13,color:"rgba(255,255,255,0.45)",marginBottom:14,lineHeight:1.6}}>
                You don't have a player profile linked yet. Link to an existing player record or create a new one.
              </div>
              {/* Search existing players */}
              <button onClick={()=>setShowLinkSearch(!showLinkSearch)}
                style={{width:"100%",padding:"11px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.12)",borderRadius:8,color:"rgba(255,255,255,0.7)",fontSize:13,fontWeight:600,cursor:"pointer",marginBottom:8}}>
                🔗 Link to existing player record
              </button>
              {showLinkSearch&&(()=>{
                const LS=()=>{
                  const [s,setS]=React.useState("");
                  const filtered=players.filter(p=>!p.member_id&&p.name.toLowerCase().includes(s.toLowerCase()));
                  return (
                    <div style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:10,padding:10,marginBottom:10}}>
                      <input style={{...G.input,marginBottom:8}} placeholder="Search player name..." value={s} onChange={e=>setS(e.target.value)}/>
                      {filtered.length===0&&s.length>0&&<div style={{fontSize:12,color:"rgba(255,255,255,0.3)",textAlign:"center",padding:"8px 0"}}>No unlinked players found</div>}
                      {filtered.slice(0,5).map(p=>(
                        <div key={p.id} onClick={()=>linkPlayerProfile(p)}
                          style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",borderRadius:8,cursor:"pointer",marginBottom:4}}
                          onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.07)"}
                          onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                          <Avatar name={p.name} size={32} colour={avCol(p.name)}/>
                          <div style={{flex:1}}>
                            <div style={{fontSize:13,fontWeight:600,color:"#fff"}}>{p.name}</div>
                            <div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>{p.position||"—"} · {p.team||"—"}</div>
                          </div>
                          <div style={{fontSize:11,color:primaryCol,fontWeight:600}}>Link →</div>
                        </div>
                      ))}
                    </div>
                  );
                };
                return <LS/>;
              })()}
              {/* Or create new */}
              <div style={{...G.card,background:"rgba(255,255,255,0.03)"}}>
                <div style={{fontSize:12,fontWeight:600,color:"rgba(255,255,255,0.5)",marginBottom:10}}>Or create a new player profile:</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10}}>
                  <div>
                    <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",marginBottom:4}}>Team</div>
                    <select style={{...G.select,marginBottom:0,fontSize:12}} value={newTeam} onChange={e=>setNewTeam(e.target.value)}>
                      <option value="">Select...</option>
                      {teams.map(t=><option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",marginBottom:4}}>Position</div>
                    <input style={{...G.input,marginBottom:0,fontSize:12}} placeholder="e.g. Midfielder" value={newPos} onChange={e=>setNewPos(e.target.value)}/>
                  </div>
                </div>
                <button onClick={()=>createPlayerProfile(newTeam,newPos)} disabled={saving}
                  style={{width:"100%",padding:"10px",background:primaryCol,border:"none",borderRadius:8,color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer"}}>
                  {saving?"Creating...":"+ Create Player Profile"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Sign out */}
        <button onClick={()=>{setScreen("logging-out");setTimeout(()=>{db.signOut().then(()=>{setCurrentUser(null);setClubId(null);setScreen("auth");setAuthMode("login");});},1500);}}
          style={{width:"100%",padding:"12px",background:"rgba(230,57,70,0.08)",border:"1px solid rgba(230,57,70,0.2)",borderRadius:10,color:"#E63946",fontSize:14,fontWeight:700,cursor:"pointer"}}>
          Sign Out
        </button>
      </div>
    );
  };

  const ClubSettingsPanel = () => {
    const [form, setForm] = useState({
      name: club.name||"", sport: club.sport||"Hockey",
      primary: club.primary||"#E63946", secondary: club.secondary||"#1e3a8a",
      ethos: club.ethos||"", mens: club.mens||0, ladies: club.ladies||0,
      juniorAges: [...(club.juniorAges||[])], swimGroups: [...(club.swimGroups||[])],
      gmsClubId: club.gmsClubId||"",
      gmsTeams: club.gmsTeams||[],
    });
    const [saving, setSaving] = useState(false);
    const [newAge, setNewAge] = useState("");
    const [newGroup, setNewGroup] = useState("");
    const [memberSearch, setMemberSearch] = useState("");

    const save = async () => {
      setSaving(true);
      try {
        const payload = {
          name: form.name, sport: form.sport,
          primary_col: form.primary, secondary_col: form.secondary,
          ethos: form.ethos, mens: form.mens, ladies: form.ladies,
          junior_ages: form.juniorAges, swim_groups: form.swimGroups,
          gms_club_id: form.gmsClubId||"",
          gms_teams: form.gmsTeams||[],
        };
        await db.updateClub(clubId, payload);
        setClub(c=>({...c, name:form.name, sport:form.sport,
          primary:form.primary, secondary:form.secondary, ethos:form.ethos,
          mens:form.mens, ladies:form.ladies,
          juniorAges:form.juniorAges, swimGroups:form.swimGroups,
          gmsClubId:form.gmsClubId||"",
          gmsTeams:form.gmsTeams||[],
        }));
        showToast("Club settings saved ✓");
      } catch(e) { showToast("Save failed — try again"); console.error(e); }
      setSaving(false);
    };

    if(!isAdmin) return (
      <div style={{...G.panel,...G.center,gap:8}}>
        <div style={{fontSize:32}}>🔒</div>
        <div style={{fontSize:14,color:"rgba(255,255,255,0.4)"}}>Club settings are admin only</div>
      </div>
    );

    return (
      <div data-tour="club-settings" style={{...G.panel,paddingBottom:100}}>
        <SectionHdr title="Club Settings"/>

        {/* Club Name & Sport */}
        <div style={G.card}>
          <div style={{...G.h3,marginBottom:12}}>Club Details</div>

          {/* Badge upload */}
          <FieldLabel>Club Badge</FieldLabel>
          <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:14}}>
            <div style={{width:72,height:72,borderRadius:"50%",background:`${form.primary}22`,border:`2px solid ${form.primary}66`,overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              {club.logo
                ? <img src={club.logo} alt="" style={{width:"100%",height:"100%",objectFit:"cover",objectPosition:club.logoPos||"center center"}}/>
                : <div style={{fontSize:28,opacity:0.4}}>🛡️</div>}
            </div>
            <div style={{flex:1}}>
              <label style={{display:"flex",alignItems:"center",gap:8,padding:"9px 14px",background:`${primaryCol}18`,border:`1.5px solid ${primaryCol}55`,borderRadius:10,cursor:"pointer",fontSize:13,fontWeight:600,color:primaryCol,marginBottom:8}}>
                📷 {club.logo?"Change Badge":"Upload Badge"}
                <input type="file" accept="image/*" style={{display:"none"}} onChange={async e=>{
                  const file=e.target.files[0];if(!file)return;
                  const reader=new FileReader();
                  reader.onload=async ev=>{
                    const original=ev.target.result;
                    let logoUrl=original;
                    if(clubId){
                      try{
                        const res=await fetch(original);const blob=await res.blob();
                        const path=`${clubId}/badge_${Date.now()}.jpg`;
                        await supabase.storage.from("photos").upload(path,blob,{upsert:true,contentType:"image/jpeg"});
                        const {data:urlData}=supabase.storage.from("photos").getPublicUrl(path);
                        logoUrl=urlData.publicUrl;
                      }catch(err){console.error("Badge upload failed:",err);}
                    }
                    setClub(c=>({...c,logo:logoUrl}));
                    db.updateClub(clubId,{logo_url:logoUrl}).catch(console.error);
                    showToast("Badge updated ✓");
                  };
                  reader.readAsDataURL(file);
                }}/>
              </label>
              {club.logo&&(
                <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:4,width:108}}>
                  {[["top left","↖"],["top center","↑"],["top right","↗"],["center left","←"],["center center","·"],["center right","→"],["bottom left","↙"],["bottom center","↓"],["bottom right","↘"]].map(([pos,icon])=>(
                    <button key={pos} onClick={()=>{setClub(c=>({...c,logoPos:pos}));db.updateClub(clubId,{logo_pos:pos}).catch(console.error);}}
                      style={{padding:5,background:club.logoPos===pos?primaryCol:"rgba(255,255,255,0.06)",border:`1px solid ${club.logoPos===pos?primaryCol:"rgba(255,255,255,0.1)"}`,borderRadius:5,color:"#fff",cursor:"pointer",fontSize:13,lineHeight:1}}>
                      {icon}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <Input label="Club Name" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))}/>
          <FieldLabel>Sport</FieldLabel>
          <div style={{display:"flex",gap:8,marginBottom:14}}>
            {[{e:"🏑",n:"Hockey"},{e:"⚽",n:"Football"},{e:"🏊",n:"Swimming"}].map(s=>(
              <button key={s.n} onClick={()=>setForm(f=>({...f,sport:s.n}))}
                style={{flex:1,padding:"10px 6px",background:form.sport===s.n?primaryCol:"rgba(255,255,255,0.04)",border:`1px solid ${form.sport===s.n?primaryCol:"rgba(255,255,255,0.1)"}`,borderRadius:10,color:form.sport===s.n?"#fff":"rgba(255,255,255,0.5)",fontSize:13,fontWeight:700,cursor:"pointer"}}>
                {s.e} {s.n}
              </button>
            ))}
          </div>
          <TextArea label="Club Ethos" value={form.ethos} onChange={e=>setForm(f=>({...f,ethos:e.target.value}))} style={{minHeight:80}}/>

          {/* GMS Club ID — Hockey only */}
          {form.sport==="Hockey"&&(
            <div style={{marginTop:14,paddingTop:14,borderTop:"1px solid rgba(255,255,255,0.07)"}}>
              <FieldLabel>England Hockey GMS Club ID <span style={{color:"rgba(255,255,255,0.3)",fontWeight:400,fontSize:11}}>— live results & tables</span></FieldLabel>
              <ProGate feature="England Hockey GMS integration">
              <input
                style={{...G.input,marginBottom:6}}
                placeholder="e.g. 19fd1b0c-e11c-46f9-9114-45c2262a1e90"
                value={form.gmsClubId}
                onChange={e=>setForm(f=>({...f,gmsClubId:e.target.value.trim()}))}
              />
              <div style={{fontSize:11,color:"rgba(255,255,255,0.35)",lineHeight:1.7,padding:"8px 10px",background:"rgba(255,255,255,0.03)",borderRadius:8}}>
                <strong style={{color:"rgba(255,255,255,0.6)"}}>How to find your Club ID:</strong><br/>
                1. Go to <span style={{color:primaryCol,fontWeight:600}}>gmsfeed.co.uk/widget</span><br/>
                2. Make sure <strong style={{color:"rgba(255,255,255,0.5)"}}>Club</strong> is selected (not Team or Competition)<br/>
                3. Click <strong style={{color:"rgba(255,255,255,0.5)"}}>Find Club</strong> and search <strong style={{color:"rgba(255,255,255,0.5)"}}>{form.name||"your club name"}</strong><br/>
                4. Copy the Club ID and paste it above<br/>
                <span style={{color:"rgba(255,255,255,0.25)"}}>This loads fixtures & results for ALL your teams automatically</span>
              </div>
              </ProGate>
            </div>
          )}
        </div>

        {/* Colours */}
        <div style={G.card}>
          <div style={{...G.h3,marginBottom:12}}>Club Colours</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            {[{k:"primary",l:"Primary"},{k:"secondary",l:"Secondary"}].map(({k,l})=>(
              <div key={k}>
                <FieldLabel>{l} Colour</FieldLabel>
                <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                  {COLOURS.map(c=>(
                    <div key={c.h} onClick={()=>setForm(f=>({...f,[k]:c.h}))}
                      style={{width:24,height:24,borderRadius:"50%",background:c.h,border:`3px solid ${form[k]===c.h?"#fff":"transparent"}`,cursor:"pointer",flexShrink:0}}/>
                  ))}
                </div>
                <div style={{marginTop:6,fontSize:11,color:"rgba(255,255,255,0.4)"}}>{COLOURS.find(c=>c.h===form[k])?.n||"Custom"}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Teams — only for non-swimming */}
        {form.sport !== "Swimming" && (
          <div style={G.card}>
            <div style={{...G.h3,marginBottom:12}}>Teams</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
              <div>
                <FieldLabel>Men's Teams</FieldLabel>
                <div style={{display:"flex",alignItems:"center",gap:12}}>
                  <button onClick={()=>setForm(f=>({...f,mens:Math.max(0,f.mens-1)}))} style={{width:32,height:32,borderRadius:"50%",background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",color:"#fff",fontSize:18,cursor:"pointer"}}>−</button>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:800,color:"#fff",minWidth:24,textAlign:"center"}}>{form.mens}</div>
                  <button onClick={()=>setForm(f=>({...f,mens:Math.min(10,f.mens+1)}))} style={{width:32,height:32,borderRadius:"50%",background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",color:"#fff",fontSize:18,cursor:"pointer"}}>+</button>
                </div>
              </div>
              <div>
                <FieldLabel>Ladies' Teams</FieldLabel>
                <div style={{display:"flex",alignItems:"center",gap:12}}>
                  <button onClick={()=>setForm(f=>({...f,ladies:Math.max(0,f.ladies-1)}))} style={{width:32,height:32,borderRadius:"50%",background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",color:"#fff",fontSize:18,cursor:"pointer"}}>−</button>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:800,color:"#fff",minWidth:24,textAlign:"center"}}>{form.ladies}</div>
                  <button onClick={()=>setForm(f=>({...f,ladies:Math.min(10,f.ladies+1)}))} style={{width:32,height:32,borderRadius:"50%",background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",color:"#fff",fontSize:18,cursor:"pointer"}}>+</button>
                </div>
              </div>
            </div>
            <FieldLabel>Junior Age Groups</FieldLabel>
            <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:10}}>
              {form.juniorAges.map(age=>(
                <div key={age} style={{display:"flex",alignItems:"center",gap:4,padding:"4px 10px",background:`${primaryCol}18`,border:`1px solid ${primaryCol}55`,borderRadius:20}}>
                  <span style={{fontSize:12,color:"#fff"}}>{age}</span>
                  <button onClick={()=>setForm(f=>({...f,juniorAges:f.juniorAges.filter(a=>a!==age)}))} style={{background:"none",border:"none",color:"rgba(255,255,255,0.4)",cursor:"pointer",fontSize:12,padding:0}}>✕</button>
                </div>
              ))}
            </div>
            <div style={{display:"flex",gap:8}}>
              <select style={{...G.select,flex:1,marginBottom:0}} value={newAge} onChange={e=>setNewAge(e.target.value)}>
                <option value="">Add age group...</option>
                {JUNIOR_AGES.filter(a=>!form.juniorAges.includes(a)).map(a=><option key={a} value={a}>{a}</option>)}
              </select>
              <button style={G.btnSm} onClick={()=>{if(newAge){setForm(f=>({...f,juniorAges:[...f.juniorAges,newAge]}));setNewAge("");}}}>+ Add</button>
            </div>
          </div>
        )}

        {/* Swimming squads */}
        {form.sport === "Swimming" && (
          <div style={G.card}>
            <div style={{...G.h3,marginBottom:12}}>Squads</div>
            <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:10}}>
              {form.swimGroups.map(g=>(
                <div key={g} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",background:"rgba(255,255,255,0.04)",borderRadius:8}}>
                  <span style={{flex:1,fontSize:13,color:"#fff"}}>{g}</span>
                  <button onClick={()=>setForm(f=>({...f,swimGroups:f.swimGroups.filter(x=>x!==g)}))} style={{background:"none",border:"none",color:"rgba(255,255,255,0.3)",cursor:"pointer",fontSize:14}}>✕</button>
                </div>
              ))}
            </div>
            <div style={{display:"flex",gap:8}}>
              <input style={{...G.input,flex:1,marginBottom:0}} value={newGroup} onChange={e=>setNewGroup(e.target.value)} placeholder="e.g. Performance, Age 12..."/>
              <button style={G.btnSm} onClick={()=>{if(newGroup.trim()){setForm(f=>({...f,swimGroups:[...f.swimGroups,newGroup.trim()]}));setNewGroup("");}}}>+ Add</button>
            </div>
          </div>
        )}

        {/* Members list */}
        <div style={G.card}>
          <div style={{fontSize:12,fontWeight:700,color:"#fff",marginBottom:10}}>Members ({users.filter(u=>u.approved).length})</div>
          <input style={{...G.input,marginBottom:8}} placeholder="Search by name or email..."
            defaultValue="" onChange={e=>setMemberSearch(e.target.value)}/>
          <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:6,marginBottom:10}}>
            <button onClick={()=>setMemberSearch("")} style={{padding:"5px 12px",background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.15)",borderRadius:20,color:"rgba(255,255,255,0.6)",fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>All</button>
            {teams.map(t=>(
              <button key={t} onClick={()=>setMemberSearch(t)} style={{padding:"5px 12px",background:memberSearch===t?primaryCol:"rgba(255,255,255,0.05)",border:`1px solid ${memberSearch===t?primaryCol:"rgba(255,255,255,0.1)"}`,borderRadius:20,color:memberSearch===t?contrastText(primaryCol):"rgba(255,255,255,0.5)",fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>{t}</button>
            ))}
          </div>
          {!memberSearch && <div style={{textAlign:"center",padding:"12px 0",color:"rgba(255,255,255,0.3)",fontSize:13}}>Type a name or filter by team to search members</div>}
          {memberSearch && users.filter(u=>u.id!==currentUser?.id&&((u.name||"").toLowerCase().includes(memberSearch.toLowerCase())||(u.email||"").toLowerCase().includes(memberSearch.toLowerCase())||u.team===memberSearch)).map(u=>(
            <div key={u.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderBottom:"1px solid rgba(255,255,255,0.05)"}}>
              <Avatar name={u.name} size={36} colour={avCol(u.name)}/>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:600,color:"#fff"}}>{u.name}</div>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>{ROLES.find(r=>r.r===u.role)?.name||u.role} · {u.team||"—"}</div>
                {!u.approved&&<div style={{fontSize:10,color:"#f0b429",marginTop:2}}>⏳ Pending approval</div>}
              </div>
              <div style={{display:"flex",gap:6}}>
                {!u.approved&&(
                  <button onClick={()=>{setUsers(us=>us.map(x=>x.id===u.id?{...x,approved:true}:x));db.approveMember(u.id).catch(console.error);showToast(`${u.name} approved ✓`);}} style={{fontSize:11,padding:"5px 10px",background:"#22c55e22",border:"1px solid #22c55e",borderRadius:6,color:"#22c55e",cursor:"pointer",fontWeight:600}}>✓ Approve</button>
                )}
                <button onClick={()=>{if(!window.confirm(`Remove ${u.name}?`))return;setUsers(us=>us.filter(x=>x.id!==u.id));setPlayers(ps=>ps.filter(p=>p.userId!==u.id));supabase.from("members").delete().eq("id",u.id).then(()=>showToast(`${u.name} removed`)).catch(console.error);}} style={{fontSize:11,padding:"5px 10px",background:"rgba(230,57,70,0.12)",border:"1px solid #E6394644",borderRadius:6,color:"#E63946",cursor:"pointer",fontWeight:600}}>Remove</button>
              </div>
            </div>
          ))}
        </div>
        <Btn col={primaryCol} onClick={save}>{saving?"Saving...":"💾 Save Changes"}</Btn>

        {/* Take the tour */}
        <button onClick={()=>{ if(window._p4mStartTour) window._p4mStartTour(); }} style={{width:"100%",padding:"12px",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:12,color:"rgba(255,255,255,0.5)",fontSize:13,fontWeight:600,cursor:"pointer",marginTop:8,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
          ❓ Take the app tour
        </button>

        {/* Stripe Connect */}
        <div style={{marginTop:24,padding:16,background:"rgba(99,102,241,0.06)",border:"1px solid rgba(99,102,241,0.2)",borderRadius:12}}>
          <div style={{fontSize:13,fontWeight:700,color:"#818cf8",marginBottom:4}}>💳 Club Payments (Stripe)</div>
          <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",marginBottom:12,lineHeight:1.5}}>
            Connect your club's bank account to accept match fees, membership and event payments directly from players through the app. P4M takes 1% per transaction.
          </div>
          {club.stripeOnboarded ? (
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <div style={{width:8,height:8,borderRadius:"50%",background:"#22c55e",flexShrink:0}}/>
              <div style={{fontSize:13,color:"#22c55e",fontWeight:600}}>Payments active — players can pay directly in app</div>
            </div>
          ) : (
            <button onClick={async()=>{
              try {
                showToast("Setting up payments...");
                const { data, error } = await supabase.functions.invoke("connect-onboard", {
                  body: { clubId, clubName: club.name, email: currentUser?.email }
                });
                if(error) { showToast("Error: " + error.message); console.error(error); return; }
                if(data?.url) window.open(data.url, "_blank");
                else showToast(data?.error || "No URL returned — check Supabase logs");
              } catch(e) { showToast("Error: " + e.message); console.error(e); }
            }} style={{width:"100%",padding:"11px",background:"rgba(99,102,241,0.15)",border:"1px solid rgba(99,102,241,0.4)",borderRadius:8,color:"#818cf8",fontSize:13,fontWeight:700,cursor:"pointer"}}>
              🔗 Connect Club Bank Account
            </button>
          )}
        </div>

        {/* ── CSV MEMBER IMPORT ── */}
        <div style={{marginTop:24,padding:16,background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:12}}>
          <div style={{fontSize:13,fontWeight:700,color:"#fff",marginBottom:4}}>📥 Import Members</div>
          <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",marginBottom:12,lineHeight:1.5}}>
            Import members from another app (Teamo, Pitchero, WhatsApp export etc). Upload a CSV file with columns: <span style={{color:primaryCol}}>name, email, role, team</span>. Members will be created as pending — they'll receive an invite to set their password.
          </div>

          {/* Download template */}
          <button onClick={()=>{
            const csv = "name,email,role,team\nJane Smith,jane@example.com,player,Ladies 1s\nJohn Doe,john@example.com,player,Mens 1s\nCoach Bob,bob@example.com,coach,all";
            const blob = new Blob([csv], {type:"text/csv"});
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a"); a.href=url; a.download="p4m_import_template.csv"; a.click();
            URL.revokeObjectURL(url);
          }} style={{padding:"8px 14px",background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.12)",borderRadius:8,color:"rgba(255,255,255,0.6)",fontSize:12,fontWeight:600,cursor:"pointer",marginBottom:10,display:"inline-block"}}>
            ⬇️ Download CSV Template
          </button>

          {/* File upload */}
          <div>
            <label style={{display:"flex",alignItems:"center",gap:8,padding:"10px 14px",background:`${primaryCol}15`,border:`1.5px dashed ${primaryCol}66`,borderRadius:10,cursor:"pointer",fontSize:13,fontWeight:600,color:primaryCol,marginBottom:10}}>
              📂 Choose CSV File
              <input type="file" accept=".csv,text/csv" style={{display:"none"}} onChange={async(e)=>{
                const file = e.target.files[0];
                if(!file) return;
                const text = await file.text();
                const lines = text.trim().split(/\r?\n/).filter(Boolean);
                if(lines.length < 2){ showToast("CSV must have a header row and at least one member"); return; }

                // Parse header — find column indexes
                const header = lines[0].toLowerCase().split(",").map(h=>h.trim().replace(/"/g,""));
                const nameIdx  = header.findIndex(h=>h.includes("name"));
                const emailIdx = header.findIndex(h=>h.includes("email"));
                const roleIdx  = header.findIndex(h=>h.includes("role"));
                const teamIdx  = header.findIndex(h=>h.includes("team"));

                if(nameIdx<0||emailIdx<0){ showToast("CSV must have 'name' and 'email' columns"); return; }

                const preview = [];
                const errors = [];
                for(let i=1; i<lines.length; i++){
                  const cols = lines[i].split(",").map(c=>c.trim().replace(/"/g,""));
                  const name  = cols[nameIdx]||"";
                  const email = cols[emailIdx]||"";
                  const role  = roleIdx>=0 ? (cols[roleIdx]||"player") : "player";
                  const team  = teamIdx>=0 ? (cols[teamIdx]||"") : "";
                  if(!name||!email){ errors.push(`Row ${i+1}: missing name or email`); continue; }
                  if(!email.includes("@")){ errors.push(`Row ${i+1}: invalid email "${email}"`); continue; }
                  preview.push({name, email, role, team});
                }

                if(errors.length>0){ showToast(`${errors.length} row(s) skipped — check format`); }
                if(preview.length===0){ showToast("No valid members found in CSV"); return; }

                // Confirm import
                const confirmMsg = "Import " + preview.length + " member" + (preview.length!==1?"s":"") + "?\n\n" + preview.slice(0,5).map(m=>"• "+m.name+" ("+m.email+")").join("\n") + (preview.length>5?"\n...and "+(preview.length-5)+" more":"");
                if(!window.confirm(confirmMsg)) return;

                // Create members in Supabase
                let created=0, skipped=0;
                showToast("Importing...");
                for(const m of preview){
                  try {
                    // Check if email already exists
                    const {data:existing} = await supabase.from("members").select("id").eq("email",m.email).eq("club_id",clubId).single();
                    if(existing){ skipped++; continue; }
                    // Create member record (no auth account yet — they sign up themselves)
                    const {error} = await supabase.from("members").insert({
                      club_id:clubId,
                      name:m.name,
                      email:m.email,
                      role:m.role||"player",
                      team:m.team||"",
                      approved:false,
                      auth_id:null,
                    });
                    if(!error) created++;
                  } catch(err){ skipped++; }
                }

                // Refresh users list
                const {data:membersData} = await supabase.from("members").select("*").eq("club_id",clubId);
                if(membersData) setUsers(membersData.map(m=>({id:m.id,name:m.name,email:m.email,role:m.role,team:m.team,approved:m.approved,auth_id:m.auth_id,photo:m.avatar_url})));

                showToast(`✓ ${created} member${created!==1?"s":""} imported${skipped>0?`, ${skipped} skipped (already exist)`:""}`);
                e.target.value=""; // reset file input
              }}/>
            </label>
          </div>

          <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",lineHeight:1.6}}>
            Imported members will appear as <span style={{color:"#f0b429"}}>Pending</span> — they sign up using their email address and are auto-linked to the club. Existing emails are skipped.
          </div>
        </div>

        {/* ── SUBSCRIPTION ───────────────────────────────────────────────────── */}
        <div style={{marginTop:24,padding:16,background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:12}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
            <div style={{fontSize:13,fontWeight:700,color:"#fff"}}>Subscription</div>
            {club.subscriptionStatus==="active"||club.subscriptionStatus==="founding"
              ? <div style={{fontSize:11,fontWeight:700,color:"#22c55e",background:"rgba(34,197,94,0.1)",border:"1px solid rgba(34,197,94,0.3)",borderRadius:20,padding:"3px 10px"}}>
                  {club.subscriptionStatus==="founding"?"🏅 Founding Member":"✓ Pro"}
                </div>
              : <div style={{fontSize:11,fontWeight:700,color:"#f0b429",background:"rgba(240,180,41,0.1)",border:"1px solid rgba(240,180,41,0.3)",borderRadius:20,padding:"3px 10px"}}>Free — Beta</div>
            }
          </div>
          {club.subscriptionStatus==="active"||club.subscriptionStatus==="founding" ? (
            <>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.45)",marginBottom:12,lineHeight:1.6}}>
                {club.subscriptionStatus==="founding"
                  ? "You're a P4M Founding Member — locked in at £9.99/month forever."
                  : "You're on Club Pro at £12.99/month. Full access to all features."
                }
              </div>
              <a href="https://billing.stripe.com" target="_blank" rel="noreferrer"
                style={{display:"block",width:"100%",padding:"10px 16px",background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.12)",borderRadius:8,color:"rgba(255,255,255,0.7)",fontSize:13,fontWeight:600,cursor:"pointer",textAlign:"center",textDecoration:"none",boxSizing:"border-box"}}>
                Manage / Cancel Subscription →
              </a>
            </>
          ) : (
            <>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.45)",marginBottom:12,lineHeight:1.6}}>
                You're on the free beta. Upgrade to lock in your plan before prices increase.
              </div>
              <button onClick={()=>setScreen("subscribe")}
                style={{width:"100%",padding:"10px 16px",background:primaryCol,border:"none",borderRadius:8,color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer"}}>
                Upgrade to P4M Pro →
              </button>
            </>
          )}
        </div>

        <div style={{marginTop:24,padding:16,background:"rgba(230,57,70,0.06)",border:"1px solid rgba(230,57,70,0.2)",borderRadius:12}}>
          <div style={{fontSize:13,fontWeight:700,color:"#E63946",marginBottom:6}}>Danger Zone</div>
          <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",marginBottom:12}}>Permanently deletes this club and all data. Cannot be undone.</div>
          <button onClick={async()=>{
            if(!window.confirm("DELETE THIS CLUB PERMANENTLY?\n\nAll players, fixtures and members will be erased."))return;
            const input=window.prompt("Type DELETE to confirm:");
            if(input!=="DELETE"){showToast("Cancelled");return;}
            try{
              await supabase.from("players").delete().eq("club_id",clubId);
              await supabase.from("fixtures").delete().eq("club_id",clubId);
              await supabase.from("posts").delete().eq("club_id",clubId);
              await supabase.from("members").delete().eq("club_id",clubId);
              await supabase.from("clubs").delete().eq("id",clubId);
              await db.signOut();
              setCurrentUser(null);setClubId(null);setScreen("auth");setAuthMode("login");
            }catch(e){showToast("Delete failed");console.error(e);}
          }} style={{width:"100%",padding:"10px 16px",background:"rgba(230,57,70,0.15)",border:"1px solid #E63946",borderRadius:8,color:"#E63946",fontSize:13,fontWeight:700,cursor:"pointer"}}>
            🗑 Delete Club Permanently
          </button>
        </div>
      </div>
    );
  };


  // ── FORMATIONS BY SPORT ─────────────────────────────────────────────────
  const FORMATIONS_BY_SPORT = {
    Hockey: [
      {name:"4-3-3", rows:[{pos:"GK",count:1},{pos:"DEF",count:4},{pos:"MID",count:3},{pos:"FWD",count:3}]},
      {name:"3-4-3", rows:[{pos:"GK",count:1},{pos:"DEF",count:3},{pos:"MID",count:4},{pos:"FWD",count:3}]},
      {name:"4-4-2", rows:[{pos:"GK",count:1},{pos:"DEF",count:4},{pos:"MID",count:4},{pos:"FWD",count:2}]},
      {name:"3-3-3-1",rows:[{pos:"GK",count:1},{pos:"DEF",count:3},{pos:"DM",count:3},{pos:"AM",count:3},{pos:"ST",count:1}]},
      {name:"3-2-3-2",rows:[{pos:"GK",count:1},{pos:"DEF",count:3},{pos:"DM",count:2},{pos:"AM",count:3},{pos:"ST",count:2}]},
    ],
    Football: [
      {name:"4-3-3", rows:[{pos:"GK",count:1},{pos:"DEF",count:4},{pos:"MID",count:3},{pos:"FWD",count:3}]},
      {name:"4-4-2", rows:[{pos:"GK",count:1},{pos:"DEF",count:4},{pos:"MID",count:4},{pos:"FWD",count:2}]},
      {name:"3-5-2", rows:[{pos:"GK",count:1},{pos:"DEF",count:3},{pos:"MID",count:5},{pos:"FWD",count:2}]},
      {name:"4-2-3-1",rows:[{pos:"GK",count:1},{pos:"DEF",count:4},{pos:"DM",count:2},{pos:"AM",count:3},{pos:"ST",count:1}]},
      {name:"3-4-3", rows:[{pos:"GK",count:1},{pos:"DEF",count:3},{pos:"MID",count:4},{pos:"FWD",count:3}]},
      {name:"5-3-2", rows:[{pos:"GK",count:1},{pos:"DEF",count:5},{pos:"MID",count:3},{pos:"FWD",count:2}]},
    ],
    default: [
      {name:"4-3-3", rows:[{pos:"GK",count:1},{pos:"DEF",count:4},{pos:"MID",count:3},{pos:"FWD",count:3}]},
      {name:"4-4-2", rows:[{pos:"GK",count:1},{pos:"DEF",count:4},{pos:"MID",count:4},{pos:"FWD",count:2}]},
    ],
  };

  // ── INDIVIDUAL PLAYER PROGRAMME (ALL SPORTS) ────────────────────────────
  const generateIndividualPlan = async (player, goals, sessionsPerWeek) => {
    if (!player) return null;
    const attrs = Object.values(ATTRS_BY_SPORT?.[club.sport] || ATTRS_BY_SPORT?.default || {}).flat();
    try {
      // Build weakness profile from scores
      const allAttrs = Object.values(ATTRS_BY_SPORT?.[club.sport]||ATTRS_BY_SPORT?.default||{}).flat();
      const scores = player.scores||{};
      const scored = allAttrs.filter(a=>scores[a]!==undefined).map(a=>({attr:a,val:scores[a]}));
      const weaknesses = scored.sort((a,b)=>a.val-b.val).slice(0,5).map(x=>`${x.attr} (${to99(x.val)}/99)`);
      const strengths = scored.sort((a,b)=>b.val-a.val).slice(0,3).map(x=>`${x.attr} (${to99(x.val)}/99)`);
      const overallRating = scored.length>0 ? Math.round(scored.reduce((s,x)=>s+to99(x.val),0)/scored.length) : null;

      const sportStr2 = club.sport==="Hockey" ? "Field Hockey (astroturf/grass — NOT ice hockey. All drills must use sticks and a hockey ball)" : club.sport;
      // Pull fitness data from player profile
      const fitnessData = player.fitness || {};
      const fitnessStr = Object.keys(fitnessData).length > 0
        ? Object.entries(fitnessData).map(([k,v])=>`${k}: ${Array.isArray(v)?v.slice(-1)[0]?.val||"no data":v}`).join(", ")
        : "No fitness tests recorded";
      const prompt = `You are an elite ${sportStr2} coach and individual development specialist. Create a personalised 6-week development programme for this player.

SPORT: ${sportStr2}

PLAYER PROFILE:
- Name: ${player.name}
- Position: ${player.position||"Not specified"}
- Team: ${player.team||"Not specified"}
- Age Group: ${player.ageGroup||player.age||"Not specified"}
- Overall Rating: ${overallRating?`${overallRating}/99`:"Not yet rated"}
- Top 3 Strengths: ${strengths.length>0?strengths.join(", "):"Not yet rated — focus on all-round development"}
- Top 5 Areas Needing Work (PRIORITISE THESE): ${weaknesses.length>0?weaknesses.join(", "):"Not yet rated — focus on fundamentals"}
- Recent Fitness Test Results: ${fitnessStr}
- Player / Coach Goals: ${(goals||[]).join(", ")||"General improvement and development"}
- Sessions Per Week: ${sessionsPerWeek||3}
- Session Length: ${(sessionsPerWeek||3) >= 3 ? "75 minutes" : "60 minutes"}

PROGRAMME REQUIREMENTS:
- Build sessions primarily around the player's WEAKEST attributes listed above
- Every session must include ${sportStr2}-specific drills — no generic or ice hockey content
- Reference the player's actual weak attributes by name in coaching points
- Include a fitness element each week that addresses their test results
- Progressively increase intensity across the 6 weeks
- Week 1-2: Foundation and technique. Week 3-4: Application under pressure. Week 5-6: Game-realistic scenarios
- Each drill must be achievable by a solo player or pair (not requiring full team)
- Include pre-session activation and post-session cool-down

Respond ONLY with valid JSON — no other text:
{
  "overview": "2-3 sentence personal overview for ${player.name} explaining the programme focus, what they will develop and why",
  "weeks": [
    {
      "week": 1,
      "theme": "Week theme",
      "objective": "By the end of this week the player should be able to...",
      "sessions": [
        {
          "day": "Tuesday",
          "title": "Session title",
          "duration": 60,
          "warmup": "Specific activation and ball work (10 mins)",
          "main": "Main session content targeting weak attributes (40 mins)",
          "cooldown": "Cool down, stretch and self-reflection (10 mins)",
          "drills": ["Sport-specific drill targeting weakness 1", "Sport-specific drill targeting weakness 2", "Conditioned game or pressure drill"],
          "coachingPoints": ["Point referencing player-specific weakness", "Technical focus point", "Mindset or pressure cue"]
        }
      ]
    }
  ]
}`;
      console.log("[P4M] Individual programme prompt →", prompt);
      const res = await supabase.functions.invoke("ai-framework", {
        body: { prompt, max_tokens: 4000 }
      });
      console.log("[P4M] Individual programme response →", res);
      const data = res.data;
      const text = data?.content?.find(c=>c.type==="text")?.text||"";
      console.log("[P4M] Raw AI text →", text);
      const parsed = JSON.parse(text.replace(/```json|```/g,"").trim());
      return parsed;
    } catch(e) {
      console.warn("[P4M] generateIndividualPlan error (using fallback):", e.message);
      const dayOptions = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
      const days = dayOptions.slice(0, sessionsPerWeek||3);
      return {
        overview: `Personal 6-week programme for ${player?.name||"player"} focused on ${(goals||[]).join(", ")||"general improvement"}.`,
        weeks: Array.from({length:6},(_,wi)=>({
          week: wi+1,
          theme: ["Foundation","Building","Development","Progression","Peak","Consolidation"][wi],
          sessions: days.map(day=>({
            day, title:"Training Session", duration:75,
            warmup:"10 min dynamic warm-up",
            main: (goals||[])[0]?`Focused work on: ${goals[0]}`:"Technical drill work and small-sided games",
            drills: [(goals||[])[0]?`Drill: ${goals[0]}`:"Skill circuit","Conditioned game"],
            coachingPoints:[`Week ${wi+1} focus: ${(goals||[])[wi%Math.max((goals||[]).length,1)]||"improvement"}`,"Quality over quantity"]
          }))
        }))
      };
    }
  };

  // ── WIZARD VIEW (Hockey/Football squad framework) ────────────────────────
  const WizardView = () => {
    const totalSteps = 6;
    return (
      <div key={`wiz-step-${wizardStep}`} style={{display:"flex",flexDirection:"column",minHeight:"60vh",padding:"0 4px"}}>
        {/* Full-screen loading overlay — rendered at top level so it covers everything */}
        {wizardLoading&&(
          <div style={{position:"fixed",inset:0,background:"rgba(8,8,16,0.97)",zIndex:999,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:32}}>
            <div style={{marginBottom:32,position:"relative",width:56,height:56}}>
              <div style={{width:80,height:80,border:`3px solid ${primaryCol}22`,borderTop:`3px solid ${primaryCol}`,borderRadius:"50%",animation:"spin 1s linear infinite",position:"absolute",inset:-12}}/>
              <div style={{width:56,height:56,border:`2px solid ${primaryCol}44`,borderBottom:`2px solid ${primaryCol}`,borderRadius:"50%",animation:"spin 0.6s linear infinite reverse",position:"absolute",inset:0}}/>
              <div style={{width:56,height:56,display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,position:"relative"}}>🧠</div>
            </div>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:40,fontWeight:900,color:"#fff",letterSpacing:3,marginBottom:6,marginTop:16}}>P4M NEXUS</div>
            <div style={{fontSize:12,fontWeight:700,color:primaryCol,letterSpacing:4,textTransform:"uppercase",marginBottom:28}}>Building your framework</div>
            <div style={{maxWidth:300,textAlign:"center"}}>
              {["Analysing your squad profile...","Applying governing body principles...","Structuring your season blocks...","Adding key milestones and fitness tests...","Finalising coaching language and cues..."].map((msg,i)=>(
                <div key={i} style={{fontSize:13,color:"rgba(255,255,255,0.45)",marginBottom:8,animation:`fadeIn 0.5s ease ${i*0.9}s both`}}>{msg}</div>
              ))}
            </div>
            <div style={{marginTop:36,fontSize:11,color:"rgba(255,255,255,0.2)",letterSpacing:2,textTransform:"uppercase"}}>This usually takes 15–20 seconds</div>
          </div>
        )}
        <div style={{height:3,background:"rgba(255,255,255,0.06)",marginBottom:0}}>
          <div style={{height:"100%",background:primaryCol,width:`${(wizardStep/totalSteps)*100}%`,transition:"width 0.3s"}}/>
        </div>
        <div style={{display:"flex",alignItems:"center",padding:"10px 0 16px"}}>
          <button onClick={()=>setWizardStep(s=>Math.max(0,s-1))} style={{background:"none",border:"none",color:"rgba(255,255,255,0.4)",fontSize:20,cursor:"pointer"}}>←</button>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.38)",letterSpacing:2,textTransform:"uppercase",marginLeft:"auto"}}>{wizardStep===6?"Review":wizardLoading?"Generating...":"Step "+wizardStep+" of "+totalSteps}</div>
        </div>
        <div style={{flex:1,overflowY:"auto"}}>
          {wizardStep===1&&(<>
            <div style={G.h2}>Which team is this for?</div>
            <select style={{...G.select,marginBottom:14}} value={frameworkTeam} onChange={e=>setFrameworkTeam(e.target.value)}>
              <option value="">Select team...</option>
              {teams.map(t=><option key={t} value={t}>{t}</option>)}
            </select>
            <div style={G.h2}>Season goals</div>
            {["Gain promotion","Avoid relegation","Win a cup competition","Develop young players","Build team cohesion","Establish a clear playing style","Improve fitness","Have fun"].map(g=>{
              const sel=wizardData.goals.includes(g);
              return <div key={g} onClick={()=>setWizardData(d=>({...d,goals:sel?d.goals.filter(x=>x!==g):[...d.goals,g]}))}
                style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",background:sel?`${primaryCol}18`:"rgba(255,255,255,0.03)",border:`1.5px solid ${sel?primaryCol:"rgba(255,255,255,0.08)"}`,borderRadius:10,cursor:"pointer",marginBottom:7}}>
                <div style={{width:20,height:20,borderRadius:4,border:`2px solid ${sel?primaryCol:"rgba(255,255,255,0.2)"}`,background:sel?primaryCol:"transparent",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,color:"#fff",flexShrink:0}}>{sel?"✓":""}</div>
                <div style={{fontSize:13,fontWeight:sel?600:400,color:sel?"#fff":"rgba(255,255,255,0.7)"}}>{g}</div>
              </div>;
            })}
            {/* Custom goals added by user */}
            {wizardData.goals.filter(g=>!["Gain promotion","Avoid relegation","Win a cup competition","Develop young players","Build team cohesion","Establish a clear playing style","Improve fitness","Have fun"].includes(g)).map(g=>(
              <div key={g} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",background:`${primaryCol}18`,border:`1.5px solid ${primaryCol}`,borderRadius:10,marginBottom:7}}>
                <div style={{width:20,height:20,borderRadius:4,background:primaryCol,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,color:"#fff",flexShrink:0}}>✓</div>
                <div style={{fontSize:13,fontWeight:600,color:"#fff",flex:1}}>{g}</div>
                <button onClick={()=>setWizardData(d=>({...d,goals:d.goals.filter(x=>x!==g)}))}
                  style={{background:"none",border:"none",color:"rgba(255,255,255,0.4)",cursor:"pointer",fontSize:18,padding:0,lineHeight:1}}>✕</button>
              </div>
            ))}
            {(()=>{
              const GoalInput = () => {
                const [val, setVal] = React.useState("");
                const add = () => { if(val.trim()) { setWizardData(d=>({...d,goals:[...d.goals,val.trim()]})); setVal(""); }};
                return (
                  <div style={{marginTop:10,display:"flex",gap:8}}>
                    <input style={{...G.input,flex:1,marginBottom:0}} placeholder="Add your own goal..."
                      value={val} onChange={e=>setVal(e.target.value)}
                      onKeyDown={e=>{if(e.key==="Enter"){add();}}}/> 
                    <button style={G.btnSm} onClick={add}>+ Add</button>
                  </div>
                );
              };
              return <GoalInput/>;
            })()}
          </>)}
          {wizardStep===2&&(<>
            <div style={G.h2}>Tell us about your squad</div>
            {["Experienced and established","Mix of experience and youth","Young and developing","New players — building from scratch","Strong individually but need team work"].map(d=>(
              <div key={d} onClick={()=>setWizardData(w=>({...w,squadDesc:d}))}
                style={{padding:"11px 14px",background:wizardData.squadDesc===d?`${primaryCol}18`:"rgba(255,255,255,0.03)",border:`1.5px solid ${wizardData.squadDesc===d?primaryCol:"rgba(255,255,255,0.08)"}`,borderRadius:10,cursor:"pointer",fontSize:13,fontWeight:wizardData.squadDesc===d?600:400,color:wizardData.squadDesc===d?"#fff":"rgba(255,255,255,0.65)",marginBottom:8}}>
                {d}
              </div>
            ))}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginTop:8}}>
              <div><FieldLabel>Sessions/week</FieldLabel>
                <div style={{display:"flex",gap:6}}>
                  {[1,2,3].map(n=><button key={n} onClick={()=>setWizardData(w=>({...w,sessions:n}))} style={{flex:1,padding:"10px 0",background:wizardData.sessions===n?primaryCol:"rgba(255,255,255,0.04)",border:`1px solid ${wizardData.sessions===n?primaryCol:"rgba(255,255,255,0.08)"}`,borderRadius:8,color:wizardData.sessions===n?contrastText(primaryCol):"rgba(255,255,255,0.5)",fontWeight:700,fontSize:14,cursor:"pointer"}}>{n}</button>)}
                </div>
              </div>
              <div><FieldLabel>Session length</FieldLabel>
                <select style={{...G.select,marginBottom:0}} value={wizardData.sessionLen} onChange={e=>setWizardData(w=>({...w,sessionLen:parseInt(e.target.value)}))}>
                  {[45,60,75,90].map(l=><option key={l} value={l}>{l} mins</option>)}
                </select>
              </div>
            </div>
            <div style={{marginTop:16}}>
              <FieldLabel>Training Days</FieldLabel>
              <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:12}}>
                {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((day,i)=>{
                  const full=["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"][i];
                  const sel=(wizardData.trainingDays||[]).includes(full);
                  return <button key={day} onClick={()=>setWizardData(w=>{
                    const days=sel?w.trainingDays.filter(x=>x!==full):[...(w.trainingDays||[]),full];
                    const times={...(w.trainingTimes||{})};
                    if(!sel&&!times[full]) times[full]="19:00";
                    return {...w,trainingDays:days,trainingTimes:times};
                  })} style={{padding:"8px 12px",background:sel?primaryCol:"rgba(255,255,255,0.04)",border:`1px solid ${sel?primaryCol:"rgba(255,255,255,0.1)"}`,borderRadius:20,color:sel?"#fff":"rgba(255,255,255,0.5)",fontSize:12,fontWeight:700,cursor:"pointer"}}>{day}</button>;
                })}
              </div>

              {/* Per-day time + location */}
              {(wizardData.trainingDays||[]).length>0&&(
                <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:12}}>
                  {(wizardData.trainingDays||[]).map(day=>(
                    <div key={day} style={{display:"grid",gridTemplateColumns:"80px 1fr",gap:8,alignItems:"center",padding:"10px 12px",background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:10}}>
                      <div style={{fontSize:13,fontWeight:700,color:primaryCol}}>{day.slice(0,3)}</div>
                      <div>
                        <FieldLabel>Start time</FieldLabel>
                        <input type="time"
                          value={(wizardData.trainingTimes||{})[day]||"19:00"}
                          onChange={e=>setWizardData(w=>({...w,trainingTimes:{...(w.trainingTimes||{}),[day]:e.target.value}}))}
                          style={{...G.input,marginBottom:0,fontSize:12}}/>
                      </div>

                    </div>
                  ))}
                </div>
              )}

              {(wizardData.trainingDays||[]).length>0&&wizardData.startDate&&(
                <div style={{padding:"10px 14px",background:`${primaryCol}18`,border:`1px solid ${primaryCol}44`,borderRadius:10,fontSize:12,color:"rgba(255,255,255,0.7)"}}>
                  📅 Will add <strong style={{color:primaryCol}}>{(wizardData.trainingDays||[]).length} session{(wizardData.trainingDays||[]).length!==1?"s":""}/week</strong> to the fixtures calendar — players will mark availability for each one
                </div>
              )}
            </div>
          </>)}
          {wizardStep===3&&(<>
            <div style={G.h2}>Strengths & Areas to Improve</div>
            <FieldLabel style={{color:"#22c55e"}}>Strengths</FieldLabel>
            {["Attacking play","Pressing","Defending as a unit","Set plays","Physicality","Technical skill","Game management","Transitions"].map(s=>{
              const sel=wizardData.strengths.includes(s);
              return <div key={s} onClick={()=>setWizardData(d=>({...d,strengths:sel?d.strengths.filter(x=>x!==s):[...d.strengths,s]}))}
                style={{padding:"9px 14px",background:sel?"rgba(34,197,94,0.12)":"rgba(255,255,255,0.03)",border:`1.5px solid ${sel?"#22c55e":"rgba(255,255,255,0.08)"}`,borderRadius:9,cursor:"pointer",fontSize:13,color:sel?"#fff":"rgba(255,255,255,0.6)",fontWeight:sel?600:400,marginBottom:6}}>{s}</div>;
            })}
            {wizardData.strengths.filter(s=>!["Attacking play","Pressing","Defending as a unit","Set plays","Physicality","Technical skill","Game management","Transitions"].includes(s)).map(s=>(
              <div key={s} style={{display:"flex",alignItems:"center",gap:12,padding:"9px 14px",background:"rgba(34,197,94,0.12)",border:"1.5px solid #22c55e",borderRadius:9,marginBottom:6}}>
                <div style={{fontSize:13,fontWeight:600,color:"#fff",flex:1}}>{s}</div>
                <button onClick={()=>setWizardData(d=>({...d,strengths:d.strengths.filter(x=>x!==s)}))}
                  style={{background:"none",border:"none",color:"rgba(255,255,255,0.4)",cursor:"pointer",fontSize:18,padding:0}}>✕</button>
              </div>
            ))}
            <div style={{display:"flex",gap:8,marginBottom:14,marginTop:4}}>
              {(()=>{const SI=()=>{const [v,setV]=React.useState("");const add=()=>{if(v.trim()){setWizardData(d=>({...d,strengths:[...d.strengths,v.trim()]}));setV("");}};return(<><input style={{...G.input,flex:1,marginBottom:0}} placeholder="Add your own strength..." value={v} onChange={e=>setV(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")add();}}/><button style={G.btnSm} onClick={add}>+ Add</button></>);};return <SI/>;})()}
            </div>
            <FieldLabel style={{color:"#E63946"}}>Areas to Improve</FieldLabel>
            {["Attacking play","Pressing","Defending as a unit","Set plays","Physicality","Technical skill","Game management","Transitions"].map(s=>{
              const sel=wizardData.weaknesses.includes(s);
              return <div key={s} onClick={()=>setWizardData(d=>({...d,weaknesses:sel?d.weaknesses.filter(x=>x!==s):[...d.weaknesses,s]}))}
                style={{padding:"9px 14px",background:sel?"rgba(230,57,70,0.12)":"rgba(255,255,255,0.03)",border:`1.5px solid ${sel?"#E63946":"rgba(255,255,255,0.08)"}`,borderRadius:9,cursor:"pointer",fontSize:13,color:sel?"#fff":"rgba(255,255,255,0.6)",fontWeight:sel?600:400,marginBottom:6}}>{s}</div>;
            })}
            {wizardData.weaknesses.filter(s=>!["Attacking play","Pressing","Defending as a unit","Set plays","Physicality","Technical skill","Game management","Transitions"].includes(s)).map(s=>(
              <div key={s} style={{display:"flex",alignItems:"center",gap:12,padding:"9px 14px",background:"rgba(230,57,70,0.12)",border:"1.5px solid #E63946",borderRadius:9,marginBottom:6}}>
                <div style={{fontSize:13,fontWeight:600,color:"#fff",flex:1}}>{s}</div>
                <button onClick={()=>setWizardData(d=>({...d,weaknesses:d.weaknesses.filter(x=>x!==s)}))}
                  style={{background:"none",border:"none",color:"rgba(255,255,255,0.4)",cursor:"pointer",fontSize:18,padding:0}}>✕</button>
              </div>
            ))}
            <div style={{display:"flex",gap:8,marginTop:4,flexWrap:"wrap"}}>
              {(()=>{const WI=()=>{const [v,setV]=React.useState("");const add=()=>{if(v.trim()){setWizardData(d=>({...d,weaknesses:[...d.weaknesses,v.trim()]}));setV("");}};return(<><input style={{...G.input,flex:1,marginBottom:0}} placeholder="Add your own area..." value={v} onChange={e=>setV(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")add();}}/><button style={G.btnSm} onClick={add}>+ Add</button></>);};return <WI/>;})()}
            </div>
          </>)}
          {wizardStep===4&&(<>
            <div style={G.h2}>Structure Your Season</div>
            <FieldLabel>Training block length</FieldLabel>
            <div style={{display:"flex",gap:8,marginBottom:16}}>
              {[{v:4,l:"4 Weeks",s:"Flexible"},{v:6,l:"6 Weeks",s:"Recommended"},{v:8,l:"8 Weeks",s:"Deep focus"}].map(b=>(
                <button key={b.v} onClick={()=>setWizardData(w=>({...w,blockLength:b.v}))} style={{flex:1,padding:"12px 6px",background:wizardData.blockLength===b.v?primaryCol:"rgba(255,255,255,0.04)",border:`1px solid ${wizardData.blockLength===b.v?primaryCol:"rgba(255,255,255,0.08)"}`,borderRadius:10,color:wizardData.blockLength===b.v?contrastText(primaryCol):"rgba(255,255,255,0.5)",fontWeight:700,fontSize:12,cursor:"pointer",textAlign:"center"}}>
                  <div>{b.l}</div><div style={{fontSize:9,opacity:0.7,marginTop:2,fontWeight:400}}>{b.s}</div>
                </button>
              ))}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              <div><FieldLabel>Season length</FieldLabel>
                <select style={{...G.select,marginBottom:0}} value={wizardData.seasonWeeks} onChange={e=>setWizardData(w=>({...w,seasonWeeks:parseInt(e.target.value)}))}>
                  {[20,24,28,32,36].map(l=><option key={l} value={l}>{l} weeks</option>)}
                </select>
              </div>
              <div><FieldLabel>Start date</FieldLabel>
                <input style={{...G.input,marginBottom:0}} type="date" value={wizardData.startDate} onChange={e=>setWizardData(w=>({...w,startDate:e.target.value}))}/>
              </div>
            </div>
            <div style={{...G.card,marginTop:12}}>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",marginBottom:4}}>This will create:</div>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:800,color:"#fff"}}>{Math.floor(wizardData.seasonWeeks/wizardData.blockLength)} training blocks</div>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.4)"}}>of {wizardData.blockLength} weeks each</div>
            </div>
          </>)}
          {wizardStep===5&&(<>
            <div style={G.h2}>Training Schedule</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.5)",marginBottom:16,lineHeight:1.6}}>
              Select the days and time your squad trains. These will be added to the fixture calendar so players can mark their availability.
            </div>
            <FieldLabel>Training Days</FieldLabel>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:16}}>
              {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map(day=>{
                const sel=(wizardData.trainingDays||[]).includes(day);
                return (
                  <button key={day} onClick={()=>setWizardData(w=>({...w,trainingDays:sel?w.trainingDays.filter(x=>x!==day):[...(w.trainingDays||[]),day]}))}
                    style={{padding:"9px 14px",background:sel?primaryCol:"rgba(255,255,255,0.04)",border:`1px solid ${sel?primaryCol:"rgba(255,255,255,0.12)"}`,borderRadius:20,color:sel?"#fff":"rgba(255,255,255,0.5)",fontSize:13,fontWeight:700,cursor:"pointer"}}>
                    {day.slice(0,3)}
                  </button>
                );
              })}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
              <div>
                <FieldLabel>Session start time</FieldLabel>
                <input type="time" value={wizardData.trainingTime||"19:00"} onChange={e=>setWizardData(w=>({...w,trainingTime:e.target.value}))}
                  style={{...G.input,marginBottom:0}}/>
              </div>
              <div>
                <FieldLabel>Duration (mins)</FieldLabel>
                <select style={{...G.select,marginBottom:0}} value={wizardData.sessionLen||75} onChange={e=>setWizardData(w=>({...w,sessionLen:parseInt(e.target.value)}))}>
                  {[45,60,75,90,105,120].map(m=><option key={m} value={m}>{m} mins</option>)}
                </select>
              </div>
            </div>
            <FieldLabel>Training Location</FieldLabel>
            {(()=>{const LI=()=>{const [v,setV]=React.useState(wizardData.trainingLocation||"");return(<input style={{...G.input,marginBottom:16}} type="text" placeholder="e.g. Astro Turf, Sports Hall" value={v} onChange={e=>setV(e.target.value)} onBlur={e=>setWizardData(w=>({...w,trainingLocation:e.target.value}))}/>);};return <LI/>;})()}
            {(wizardData.trainingDays||[]).length>0&&wizardData.startDate&&(
              <div style={{padding:"12px 14px",background:`${primaryCol}18`,border:`1px solid ${primaryCol}44`,borderRadius:10,fontSize:13,color:"rgba(255,255,255,0.7)",lineHeight:1.6}}>
                📅 Will create <strong style={{color:primaryCol}}>{(wizardData.trainingDays||[]).length * Math.floor(wizardData.seasonWeeks/1)}</strong> training sessions across the season for <strong style={{color:primaryCol}}>{frameworkTeam||"your team"}</strong> — players will be prompted to mark availability for each one.
              </div>
            )}
          </>)}
          {wizardStep===99&&(<>
            <div style={{textAlign:"center",padding:"20px 0 32px"}}>
              <div style={{fontSize:48,marginBottom:16}}>✅</div>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:32,fontWeight:900,color:"#fff",marginBottom:8}}>Framework Saved!</div>
              <div style={{fontSize:14,color:"rgba(255,255,255,0.5)",lineHeight:1.6,maxWidth:280,margin:"0 auto 28px"}}>
                Your {frameworkTeam||"squad"} season framework has been built and saved. You can view it, alter it or delete it from the Coach tab.
              </div>
              <button onClick={()=>{setWizardStep(0);setFramework(null);}}
                style={{width:"100%",padding:"14px",background:primaryCol,border:"none",borderRadius:12,color:"#fff",fontSize:16,fontWeight:800,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1,marginBottom:10}}>
                ✓ Done — Back to Coach
              </button>
              <button onClick={()=>{setWizardStep(6);}}
                style={{width:"100%",padding:"14px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:12,color:"rgba(255,255,255,0.6)",fontSize:14,fontWeight:600,cursor:"pointer",marginBottom:10}}>
                👁 View Framework Now
              </button>
              <button onClick={()=>{setWizardData({ goals:[], squadDesc:"", sessions:2, sessionLen:75, strengths:[], weaknesses:[], blockLength:6, seasonWeeks:28, startDate:"", trainingDays:[], trainingTimes:{}, trainingLocations:{}, trainingLocation:"", trainingTime:"" });setFrameworkTeam("");setFramework(null);setWizardStep(1);}}
                style={{width:"100%",padding:"14px",background:"transparent",border:"none",color:"rgba(255,255,255,0.35)",fontSize:13,cursor:"pointer"}}>
                + Create another framework
              </button>
            </div>
          </>)}

          {wizardStep===6&&(<>
            <div style={G.h2}>Ready to Build</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.45)",marginBottom:14,lineHeight:1.6}}>Review your details below. Tap Generate when you're happy — P4M Nexus will build your full season framework.</div>
            <div style={{...G.card,marginBottom:16}}>
              {[["Team",frameworkTeam||"Not set"],["Goals",(wizardData.goals||[]).join(", ")||"Not set"],["Squad",wizardData.squadDesc||"Not set"],["Sessions",`${wizardData.sessions}x · ${wizardData.sessionLen} mins`],["Training Days",wizardData.trainingDays?.join(", ")||"Not set"],["Train Time", (wizardData.trainingDays||[]).length>0 ? (wizardData.trainingDays||[]).map(d=>`${d.slice(0,3)} ${(wizardData.trainingTimes||{})[d]||"19:00"}`).join(", ") : "Not set"],["Location",wizardData.trainingLocation||"Not set"],["Structure",`${Math.floor(wizardData.seasonWeeks/wizardData.blockLength)} blocks of ${wizardData.blockLength} weeks`]].map(([l,v])=>(
                <div key={l} style={{display:"flex",gap:12,padding:"7px 0",borderBottom:"1px solid rgba(255,255,255,0.05)",fontSize:13}}>
                  <div style={{color:"rgba(255,255,255,0.38)",minWidth:90}}>{l}</div>
                  <div style={{color:"rgba(255,255,255,0.8)",flex:1}}>{v}</div>
                </div>
              ))}
            </div>
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>setWizardStep(1)} style={{...G.btnGhost,flex:1}}>← Adjust</button>
              <button onClick={generateFrameworkWithAI}
                style={{flex:2,padding:"13px",background:primaryCol,border:"none",borderRadius:12,color:"#fff",fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1}}>
                🧠 Generate Framework
              </button>
            </div>
          </>)}
        </div>
        {wizardStep<6&&<div style={{paddingTop:16}}><Btn col={primaryCol} onClick={()=>setWizardStep(s=>s+1)}>{wizardStep===5?"Review & Generate →":"Next →"}</Btn></div>}
      </div>
    );
  };

  // ── LINEUP PANEL ────────────────────────────────────────────────────────
  // Player-facing read-only lineup view with tactics
  const LineupViewPanel = () => {
    const fixtureId = panel.startsWith("lineup-view-") ? panel.replace("lineup-view-","") : null;
    const fixture = fixtures.find(f=>f.id===fixtureId);
    const lineup = lineups[fixtureId];
    if(!fixture||!lineup) return <div style={G.panel}><button onClick={()=>navigate("fixtures")} style={{background:"none",border:"none",color:primaryCol,fontSize:13,cursor:"pointer"}}>← Back</button><div style={{textAlign:"center",padding:"48px 20px",color:"rgba(255,255,255,0.4)"}}>No lineup published yet</div></div>;

    const myTactics = (lineup.playerTactics||{})[currentUser?.id];
    const starters = lineup.starters||[];
    const subs = lineup.subs||[];

    return (
      <div style={{...G.panel,paddingBottom:100}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
          <button onClick={()=>navigate("fixtures")} style={{background:"none",border:"none",color:"rgba(255,255,255,0.4)",fontSize:22,cursor:"pointer"}}>←</button>
          <div style={{flex:1}}>
            <div style={G.h2}>{club.name} vs {fixture.opponent}</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.4)"}}>{fmtDate(fixture.date)}{fixture.time?` · ${fixture.time}`:""} · {lineup.formation||""}</div>
          </div>
        </div>

        {/* Team tactics — visible to everyone */}
        {lineup.teamTactics&&(
          <div style={{...G.card,marginBottom:12,borderLeft:`3px solid rgba(255,255,255,0.15)`}}>
            <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.5)",marginBottom:6,letterSpacing:1}}>🗺 TEAM TACTICS</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.85)",lineHeight:1.7}}>{lineup.teamTactics}</div>
          </div>
        )}

        {/* Individual instructions — only shown to that player */}
        {myTactics&&(
          <div style={{...G.card,marginBottom:16,borderLeft:`3px solid ${primaryCol}`,background:`${primaryCol}10`}}>
            <div style={{fontSize:11,fontWeight:700,color:primaryCol,marginBottom:6,letterSpacing:1}}>📋 YOUR INSTRUCTIONS</div>
            <div style={{fontSize:14,color:"#fff",lineHeight:1.7}}>{myTactics}</div>
          </div>
        )}

        {/* Starting XI */}
        <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.4)",letterSpacing:1,marginBottom:8}}>STARTING {starters.length}</div>
        <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:16}}>
          {starters.map((p,i)=>{
            const isMe = p?.id===currentUser?.id || p?.auth_id===currentUser?.auth_id;
            const theirTactics = (lineup.playerTactics||{})[p?.id];
            return (
              <div key={i} style={{...G.card,padding:"10px 14px",borderLeft:isMe?`3px solid ${primaryCol}`:"none",background:isMe?`${primaryCol}0a`:"rgba(255,255,255,0.03)"}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <div style={{width:32,height:32,borderRadius:"50%",background:`linear-gradient(135deg,${secondaryCol},${primaryCol})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:"#fff",flexShrink:0,overflow:"hidden"}}>
                    {p?.photo?<img src={p.photo} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>:inits(p?.name||"")}
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:isMe?700:500,color:isMe?"#fff":"rgba(255,255,255,0.8)"}}>{p?.name||"—"}{isMe&&<span style={{color:primaryCol,fontSize:11}}> (You)</span>}</div>
                    <div style={{fontSize:11,color:"rgba(255,255,255,0.35)"}}>{p?.position||""}</div>
                  </div>
                  <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.3)",fontFamily:"'Barlow Condensed',sans-serif"}}>{i===0?"GK":""}</div>
                </div>
                {isMe&&theirTactics&&(
                  <div style={{marginTop:8,padding:"6px 10px",background:`${primaryCol}18`,borderRadius:6,fontSize:12,color:"rgba(255,255,255,0.7)"}}>{theirTactics}</div>
                )}
              </div>
            );
          })}
        </div>

        {/* Subs */}
        {subs.length>0&&(
          <>
            <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.4)",letterSpacing:1,marginBottom:8}}>SUBSTITUTES</div>
            <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
              {subs.map((p,i)=>{
                const isMe = p?.id===currentUser?.id;
                return (
                  <div key={i} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:3}}>
                    <div style={{width:44,height:44,borderRadius:"50%",background:isMe?`linear-gradient(135deg,${primaryCol},${secondaryCol})`:`linear-gradient(135deg,${secondaryCol},${primaryCol})`,border:`2px solid ${isMe?primaryCol:"rgba(255,255,255,0.2)"}`,overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center"}}>
                      {p?.photo?<img src={p.photo} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>:<div style={{fontSize:12,fontWeight:700,color:"#fff"}}>{inits(p?.name||"")}</div>}
                    </div>
                    <div style={{fontSize:8,fontWeight:700,color:isMe?primaryCol:"rgba(255,255,255,0.4)",textAlign:"center",maxWidth:46}}>{p?.name?.split(" ").pop().toUpperCase()}{isMe&&" ★"}</div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  };

  const LineupPanel = () => {
    const fixtureId = panel.startsWith("lineup-") ? panel.replace("lineup-","") : null;
    const fixture   = fixtures.find(f=>f.id===fixtureId);
    const teamPlayers = fixture ? players.filter(p=>p.team===fixture.team) : [];
    const existing  = lineups[fixtureId]||{starters:[],subs:[],formation:"4-3-3",slotMap:{}};
    const sportFormations = FORMATIONS_BY_SPORT[club.sport]||FORMATIONS_BY_SPORT.default;
    const [selectedFormation, setSelectedFormation] = useState(existing.formation||sportFormations[0]?.name||"4-3-3");
    const [slotMap, setSlotMap]       = useState(existing.slotMap||
      Object.fromEntries((existing.starters||[]).map((p,i)=>[i,p]).filter(([,p])=>p))
    );
    const [subsDraft, setSubsDraft]   = useState(existing.subs||[]);
    const [pickingSlot, setPickingSlot] = useState(null);
    const [search, setSearch]         = useState("");

    const formationDef = sportFormations.find(f=>f.name===selectedFormation)||sportFormations[0];
    const rows = formationDef?.rows||[];
    let flatIdx2 = 0;
    const rowGroups = rows.map(row=>({pos:row.pos,slots:Array.from({length:row.count},()=>({pos:row.pos,flatIdx:flatIdx2++}))}));
    const usedIds = [...Object.values(slotMap),...subsDraft].filter(Boolean).map(p=>p?.id);
    const available = teamPlayers.filter(p=>!usedIds.includes(p.id)&&p.name.toLowerCase().includes((search||"").toLowerCase()));

    const saveLineup = () => {
      const maxSlot = Math.max(...Object.keys(slotMap).map(Number),-1);
      const orderedStarters = Array.from({length:maxSlot+1},(_,i)=>slotMap[i]||null);
      const lineupData = {starters:orderedStarters.filter(Boolean),subs:subsDraft.filter(Boolean),formation:selectedFormation,slotMap};
      setLineups(l=>({...l,[fixtureId]:lineupData}));
      if(clubId) db.saveLineup(clubId,fixtureId,lineupData).catch(e=>console.error("Lineup save failed:",e));
      showToast("Lineup published ✓");navigate("fixtures");
    };

    if(!fixture) return <div style={G.panel}><button onClick={()=>navigate("fixtures")} style={{background:"none",border:"none",color:primaryCol,fontSize:13,cursor:"pointer"}}>← Back to fixtures</button></div>;

    return (
      <div style={{...G.panel,paddingBottom:100}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
          <button onClick={()=>navigate("fixtures")} style={{background:"none",border:"none",color:"rgba(255,255,255,0.4)",fontSize:22,cursor:"pointer"}}>←</button>
          <div style={{flex:1}}>
            <div style={G.h2}>{lineups[fixtureId]?"Edit Lineup":"Build Lineup"}</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.4)"}}>{club.name} vs {fixture.opponent} · {fmtDate(fixture.date)}</div>
          </div>
          <button onClick={saveLineup} style={{background:primaryCol,color:"#fff",border:"none",borderRadius:9,padding:"9px 18px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:14,fontWeight:700,cursor:"pointer"}}>Publish ✓</button>
        </div>
        <FieldLabel>Formation</FieldLabel>
        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>
          {sportFormations.map(f=>(
            <button key={f.name} onClick={()=>{setSelectedFormation(f.name);setSlotMap({});setPickingSlot(null);}}
              style={{padding:"7px 14px",background:selectedFormation===f.name?primaryCol:"rgba(255,255,255,0.05)",border:`1px solid ${selectedFormation===f.name?primaryCol:"rgba(255,255,255,0.1)"}`,borderRadius:20,color:selectedFormation===f.name?"#fff":"rgba(255,255,255,0.55)",fontFamily:"'Barlow Condensed',sans-serif",fontSize:14,fontWeight:700,cursor:"pointer"}}>
              {f.name}
            </button>
                          ))}
        <div style={{fontSize:11,color:"rgba(255,255,255,0.35)",marginBottom:14,display:"flex",gap:6,flexWrap:"wrap"}}>
          {rows.map((r,i)=><span key={i} style={{padding:"2px 8px",background:"rgba(255,255,255,0.05)",borderRadius:20}}>{r.count}x {r.pos}</span>)}
        </div>
        <div style={{position:"relative",width:"100%",borderRadius:10,overflow:"hidden",background:club.sport==="Tennis"?"linear-gradient(180deg,#1565c0,#1976d2)":"linear-gradient(180deg,#1a6b1a 0%,#1e7a1e 25%,#1a6b1a 50%,#1e7a1e 75%,#1a6b1a 100%)",boxShadow:"inset 0 0 0 2px rgba(255,255,255,0.15)",padding:"20px 8px",minHeight:Math.max(400, rowGroups.length * 90 + 60),marginBottom:16}}>
          <SportPitch sport={club.sport}/>
          <div style={{position:"relative",zIndex:2,display:"flex",flexDirection:"column",gap:10}}>
            {[...rowGroups].reverse().map((group,gi)=>(
              <div key={gi} style={{display:"flex",justifyContent:"space-evenly",alignItems:"center",flex:1,minHeight:80}}>
                {group.slots.map(slot=>{
                  const player=slotMap[slot.flatIdx]||null;
                  const isPicking=pickingSlot?.type==="starter"&&pickingSlot?.flatIdx===slot.flatIdx;
                  return (
                    <div key={slot.flatIdx} onClick={()=>setPickingSlot(isPicking?null:{type:"starter",flatIdx:slot.flatIdx,pos:slot.pos})}
                      style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2,cursor:"pointer"}}>
                      <div style={{fontSize:8,fontWeight:800,color:isPicking?"#fff":primaryCol,background:isPicking?primaryCol:"rgba(0,0,0,0.5)",padding:"1px 5px",borderRadius:3,letterSpacing:1,fontFamily:"'Barlow Condensed',sans-serif"}}>{slot.pos}</div>
                      <div style={{width:44,height:44,borderRadius:"50%",background:player?`linear-gradient(135deg,${secondaryCol},${primaryCol})`:"rgba(0,0,0,0.4)",border:`2px solid ${isPicking?"#fff":player?primaryCol:"rgba(255,255,255,0.3)"}`,overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:isPicking?"0 0 12px #fff":player?`0 0 8px ${primaryCol}88`:"none"}}>
                        {player?.photo?<img src={player.photo||player.photo_url} alt="" style={{width:"100%",height:"100%",objectFit:"cover",objectPosition:"top"}}/>:<div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:player?13:18,fontWeight:900,color:player?"#fff":"rgba(255,255,255,0.3)"}}>{player?inits(player.name):"+"}</div>}
                      </div>
                      <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:8,fontWeight:700,color:"#fff",textShadow:"0 1px 3px rgba(0,0,0,0.9)",maxWidth:46,textAlign:"center",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{player?player.name.split(" ").pop().toUpperCase():""}</div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
        {pickingSlot?.type==="starter"&&(
          <div style={{...G.card,marginBottom:12,border:`1px solid ${primaryCol}44`}}>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:14,fontWeight:700,color:primaryCol,marginBottom:8}}>Selecting {pickingSlot.pos}</div>
            <input style={{...G.input,marginBottom:8}} placeholder="Search..." value={search} onChange={e=>setSearch(e.target.value)} autoFocus/>
            <div style={{maxHeight:180,overflowY:"auto",display:"flex",flexDirection:"column",gap:4}}>
              {available.map(p=>(
                <div key={p.id} onClick={()=>{setSlotMap(m=>({...m,[pickingSlot.flatIdx]:p}));setPickingSlot(null);setSearch("");}}
                  style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",background:"rgba(255,255,255,0.04)",borderRadius:8,cursor:"pointer"}}>
                  <Avatar name={p.name} photo={p.photo||p.photo_url} size={30} colour={avCol(p.name)}/>
                  <div style={{flex:1}}><div style={{fontSize:13,fontWeight:600,color:"#fff"}}>{p.name}</div><div style={{fontSize:11,color:"rgba(255,255,255,0.38)"}}>{p.position||"—"}</div></div>
                </div>
              ))}
              {slotMap[pickingSlot?.flatIdx]&&<button onClick={()=>{setSlotMap(m=>{const n={...m};delete n[pickingSlot.flatIdx];return n;});setPickingSlot(null);}} style={{...G.btnGhost,fontSize:12,color:"#E63946",marginTop:4}}>Remove player from slot</button>}
            </div>
          </div>
        )}
        <FieldLabel>Substitutes (up to 5)</FieldLabel>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:14}}>
          {Array.from({length:5},(_,i)=>{
            const p=subsDraft[i]||null;
            const isPicking=pickingSlot?.type==="sub"&&pickingSlot?.idx===i;
            return (
              <div key={i} onClick={()=>setPickingSlot(isPicking?null:{type:"sub",idx:i})}
                style={{display:"flex",flexDirection:"column",alignItems:"center",gap:3,cursor:"pointer"}}>
                <div style={{width:44,height:44,borderRadius:"50%",background:p?`linear-gradient(135deg,${secondaryCol},${primaryCol})`:"rgba(255,255,255,0.06)",border:`2px solid ${isPicking?"#fff":p?primaryCol:"rgba(255,255,255,0.2)"}`,overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center"}}>
                  {p?.photo?<img src={p.photo||p.photo_url} alt="" style={{width:"100%",height:"100%",objectFit:"cover",objectPosition:"top"}}/>:<div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:p?12:16,fontWeight:900,color:"rgba(255,255,255,0.3)"}}>{p?inits(p.name):"+"}</div>}
                </div>
                <div style={{fontSize:8,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,color:"rgba(255,255,255,0.5)",textAlign:"center",maxWidth:46,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p?p.name.split(" ").pop().toUpperCase():"SUB"}</div>
              </div>
            );
          })}
        </div>
        {pickingSlot?.type==="sub"&&(
          <div style={{...G.card,marginBottom:12,border:`1px solid ${primaryCol}44`}}>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:14,fontWeight:700,color:primaryCol,marginBottom:8}}>Selecting Sub {pickingSlot.idx+1}</div>
            <input style={{...G.input,marginBottom:8}} placeholder="Search..." value={search} onChange={e=>setSearch(e.target.value)}/>
            <div style={{maxHeight:180,overflowY:"auto",display:"flex",flexDirection:"column",gap:4}}>
              {available.map(p=>(
                <div key={p.id} onClick={()=>{const nd=[...subsDraft];nd[pickingSlot.idx]=p;setSubsDraft(nd);setPickingSlot(null);setSearch("");}}
                  style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",background:"rgba(255,255,255,0.04)",borderRadius:8,cursor:"pointer"}}>
                  <Avatar name={p.name} photo={p.photo||p.photo_url} size={30} colour={avCol(p.name)}/>
                  <div><div style={{fontSize:13,fontWeight:600,color:"#fff"}}>{p.name}</div><div style={{fontSize:11,color:"rgba(255,255,255,0.38)"}}>{p.position||"—"}</div></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── TACTICS SECTION ── */}
        <div style={{marginTop:8}}>
          <FieldLabel>Team Tactics</FieldLabel>
          <textarea
            id={`tactics-team-${fixtureId}`}
            defaultValue={existing.teamTactics||""}
            onBlur={e=>{
              const val=e.target.value;
              const updated={...lineups[fixtureId]||{},teamTactics:val};
              setLineups(l=>({...l,[fixtureId]:updated}));
              if(clubId) db.saveLineup(clubId,fixtureId,updated).catch(console.error);
            }}
            placeholder={`e.g. High press from the front. Win the ball back in their half. ${club.sport==="Hockey"?"Use width on transitions, early ball into the circle.":"Quick transitions, exploit space in behind."}`}
            style={{...G.textarea,minHeight:80,marginBottom:14}}
          />

          <FieldLabel>Individual Player Instructions</FieldLabel>
          <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:14}}>
            {Object.values(slotMap).filter(Boolean).concat(subsDraft.filter(Boolean)).map(p=>(
              <div key={p.id} style={{...G.card,padding:"10px 12px"}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                  <Avatar name={p.name} photo={p.photo||p.photo_url} size={28} colour={avCol(p.name)}/>
                  <div style={{fontSize:13,fontWeight:600,color:"#fff"}}>{p.name}</div>
                  <div style={{fontSize:11,color:primaryCol,marginLeft:"auto"}}>{p.position||""}</div>
                </div>
                <input
                  id={`tactics-player-${fixtureId}-${p.id}`}
                  defaultValue={(existing.playerTactics||{})[p.id]||""}
                  onBlur={e=>{
                    const val=e.target.value.trim();
                    const updated={
                      ...(lineups[fixtureId]||{}),
                      playerTactics:{...((lineups[fixtureId]||{}).playerTactics||{}),[p.id]:val}
                    };
                    setLineups(l=>({...l,[fixtureId]:updated}));
                    if(clubId) db.saveLineup(clubId,fixtureId,updated).catch(console.error);
                  }}
                  placeholder={`Instructions for ${p.name.split(" ")[0]}...`}
                  style={{...G.input,marginBottom:0,fontSize:12}}
                />
              </div>
            ))}
            {Object.values(slotMap).filter(Boolean).length===0&&<div style={{fontSize:12,color:"rgba(255,255,255,0.3)",textAlign:"center",padding:"10px 0"}}>Add players to the lineup to set individual instructions</div>}
          </div>
        </div>
      </div>
    </div>
    );
  };


  const StaffDetailPanel = () => {
    const [removing, setRemoving] = useState(false);
    const [photoFrameLockedStaff, setPhotoFrameLockedStaff] = useState(true);
    const u = currentStaff;
    if(!u) return null;
    const isOwnProfile = currentUser?.id===u.id||currentUser?.name===u.name;
    const canEdit = isAdmin||isOwnProfile;
    const roleLabel = r => ({lead_coach:"Lead Coach",coach:"Coach",captain:"Captain",admin:"Club Admin"}[r]||"Staff");

    const uploadStaffPhoto = async (file) => {
      if(!file) return;
      setRemoving(true);
      const reader = new FileReader();
      reader.onload = async e => {
        const original = e.target.result;
        let url = original;
        if(clubId) {
          try {
            const res=await fetch(original);const blob=await res.blob();
            const path=`${clubId}/staff/${u.id}_${Date.now()}.jpg`;
            await supabase.storage.from("photos").upload(path,blob,{upsert:true,contentType:"image/jpeg"});
            const {data:ud}=supabase.storage.from("photos").getPublicUrl(path);
            url=ud.publicUrl;
          } catch(e){console.error(e);}
        }
        const updated={...u,photo:url};
        setCurrentStaff(updated);setUsers(us=>us.map(x=>x.id===u.id?updated:x));
        supabase.from("members").update({avatar_url:url}).eq("id",u.id).then(()=>{}).catch(console.error);
        showToast("Photo updated ✓");setRemoving(false);
      };
      reader.readAsDataURL(file);
    };

    return (
      <div style={{...G.panel,paddingBottom:100}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20}}>
          <button onClick={()=>{setCurrentStaff(null);setPanel("players");}} style={{background:"none",border:"none",color:"rgba(255,255,255,0.4)",fontSize:22,cursor:"pointer"}}>←</button>
          <div style={{...G.h2,flex:1}}>{u.name}</div>
          {isAdmin&&<button onClick={()=>{if(!window.confirm(`Archive ${u.name}? Data is retained but they will be hidden from active views.`))return;
            setUsers(us=>us.filter(x=>x.id!==u.id));
            supabase.from("members").update({is_archived:true}).eq("id",u.id).then(()=>{}).catch(console.error);
            setCurrentStaff(null);setPanel("players");showToast(`${u.name} archived`);
          }} style={{background:"rgba(230,57,70,0.12)",color:"#E63946",border:"1px solid #E6394644",borderRadius:9,padding:"9px 14px",fontSize:13,fontWeight:700,cursor:"pointer"}}>🗑</button>}
        </div>
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:12,marginBottom:24}}>
          <CoachCard user={u} size="full" clubLogo={club.logo} primaryCol={primaryCol} secondaryCol={secondaryCol} teamColour={teamDominantCol(u.team)}/>
          {canEdit&&(
            <div style={{display:"flex",gap:8,flexWrap:"wrap",justifyContent:"center"}}>
              <label style={{display:"flex",alignItems:"center",gap:6,padding:"9px 14px",background:`${primaryCol}18`,border:`1.5px solid ${primaryCol}55`,borderRadius:10,cursor:"pointer",fontSize:12,fontWeight:600,color:primaryCol}}>
                {removing?"⏳ Uploading...":"📁 Upload Photo"}
                <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>uploadStaffPhoto(e.target.files[0])}/>
              </label>
              <label style={{display:"flex",alignItems:"center",gap:6,padding:"9px 14px",background:`${primaryCol}18`,border:`1.5px solid ${primaryCol}55`,borderRadius:10,cursor:"pointer",fontSize:12,fontWeight:600,color:primaryCol}}>
                📷 Camera
                <input type="file" accept="image/*" capture="user" style={{display:"none"}} onChange={e=>uploadStaffPhoto(e.target.files[0])}/>
              </label>
              <button onClick={()=>setModal("photo-tips")} style={{display:"flex",alignItems:"center",gap:6,padding:"9px 14px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:10,cursor:"pointer",fontSize:12,fontWeight:600,color:"rgba(255,255,255,0.5)"}}>
                💡 Tips
              </button>
            </div>
          )}
          {u.photo&&canEdit&&(photoFrameLockedStaff ? (
              <button onClick={()=>setPhotoFrameLockedStaff(false)}
                style={{padding:"8px 16px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:10,color:"rgba(255,255,255,0.5)",fontSize:12,fontWeight:600,cursor:"pointer"}}>
                ✏️ Edit Photo Frame
              </button>
            ) : (()=>{
            const px=u.photoX??0,py=u.photoY??0,pz=u.photoZoom??120;
            const step=8,zstep=10;
            const nudge=(dx,dy,dz)=>{
              const nx=Math.max(-200,Math.min(200,px+dx));
              const ny=Math.max(-200,Math.min(200,py+dy));
              const nz=Math.max(80,Math.min(300,pz+dz));
              const upd={...u,photoX:nx,photoY:ny,photoZoom:nz};
              setCurrentStaff(upd);setUsers(us=>us.map(x=>x.id===u.id?upd:x));
              supabase.from("members").update({photo_x:nx,photo_y:ny,photo_zoom:nz}).eq("id",u.id).then(()=>{}).catch(console.error);
            };
            const btn=(label,dx,dy,dz)=>(
              <button onClick={()=>nudge(dx,dy,dz)} style={{width:38,height:38,background:"rgba(255,255,255,0.07)",border:"1px solid rgba(255,255,255,0.15)",borderRadius:8,color:"#fff",fontSize:16,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,flexShrink:0}}>
                {label}
              </button>
            );
            return (
              <div style={{width:"100%",maxWidth:300,padding:"12px 14px",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:12}}>
                <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.5)",letterSpacing:1,marginBottom:10,textAlign:"center"}}>📐 FRAME PHOTO</div>
                <div style={{display:"grid",gridTemplateColumns:"38px 38px 38px",gridTemplateRows:"38px 38px 38px",gap:4,justifyContent:"center",marginBottom:10}}>
                  <div/>{btn("▲",0,-step,0)}<div/>
                  {btn("◀",-step,0,0)}
                  <div style={{width:38,height:38,background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,color:"rgba(255,255,255,0.3)"}}>MOVE</div>
                  {btn("▶",step,0,0)}
                  <div/>{btn("▼",0,step,0)}<div/>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8,justifyContent:"center"}}>
                  {btn("−",0,0,-zstep)}
                  <div style={{flex:1,textAlign:"center"}}>
                    <div style={{fontSize:10,color:"rgba(255,255,255,0.4)"}}>Zoom</div>
                    <div style={{fontSize:13,fontWeight:700,color:"#fff"}}>{pz}%</div>
                  </div>
                  {btn("+",0,0,zstep)}
                </div>
                <button onClick={()=>nudge(-px,-py,120-pz)} style={{width:"100%",marginTop:10,padding:"5px",background:"none",border:"1px solid rgba(255,255,255,0.08)",borderRadius:7,color:"rgba(255,255,255,0.3)",fontSize:10,cursor:"pointer"}}>Reset</button>
              </div>
            );
          })())}
        </div>
        <div style={G.card}>
          <div style={{...G.h3,marginBottom:14}}>Details</div>
          <div style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid rgba(255,255,255,0.05)"}}>
            <span style={{fontSize:12,color:"rgba(255,255,255,0.4)"}}>Role</span>
            {canEdit&&isAdmin
              ? <select value={u.role} onChange={e=>{const upd={...u,role:e.target.value};setCurrentStaff(upd);setUsers(us=>us.map(x=>x.id===u.id?upd:x));supabase.from("members").update({role:e.target.value}).eq("id",u.id).then(()=>{}).catch(console.error);}} style={{...G.select,width:"auto",marginBottom:0,fontSize:12,padding:"4px 8px"}}>
                {["lead_coach","coach","captain","admin"].map(r=><option key={r} value={r}>{roleLabel(r)}</option>)}
              </select>
              : <span style={{fontSize:13,fontWeight:600,color:"#fff"}}>{roleLabel(u.role)}</span>}
          </div>
          <div style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid rgba(255,255,255,0.05)"}}>
            <span style={{fontSize:12,color:"rgba(255,255,255,0.4)"}}>Team</span>
            {canEdit&&isAdmin
              ? <select value={u.team||"all"} onChange={e=>{const upd={...u,team:e.target.value};setCurrentStaff(upd);setUsers(us=>us.map(x=>x.id===u.id?upd:x));supabase.from("members").update({team:e.target.value}).eq("id",u.id).then(()=>{}).catch(console.error);}} style={{...G.select,width:"auto",marginBottom:0,fontSize:12,padding:"4px 8px"}}>
                <option value="all">Whole Club</option>
                {teams.map(t=><option key={t} value={t}>{t}</option>)}
              </select>
              : <span style={{fontSize:13,fontWeight:600,color:"#fff"}}>{u.team==="all"?"Whole Club":u.team||"—"}</span>}
          </div>
          <div style={{display:"flex",justifyContent:"space-between",padding:"8px 0"}}>
            <span style={{fontSize:12,color:"rgba(255,255,255,0.4)"}}>Email</span>
            <span style={{fontSize:13,fontWeight:600,color:"#fff"}}>{u.email||"—"}</span>
          </div>
        </div>
      </div>
    );
  };


  const PendingPanel = () => {
    const pending = users.filter(u => !u.approved);
    return (
      <div style={{...G.panel,paddingBottom:100}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20}}>
          <button onClick={()=>navigate("home")} style={{background:"none",border:"none",color:"rgba(255,255,255,0.4)",fontSize:22,cursor:"pointer"}}>←</button>
          <div style={{...G.h2,flex:1}}>Pending Approvals</div>
          <div style={{fontSize:13,color:"#f0b429",fontWeight:700}}>{pending.length} awaiting</div>
        </div>
        {pending.length>0&&(
          <button onClick={()=>{
            if(!window.confirm(`Approve all ${pending.length} members?`))return;
            setUsers(us=>us.map(u=>({...u,approved:true})));
            pending.forEach(u=>{
              supabase.from("members").update({approved:true}).eq("id",u.id).then(()=>{}).catch(console.error);
            });
            showToast(`${pending.length} members approved ✓`);
          }} style={{width:"100%",padding:"11px",background:`${primaryCol}22`,border:`1px solid ${primaryCol}66`,borderRadius:10,color:primaryCol,fontSize:13,fontWeight:700,cursor:"pointer",marginBottom:14}}>
            ✓ Approve All ({pending.length})
          </button>
        )}
        {pending.length===0?(
          <div style={{...G.center,padding:"48px 20px",flexDirection:"column",gap:10}}>
            <div style={{fontSize:40}}>✅</div>
            <div style={{fontSize:15,fontWeight:700,color:"#fff"}}>All caught up</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.4)"}}>No pending approvals</div>
          </div>
        ):(
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {pending.map(u=>(
              <div key={u.id} style={{...G.card}}>
                <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
                  <Avatar name={u.name} size={44}/>
                  <div style={{flex:1}}>
                    <div style={{fontSize:16,fontWeight:700,color:"#fff"}}>{u.name}</div>
                    <div style={{fontSize:12,color:"rgba(255,255,255,0.45)",marginTop:2}}>{u.role} · {u.team||"No team"}</div>
                    <div style={{fontSize:11,color:"rgba(255,255,255,0.3)"}}>{u.email}</div>
                  </div>
                  <div style={{padding:"4px 10px",background:"rgba(240,180,41,0.15)",border:"1px solid rgba(240,180,41,0.3)",borderRadius:20,fontSize:10,fontWeight:700,color:"#f0b429"}}>PENDING</div>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>{
                    setUsers(us=>us.map(x=>x.id===u.id?{...x,approved:true}:x));
                    supabase.from("members").update({approved:true}).eq("id",u.id).then(()=>{}).catch(console.error);
                    showToast(`${u.name} approved ✓`);
                  }} style={{flex:1,padding:"10px",background:primaryCol,border:"none",borderRadius:8,color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer"}}>
                    ✓ Approve
                  </button>
                  <button onClick={()=>{
                    if(!window.confirm(`Decline ${u.name}?`))return;
                    setUsers(us=>us.filter(x=>x.id!==u.id));
                    supabase.from("members").delete().eq("id",u.id).then(()=>{}).catch(console.error);
                    showToast(`${u.name} declined`);
                  }} style={{padding:"10px 16px",background:"rgba(230,57,70,0.1)",border:"1px solid rgba(230,57,70,0.3)",borderRadius:8,color:"#E63946",fontSize:13,fontWeight:700,cursor:"pointer"}}>
                    ✕ Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };


  const DrillsPanel = () => {
    const [drillTab, setDrillTab] = useState("skills");
    const [filterCat, setFilterCat] = useState("all");
    const [filterDiff, setFilterDiff] = useState("all");
    const [searchQ, setSearchQ] = useState("");
    const [selectedItem, setSelectedItem] = useState(null);
    const [favourites, setFavourites] = useState(()=>JSON.parse(localStorage.getItem("p4m_drill_favs")||"[]"));

    const toggleFav = (id) => {
      const next = favourites.includes(id) ? favourites.filter(x=>x!==id) : [...favourites, id];
      setFavourites(next);
      localStorage.setItem("p4m_drill_favs", JSON.stringify(next));
    };

    // ── Football pitch diagram SVGs ─────────────────────────────────
    const FOOTBALL_DIAGRAMS = {
      fd1: ({col}) => (
        <svg viewBox="0 0 300 200" style={{width:"100%",height:"auto"}}>
          <rect width="300" height="200" fill="#2d5a27" rx="8"/>
          <rect x="10" y="10" width="280" height="180" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5"/>
          <circle cx="150" cy="100" r="45" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5"/>
          <circle cx="150" cy="100" r="3" fill="white"/>
          {/* Rondo circle of players */}
          {[0,1,2,3,4,5,6].map(i=>{
            const angle = (i/7)*Math.PI*2;
            const x = 150 + Math.cos(angle)*40;
            const y = 100 + Math.sin(angle)*40;
            return <circle key={i} cx={x} cy={y} r="7" fill={col} stroke="white" strokeWidth="1"/>;
          })}
          {/* 2 defenders in middle */}
          <circle cx="143" cy="95" r="7" fill="#E63946" stroke="white" strokeWidth="1"/>
          <circle cx="157" cy="105" r="7" fill="#E63946" stroke="white" strokeWidth="1"/>
          <text x="150" y="170" textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="10">Rondo — Keep possession from 2 defenders</text>
        </svg>
      ),
      fd3: ({col}) => (
        <svg viewBox="0 0 300 200" style={{width:"100%",height:"auto"}}>
          <rect width="300" height="200" fill="#2d5a27" rx="8"/>
          <rect x="10" y="10" width="280" height="180" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5"/>
          {/* 4 small goals */}
          <rect x="10" y="85" width="12" height="30" fill="none" stroke="white" strokeWidth="2"/>
          <rect x="278" y="85" width="12" height="30" fill="none" stroke="white" strokeWidth="2"/>
          <rect x="135" y="10" width="30" height="12" fill="none" stroke="white" strokeWidth="2"/>
          <rect x="135" y="178" width="30" height="12" fill="none" stroke="white" strokeWidth="2"/>
          <text x="150" y="105" textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="9">Attack any 2 goals</text>
          <text x="150" y="175" textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="10">4-Goal Game — switch play to find space</text>
        </svg>
      ),
      fd5: ({col}) => (
        <svg viewBox="0 0 300 200" style={{width:"100%",height:"auto"}}>
          <rect width="300" height="200" fill="#2d5a27" rx="8"/>
          <rect x="10" y="10" width="280" height="180" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5"/>
          {/* 3 players */}
          <circle cx="60" cy="100" r="8" fill={col} stroke="white" strokeWidth="1"/>
          <text x="60" y="104" textAnchor="middle" fill="white" fontSize="8">A</text>
          <circle cx="150" cy="100" r="8" fill={col} stroke="white" strokeWidth="1"/>
          <text x="150" y="104" textAnchor="middle" fill="white" fontSize="8">B</text>
          <circle cx="240" cy="70" r="8" fill={col} stroke="white" strokeWidth="1"/>
          <text x="240" y="74" textAnchor="middle" fill="white" fontSize="8">C</text>
          {/* Arrows */}
          <line x1="68" y1="100" x2="138" y2="100" stroke="white" strokeWidth="1.5" markerEnd="url(#arr)"/>
          <line x1="150" y1="92" x2="150" y2="60" stroke="rgba(255,255,255,0.5)" strokeWidth="1" strokeDasharray="4,3"/>
          <line x1="158" y1="95" x2="232" y2="74" stroke="white" strokeWidth="1.5" markerEnd="url(#arr)"/>
          <defs><marker id="arr" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="white"/></marker></defs>
          <text x="150" y="175" textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="10">A→B, B plays back, A runs forward to C</text>
        </svg>
      ),
      fd6: ({col}) => (
        <svg viewBox="0 0 300 200" style={{width:"100%",height:"auto"}}>
          <rect width="300" height="200" fill="#2d5a27" rx="8"/>
          <rect x="10" y="10" width="280" height="180" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5"/>
          {/* Goal */}
          <rect x="120" y="10" width="60" height="15" fill="none" stroke="white" strokeWidth="2"/>
          {/* Wide players */}
          <circle cx="20" cy="100" r="8" fill={col} stroke="white" strokeWidth="1"/>
          <text x="20" y="104" textAnchor="middle" fill="white" fontSize="8">W</text>
          <circle cx="280" cy="100" r="8" fill={col} stroke="white" strokeWidth="1"/>
          <text x="280" y="104" textAnchor="middle" fill="white" fontSize="8">W</text>
          {/* Strikers in box */}
          <circle cx="135" cy="55" r="8" fill={col} stroke="white" strokeWidth="1"/>
          <circle cx="165" cy="55" r="8" fill={col} stroke="white" strokeWidth="1"/>
          {/* Cross arrows */}
          <path d="M28,95 Q70,40 130,50" stroke="white" strokeWidth="1.5" fill="none" markerEnd="url(#arr2)"/>
          <path d="M272,95 Q230,40 170,50" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5" fill="none" strokeDasharray="4,3"/>
          <defs><marker id="arr2" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="white"/></marker></defs>
          <text x="150" y="178" textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="10">Alternate crosses, attackers make runs</text>
        </svg>
      ),
      fd8: ({col}) => (
        <svg viewBox="0 0 300 200" style={{width:"100%",height:"auto"}}>
          <rect width="300" height="200" fill="#2d5a27" rx="8"/>
          <rect x="10" y="10" width="280" height="180" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5"/>
          {/* Goal */}
          <rect x="105" y="10" width="90" height="15" fill="none" stroke="white" strokeWidth="2"/>
          {/* Attacker */}
          <circle cx="70" cy="140" r="9" fill={col} stroke="white" strokeWidth="1.5"/>
          <text x="70" y="144" textAnchor="middle" fill="white" fontSize="8">ATT</text>
          {/* Defender */}
          <circle cx="150" cy="110" r="9" fill="#E63946" stroke="white" strokeWidth="1.5"/>
          <text x="150" y="114" textAnchor="middle" fill="white" fontSize="8">DEF</text>
          {/* Arrow */}
          <path d="M78,132 L142,118" stroke="white" strokeWidth="1.5" markerEnd="url(#arr3)"/>
          <defs><marker id="arr3" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="white"/></marker></defs>
          <text x="150" y="178" textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="10">1v1 — attacker starts with 5m advantage</text>
        </svg>
      ),
      fd10: ({col}) => (
        <svg viewBox="0 0 300 200" style={{width:"100%",height:"auto"}}>
          <rect width="300" height="200" fill="#2d5a27" rx="8"/>
          <rect x="10" y="10" width="280" height="180" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5"/>
          {/* Goals */}
          <rect x="120" y="10" width="60" height="12" fill="none" stroke="white" strokeWidth="2"/>
          <rect x="120" y="178" width="60" height="12" fill="none" stroke="white" strokeWidth="2"/>
          {/* Team A */}
          {[[80,60],[120,80],[160,60],[100,100],[140,100]].map(([x,y],i)=>(
            <circle key={i} cx={x} cy={y} r="8" fill={col} stroke="white" strokeWidth="1"/>
          ))}
          {/* Team B */}
          {[[80,130],[120,110],[160,130],[100,150],[140,150]].map(([x,y],i)=>(
            <circle key={i} cx={x} cy={y} r="8" fill="#E63946" stroke="white" strokeWidth="1"/>
          ))}
          <text x="150" y="175" textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="10">5v5 SSG — high intensity, lots of touches</text>
        </svg>
      ),
    };

    const FOOTBALL_SKILLS = [
      { id:"fsk1", type:"skill", cat:"Technical", name:"First Touch", diff:"Beginner", icon:"⚽",
        description:"The ability to control the ball with different surfaces and direct it into useful space immediately upon receiving.",
        aims:"By the end of this session players should be able to receive a pass under pressure and set themselves for the next action in one smooth touch.",
        points:["Adopt an open body shape before the ball arrives","Cushion the ball — don't let it bounce off a rigid surface","Direct the touch into space away from pressure","Eyes on the ball, then scan before the touch lands","Use inside foot, outside foot, thigh and chest depending on height"],
        mistakes:["Letting the ball run too far — too heavy a touch","Watching the ball rather than cushioning it","Body shape sideways on — limits options","Receiving with head down — no awareness before touch"],
        when:"Every time you receive under pressure, in tight spaces, when turning, or when a first touch can immediately unlock space." },
      { id:"fsk2", type:"skill", cat:"Technical", name:"Passing — Short & Medium", diff:"Beginner", icon:"⚽",
        description:"Using the inside of the foot to deliver accurate, weighted passes over short and medium distances.",
        aims:"Players should be able to deliver a pass to a teammate's preferred foot at the right weight and timing consistently.",
        points:["Plant foot next to the ball, pointing at target","Strike through the middle of the ball","Follow through toward the target","Weight of pass — firm enough to arrive cleanly, not so hard it's uncontrollable","Communication — call for the ball, look before passing"],
        mistakes:["Toeing the ball rather than using inside foot","Leaning back — ball lifts","Not looking up before passing","Passing behind a moving teammate"],
        when:"Building out from the back, combination play in tight areas, switching play through midfield." },
      { id:"fsk3", type:"skill", cat:"Technical", name:"Shooting Technique", diff:"Intermediate", icon:"⚽",
        description:"Generating power and accuracy from different shooting positions — driven, placed, volleys and half-volleys.",
        aims:"Players should be able to strike a ball cleanly toward a specific area of goal from varied positions and angles.",
        points:["Plant foot beside the ball — not behind it","Strike with laces for power, inside foot for placement","Head over the ball — keep the shot down","Follow through in the direction of target","Body shape — lean over, not back"],
        mistakes:["Leaning back causes the ball to lift over the bar","Planting foot too far from ball — poor contact","Looking up at goal too early","No follow through — punch rather than drive"],
        when:"Shooting opportunities inside the penalty area, long range efforts, volleys from crosses and corners." },
      { id:"fsk4", type:"skill", cat:"Technical", name:"Dribbling & Ball Mastery", diff:"Intermediate", icon:"⚽",
        description:"Close control moves — step-overs, scissors, Cruyff turns, drag-backs — used to beat a defender or escape pressure.",
        aims:"Players should be able to execute at least two dribbling moves at match pace and apply them in a 1v1 situation.",
        points:["Sell the move — look at the defender, commit them","Change of pace before and after the move","Close control — keep the ball within touching distance","Head up — know where the next action is before completing the move","Weight on balls of feet — stay balanced and agile"],
        mistakes:["Telegraphing the move — no deception","Touching the ball too far after the move — defender recovers","No change of pace — move but no acceleration","Using the move in the wrong situation — too deep, no support"],
        when:"Wide areas to beat a fullback, in the final third to create shooting opportunities, pressing triggers to escape pressure in tight spaces." },
      { id:"fsk5", type:"skill", cat:"Technical", name:"Crossing", diff:"Intermediate", icon:"⚽",
        description:"Delivering the ball from wide positions — driven, whipped, near post, far post and cut-back deliveries.",
        aims:"Players should be able to identify the correct type of cross and deliver it accurately based on the movement of teammates in the box.",
        points:["Look up and identify runs before delivering","Approach angle — come at the ball to generate pace and bend","Contact point — inside foot for curl, laces for driven","Non-kicking foot points toward target","Follow through — don't decelerate at contact"],
        mistakes:["Crossing into the goalkeeper's hands — no bend or pace variation","Late cross — runners have stopped","Overhitting — ball out of play","Underhitting — easily cut out"],
        when:"When wide players have beaten their fullback, when teammates are making runs in behind, when the ball is played into wide areas on the last line." },
      { id:"fsk6", type:"skill", cat:"Defending", name:"1v1 Defending", diff:"Intermediate", icon:"⚽",
        description:"Technique for closing down an attacker, showing them inside or outside, and winning the ball at the right moment.",
        aims:"Players should be able to delay an attacker, force them into a less dangerous area and time a tackle or intercept correctly.",
        points:["Stay on your feet — don't dive in","Jockey — side-on stance, low centre of gravity","Force the attacker onto their weaker foot","Wait for the heavy touch before committing","Recovery pace — first step backward, then alongside the attacker"],
        mistakes:["Committing too early — lunging, easily beaten","Square on to the attacker — easy to go either side","Standing upright — slow to react","Ball-watching rather than body-watching"],
        when:"When an attacker has received behind your midfield, in wide areas where cover is limited, 1v1 situations in defensive transition." },
      { id:"fsk7", type:"skill", cat:"Tactical", name:"Positioning & Support Angles", diff:"Advanced", icon:"⚽",
        description:"Creating good passing options by taking up the right body shape, distance and angle to support the ball carrier.",
        aims:"Players should understand why width, depth and angle matter and consistently provide usable passing options in all areas of the pitch.",
        points:["Open body shape — face the play, back to goal when necessary","Create triangles — give the ball carrier two options minimum","Distance — close enough to receive but far enough to give time on the ball","Scan constantly — update your picture before the ball arrives","Move as the ball moves — don't be static"],
        mistakes:["Standing directly behind the ball carrier — no angle","Too close — taken out of the game if ball carrier is pressed","Too far — pass takes too long, intercepted","Not adjusting position as the ball moves"],
        when:"In all phases of possession play — building out, through the lines in midfield, creating overloads in the final third." },
      { id:"fsk8", type:"skill", cat:"Technical", name:"Heading", diff:"Intermediate", icon:"⚽",
        description:"Attacking and defensive headers — technique, timing and direction using the forehead.",
        aims:"Players should be able to direct a header toward goal or to a teammate consistently, and win defensive aerial duels.",
        points:["Attack the ball — don't wait for it","Strike with the forehead, not the crown","Eyes open on contact","Generate power from the neck and upper body","Time the jump — peak at the ball's arrival"],
        mistakes:["Heading with eyes closed","Letting the ball hit the top of the head — poor direction","Not attacking the ball — it hits you rather than you hitting it","Poor jump timing — no power"],
        when:"Attacking set pieces, defensive clearances, flick-ons in the box, winning second balls from long passes." },
    ];

    const FOOTBALL_DRILLS = [
      { id:"fd1", type:"drill", cat:"Possession", name:"Rondo — Keep Ball", diff:"All levels", age:"All", players:"8-12", equipment:"Bibs, cones", area:"10m circle",
        objective:"Develop quick passing, decision-making under pressure and movement off the ball.",
        aims:"By the end players should be scanning before receiving and playing one or two touch accurately under defensive pressure.",
        setup:"Mark a circle approximately 10m diameter. 2 players defend in the middle. Remaining players on the outside keep the ball.",
        howItWorks:"Defenders try to win the ball or force it out. When a defender wins the ball, the player who lost it replaces them. Count consecutive passes — set a team target.",
        points:["Scan before you receive — know where the next pass goes","Angle your body to play forward when possible","Move off the ball — don't stand still after passing","Quick feet — adjust to receive, don't wait flat-footed","Communicate — call for the ball with name and gesture"],
        progressions:["Two-touch limit","One-touch only","Directional rondo — must play forward through the square","Add a neutral player outside who can combine once"],
        regressions:["Larger circle","Three defenders","No touch limit","Defenders shadow only — no tackling"] },
      { id:"fd2", type:"drill", cat:"Possession", name:"3v1 Square", diff:"Beginner", age:"U10+", players:"4 per group", equipment:"4 cones", area:"10x10m",
        objective:"Improve support angles, quick passing and movement to maintain possession against a single defender.",
        aims:"Players should be able to maintain possession for 10+ consecutive passes in a 3v1 before the session ends.",
        setup:"Mark a 10x10m square. 1 defender inside, 3 attackers on the outside edges. Rotate defender every 30 seconds or when they win the ball.",
        howItWorks:"3 attackers pass between themselves. Defender tries to intercept or force an error. Players can move along their edge but not cross into the middle.",
        points:["Always provide two passing options to the ball carrier","Take up your position early — don't sprint to the ball","Weight of pass — firm enough to reach but controllable","Move immediately after passing — find a new angle","Patience — if no pass is on, keep the ball safe"],
        progressions:["Two-touch limit","Players can move freely (not restricted to edges)","Add a second defender"],
        regressions:["Larger square","Defender shadows only — no tackling","No touch limit"] },
      { id:"fd3", type:"drill", cat:"Attack vs Defence", name:"4-Goal Game", diff:"Intermediate", age:"U12+", players:"8-16", equipment:"8 small goals or cones, bibs", area:"30x20m",
        objective:"Encourage switching play, quick transitions and awareness of multiple goal options.",
        aims:"Players should be switching play instinctively when one side is blocked and identifying goal opportunities in both directions.",
        setup:"30x20m pitch with a small goal at each corner. Two teams. No GKs. Each team attacks two goals and defends two goals.",
        howItWorks:"Normal rules apply. Teams must stay alert to both attack and defensive directions simultaneously. Coach can award extra points for goals scored after a switch of play.",
        points:["Look across before receiving — identify which goal to attack","Switch play when pressure is on one side","Compact defensively — cover both goals","Quick transitions — when you win the ball, attack immediately","Communication — who covers which goal"],
        progressions:["Must score both goals in one attack for 2 points","Touch limit","Add a neutral player on each team"],
        regressions:["Larger pitch","Extra points for any switch of play","No pressing rule"] },
      { id:"fd4", type:"drill", cat:"Pressing", name:"Pressing Trigger Drill", diff:"Advanced", age:"U14+", players:"10-14", equipment:"Bibs, cones", area:"Half pitch",
        objective:"Develop coordinated team pressing from a set shape, triggered by specific moments.",
        aims:"Players should be able to identify pressing triggers and initiate a coordinated press as a unit within 2 seconds of the trigger.",
        setup:"Half pitch. Team set in defensive shape (e.g. 4-4-2). Coach or server plays ball to GK or back player. Press is triggered when coach calls a keyword.",
        howItWorks:"When the trigger is called (e.g. back pass, slow ball, poor body shape), the closest player leads the press. Second player cuts passing lane. Rest of team compact. GK organises.",
        points:["First presser — fast, angle to force inside or outside","Second presser — anticipate and cut the escape pass","Rest of team — step up and compact in unison","Don't press without a trigger — hold shape","Communicate the trigger — verbal cues to organise the press"],
        progressions:["Add an opposition team to play through the press","Remove the keyword — players identify the trigger themselves","Perform the drill under fatigue (after a shuttle run)"],
        regressions:["Walking speed first — walk through the shape","Pre-set single trigger only","Small group 4v3 pressing"] },
      { id:"fd5", type:"drill", cat:"Build Up Play", name:"Third Man Run", diff:"Advanced", age:"U14+", players:"6-10", equipment:"Cones, goal", area:"20x15m + goal",
        objective:"Develop timing of runs, combination play and the ability to play into third man runners in behind.",
        aims:"Players should execute the combination smoothly and the runner should arrive at the end ball in stride ready to finish.",
        setup:"Grid 20x15m. A starts with ball. B in middle. C starts at far end. Progress the combination to a finish on goal.",
        howItWorks:"A plays to B. B plays back to A. Meanwhile C makes a run in behind. A plays forward into C's run. C finishes. Rotate positions after each rep.",
        points:["C — time your run so you arrive as the ball arrives","A — weight the through ball into the run, not to the feet","B — play quickly, no hesitation — be the connector","Angles — B must check away before receiving to create space","Communication — C calls the run verbally or with gesture"],
        progressions:["Add a defender tracking C's run","Must finish first time","Two combinations before shooting","Third man runner switches — now B goes in behind"],
        regressions:["No defender","Slow the sequence down","Focus on the run timing only — no finish required"] },
      { id:"fd6", type:"drill", cat:"Finishing", name:"Crossing & Finishing", diff:"Intermediate", age:"U12+", players:"8-16", equipment:"Balls, cones, goals", area:"Full width penalty box",
        objective:"Improve the quality of crossing delivery and the variety of runs and finishes in the penalty area.",
        aims:"Crossers should deliver to a specific zone consistently. Attackers should attack the near post, far post and cut-back with varied finishing technique.",
        setup:"Crossers alternating both wings. 2-3 attackers in the box. GK in goal. Server feeds wide player. Wide player carries and crosses.",
        howItWorks:"Crosser receives from server and delivers into box. Attackers make pre-planned runs — one near post, one far post. Rotate crossers, attackers and GK. Award 2 points for a goal from the cut-back.",
        points:["Crosser — look up before crossing, identify runs","Near post runner — sharp angle, check away then attack","Far post runner — time the run to arrive as ball does","Finish with the correct technique for the delivery — volley, header, side foot","GK — communicate and claim crosses aggressively"],
        progressions:["Add a CB in the box","First-time finish rule","Combine with a build-up sequence before the cross"],
        regressions:["Static crosser — focus on delivery shape first","Near post serves only","No defender in the box"] },
      { id:"fd7", type:"drill", cat:"Transitions", name:"Transition Game — Counter-Press", diff:"Advanced", age:"U14+", players:"12-18", equipment:"Bibs, cones, goals", area:"Full pitch",
        objective:"Develop counter-pressing habits and fast attacking transitions immediately after winning the ball.",
        aims:"The team that wins the ball should be in a shooting position within 6 seconds. The team that loses the ball should press to win it back within 5.",
        setup:"Full pitch split into 3 zones. Normal match but with a 5-second counter-press rule — if you lose the ball you have 5 seconds to win it back. If you succeed, counter-attack.",
        howItWorks:"Play normal football. When possession is lost, coach counts 5 seconds. If ball not won back in 5, team must reset into shape. If won back, fast attack. Award extra point for goals within 4 passes of winning the ball.",
        points:["Nearest player presses immediately — no hesitation","Second player cuts escape pass — don't both chase the ball","Ball-winners — play forward at pace when you win it","Wide players — make themselves available immediately","Striker — spin in behind the moment the ball is won"],
        progressions:["Countdown timer shown to players","Condition: must score within 4 passes of winning ball","Add a pressing zone — only press in final third"],
        regressions:["Larger transition zones","Remove the 5-second rule","Smaller numbers — 7v7"] },
      { id:"fd8", type:"drill", cat:"Attack vs Defence", name:"1v1 to Goal", diff:"Intermediate", age:"U12+", players:"8-14", equipment:"Goal, GK, cones", area:"20m channel",
        objective:"Develop attacking 1v1 skills — dribbling, moves and finishing — and defensive 1v1 technique under real pressure.",
        aims:"Attackers should be able to execute a dribbling move at pace and finish consistently. Defenders should delay and force outside without diving in.",
        setup:"20m run-in. Full-size goal with GK. Attacker starts with ball. Defender starts 5m behind — disadvantage. Cone marks the 1v1 start point.",
        howItWorks:"Attacker dribbles at speed. Defender chases and engages from 5m behind. 1v1 from the cone. Attacker tries to score. If defender wins ball, they counter-attack to a mini-goal 30m away. Rotate every 3 reps.",
        points:["Attacker — engage early, commit the defender, execute the move","Change of pace — use speed before and after the move","Defender — don't dive in, jockey, force onto weaker foot","Defender — wait for the heavy touch before tackling","GK — stay on their line until the last moment, don't commit early"],
        progressions:["Equal start — no disadvantage","Add second defender after 3 seconds","Attacker must take on the defender — cannot shoot straight away"],
        regressions:["Defender passive — can only shadow","Larger goal","No GK — open goal"] },
      { id:"fd9", type:"drill", cat:"Finishing", name:"2v1 + GK Combination Finish", diff:"Intermediate", age:"U12+", players:"6-12", equipment:"Goal, GK, cones", area:"25m box",
        objective:"Develop quick combination play in the final third and clinical finishing under defensive pressure.",
        aims:"Two attackers should consistently combine in 3 passes or fewer and produce a shot on target in a 2v1 situation.",
        setup:"Box starts 25m from goal. Server feeds ball to first attacker. Second attacker runs from a cone 5m away. One defender enters from the side 3 seconds after the ball is played.",
        howItWorks:"Two attackers combine to beat the one defender and finish. 3-pass limit. Defender enters with a 3-second delay. GK in goal. Rotate server, attackers, defender and GK.",
        points:["First attacker — control and lay off quickly","Second attacker — time your run to arrive on the combination","Don't crowd each other — maintain width and depth","Finish early — don't wait for the perfect moment","1-2s — use the wall pass to beat the defender"],
        progressions:["Add a second defender","Must include a 1-2 before shooting","First-time finish only"],
        regressions:["No defender first","Larger playing area","No pass limit — take your time"] },
      { id:"fd10", type:"drill", cat:"Small-Sided Game", name:"5v5 + GKs — Theme SSG", diff:"All levels", age:"All", players:"12-14", equipment:"Goals, GKs, bibs", area:"40x30m",
        objective:"Consolidate the session theme in a realistic game environment with high intensity and lots of ball contacts.",
        aims:"Players should demonstrate the session's key coaching point at least twice in the game without prompting.",
        setup:"40x30m pitch. Normal goals with GKs. Two teams of 5. Normal rules but with the session theme as a condition.",
        howItWorks:"Play normally. Coach freezes play when a player demonstrates the session theme well (positive freeze) or misses an opportunity to apply it (teachable moment). Finish with a free game — no conditions.",
        points:["Apply the session theme — don't forget it in the game","High intensity — this is match pace","Communication — talk to each other constantly","GKs — stay engaged, organise the defence","Encourage creativity — use the whole pitch"],
        progressions:["Add session theme condition (e.g. must switch play before scoring)","Touch limit","Overloads 6v4"],
        regressions:["Larger pitch","Extra neutral player who always plays with the team in possession","Remove all conditions — free game"] },
    ];

    const HOCKEY_SKILLS = [
      // Dribbling
      { id:"hs_d1", type:"skill", cat:"Dribbling", name:"Indian Dribble", diff:"Beginner", desc:"The foundation of hockey dribbling — alternate the ball from forehand to reverse side while moving forward.", points:["Keep the ball in front and slightly to the right of your feet","Push the ball with the toe of the stick, not the middle","Stay low with bent knees — don't stand upright","Eyes up to scan the field while dribbling","Short, quick touches — maintain close control"], mistakes:["Letting the ball drift too far ahead","Standing too upright","Looking down at the ball constantly"], when:"Creating space in midfield, carrying under pressure, building from the back" },
      { id:"hs_d2", type:"skill", cat:"Dribbling", name:"Drag Left", diff:"Intermediate", desc:"Pull the ball from right to left across your body to wrong-foot a defender.", points:["Approach the defender at pace to commit them","Place left foot beside the ball before dragging","Use the hook of the stick to pull the ball left","Accelerate immediately after the drag","Sell the fake — look right before going left"], mistakes:["Executing too slowly","Not committing the defender first","Dragging without accelerating away"], when:"1v1 situations when defender is overcommitting to the right" },
      { id:"hs_d3", type:"skill", cat:"Dribbling", name:"Herringbone", diff:"Intermediate", desc:"A zig-zag dribbling skill using alternate forehand and reverse touches to beat defenders.", points:["Quick alternating touches — forehand then reverse","Change of direction must be sharp and decisive","Keep the ball close — no more than a stick length away","Use body feint to sell the direction change","Accelerate through the gap created"], mistakes:["Slow, telegraphed direction changes","Ball drifting too wide","No body feint to deceive defender"], when:"Breaking through a defensive press, creating space in the midfield third" },
      { id:"hs_d4", type:"skill", cat:"Dribbling", name:"Reverse Turn Out", diff:"Advanced", desc:"Receive the ball in a closed position and spin out on the reverse side to face forward.", points:["Receive ball on backhand side, body side-on","Pivot on left foot, rotating clockwise","Use reverse stick to guide ball through the turn","Head up before completing turn — know your next pass","Complete the turn explosively — don't slow down in the turn"], mistakes:["Turning into pressure","Not scanning before receiving","Slow pivot allowing defender to intercept"], when:"Receiving under pressure in midfield, turning play from defence to attack" },
      { id:"hs_d5", type:"skill", cat:"Dribbling", name:"Shield and Carry", diff:"Intermediate", desc:"Use your body position to protect the ball when under pressure from a chasing defender.", points:["Keep body between defender and ball","Ball on forehand side, defender on your left","Low centre of gravity — bent knees, wide stance","Feel the defender's pressure — don't look back","Use small touches to maintain control while shielding"], mistakes:["Leaning backwards","Letting ball get too far from body","Stopping moving — keep driving forward"], when:"Holding possession when chased from behind, protecting the ball near the sideline" },
      // Passing
      { id:"hs_p1", type:"skill", cat:"Passing", name:"Push Pass", diff:"Beginner", desc:"The most accurate pass in hockey — a short to medium range pass using a pushing motion.", points:["Ball starts beside right foot, weight on left foot","Smooth transfer of weight right to left through the pass","Low backswing — this is a push, not a hit","Follow through low and in the direction of the target","Wrists turn through at point of contact for direction"], mistakes:["Slapping instead of pushing","High backswing causing mistiming","No weight transfer — pass loses pace"], when:"Most pass situations — the default passing technique at all distances up to 20m" },
      { id:"hs_p2", type:"skill", cat:"Passing", name:"Slap Pass", diff:"Intermediate", desc:"A faster, flatter pass used for longer distances or when you need to release the ball quickly.", points:["Ball positioned further right — between feet","Backswing low and fast","Strike through the bottom of the ball","Keep the stick head low after contact","Good for quick ball recycling — disguise direction before releasing"], mistakes:["Too high a backswing — risk of dangerous play","Ball too close — cramped swing","No disguise — defender reads the pass early"], when:"Switching play across the pitch, fast break attacks, long outlet passes" },
      { id:"hs_p3", type:"skill", cat:"Passing", name:"Reverse Stick Sweep", diff:"Intermediate", desc:"A pass using the reverse side of the stick — essential for playing the ball left without turning.", points:["Rotate grip slightly to open reverse face","Ball positioned to the left of centre","Low sweeping motion through the ball","Follow through pointing at target","Use body rotation for power — don't arm only"], mistakes:["Jabbing at the ball rather than sweeping","Open stick face causing lifted pass","Not rotating body — pass lacks pace"], when:"Moving ball left quickly, one-touch passing in tight areas, quick combinations" },
      { id:"hs_p4", type:"skill", cat:"Passing", name:"Aerial Pass", diff:"Advanced", desc:"A lifted pass used to play over defenders or cover long distances quickly.", points:["Ball well ahead of feet — room for backswing","Open the stick face by rolling wrists back","Strike up through the ball — not just forward","Full follow through, upward and forward","Signal to teammate before lifting — they need to prepare"], mistakes:["Insufficient follow through — ball doesn't get height","Ball too close to feet — cramped swing","No warning to receiver — they can't prepare"], when:"Playing over a press, switching play quickly, penalty corners" },
      // Receiving
      { id:"hs_r1", type:"skill", cat:"Receiving", name:"Open Forehand Receive", diff:"Beginner", desc:"Receiving the ball on the forehand side in an open body position ready to play forward.", points:["Lead early to create space — don't wait for the ball","Open body position — chest facing passer slightly","Soft hands — give with the ball on contact","Take ball on the move whenever possible","First touch into space — set yourself up for the next action"], mistakes:["Receiving flat-footed","Hard hands — ball bounces off stick","Taking touch across body — slows down play"], when:"Most receiving situations — building from defence, receiving in space" },
      { id:"hs_r2", type:"skill", cat:"Receiving", name:"Closed Receive and Turn", diff:"Intermediate", desc:"Receive the ball in a closed body position and use momentum to turn into space.", points:["Receive side-on, ball on forehand","Shield the ball with body from the defender","Small first touch to set up the turn","Turn in the direction you've already scanned","Drive away from pressure immediately after turning"], mistakes:["Turning into the defender","Not scanning before receiving","Slow first touch — defender closes down"], when:"Receiving under pressure from behind, turning play in midfield" },
      { id:"hs_r3", type:"skill", cat:"Receiving", name:"First Touch Into Space", diff:"Intermediate", desc:"Using your first touch to move the ball into space rather than just stopping it.", points:["Decide direction of first touch before ball arrives","Angle the stick face toward target space","Cushion then redirect — not just a stop","Communicate with passer if possible — show where you want it","Immediately drive into the space created"], mistakes:["First touch back the way ball came","Heavy first touch — ball runs too far","No space identified before receiving"], when:"Counter-attacks, breaking through pressure, creating passing angles" },
      // Shooting
      { id:"hs_sh1", type:"skill", cat:"Shooting", name:"Penalty Corner Drag Flick", diff:"Advanced", desc:"The drag flick — the most powerful scoring technique from penalty corners.", points:["Low, explosive approach — stay low through entire motion","Ball drag back through swing creating whip effect","Rotate hips and shoulders through contact — generate power","Release point determines direction — early=left, late=right","Follow through fully — stick finishes high"], mistakes:["Standing up through the shot — loss of power","Ball too far forward in drag — no snap effect","No hip rotation — using arms only"], when:"Penalty corners — the primary scoring option at set plays" },
      { id:"hs_sh2", type:"skill", cat:"Shooting", name:"Deflection at Goal", diff:"Intermediate", desc:"Redirecting a cross or pass into goal using a deflection touch.", points:["Early run to deflection point — arrive before the ball","Get in line with the ball — don't reach for it","Small contact point — redirect don't stop","Angle stick face toward target corner","Stay balanced — weight low, ready to adjust"], mistakes:["Moving too late — ball passes before you arrive","Too big a touch — GK can react","Standing upright — can't adjust to ball height"], when:"Penalty corners, open play crosses, set plays" },
      // Tackling
      { id:"hs_t1", type:"skill", cat:"Tackling", name:"Jab Tackle", diff:"Beginner", desc:"A quick jab of the stick to knock the ball away from an attacker without committing.", points:["Stay balanced — don't lunge or over-commit","Quick extension of stick toward ball","Aim for the space between stick and ball","Recover position immediately after the jab","Use when attacker is off-balance or looking away"], mistakes:["Diving in — attacker steps around you","Jabbing at the player not the ball","No recovery — getting caught out of position"], when:"1v1 defending, pressing high, defending against fast attackers" },
      { id:"hs_t2", type:"skill", cat:"Tackling", name:"Block Tackle", diff:"Intermediate", desc:"A committed tackle using the flat face of the stick to block the ball.", points:["Get close before committing — don't tackle from distance","Low body position — stick and body go to ground together","Aim flat stick face at ball","Strong wrists — don't let attacker push through","Win possession cleanly — drive ball away from attacker"], mistakes:["Tackling from distance — attacker steps around","High stick approach — safety risk","Weak wrists — attacker plays through tackle"], when:"Close defending situations, when attacker is not at full speed" },
    ];

    const HOCKEY_DRILLS = [
      { id:"hd1", type:"drill", cat:"Dribbling", name:"Minefield Dribble", diff:"Beginner", age:"U12+", players:"4-8", equipment:"8 cones, 1 ball per player", area:"20x15m",
        objective:"Improve close ball control and dribbling vision while navigating obstacles.",
        setup:"Scatter 8 cones randomly across a 20x15m grid. Players start on one end with a ball each.",
        howItWorks:"Players dribble from one end to the other avoiding cones. Player who hits fewest cones wins. Run 3 times.",
        points:["Head up — use peripheral vision not eyes on ball","Short, quick touches — maintain close control","Change direction smoothly — don't jerk","Stay low — bent knees, good balance"],
        progressions:["Add defenders who can jab tackle","Introduce time limit","Players call out number of fingers coach holds up"],
        regressions:["Wider spacing between cones","Slower pace — focus on technique first","Remove competition element"], icon:"🏑" },
      { id:"hd2", type:"drill", cat:"Passing", name:"Triangle Passing Circuit", diff:"Beginner", age:"All", players:"3+", equipment:"3 cones, 1 ball", area:"15x15m",
        objective:"Develop passing accuracy, weight of pass and movement after passing.",
        setup:"3 cones in a triangle, 15m between each. One player at each cone.",
        howItWorks:"Player at cone A passes to B, follows their pass to cone B. B passes to C and follows. C passes to A and follows. Continuous circuit.",
        points:["Pass then move — don't stand still","Call for the ball before it arrives","Weight of pass — firm enough to reach but not too hard","First touch takes ball forward into space"],
        progressions:["Two touches only then one touch","Add fourth player as defender in middle","Reverse direction every 30 seconds","Increase triangle size for longer passes"],
        regressions:["Start stationary — master technique before movement","Reduce triangle size","Allow more time between passes"], icon:"🔺" },
      { id:"hd3", type:"drill", cat:"Attacking", name:"3v2 Attack Drill", diff:"Intermediate", age:"U14+", players:"5", equipment:"4 cones, 1 ball, cones for goal", area:"30x20m",
        objective:"Develop attacking combinations and decision-making in overload situations.",
        setup:"Mark a 30x20m channel. 3 attackers start at one end, 2 defenders in the channel. Small goal at far end.",
        howItWorks:"Attackers have 30 seconds to score. If defenders win ball they clear beyond the start line. Rotate after each attack.",
        points:["Attackers: use width — spread the defenders","Quick ball movement — don't let defenders recover","Decision: when to pass vs when to run at goal","Defenders: stay compact — don't both chase ball"],
        progressions:["Add time pressure — 20 seconds","Give defenders a counterattack goal","Add GK to make it harder to score"],
        regressions:["Make it 3v1 first","Give attackers more time","Make goal bigger"], icon:"⚡" },
      { id:"hd4", type:"drill", cat:"Defending", name:"Channel Defence 1v1", diff:"Intermediate", age:"U14+", players:"2", equipment:"4 cones, 1 ball", area:"10x30m",
        objective:"Develop 1v1 defending technique — delay, channel and tackle timing.",
        setup:"10m wide channel, 30m long. Attacker starts at one end with ball, defender starts 5m ahead.",
        howItWorks:"Attacker tries to reach the far end. Defender must channel attacker to the sideline and prevent them reaching the end line.",
        points:["Defender: side-on position — show the attacker outside","Bent knees — ready to move in any direction","Stay patient — don't dive in","Channel don't chase — control where attacker goes","Jab tackle when attacker's head is down or balance poor"],
        progressions:["Defender starts level with attacker","Add a goal at far end to score into","Attacker can go either side — no channelling cue"],
        regressions:["Defender starts ahead — easier to get in position","No tackling allowed — just channel practice","Walk speed first"], icon:"🛡" },
      { id:"hd5", type:"drill", cat:"Shooting", name:"Continuous Shooting Circuit", diff:"Intermediate", age:"U12+", players:"4-8", equipment:"8 balls, cones, goal", area:"Circle area",
        objective:"Develop shooting technique under fatigue with varied angles.",
        setup:"Cones at 4 positions around the circle edge — left, right, top, wide right. Balls stacked at each cone.",
        howItWorks:"Player starts top right, shoots, sprints to next cone, shoots again. Complete circuit (4 shots). Next player goes. Score based on goals.",
        points:["Set up ball before shooting — one touch to position","Low, hard shot — don't try to lift","Far post shots — aim away from GK","Move into next position before previous player has shot"],
        progressions:["Add GK","Dribble from 23m before shooting","Defender chasing from behind creates pressure"],
        regressions:["Stationary shots first","No time limit — focus on technique","Larger goal target area"], icon:"🥅" },
      { id:"hd6", type:"drill", cat:"Possession", name:"Rondo 4v1", diff:"Beginner", age:"All", players:"5", equipment:"4 cones, 1 ball", area:"10x10m",
        objective:"Develop quick passing, movement and decision-making under pressure.",
        setup:"4 cones marking a 10x10m square. 4 players on outside, 1 in middle.",
        howItWorks:"Outside players pass to keep ball from middle player. If middle player intercepts or forces error, player who made error swaps in.",
        points:["Two touch maximum — don't hold the ball","Move along the side after passing — give a passing angle","Body open — can see all pass options","Middle player: press the ball, don't chase shadows"],
        progressions:["One touch only","2 players in the middle","Reduce square size to 7x7m"],
        regressions:["3 touches allowed","Larger 12x12m square","Allow walking pace"], icon:"⭕" },
      { id:"hd7", type:"drill", cat:"Penalty Corners", name:"Corner Routine Practice", diff:"Intermediate", age:"U14+", players:"5", equipment:"Ball, cones, goal, GK optional", area:"Circle",
        objective:"Practise penalty corner setup, injection, stop and shot in match conditions.",
        setup:"Full penalty corner setup — injector behind backline, stopper at circle edge, shooter at top.",
        howItWorks:"Full corner routine — injection, stop, drag flick or hit. Vary the routine each time. Rotate all roles.",
        points:["Injector: low, hard injection to stopper's stick","Stopper: dead stop — ball must be still before shot","Shooter: choose target corner before running — commit to it","Communication: all three must be ready before routine starts"],
        progressions:["Add a GK","Add two defenders on posts","Introduce fake variation — dummy the shot then pass"],
        regressions:["No GK or defenders — technique focus","Stationary stop practice before full routine","Walk-through at slow speed first"], icon:"🎯" },
    ];

    const SKILLS = club.sport==="Football" ? FOOTBALL_SKILLS : HOCKEY_SKILLS;
    const DRILLS = club.sport==="Football" ? FOOTBALL_DRILLS : HOCKEY_DRILLS;
    const allItems = drillTab==="skills" ? SKILLS : DRILLS;
    const cats = ["all",...new Set(allItems.map(x=>x.cat))];
    const diffs = ["all","Beginner","Intermediate","Advanced"];
    const filtered = allItems.filter(x=>{
      if(filterCat!=="all"&&x.cat!==filterCat) return false;
      if(filterDiff!=="all"&&x.diff!==filterDiff) return false;
      if(searchQ&&!x.name.toLowerCase().includes(searchQ.toLowerCase())&&!x.cat.toLowerCase().includes(searchQ.toLowerCase())) return false;
      return true;
    });

    const diffCol = d=>d==="Beginner"?"#22c55e":d==="Intermediate"?"#f0b429":"#E63946";

    // Detail view
    if(selectedItem) {
      const item = selectedItem;
      const isFav = favourites.includes(item.id);
      return (
        <div style={{...G.panel,paddingBottom:100}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
            <button onClick={()=>setSelectedItem(null)} style={{background:"none",border:"none",color:"rgba(255,255,255,0.4)",fontSize:22,cursor:"pointer"}}>←</button>
            <div style={{flex:1}}><div style={G.h2}>{item.name}</div></div>
            <button onClick={()=>toggleFav(item.id)} style={{background:"none",border:"none",fontSize:22,cursor:"pointer"}}>{isFav?"❤️":"🤍"}</button>
          </div>

          {/* SVG Diagram — hockey or football */}
          {item.type==="drill" && (
          <div style={{marginBottom:16}}>
            <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:1.5,color:"rgba(255,255,255,0.38)",marginBottom:8}}>Drill Diagram</div>
            <SessionDiagram drillText={item.name+" "+(item.objective||"")+" "+(item.setup||"")} sessionText={item.cat} col={primaryCol} sport={club.sport}/>
          </div>
        )}
      {item.type==="drill" && (DRILL_DIAGRAMS[item.id] || FOOTBALL_DIAGRAMS?.[item.id]) && (
            <div style={{...G.card,marginBottom:16,padding:12,background:"rgba(26,107,26,0.15)",border:"1px solid rgba(255,255,255,0.08)"}}>
              {DRILL_DIAGRAMS[item.id]
                ? React.createElement(DRILL_DIAGRAMS[item.id], {col:primaryCol})
                : React.createElement(FOOTBALL_DIAGRAMS[item.id], {col:primaryCol})}
            </div>
          )}

          {/* Aims box */}
          {item.aims&&(
            <div style={{...G.card,marginBottom:12,background:`${primaryCol}0a`,border:`1px solid ${primaryCol}33`}}>
              <div style={{fontSize:12,fontWeight:700,color:primaryCol,marginBottom:6,letterSpacing:1}}>🎯 SESSION AIM</div>
              <div style={{fontSize:13,color:"rgba(255,255,255,0.85)",lineHeight:1.6,fontStyle:"italic"}}>{item.aims}</div>
            </div>
          )}

          {/* Tags */}
          <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:16}}>
            <span style={{padding:"4px 12px",background:`${diffCol(item.diff)}22`,border:`1px solid ${diffCol(item.diff)}44`,borderRadius:20,fontSize:11,fontWeight:700,color:diffCol(item.diff)}}>{item.diff}</span>
            <span style={{padding:"4px 12px",background:`${primaryCol}18`,border:`1px solid ${primaryCol}44`,borderRadius:20,fontSize:11,color:primaryCol}}>{item.cat}</span>
            {item.type==="drill"&&<>
              <span style={{padding:"4px 12px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:20,fontSize:11,color:"rgba(255,255,255,0.5)"}}>👥 {item.players}</span>
              <span style={{padding:"4px 12px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:20,fontSize:11,color:"rgba(255,255,255,0.5)"}}>📐 {item.area}</span>
              <span style={{padding:"4px 12px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:20,fontSize:11,color:"rgba(255,255,255,0.5)"}}>🎂 {item.age}</span>
            </>}
          </div>

          {(item.desc||item.description)&&<div style={{...G.card,marginBottom:12}}>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.8)",lineHeight:1.7}}>{item.desc||item.description}</div>
          </div>}

          {item.type==="skill"&&item.points&&<>
            <div style={{...G.card,marginBottom:12}}>
              <div style={{fontSize:12,fontWeight:700,color:primaryCol,marginBottom:8,letterSpacing:1}}>KEY COACHING POINTS</div>
              {(item.points||[]).map((p,i)=><div key={i} style={{display:"flex",gap:8,marginBottom:6}}><span style={{color:primaryCol,flexShrink:0,fontWeight:700}}>→</span><div style={{fontSize:13,color:"rgba(255,255,255,0.8)",lineHeight:1.5}}>{p}</div></div>)}
            </div>
            <div style={{...G.card,marginBottom:12}}>
              <div style={{fontSize:12,fontWeight:700,color:"#E63946",marginBottom:8,letterSpacing:1}}>COMMON MISTAKES</div>
              {(item.mistakes||[]).map((m,i)=><div key={i} style={{display:"flex",gap:8,marginBottom:6}}><span style={{color:"#E63946",flexShrink:0}}>✗</span><div style={{fontSize:13,color:"rgba(255,255,255,0.7)",lineHeight:1.5}}>{m}</div></div>)}
            </div>
            {item.when&&<div style={{...G.card,marginBottom:12}}>
              <div style={{fontSize:12,fontWeight:700,color:"#22c55e",marginBottom:6,letterSpacing:1}}>WHEN TO USE IN A GAME</div>
              <div style={{fontSize:13,color:"rgba(255,255,255,0.7)",lineHeight:1.6}}>{item.when}</div>
            </div>}
          </>}

          {item.type==="drill"&&item.objective&&<>
            <div style={{...G.card,marginBottom:12}}>
              <div style={{fontSize:12,fontWeight:700,color:"rgba(255,255,255,0.5)",marginBottom:6,letterSpacing:1}}>OBJECTIVE</div>
              <div style={{fontSize:13,color:"rgba(255,255,255,0.8)",lineHeight:1.6}}>{item.objective}</div>
            </div>
            <div style={{...G.card,marginBottom:12}}>
              <div style={{fontSize:12,fontWeight:700,color:"rgba(255,255,255,0.5)",marginBottom:6,letterSpacing:1}}>SETUP</div>
              <div style={{fontSize:13,color:"rgba(255,255,255,0.8)",lineHeight:1.6}}>{item.setup}</div>
              <div style={{fontSize:12,fontWeight:700,color:"rgba(255,255,255,0.5)",margin:"10px 0 6px",letterSpacing:1}}>HOW IT WORKS</div>
              <div style={{fontSize:13,color:"rgba(255,255,255,0.8)",lineHeight:1.6}}>{item.howItWorks}</div>
            </div>
            <div style={{...G.card,marginBottom:12}}>
              <div style={{fontSize:12,fontWeight:700,color:primaryCol,marginBottom:8,letterSpacing:1}}>COACHING POINTS</div>
              {(item.points||[]).map((p,i)=><div key={i} style={{display:"flex",gap:8,marginBottom:6}}><span style={{color:primaryCol,flexShrink:0,fontWeight:700}}>→</span><div style={{fontSize:13,color:"rgba(255,255,255,0.8)",lineHeight:1.5}}>{p}</div></div>)}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
              <div style={{...G.card}}>
                <div style={{fontSize:12,fontWeight:700,color:"#22c55e",marginBottom:8,letterSpacing:1}}>PROGRESSIONS</div>
                {(item.progressions||[]).map((p,i)=><div key={i} style={{fontSize:12,color:"rgba(255,255,255,0.7)",marginBottom:4,lineHeight:1.4}}>↑ {p}</div>)}
              </div>
              <div style={{...G.card}}>
                <div style={{fontSize:12,fontWeight:700,color:"#f0b429",marginBottom:8,letterSpacing:1}}>REGRESSIONS</div>
                {(item.regressions||[]).map((r,i)=><div key={i} style={{fontSize:12,color:"rgba(255,255,255,0.7)",marginBottom:4,lineHeight:1.4}}>↓ {r}</div>)}
              </div>
            </div>
            {isCoach&&(
              <button onClick={()=>{
                const session={id:uid(),opponent:`Training · ${item.name}`,date:new Date().toISOString().split("T")[0],time:"19:00",location:"Training Ground",team:myTeams||"all",isSession:true,result:null,availability:{}};
                setFixtures(fs=>[...fs,session]);
                db.createFixture(clubId,session).catch(console.error);
                showToast(`${item.name} added to calendar ✓`);
                navigate("fixtures");
              }} style={{width:"100%",padding:"13px",background:primaryCol,border:"none",borderRadius:12,color:contrastText(primaryCol),fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1}}>
                📅 ADD TO TRAINING CALENDAR
              </button>
            )}
          </>}
        </div>
      );
    }

    return (
      <div style={{...G.panel,paddingBottom:100}}>
        <div style={{...G.h2,marginBottom:4}}>Drills & Skills</div>
        <div style={{fontSize:12,color:"rgba(255,255,255,0.38)",marginBottom:14}}>{club.sport==="Football"?"Football":club.sport==="Swimming"?"Swimming":"Hockey"} · Tap any item for full details</div>

        {/* Main tabs */}
        <div style={{display:"flex",gap:0,marginBottom:14,background:"rgba(255,255,255,0.04)",borderRadius:10,padding:3}}>
          {[{k:"skills",l:"🎓 Skills"},{k:"drills",l:`${club.sport==="Football"?"⚽":club.sport==="Swimming"?"🏊":"🏑"} Drills`}].map(t=>(
            <button key={t.k} onClick={()=>{setDrillTab(t.k);setFilterCat("all");setFilterDiff("all");}} style={{flex:1,padding:"9px",background:drillTab===t.k?primaryCol:"transparent",border:"none",borderRadius:8,color:drillTab===t.k?contrastText(primaryCol):"rgba(255,255,255,0.45)",fontSize:13,fontWeight:700,cursor:"pointer"}}>
              {t.l}
            </button>
          ))}
        </div>

        {/* Search */}
        <input
          style={{...G.input,marginBottom:10}}
          placeholder="Search..."
          value={searchQ}
          onChange={e=>setSearchQ(e.target.value)}
        />

        {/* Filters */}
        <div style={{display:"flex",gap:6,marginBottom:8,overflowX:"auto",paddingBottom:4}}>
          {cats.map(c=>(
            <button key={c} onClick={()=>setFilterCat(c)} style={{padding:"5px 12px",background:filterCat===c?primaryCol:"rgba(255,255,255,0.04)",border:`1px solid ${filterCat===c?primaryCol:"rgba(255,255,255,0.08)"}`,borderRadius:20,color:filterCat===c?contrastText(primaryCol):"rgba(255,255,255,0.45)",fontSize:11,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>
              {c==="all"?"All Categories":c}
            </button>
          ))}
        </div>
        <div style={{display:"flex",gap:6,marginBottom:14,overflowX:"auto",paddingBottom:4}}>
          {diffs.map(d=>(
            <button key={d} onClick={()=>setFilterDiff(d)} style={{padding:"5px 12px",background:filterDiff===d?diffCol(d):"rgba(255,255,255,0.04)",border:`1px solid ${filterDiff===d?diffCol(d):"rgba(255,255,255,0.08)"}`,borderRadius:20,color:filterDiff===d?"#fff":"rgba(255,255,255,0.45)",fontSize:11,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>
              {d==="all"?"All Levels":d}
            </button>
          ))}
        </div>

        {/* Results count */}
        <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",marginBottom:10}}>{filtered.length} {drillTab==="skills"?"skills":"drills"} found</div>

        {/* Item cards */}
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {filtered.map(item=>(
            <div key={item.id} onClick={()=>setSelectedItem(item)} style={{...G.card,cursor:"pointer",display:"flex",alignItems:"center",gap:12,padding:"12px 14px"}}>
              <div style={{width:46,height:46,borderRadius:10,background:`${primaryCol}18`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0}}>{item.icon||"🏑"}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
                  <div style={{fontSize:14,fontWeight:700,color:"#fff"}}>{item.name}</div>
                  {favourites.includes(item.id)&&<span style={{fontSize:12}}>❤️</span>}
                </div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  <span style={{fontSize:10,color:primaryCol,fontWeight:600}}>{item.cat}</span>
                  <span style={{fontSize:10,color:"rgba(255,255,255,0.35)"}}>· {item.diff}</span>
                  {item.type==="drill"&&<span style={{fontSize:10,color:"rgba(255,255,255,0.35)"}}>· {item.players} players</span>}
                </div>
              </div>
              <div style={{fontSize:18,color:"rgba(255,255,255,0.2)",flexShrink:0}}>›</div>
            </div>
          ))}
          {filtered.length===0&&(
            <div style={{textAlign:"center",padding:"32px 20px",color:"rgba(255,255,255,0.3)",fontSize:13}}>No results — try different filters</div>
          )}
        </div>

        <div style={{marginTop:20,padding:"12px 14px",background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:10,textAlign:"center"}}>

        </div>
      </div>
    );
  };


  const renderPanel = () => {
    switch(panel) {
      case "home":        return <HomePanel/>;
      case "fixtures":    return <CalendarPanel/>;
      case "calendar":    return <CalendarPanel/>;
      case "clubhub":     return <ClubHubPanel/>;
      case "coach":       return <CoachPanel/>;
      case "players":     return <PlayersPanel/>;
      case "pending":     return <PendingPanel/>;
      case "drills":      return <DrillsPanel/>;
      case "staff-detail": return <StaffDetailPanel/>;
      case "detail":      return <DetailPanel/>;
      case "squad-cards": return <SquadCardsPanel teamFilter={squadCardsTeam}/>;
      case "results":     return <ResultsPanel/>;
      case "payments":    return <PaymentsPanel/>;
      case "events":      return <EventsPanel/>;
      case "festivals":   return <FestivalsPanel/>;
      case "shop":        return <ShopPanel/>;
      case "club-settings": return <ClubSettingsPanel/>;
      default:
        if(panel.startsWith("lineup-view-")) return <LineupViewPanel/>;
        if(panel.startsWith("lineup-"))      return <LineupPanel/>;
        if(panel.startsWith("team-"))        return <TeamProfilePanel/>;
        if(panel==="messages")               return <MessagesPanel/>;
        if(panel==="profile-settings")       return <ProfileSettingsPanel/>;
        return <HomePanel/>;
    }
  };


  return (
    <div style={G.app}>
      <StyleTag/>
      <div style={G.header}>
        <div style={{width:34,height:34,borderRadius:"50%",background:`linear-gradient(135deg,${primaryCol},${secondaryCol})`,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Barlow Condensed',sans-serif",fontSize:11,fontWeight:800,color:"#fff",overflow:"hidden",flexShrink:0}}>
          {club.logo?<img src={club.logo} alt="" style={{width:"100%",height:"100%",objectFit:"cover",objectPosition:club.logoPos||"center center"}}/>:inits(club.name||"P4M")}
        </div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:15,fontWeight:800,color:"#fff",lineHeight:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{club.name||"My Club"}</div>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.35)",marginTop:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{club.sport||"Sport"} · {currentUser?.name||""} · {ROLES.find(r=>r.r===currentUser?.role)?.name||""}</div>
        </div>
        {/* Membership badge + log out */}
        <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:5,flexShrink:0,marginLeft:8}}>
          {club.subscriptionStatus==="founding" ? (
            <div style={{display:"flex",alignItems:"center",gap:4,background:"rgba(234,179,8,0.12)",border:"1px solid rgba(234,179,8,0.35)",borderRadius:6,padding:"4px 9px"}}>
              <span style={{fontSize:9}}>🏅</span>
              <span style={{fontSize:9,fontWeight:800,color:"#eab308",letterSpacing:0.5,fontFamily:"'Barlow Condensed',sans-serif"}}>Founding Member</span>
            </div>
          ) : isPro ? (
            <div style={{display:"flex",alignItems:"center",gap:4,background:"rgba(34,197,94,0.1)",border:"1px solid rgba(34,197,94,0.3)",borderRadius:6,padding:"4px 9px"}}>
              <span style={{fontSize:9}}>✓</span>
              <span style={{fontSize:9,fontWeight:800,color:"#22c55e",letterSpacing:0.5,fontFamily:"'Barlow Condensed',sans-serif"}}>Club Pro</span>
            </div>
          ) : (
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <span style={{fontSize:9,fontWeight:600,color:"rgba(255,255,255,0.35)",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:0.3}}>Club Membership</span>
              <button onClick={()=>setScreen("subscribe")} style={{display:"flex",alignItems:"center",gap:4,background:primaryCol,border:"none",borderRadius:6,padding:"4px 9px",cursor:"pointer"}}>
                <span style={{fontSize:9,fontWeight:800,color:"#fff",letterSpacing:0.5,fontFamily:"'Barlow Condensed',sans-serif"}}>Upgrade →</span>
              </button>
            </div>
          )}
          <button onClick={async()=>{await db.signOut();setCurrentUser(null);setClubId(null);setScreen("auth");setAuthMode("login");setPanel("home");}} style={{background:"none",border:"none",color:"rgba(255,255,255,0.3)",fontSize:10,fontWeight:600,cursor:"pointer",padding:0,letterSpacing:0.3}}>Log out</button>
        </div>
      </div>
      <div style={G.content}>{renderPanel()}</div>
      <nav style={G.nav}>
        {coreNavItems.map(n=>(
          <button key={n.p} data-tour={n.p==="players"?"nav-players":undefined} onClick={()=>navigate(n.p)} style={{...G.navBtn(activeNav===n.p,primaryCol)}}>
            {activeNav===n.p&&<div style={{position:"absolute",top:0,left:"15%",right:"15%",height:2,background:primaryCol,borderRadius:"0 0 2px 2px"}}/>}
            <span style={{fontSize:18,lineHeight:1}}>{n.icon}</span>
            <span>{n.label}</span>
          </button>
        ))}
        <button data-tour="nav-more" onClick={()=>setMenuOpen(o=>!o)} style={{...G.navBtn(menuOpen,primaryCol)}}>
          {menuOpen&&<div style={{position:"absolute",top:0,left:"15%",right:"15%",height:2,background:primaryCol,borderRadius:"0 0 2px 2px"}}/>}
          <span style={{fontSize:18,lineHeight:1}}>☰</span>
          <span>More</span>
        </button>
      </nav>
      {menuOpen&&(
        <div style={{position:"fixed",bottom:58,left:0,right:0,background:"#14141f",border:"1px solid rgba(255,255,255,0.1)",borderRadius:"16px 16px 0 0",zIndex:800,padding:"8px 0 12px",boxShadow:"0 -8px 32px rgba(0,0,0,0.6)"}}>
          <div style={{width:36,height:4,background:"rgba(255,255,255,0.15)",borderRadius:2,margin:"0 auto 12px"}}/>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:4,padding:"0 8px"}}>
            {moreNavItems.map(n=>(
              <button key={n.p}
                data-tour={n.p==="club-settings"?"nav-more-club":n.p==="payments"?"nav-more-payments":n.p==="results"?"nav-more-results":undefined}
                onClick={()=>{navigate(n.p);setMenuOpen(false);}}
                style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"10px 4px",background:activeNav===n.p?`${primaryCol}22`:"transparent",border:`1px solid ${activeNav===n.p?primaryCol:"rgba(255,255,255,0.06)"}`,borderRadius:10,cursor:"pointer",color:activeNav===n.p?primaryCol:"rgba(255,255,255,0.5)",fontSize:10,fontWeight:600,gap:4}}>
                <span style={{fontSize:22}}>{n.icon}</span>
                <span>{n.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {menuOpen&&<div style={{position:"fixed",inset:0,zIndex:799}} onClick={()=>setMenuOpen(false)}/>}

      <Modal open={modal==="fixture"} onClose={()=>setModal(null)} title={`Add ${FIXTURE_TERM}`}>
        <Input label={isSwimming?"Meet/Gala Name":"Opponent"} placeholder={isSwimming?"e.g. NW Regional Championships":"e.g. Pendle Forest"} value={fixForm.opponent} onChange={e=>setFixForm(f=>({...f,opponent:e.target.value}))}/>
        {isSwimming ? (
          <div style={{marginBottom:12}}>
            <FieldLabel>Age Groups / Squads</FieldLabel>
            <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
              {teams.map(t=>{
                const cur=Array.isArray(fixForm.teams)?fixForm.teams:(fixForm.team?[fixForm.team]:[]);
                const sel=cur.includes(t);
                return <div key={t} onClick={()=>setFixForm(f=>{
                  const c=Array.isArray(f.teams)?f.teams:(f.team?[f.team]:[]);
                  const n=sel?c.filter(x=>x!==t):[...c,t];
                  return {...f,teams:n,team:n[0]||""};
                })} style={{padding:"6px 12px",background:sel?`${primaryCol}22`:"rgba(255,255,255,0.04)",border:`1px solid ${sel?primaryCol:"rgba(255,255,255,0.1)"}`,borderRadius:20,fontSize:12,color:sel?primaryCol:"rgba(255,255,255,0.6)",cursor:"pointer",fontWeight:sel?600:400}}>{t}</div>;
              })}
            </div>
          </div>
        ) : (
          <Select label="Team" value={fixForm.team} onChange={e=>setFixForm(f=>({...f,team:e.target.value}))}>
            <option value="">Select team</option>{teams.map(t=><option key={t} value={t}>{t}</option>)}
        </Select>
        )}
        {!isSwimming&&<div style={{marginBottom:12}}>
          <FieldLabel>Home or Away?</FieldLabel>
          <div style={{display:"flex",gap:8}}>
            {[{v:true,l:"🏠 Home"},{v:false,l:"✈️ Away"}].map(opt=>(
              <button key={String(opt.v)} onClick={()=>setFixForm(f=>({...f,isHome:opt.v}))} style={{flex:1,padding:"10px",background:fixForm.isHome===opt.v?primaryCol:"rgba(255,255,255,0.04)",border:`1px solid ${fixForm.isHome===opt.v?primaryCol:"rgba(255,255,255,0.1)"}`,borderRadius:9,color:fixForm.isHome===opt.v?"#fff":"rgba(255,255,255,0.5)",fontWeight:700,fontSize:13,cursor:"pointer"}}>{opt.l}</button>
            ))}
          </div>
        </div>}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <Input label="Date" type="date" value={fixForm.date} onChange={e=>setFixForm(f=>({...f,date:e.target.value}))}/>
          <Input label="Time" type="time" value={fixForm.time} onChange={e=>setFixForm(f=>({...f,time:e.target.value}))}/>
        </div>
        <Input label="Location" placeholder="e.g. Westholme School" value={fixForm.location} onChange={e=>setFixForm(f=>({...f,location:e.target.value}))}/>
        <Btn col={primaryCol} onClick={()=>{
          if(!fixForm.opponent||!fixForm.date){showToast("Please enter opponent and date");return;}
          const nf={id:uid(),...fixForm,result:null,availability:{},goalsFor:null,goalsAgainst:null};
          setFixtures(fs=>[...fs,nf]);
          if(clubId)db.createFixture(clubId,nf).catch(console.error);
          setFixForm({opponent:"",date:"",time:"14:00",location:"",team:fixForm.team,isHome:true});
          setModal(null);showToast("Fixture added 📅");
        }}>{isSwimming?"Add Gala":"Add Fixture"}</Btn>
      </Modal>

      <Modal open={modal==="post"} onClose={()=>setModal(null)} title="New Post">
        <Select label="Type" value={postForm.tag} onChange={e=>setPostForm(f=>({...f,tag:e.target.value}))}>
          <option value="team">{isSwimming?"🏊 Squad post":"👥 Club post"}</option>
          <option value="announce">📢 Announcement</option>
        </Select>
        <Select label="Team" value={postForm.team} onChange={e=>setPostForm(f=>({...f,team:e.target.value}))}>
          <option value="all">{isSwimming?"All squads":"All teams"}</option>{teams.map(t=><option key={t} value={t}>{t}</option>)}
        </Select>
        <TextArea label="Message" placeholder="Share something with the club..." value={postForm.body} onChange={e=>setPostForm(f=>({...f,body:e.target.value}))} style={{minHeight:120}}/>
        <Btn col={primaryCol} onClick={()=>{
          if(!postForm.body.trim()&&!postForm.photo){showToast("Write something or add a photo");return;}
          const authorPlayer = players.find(pl=>pl.userId===currentUser?.id||pl.auth_id===currentUser?.id)||players.find(pl=>pl.name===currentUser?.name);
              const authorPhoto = authorPlayer?.photo || currentUser?.photo || null;
              setPosts(ps=>[{id:uid(),authorId:currentUser?.id,author:currentUser?.name||"Member",authorPhoto,body:filterSwears(postForm.body),tag:postForm.tag,team:postForm.team,time:Date.now(),likes:0,liked:false,pinned:false,photo:postForm.photo||null,comments:[],reactions:{}},...ps]);
          setPostForm({body:"",tag:"team",team:"",photo:null});showToast("Posted ✓");
        }}>Post</Btn>
      </Modal>

      <Modal open={modal==="add-player"} onClose={()=>setModal(null)} title={club.sport==="Swimming"?"Add Athlete":"Add Player"}>
        <Input label="Full name" placeholder={club.sport==="Swimming"?"e.g. Emma Johnson":"e.g. Tom Henderson"} value={playerForm?.name||""} onChange={e=>setPlayerForm(f=>({...f,name:e.target.value}))}/>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          {club.sport==="Swimming"
            ? <Select label="Distance" value={playerForm?.position||""} onChange={e=>setPlayerForm(f=>({...f,position:e.target.value}))}>
                <option value="">Select...</option>
                <option value="Short Course">Short Course</option>
                <option value="Long Course">Long Course</option>
                <option value="All Rounder">All Rounder</option>
                <option value="Sprint Specialist">Sprint Specialist</option>
                <option value="Distance Specialist">Distance Specialist</option>
              </Select>
            : <Input label="Position" placeholder="e.g. MID" value={playerForm?.position||""} onChange={e=>setPlayerForm(f=>({...f,position:e.target.value}))}/>}
          <Select label={isSwimming?"Age Group":"Team"} value={playerForm?.team||""} onChange={e=>setPlayerForm(f=>({...f,team:e.target.value}))}>
            <option value="">Select team</option>{teams.map(t=><option key={t} value={t}>{t}</option>)}
          </Select>
        </div>
        {club.sport==="Swimming"&&(
          <div style={{marginBottom:12}}>
            <FieldLabel>Specialist Events (select all that apply)</FieldLabel>
            <div style={{display:"flex",flexWrap:"wrap",gap:6,maxHeight:160,overflowY:"auto"}}>
              {["50m Freestyle","100m Freestyle","200m Freestyle","400m Freestyle","800m Freestyle","1500m Freestyle",
                "50m Backstroke","100m Backstroke","200m Backstroke",
                "50m Breaststroke","100m Breaststroke","200m Breaststroke",
                "50m Butterfly","100m Butterfly","200m Butterfly",
                "200m IM","400m IM","4x100m Freestyle Relay","4x200m Freestyle Relay","4x100m Medley Relay"
              ].map(ev=>{
                const sel=(playerForm?.events||[]).includes(ev);
                return (
                  <div key={ev} onClick={()=>{setPlayerForm(f=>({...f,events:sel?(f.events||[]).filter(x=>x!==ev):[...(f.events||[]),ev]}));}}
                    style={{padding:"4px 10px",background:sel?`${primaryCol}22`:"rgba(255,255,255,0.04)",border:`1px solid ${sel?primaryCol:"rgba(255,255,255,0.1)"}`,borderRadius:20,fontSize:11,color:sel?primaryCol:"rgba(255,255,255,0.6)",cursor:"pointer",fontWeight:sel?600:400}}>
                    {ev}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <Input label="PIN (4–6 digits)" type="password" placeholder="Share privately with athlete" maxLength={6} value={playerForm?.pin||""} onChange={e=>setPlayerForm(f=>({...f,pin:e.target.value}))}/>
        <Btn col={primaryCol} onClick={()=>{
          if(!playerForm?.name){showToast("Please enter a name");return;}
          if(!playerForm?.pin||playerForm.pin.length<4){showToast("PIN must be at least 4 digits");return;}
          const newPlayer = {id:uid(),...playerForm,scores:{},fitness:{},feedback:[],photo:null,lastAssessed:null};
          setPlayers(ps=>[...ps,newPlayer]);
          if (clubId) db.createPlayer(clubId, newPlayer).catch(console.error);
          setPlayerForm({name:"",position:"",team:"",pin:"",events:[]});setModal(null);showToast(club.sport==="Swimming"?"Athlete added 🏊":"Player added 👤");
        }}>{club.sport==="Swimming"?"Add Athlete":"Add Player"}</Btn>
      </Modal>

      <Modal open={modal==="feedback"} onClose={()=>setModal(null)} title="Match Feedback">
        <Select label="Fixture" value={fbForm.fixIdx} onChange={e=>setFbForm(f=>({...f,fixIdx:e.target.value}))}>
          <option value="">Ad hoc / custom</option>{fixtures.map((f,i)=><option key={i} value={i}>{fmtShort(f.date)} vs {f.opponent}</option>)}
        </Select>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <Input label="Custom match" placeholder="vs ..." value={fbForm.custom} onChange={e=>setFbForm(f=>({...f,custom:e.target.value}))}/>
          <Select label="Result" value={fbForm.result} onChange={e=>setFbForm(f=>({...f,result:e.target.value}))}>
            <option value="">—</option><option value="W">W</option><option value="D">D</option><option value="L">L</option>
          </Select>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
          <div><FieldLabel>Goals For</FieldLabel><select style={{...G.select,marginBottom:0}} value={fbForm.goalsFor||"0"} onChange={e=>setFbForm(f=>({...f,goalsFor:e.target.value}))}>{Array.from({length:21},(_,i)=><option key={i} value={i}>{i}</option>)}</select></div>
          <div><FieldLabel>Goals Against</FieldLabel><select style={{...G.select,marginBottom:0}} value={fbForm.goalsAgainst||"0"} onChange={e=>setFbForm(f=>({...f,goalsAgainst:e.target.value}))}>{Array.from({length:21},(_,i)=><option key={i} value={i}>{i}</option>)}</select></div>
        </div>
        <TextArea label="Feedback" placeholder="What went well, what to improve..." value={fbForm.note} onChange={e=>setFbForm(f=>({...f,note:e.target.value}))} style={{minHeight:120}}/>
        <Btn col={primaryCol} onClick={()=>{
          if(!fbForm.note.trim()||!currentPlayer){showToast("Please write feedback");return;}
          let match="Match",date=new Date().toISOString().split("T")[0];
          if(fbForm.fixIdx!==""){const fix=fixtures[parseInt(fbForm.fixIdx)];if(fix){match=`vs ${fix.opponent}`;date=fix.date;}}
          else if(fbForm.custom) match=fbForm.custom;
          const fb={match,date,result:fbForm.result,goalsFor:fbForm.goalsFor?parseInt(fbForm.goalsFor):null,goalsAgainst:fbForm.goalsAgainst?parseInt(fbForm.goalsAgainst):null,note:fbForm.note,playerResponse:null};
          const updated={...currentPlayer,feedback:[fb,...(currentPlayer.feedback||[])]};
          setCurrentPlayer(updated);setPlayers(ps=>ps.map(p=>p.id===currentPlayer.id?updated:p));
          if(clubId) db.addFeedback(clubId,currentPlayer.id,fb).catch(console.error);
          setFbForm({fixIdx:"",custom:"",result:"",goalsFor:"",goalsAgainst:"",note:""});setModal(null);showToast("Feedback added 📝");
        }}>Add Feedback</Btn>
      </Modal>

      <Modal open={modal==="payment"} onClose={()=>setModal(null)} title="Request Payment">
        <Input label="Title" placeholder="e.g. April Match Fee" value={payForm.title} onChange={e=>setPayForm(f=>({...f,title:e.target.value}))}/>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <Input label="Amount (£)" type="number" placeholder="5.00" value={payForm.amount} onChange={e=>setPayForm(f=>({...f,amount:e.target.value}))}/>
          <Select label="Type" value={payForm.type} onChange={e=>setPayForm(f=>({...f,type:e.target.value}))}>
            <option value="match">Match Fee</option><option value="membership">Membership</option><option value="fine">Fine</option><option value="other">Other</option>
          </Select>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <Input label="Due date" type="date" value={payForm.dueDate} onChange={e=>setPayForm(f=>({...f,dueDate:e.target.value}))}/>
          <Select label="Team" value={payForm.team} onChange={e=>setPayForm(f=>({...f,team:e.target.value}))}>
            <option value="all">{isSwimming?"All squads":"All teams"}</option>{teams.map(t=><option key={t} value={t}>{t}</option>)}
          </Select>
        </div>
        <Btn col={primaryCol} onClick={()=>{
          if(!payForm.title||!payForm.amount){showToast("Please fill all fields");return;}
          const np={id:uid(),...payForm,amount:parseFloat(payForm.amount),status:"pending"};
          setPayments(ps=>[...ps,np]);
          if(clubId){try{db.createPayment(clubId,np);}catch(e){console.error(e);}}
          setPayForm({title:"",amount:"",type:"match",dueDate:"",team:""});setModal(null);showToast("Payment request created 💸");
        }}>Create Request</Btn>
      </Modal>

      <Modal open={modal==="league-table"} onClose={()=>setModal(null)} title="Add League Team">
        <Input label="Team name" placeholder="e.g. Pendle Forest" value={ltForm.team} onChange={e=>setLtForm(f=>({...f,team:e.target.value}))}/>
        <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:8}}>
          {[["P","p"],["W","w"],["D","d"],["L","l"],["GF","gf"]].map(([l,k])=>(
            <div key={k}><FieldLabel>{l}</FieldLabel><input type="number" min={0} value={ltForm[k]} onChange={e=>setLtForm(f=>({...f,[k]:parseInt(e.target.value)||0}))} style={{...G.input,textAlign:"center",padding:"8px 4px"}}/></div>
          ))}
        </div>
        <Btn col={primaryCol} style={{marginTop:4}} onClick={()=>{
          if(!ltForm.team){showToast("Enter team name");return;}
          setLeagueTable(lt=>[...lt,{...ltForm,ga:ltForm.p-(ltForm.gf||0)}]);
          setLtForm({team:"",p:0,w:0,d:0,l:0,gf:0});setModal(null);showToast("Team added ✓");
        }}>Add to Table</Btn>
      </Modal>

      <Modal open={modal==="add-event"} onClose={()=>setModal(null)} title="Add Event">
        <Input label="Event name" placeholder="e.g. End of Season Awards" value={eventForm.title} onChange={e=>setEventForm(f=>({...f,title:e.target.value}))}/>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <Input label="Date" type="date" value={eventForm.date} onChange={e=>setEventForm(f=>({...f,date:e.target.value}))}/>
          <Input label="Time" type="time" value={eventForm.time} onChange={e=>setEventForm(f=>({...f,time:e.target.value}))}/>
        </div>
        <Input label="Location" placeholder="e.g. Clitheroe Golf Club" value={eventForm.location} onChange={e=>setEventForm(f=>({...f,location:e.target.value}))}/>
        <TextArea label="Description" placeholder="Details about the event..." value={eventForm.description} onChange={e=>setEventForm(f=>({...f,description:e.target.value}))} style={{minHeight:80}}/>
        <div style={{marginBottom:12}}>
          <FieldLabel>Event photo (optional)</FieldLabel>
          <label style={{display:"flex",alignItems:"center",gap:8,padding:"10px 14px",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:10,cursor:"pointer",fontSize:13,color:eventForm.photo?"#22c55e":"rgba(255,255,255,0.5)"}}>
            {eventForm.photo?"📷 Photo added ✓":"📷 Add photo"}
            <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>{const f=e.target.files[0];if(!f)return;const r=new FileReader();r.onload=ev=>setEventForm(ef=>({...ef,photo:ev.target.result}));r.readAsDataURL(f);}}/>
          </label>
        </div>
        <Btn col={primaryCol} onClick={()=>{
          if(!eventForm.title||!eventForm.date){showToast("Please enter name and date");return;}
          const ne={id:Date.now().toString(),...eventForm,rsvp:{}};
          setEvents(es=>[...es,ne]);
          if(clubId) db.createEvent(clubId,{title:ne.title,date:ne.date,time:ne.time,location:ne.location,description:ne.description,type:ne.type,photo_url:ne.photo}).catch(console.error);
          setEventForm({title:"",date:"",time:"",location:"",description:"",type:"social",photo:null});
          setModal(null);showToast("Event added 🎉");
        }}>Add Event</Btn>
      </Modal>

      <Modal open={modal==="add-festival"} onClose={()=>setModal(null)} title="Add Tournament / Cup">
        <Input label="Name" placeholder="e.g. NW Junior Festival U12 / Club Cup Final" value={festivalForm.title} onChange={e=>setFestivalForm(f=>({...f,title:e.target.value}))}/>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <Input label="Date" type="date" value={festivalForm.date} onChange={e=>setFestivalForm(f=>({...f,date:e.target.value}))}/>
          <Select label="Type" value={festivalForm.type||"junior"} onChange={e=>setFestivalForm(f=>({...f,type:e.target.value}))}>
            <option value="junior">Junior Festival</option>
            <option value="adult">Adult Tournament</option>
            <option value="cup">Cup Competition</option>
          </Select>
        </div>
        {(!festivalForm.type||festivalForm.type==="junior")&&(
          <Select label="Age Group" value={festivalForm.ageGroup} onChange={e=>setFestivalForm(f=>({...f,ageGroup:e.target.value}))}>
            {["U8","U10","U12","U14","U16","U18"].map(a=><option key={a} value={a}>{a}</option>)}
          </Select>
        )}
        <Input label="Location" placeholder="e.g. Altrincham HC" value={festivalForm.location} onChange={e=>setFestivalForm(f=>({...f,location:e.target.value}))}/>
        <TextArea label="Description" placeholder="Format, teams, details..." value={festivalForm.description} onChange={e=>setFestivalForm(f=>({...f,description:e.target.value}))} style={{minHeight:70}}/>
        <div style={{marginBottom:12}}>
          <FieldLabel>Photo (optional)</FieldLabel>
          <label style={{display:"flex",alignItems:"center",gap:8,padding:"10px 14px",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:10,cursor:"pointer",fontSize:13,color:festivalForm.photo?"#22c55e":"rgba(255,255,255,0.5)"}}>
            {festivalForm.photo?"📷 Photo added ✓":"📷 Add photo"}
            <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>{const f=e.target.files[0];if(!f)return;const r=new FileReader();r.onload=ev=>setFestivalForm(ef=>({...ef,photo:ev.target.result}));r.readAsDataURL(f);}}/>
          </label>
        </div>
        <Btn col={primaryCol} onClick={()=>{
          if(!festivalForm.title||!festivalForm.date){showToast("Please enter name and date");return;}
          const nt={id:Date.now().toString(),...festivalForm,availability:{}};
          setFestivals(fs=>[...fs,nt]);
          if(clubId) db.createTournament(clubId,{title:nt.title,date:nt.date,location:nt.location,description:nt.description,age_group:nt.ageGroup,type:nt.type,photo_url:nt.photo}).catch(console.error);
          setFestivalForm({title:"",date:"",location:"",description:"",ageGroup:"U12",photo:null,type:"junior"});
          setModal(null);showToast("Tournament added 🏆");
        }}>Add Tournament</Btn>
      </Modal>

      <Modal open={modal==="swim-feedback"} onClose={()=>setModal(null)} title="Swimming Meet Feedback">
        <Input label="Meet name" placeholder="e.g. NW Regional Championships" value={fbForm.custom} onChange={e=>setFbForm(f=>({...f,custom:e.target.value}))}/>
        <Input label="Date" type="date" value={fbForm.fixIdx} onChange={e=>setFbForm(f=>({...f,fixIdx:e.target.value}))}/>
        <div style={{marginBottom:12}}>
          <FieldLabel>Races & Times</FieldLabel>
          {(fbForm.races||[]).map((r,i)=>(
            <div key={i} style={{display:"flex",gap:8,marginBottom:6,alignItems:"center"}}>
              <div style={{flex:2,fontSize:12,color:"rgba(255,255,255,0.7)",background:"rgba(255,255,255,0.05)",padding:"6px 10px",borderRadius:7}}>{r.event}</div>
              <div style={{flex:1,fontSize:12,color:primaryCol,fontWeight:700,textAlign:"center"}}>{r.time}</div>
              {r.pb&&<span style={{fontSize:10,color:"#f0b429",fontWeight:700}}>PB</span>}
              <button onClick={()=>setFbForm(f=>({...f,races:f.races.filter((_,j)=>j!==i)}))} style={{background:"none",border:"none",color:"rgba(255,255,255,0.3)",cursor:"pointer",fontSize:12}}>✕</button>
            </div>
          ))}
          <SwimRaceAdder onAdd={race=>setFbForm(f=>({...f,races:[...(f.races||[]),race]}))} primaryCol={primaryCol}/>
        </div>
        <TextArea label="Coach notes" placeholder="Overall performance, technique observations, areas to improve..." value={fbForm.note} onChange={e=>setFbForm(f=>({...f,note:e.target.value}))} style={{minHeight:80}}/>
        <Btn col={primaryCol} onClick={()=>{
          if(!fbForm.custom||!currentPlayer){showToast("Enter meet name");return;}
          const fb={match:fbForm.custom,date:fbForm.fixIdx||new Date().toISOString().split("T")[0],result:"",note:fbForm.note,races:fbForm.races||[],playerResponse:null,type:"swim"};
          const updated={...currentPlayer,feedback:[fb,...(currentPlayer.feedback||[])]};
          setCurrentPlayer(updated);setPlayers(ps=>ps.map(p=>p.id===currentPlayer.id?updated:p));
          if(clubId) db.addFeedback(clubId,currentPlayer.id,{...fb,races:fb.races||[]}).catch(console.error);
          setFbForm({fixIdx:"",custom:"",result:"",goalsFor:"",goalsAgainst:"",note:"",races:[]});
          setModal(null);showToast("Meet feedback added 🏊");
        }}>Add Meet Feedback</Btn>
      </Modal>

      {/* Photo Tips Modal */}
      <Modal open={modal==="photo-tips"} onClose={()=>setModal(null)} title="📷 Getting the Best Player Card Photo">
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {[
            {icon:"🎨",title:"Plain background",tip:"Stand against a single-colour wall — white, grey or your club colour. Avoid busy backgrounds."},
            {icon:"💡",title:"Good lighting",tip:"Face a window or light source. Bright even light gives the cleanest background removal."},
            {icon:"📐",title:"Portrait framing",tip:"Hold phone upright. Frame from just above your head to your waist."},
            {icon:"🧍",title:"Face the camera",tip:"Look straight at the camera, stand upright."},
            {icon:"🎽",title:"Wear your kit",tip:"Club kit looks best. Make sure it contrasts with the background."},
            {icon:"📱",title:"Use daylight",tip:"Daylight gives the cleanest result. Avoid harsh artificial lighting."},
          ].map(({icon,title,tip})=>(
            <div key={title} style={{display:"flex",gap:12,padding:"10px 12px",background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:10}}>
              <div style={{fontSize:22,flexShrink:0}}>{icon}</div>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:"#fff",marginBottom:2}}>{title}</div>
                <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",lineHeight:1.5}}>{tip}</div>
              </div>
            </div>
          ))}
          <div style={{padding:"10px 14px",background:"rgba(230,57,70,0.1)",border:"1px solid rgba(230,57,70,0.3)",borderRadius:10,fontSize:12,color:"rgba(255,255,255,0.7)",lineHeight:1.5}}>
            💡 A plain white wall behind a coloured kit gives the cleanest cutout every time.
          </div>
        </div>
      </Modal>

      {/* Photo Preview Modal */}
      <Modal open={modal==="photo-preview"} onClose={()=>{setModal(null);setPhotoPreview(null);}} title="📷 Photo Preview">
        {photoPreview&&(
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <div style={{display:"grid",gridTemplateColumns:photoPreview.processed?"1fr 1fr":"1fr",gap:10}}>
              <div style={{textAlign:"center"}}>
                <div style={{fontSize:10,color:"rgba(255,255,255,0.4)",marginBottom:6,fontWeight:700,letterSpacing:1}}>ORIGINAL</div>
                <img src={photoPreview.original} alt="" style={{width:"100%",maxHeight:200,objectFit:"cover",borderRadius:10,border:"1px solid rgba(255,255,255,0.1)"}}/>
              </div>
              {photoPreview.processed&&(
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:10,color:"rgba(255,255,255,0.4)",marginBottom:6,fontWeight:700,letterSpacing:1}}>BG REMOVED</div>
                  <div style={{background:"repeating-conic-gradient(rgba(255,255,255,0.05) 0% 25%,transparent 0% 50%) 0 0/16px 16px",borderRadius:10,overflow:"hidden"}}>
                    <img src={photoPreview.processed} alt="" style={{width:"100%",maxHeight:200,objectFit:"cover",display:"block"}}/>
                  </div>
                </div>
              )}
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {!photoPreview.processed&&(
                <button onClick={runBgRemoval} disabled={photoPreview.processing}
                  style={{padding:"11px",background:primaryCol,border:"none",borderRadius:10,color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",opacity:photoPreview.processing?0.6:1}}>
                  {photoPreview.processing?"⏳ Processing...":"✨ Remove Background"}
                </button>
              )}
              {photoPreview.processed&&(
                <>
                  <button onClick={()=>applyPhotoFromPreview(photoPreview.processed)}
                    style={{padding:"11px",background:primaryCol,border:"none",borderRadius:10,color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer"}}>
                    ✓ Use Background Removed Version
                  </button>
                  <button onClick={runBgRemoval}
                    style={{padding:"9px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:10,color:"rgba(255,255,255,0.6)",fontSize:12,cursor:"pointer"}}>
                    🔄 Try Again
                  </button>
                </>
              )}
              <button onClick={()=>applyPhotoFromPreview(photoPreview.original)}
                style={{padding:"11px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.12)",borderRadius:10,color:"rgba(255,255,255,0.7)",fontSize:13,fontWeight:600,cursor:"pointer"}}>
                📷 Use Original (no bg removal)
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Add Staff Modal */}
      <Modal open={modal==="add-staff"} onClose={()=>setModal(null)} title="Add Staff Member">
        <Input label="Full name" placeholder="e.g. Sarah Jones" value={playerForm?.name||""} onChange={e=>setPlayerForm(f=>({...f,name:e.target.value}))}/>
        <FieldLabel>Role</FieldLabel>
        <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:14}}>
          {["Lead Coach","Coach","Assistant Coach","Captain","Vice Captain","Treasurer","Committee","Admin","Volunteer"].map(r=>(
            <button key={r} onClick={()=>setPlayerForm(f=>({...f,position:r}))}
              style={{padding:"6px 12px",background:playerForm?.position===r?primaryCol:"rgba(255,255,255,0.04)",border:`1px solid ${playerForm?.position===r?primaryCol:"rgba(255,255,255,0.1)"}`,borderRadius:20,color:playerForm?.position===r?"#fff":"rgba(255,255,255,0.6)",fontSize:12,fontWeight:600,cursor:"pointer"}}>
              {r}
            </button>
          ))}
        </div>
        <FieldLabel>Assigned to</FieldLabel>
        <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap"}}>
          <button onClick={()=>setPlayerForm(f=>({...f,team:"all"}))}
            style={{padding:"7px 14px",background:playerForm?.team==="all"?primaryCol:"rgba(255,255,255,0.04)",border:`1px solid ${playerForm?.team==="all"?primaryCol:"rgba(255,255,255,0.1)"}`,borderRadius:20,color:playerForm?.team==="all"?"#fff":"rgba(255,255,255,0.6)",fontSize:12,fontWeight:600,cursor:"pointer"}}>
            🏟 Whole Club
          </button>
          {teams.map(t=>(
            <button key={t} onClick={()=>setPlayerForm(f=>({...f,team:t}))}
              style={{padding:"7px 14px",background:playerForm?.team===t?primaryCol:"rgba(255,255,255,0.04)",border:`1px solid ${playerForm?.team===t?primaryCol:"rgba(255,255,255,0.1)"}`,borderRadius:20,color:playerForm?.team===t?"#fff":"rgba(255,255,255,0.6)",fontSize:12,fontWeight:600,cursor:"pointer"}}>
              {t}
            </button>
          ))}
        </div>
        <Btn col={primaryCol} onClick={()=>{
          if(!playerForm?.name){showToast("Please enter a name");return;}
          if(!playerForm?.position){showToast("Please select a role");return;}
          if(!playerForm?.team){showToast("Please select a team or whole club");return;}
          const r=playerForm.position==="Lead Coach"?"lead_coach":playerForm.position==="Admin"?"admin":playerForm.position==="Captain"||playerForm.position==="Vice Captain"?"captain":"coach";
          const newStaff={id:uid(),name:playerForm.name,role:r,position:playerForm.position,team:playerForm.team,approved:true,photo:null,email:`${playerForm.name.toLowerCase().replace(/\s/g,"")}@staff.local`};
          setUsers(us=>[...us,newStaff]);
          supabase.from("members").insert({club_id:clubId,name:newStaff.name,email:newStaff.email,role:newStaff.role,team:newStaff.team,approved:true,auth_id:null}).then(()=>{}).catch(console.error);
          setPlayerForm({name:"",position:"",team:"",events:[]});
          setModal(null);showToast(`${playerForm.name} added to staff ✓`);
        }}>Add Staff Member</Btn>
      </Modal>

      {modal&&modal.startsWith("lightbox-")&&(()=>{
        const postId=modal.replace("lightbox-","");
        const post=posts.find(p=>p.id===postId);
        return post?.photo?(
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.95)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setModal(null)}>
            <img src={post.photo} alt="" style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain"}}/>
            <button style={{position:"absolute",top:16,right:16,background:"rgba(255,255,255,0.1)",border:"none",borderRadius:"50%",width:36,height:36,color:"#fff",fontSize:18,cursor:"pointer"}}>✕</button>
          </div>
        ):null;
      })()}

      {/* Notification pre-prompt — friendly ask before browser prompt */}
      {showNotifPrompt&&(
        <div style={{
          position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",
          zIndex:10000,display:"flex",alignItems:"flex-end",padding:"0 0 80px",
        }} onClick={()=>setShowNotifPrompt(false)}>
          <div onClick={e=>e.stopPropagation()} style={{
            width:"100%",background:"#14141f",
            borderRadius:"20px 20px 0 0",
            border:"1px solid rgba(255,255,255,0.08)",
            padding:"24px 20px",
          }}>
            <div style={{width:40,height:4,background:"rgba(255,255,255,0.15)",borderRadius:2,margin:"0 auto 20px"}}/>
            <div style={{fontSize:40,textAlign:"center",marginBottom:12}}>🔔</div>
            <div style={{fontSize:18,fontWeight:700,color:"#fff",textAlign:"center",marginBottom:8}}>Stay in the loop</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.5)",textAlign:"center",lineHeight:1.6,marginBottom:24}}>
              Get notified about training sessions, match feedback, approvals and club updates. You can turn this off any time.
            </div>
            <button onClick={async()=>{
              setShowNotifPrompt(false);
              await registerPushNotifications();
            }} style={{width:"100%",padding:"14px",background:primaryCol,border:"none",borderRadius:12,color:contrastText(primaryCol),fontSize:15,fontWeight:700,cursor:"pointer",marginBottom:10}}>
              Yes, enable notifications
            </button>
            <button onClick={()=>{
              setShowNotifPrompt(false);
              localStorage.setItem("p4m_notif_dismissed","1");
            }} style={{width:"100%",padding:"14px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:12,color:"rgba(255,255,255,0.45)",fontSize:14,cursor:"pointer"}}>
              Not now
            </button>
          </div>
        </div>
      )}

      {/* Notification permission banner — shown when blocked */}
      {showNotifBanner&&(
        <div style={{
          position:"fixed",bottom:70,left:12,right:12,
          background:"linear-gradient(135deg,#1a1a2e,#09090f)",
          border:"1px solid rgba(240,180,41,0.4)",
          borderRadius:16,padding:"14px 16px",
          zIndex:9999,
          boxShadow:"0 8px 32px rgba(0,0,0,0.8)",
          display:"flex",alignItems:"center",gap:12,
        }}>
          <div style={{fontSize:28,flexShrink:0}}>🔔</div>
          <div style={{flex:1}}>
            <div style={{fontSize:14,fontWeight:700,color:"#fff",marginBottom:2}}>Enable notifications</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.5)",lineHeight:1.4}}>
              To enable notifications: tap the padlock icon in your browser address bar → Notifications → Allow
            </div>
          </div>
          <button onClick={()=>setShowNotifBanner(false)} style={{padding:"6px 10px",background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:8,color:"rgba(255,255,255,0.5)",fontSize:11,cursor:"pointer",flexShrink:0}}>
            ✕
          </button>
        </div>
      )}

      {/* PWA Install Banner */}
      {showInstallBanner&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:99999,display:"flex",alignItems:"flex-end"}} onClick={()=>{setShowInstallBanner(false);localStorage.setItem("p4m_install_dismissed","1");}}>
          <div onClick={e=>e.stopPropagation()} style={{width:"100%",background:"linear-gradient(180deg,#0f0f1a,#09090f)",borderRadius:"24px 24px 0 0",padding:"28px 24px 40px",border:"1px solid rgba(255,255,255,0.08)"}}>
            {/* Handle */}
            <div style={{width:40,height:4,background:"rgba(255,255,255,0.15)",borderRadius:2,margin:"0 auto 24px"}}/>
            {/* Logo */}
            <div style={{display:"flex",justifyContent:"center",marginBottom:16}}>
              <div style={{width:80,height:80,borderRadius:20,background:`linear-gradient(135deg,${primaryCol},#1e3a8a)`,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:`0 8px 32px ${primaryCol}66`}}>
                <P4MLogo width={56}/>
              </div>
            </div>
            <div style={{textAlign:"center",marginBottom:24}}>
              <div style={{fontSize:22,fontWeight:800,color:"#fff",marginBottom:8,fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1}}>DOWNLOAD P4M</div>
              <div style={{fontSize:14,color:"rgba(255,255,255,0.55)",lineHeight:1.6}}>Install the app for the full experience — faster, works offline, and gets push notifications for training and match updates.</div>
            </div>
            {/* Features */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:24}}>
              {[["⚡","Faster loading"],["🔔","Push notifications"],["📵","Works offline"],["🏠","Home screen icon"]].map(([icon,label])=>(
                <div key={label} style={{display:"flex",alignItems:"center",gap:8,padding:"10px 12px",background:"rgba(255,255,255,0.04)",borderRadius:10,border:"1px solid rgba(255,255,255,0.06)"}}>
                  <span style={{fontSize:18}}>{icon}</span>
                  <span style={{fontSize:12,color:"rgba(255,255,255,0.6)",fontWeight:500}}>{label}</span>
                </div>
              ))}
            </div>
            {/* Install button — Android/Desktop */}
            {installPrompt&&(
              <button onClick={async()=>{
                installPrompt.prompt();
                const {outcome} = await installPrompt.userChoice;
                setInstallPrompt(null);
                setShowInstallBanner(false);
                if(outcome==="accepted") showToast("P4M installed ✓");
                else localStorage.setItem("p4m_install_dismissed","1");
              }} style={{width:"100%",padding:"16px",background:primaryCol,border:"none",borderRadius:14,color:contrastText(primaryCol),fontSize:16,fontWeight:800,cursor:"pointer",marginBottom:10,fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1}}>
                📲 INSTALL APP NOW
              </button>
            )}
            {/* iOS instructions */}
            {!installPrompt&&/iphone|ipad|ipod/i.test(navigator.userAgent)&&(
              <div style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:14,padding:"14px 16px",marginBottom:10}}>
                <div style={{fontSize:13,fontWeight:700,color:"#fff",marginBottom:8}}>To install on iPhone:</div>
                <div style={{fontSize:12,color:"rgba(255,255,255,0.55)",lineHeight:1.8}}>
                  1. Tap the <strong style={{color:"#fff"}}>Share</strong> button <span style={{fontSize:14}}>⬆️</span> in Safari<br/>
                  2. Scroll down and tap <strong style={{color:"#fff"}}>"Add to Home Screen"</strong><br/>
                  3. Tap <strong style={{color:"#fff"}}>"Add"</strong>
                </div>
              </div>
            )}
            <button onClick={()=>{
              setShowInstallBanner(false);
              setInstallPrompt(null);
              setInstallDismissed(true);
              localStorage.setItem("p4m_install_dismissed","1");
            }} style={{width:"100%",padding:"12px",background:"none",border:"1px solid rgba(255,255,255,0.08)",borderRadius:14,color:"rgba(255,255,255,0.35)",fontSize:13,cursor:"pointer"}}>
              Not now
            </button>
          </div>
        </div>
      )}

      {/* Download button — mobile browser only, hidden on desktop and when installed */}
      {!isPWA && !installDismissed && /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent) && (
        <div style={{
          position:"fixed", bottom:0, left:0, right:0,
          background:"linear-gradient(to top,rgba(9,9,15,0.98) 70%,transparent)",
          padding:"12px 16px 28px",
          zIndex:9000,
          pointerEvents:"none",
          display:"flex", justifyContent:"center",
        }}>
          <button
            onClick={async()=>{
              if(installPrompt) {
                // Trigger native install prompt immediately
                installPrompt.prompt();
                const {outcome} = await installPrompt.userChoice;
                setInstallPrompt(null);
                if(outcome==="accepted") {
                  showToast("P4M installed ✓");
                  localStorage.setItem("p4m_install_dismissed","1");
                }
              } else {
                // iOS — show step by step instructions
                setShowInstallBanner(true);
              }
            }}
            style={{
              pointerEvents:"all",
              padding:"14px 32px",
              background:primaryCol,
              border:"none",
              borderRadius:50,
              color:contrastText(primaryCol),
              fontSize:15,
              fontWeight:800,
              cursor:"pointer",
              fontFamily:"'Barlow Condensed',sans-serif",
              letterSpacing:1,
              boxShadow:`0 4px 24px ${primaryCol}99`,
              display:"flex", alignItems:"center", gap:8,
            }}>
            📲 Download the App
          </button>
        </div>
      )}

      {toast&&<Toast msg={toast}/>}

      {/* Onboarding tip modal */}
      {/* Product tour */}
      {isAdmin&&<TourOverlay/>}

      {onboardTip&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:99998,display:"flex",alignItems:"flex-end"}}
          onClick={()=>dismissTip(onboardTip)}>
          <div onClick={e=>e.stopPropagation()} style={{width:"100%",background:"linear-gradient(180deg,#0f0f1a,#09090f)",borderRadius:"20px 20px 0 0",padding:"24px 20px 36px",border:"1px solid rgba(255,255,255,0.08)"}}>
            <div style={{width:36,height:4,background:"rgba(255,255,255,0.15)",borderRadius:2,margin:"0 auto 20px"}}/>
            <div style={{fontSize:18,fontWeight:800,color:"#fff",marginBottom:8,fontFamily:"'Barlow Condensed',sans-serif"}}>{onboardTip.title}</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.6)",lineHeight:1.6,marginBottom:20}}>{onboardTip.msg}</div>
            <div style={{display:"flex",gap:10}}>
              {onboardTip.nextPanel&&onboardTip.btn!=="Got it"&&(
                <button onClick={()=>dismissTip(onboardTip, onboardTip.nextPanel)}
                  style={{flex:1,padding:"12px",background:primaryCol,border:"none",borderRadius:12,color:contrastText(primaryCol),fontSize:14,fontWeight:700,cursor:"pointer"}}>
                  {onboardTip.btn||"Let's go →"}
                </button>
              )}
              <button onClick={()=>dismissTip(onboardTip)}
                style={{flex:onboardTip.nextPanel&&onboardTip.btn!=="Got it"?0:1,padding:"12px 20px",background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:12,color:"rgba(255,255,255,0.5)",fontSize:14,cursor:"pointer"}}>
                {onboardTip.btn==="Got it"?"Got it ✓":"Skip"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
